import { DocumentMetadataForm } from './components/memory/DocumentMetadataForm';
import { DocumentViewer } from './components/memory/DocumentViewer';
import { DocumentAclForm } from './components/memory/DocumentAclForm';
// TRUTH-SOURCE-v18.10.69-PLANEACION-SUPABASE-DOMINANCE-RESTORE
// Restoration of planning logic and atomic persistence for Diagnostic/Projects.
import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';
import {
    Building, Building2, Users, Target, BarChart3, ChevronRight, Search, Plus,
    Filter, MoreHorizontal, Download, LayoutDashboard, Calendar,
    MessageSquare, Bell, Settings, LogOut, Shield, ChevronDown,
    CheckCircle2, AlertCircle, Clock, Trash2, Edit, Save, X, Menu,
    Zap, BookOpen, Sparkles, Send, MoreVertical, Edit3, Database,
    FileText, TrendingUp, Calculator, CheckSquare, ChevronLeft, Check, LayoutGrid, RotateCcw,
    Briefcase, FolderOpen, ArrowRight, ArrowLeft, PieChart as PieChartIcon,
    LogOut as LogOutIcon, MoreHorizontal as MoreHorizontalIcon, ShieldCheck,
    Copy, Activity, GripVertical, History, HelpCircle, Eye, Info, TrendingDown, User, Lightbulb,
    UserCheck, Layers, Trello, Book, Layout as LayoutIcon, PlusSquare, RefreshCw, AlertTriangle, File, Bot, Crown, Award, Globe, ShieldAlert, ShieldX, EyeOff, Lock, FilePlus
} from 'lucide-react';
import type { UserRole } from './lib/supabase-service';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    AreaChart,
    Area,
    LineChart,
    Line,
    ComposedChart
} from 'recharts';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { useLocation, useNavigate, Routes, Route, Link, useSearchParams, Navigate } from 'react-router-dom';
import * as db from './lib/supabase-service';
import { getStructureResponsibleLabel, STRUCTURE_RESPONSIBLES_LOAD_ERROR } from './lib/structure-responsibles';
import TrackingModule from './components/tracking/TrackingModule';
import { askAI } from './lib/ai-service';
import AIDeskCoparmex from './components/AIDeskCoparmex';
import OrgStructureModule from './components/OrgStructureModule';
import UserManagementModule from './components/UserManagementModule';
import { UsersRbacMockPanel } from './components/users/UsersRbacMockPanel';
import BackupRecoveryCenter from './components/admin/BackupRecoveryCenter';
import GovernanceOperationalControlCenter from './components/operational-control/GovernanceOperationalControlCenter';
import OperationalControlSettingsTab from './components/operational-control/OperationalControlSettingsTab';
import { AuditCardexPanel } from './components/admin/AuditCardexPanel';
import { recordAuditEvent } from './lib/audit-logger';
import { hasCapability } from './lib/rbac-capabilities';
import { canEditAuthorizedModule as canEditModule, canViewAuthorizedModule } from './lib/module-authorization';
import preflightRealData from '../docs/sync-backups/SNAPSHOT_PREFLIGHT_REAL_READONLY_v18.24.18A.json';

import { cn, getColWidthOption, cycleColWidth, GenericTableEditor, SWOTEditor } from './components/SharedComponents';
import {
    calculateProratedCompliance, calculateVPPerformance, getIndicatorMetrics, normalizeVPType, parseNumberSafe, SystemSettings, DEFAULT_SETTINGS, SettingsContext, useRole, canAvalVpPlan,
    ALL_MODULES, getUserModules, canAccessModule, getModulePermission, sanitizeAccessibleModules, parseModulePermission, getModulesForRole, safeModuleEquals, getElapsedCompletedMonths
} from './lib/shared-logic';

export {
    calculateProratedCompliance, calculateVPPerformance, SettingsContext, useRole,
    ALL_MODULES, getUserModules, canEditModule, canAccessModule, getModulePermission, sanitizeAccessibleModules, parseModulePermission, getModulesForRole, safeModuleEquals, getElapsedCompletedMonths
};
export type { SystemSettings };


import {
    safeUpdateContent,
    calculateDetailedProgress,
    calculateDetailedGoal,
    normalizeSectionContent,
    detectStructuralType,
    getNormalizedData,
    getSectionSchemaFromContent,
    resolveCanonicalSectionNumber,
    computeSectionMetrics,
    PLANNING_SECTION_CONTRACT
} from './lib/planning-logic';

import {
    ProyectosCardsEditor,
    DiagnosticoFormat,
    ComisionesManager
} from './components/PlanningEditors';









const normalize = (s: any) => {
    if (s === null || s === undefined) return "";
    return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
};

function canonicalInstanceType(type: any): 'COMISION' | 'COMITE' | 'CONSEJERIA' | string {
    const t = normalize(type || '').replace(/[\s_]/g, '');
    if (t === 'COMISION' || t === 'COMISIONESTRATEGICA' || t === 'COMISIONES') return 'COMISION';
    if (t === 'COMITE' || t === 'COMITES') return 'COMITE';
    if (t === 'CONSEJERIA' || t === 'CONSEJERIAS') return 'CONSEJERIA';
    return t;
}

const isVpPlanEvaluable = (inst: any): boolean => {
    return parseNumberSafe(inst?.planGoal ?? inst?.total_goal, 0) > 0;
};

const isVpKpiEvaluable = (inst: any): boolean => {
    return parseNumberSafe(inst?.kpiMeta ?? inst?.totalMeta, 0) > 0 && !!(inst?.kpiEligible || inst?.hasActiveKpis);
};

const buildVpPerformanceSettings = (instances: any[], settings: any) => {
    const activeInstances = (instances || []).filter((inst: any) => {
        const status = normalize(inst?.status || inst?.estado || '').replace(/[\s_]/g, '');
        return inst?.active !== false
            && inst?.is_active !== false
            && status !== 'INACTIVO'
            && status !== 'INACTIVE'
            && status !== 'ARCHIVED'
            && status !== 'ELIMINADO';
    });

    const counts = activeInstances.reduce((acc: Record<string, number>, inst: any) => {
        const type = canonicalInstanceType(inst?.type || inst?.commission_type);
        if (type === 'COMISION' || type === 'COMITE' || type === 'CONSEJERIA') {
            acc[type] = (acc[type] || 0) + 1;
        }
        return acc;
    }, { COMISION: 0, COMITE: 0, CONSEJERIA: 0 });

    return {
        ...settings,
        weight_comisiones: parseNumberSafe(settings?.weight_comisiones, 50) * Math.max(counts.COMISION, 1),
        weight_comites: parseNumberSafe(settings?.weight_comites, 30) * Math.max(counts.COMITE, 1),
        weight_consejerias: parseNumberSafe(settings?.weight_consejerias, 20) * Math.max(counts.CONSEJERIA, 1)
    };
};

const getVPId = (u: any) => u?.vicepresidency_id || u?.instance_id || null;

import { APP_VERSION as CENTRAL_VERSION, APP_DATE as CENTRAL_DATE, getComplianceVisualState } from './lib/shared-logic';

export const METRICS_ENGINE_VERSION = CENTRAL_VERSION;
export const APP_VERSION = "18.27.77";

export const getShortVersion = (versionStr: string): string => {
    const match = String(versionStr || '').match(/v?(\d+\.\d+\.\d+)/i);
    return match ? `v${match[1]}` : '';
};

export const SECURITY_BASELINE_VERSION = "v18.27.67-SECURITY-NO-REGRESSION-GATE";
export const APP_BUILD = "2026.07.17.RC1";
export const APP_DATE = CENTRAL_DATE;

const FORENSIC_MODE = false;

// --- SISTEMA DE NOTIFICACIONES GLOBAL ---
let globalShowToast: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void = () => { };
export const showToast = (msg: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    globalShowToast(msg, type);
};

// APP_DATE se declara arriba en el bloque de versiones core
export const APP_AUTHOR = 'IAPRIORI AI - Gobernanza INSTITUCIONAL';


if (typeof window !== 'undefined') {
    (window as any).APP_BUILD_INFO = { version: APP_VERSION, build: APP_BUILD };
}

const COMISION_TYPE = 'COMISION';
const INSTANCE_TYPES = [COMISION_TYPE, 'COMITE', 'CONSEJERIA'];
const SYNC_EXPECTED_PLAN_SECTION_COUNT = 9;

type RegularizationPhase = 'PLAN_BASE' | 'SECTIONS' | 'ITEMS' | 'VISUAL_SCHEMA' | 'GOALS' | 'READY';

type SyncDestinationPreflight = {
    commission_id: string;
    name: string;
    has_plan: boolean;
    plan_id: string | null;
    section_count: number;
    item_count: number;
    has_config_keys: boolean;
    total_goal: number;
    total_progress: number;
    missing_phase: RegularizationPhase;
    next_allowed_action: string;
    action_supported: boolean;
    snapshot_status: string;
};

type SelectiveMappingStatus = 'MATCH' | 'SUGERIDO' | 'NO_MATCH' | 'EXCLUIR';
type SelectiveMappingAction =
    | 'COPIAR_SECCION_COMPLETA'
    | 'ESTRUCTURA_FORMATO_METAS'
    | 'SOLO_ESTRUCTURA'
    | 'ESTRUCTURA_FORMATO'
    | 'ESTRUCTURA_METAS'
    | 'SOLO_FORMATO_VISUAL'
    | 'SOLO_METAS'
    | 'CONSERVAR_DESTINO'
    | 'EXCLUIR_SECCION'
    | 'MAPEO_MANUAL';
type SelectiveOperationalMode =
    | 'SOLO_ESTRUCTURA_ITEMS'
    | 'ESTRUCTURA_FORMATO'
    | 'ESTRUCTURA_METAS'
    | 'ESTRUCTURA_FORMATO_METAS';
type SelectiveMappingFilter =
    | 'TODAS'
    | 'SUGERIDAS'
    | 'MATCH'
    | 'EXCLUIDAS'
    | 'MANUALES'
    | 'RIESGO'
    | 'METAS'
    | 'FORMATO';

type SelectiveMappingRow = {
    source_key: string;
    source_section_id: string | number | null;
    source_section_number: number;
    source_section_name: string;
    source_order_index: number;
    target_section_id: string | null;
    target_section_number: number | null;
    target_section_name: string;
    target_order_index: number | null;
    match_by_name: boolean;
    match_by_order: boolean;
    status: SelectiveMappingStatus;
    default_action: SelectiveMappingAction;
    recommended_action: SelectiveMappingAction;
    impact_label: string;
    applies_to_instance: boolean;
    available_target_options: Array<{ id: string; label: string }>;
};

const SELECTIVE_MAPPING_ACTION_LABELS: Record<SelectiveMappingAction, string> = {
    COPIAR_SECCION_COMPLETA: 'Estructura + formato + metas',
    ESTRUCTURA_FORMATO_METAS: 'Estructura + formato + metas',
    SOLO_ESTRUCTURA: 'Solo estructura/items',
    ESTRUCTURA_FORMATO: 'Estructura + formato',
    ESTRUCTURA_METAS: 'Estructura + metas',
    SOLO_FORMATO_VISUAL: 'Solo formato visual',
    SOLO_METAS: 'Solo metas',
    CONSERVAR_DESTINO: 'Conservar destino',
    EXCLUIR_SECCION: 'Excluir sección',
    MAPEO_MANUAL: 'Mapeo manual'
};

const SELECTIVE_MAPPING_ACTION_HELP: Record<SelectiveMappingAction, string> = {
    COPIAR_SECCION_COMPLETA: 'No copia avances, evidencias ni datos capturados.',
    ESTRUCTURA_FORMATO_METAS: 'Alineación completa origen y destino.',
    SOLO_ESTRUCTURA: 'Copia secciones o herramientas faltantes.',
    ESTRUCTURA_FORMATO: 'Prepara estructura/items + formato, sin metas ni avances.',
    ESTRUCTURA_METAS: 'Prepara estructura/items + metas, sin tocar progreso.',
    SOLO_FORMATO_VISUAL: 'Copia columnas, visibilidad y configuración.',
    SOLO_METAS: 'Copia metas, no avances.',
    CONSERVAR_DESTINO: 'No modifica esta sección.',
    EXCLUIR_SECCION: 'No aplica al destino.',
    MAPEO_MANUAL: 'Elegir sección equivalente.'
};
const SELECTIVE_OPERATIONAL_MODE_META: Record<SelectiveOperationalMode, {
    label: string;
    shortLabel: string;
    description: string;
    preferredAction: SelectiveMappingAction;
    tone: string;
    preparedOnly: boolean;
}> = {
    SOLO_ESTRUCTURA_ITEMS: {
        label: 'Solo estructura',
        shortLabel: 'Solo estructura',
        description: 'Copia nombres, section_number, order_index e items estructurales.',
        preferredAction: 'SOLO_ESTRUCTURA',
        tone: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300',
        preparedOnly: false
    },
    ESTRUCTURA_FORMATO: {
        label: 'Estructura + formato',
        shortLabel: 'Estructura + formato',
        description: 'Prepara estructura/items con llaves visuales permitidas.',
        preferredAction: 'ESTRUCTURA_FORMATO',
        tone: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
        preparedOnly: true
    },
    ESTRUCTURA_METAS: {
        label: 'Estructura + metas',
        shortLabel: 'Estructura + metas',
        description: 'Prepara estructura/items con metas sin tocar current_value ni progress.',
        preferredAction: 'ESTRUCTURA_METAS',
        tone: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
        preparedOnly: true
    },
    ESTRUCTURA_FORMATO_METAS: {
        label: 'Estructura + formato + metas',
        shortLabel: 'Estructura + formato + metas',
        description: 'Prepara el flujo mas completo con protección total de datos capturados.',
        preferredAction: 'ESTRUCTURA_FORMATO_METAS',
        tone: 'border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-300',
        preparedOnly: false
    }
};
const SELECTIVE_MAPPING_FILTER_LABELS: Record<SelectiveMappingFilter, string> = {
    TODAS: 'Mostrar todas',
    SUGERIDAS: 'Solo sugeridas',
    MATCH: 'Solo match',
    EXCLUIDAS: 'Solo excluidas',
    MANUALES: 'Solo manuales',
    RIESGO: 'Solo con riesgo',
    METAS: 'Solo con metas',
    FORMATO: 'Solo con formato'
};
const ADVANCED_SYNC_BUTTON_META = {
    DRY_RUN: {
        label: 'Vista previa tecnica',
        help: 'Simula sin escribir.'
    },
    STRUCTURE: {
        label: 'Sincronizar estructura/items',
        help: 'Agrega secciones/items faltantes. No copia metas ni avances.'
    },
    VISUAL: {
        label: 'Sincronizar formato visual',
        help: 'Copia solo configuracion visual _config_*. No copia datos capturados.'
    },
    GOALS: {
        label: 'Sincronizar metas',
        help: 'Copia solo metas. No copia avances ni evidencias.'
    },
    SELECTIVE: {
        label: 'Ejecutar seleccion',
        help: 'Ejecuta solo las acciones elegidas por seccion sin copiar avances ni evidencias.'
    }
} as const;

const getSectionNumberForMapping = (section: any, fallbackIndex: number = 0) => {
    const canonical = resolveCanonicalSectionNumber(section);
    if (canonical) return canonical;
    const fromField = Number(section?.section_number);
    if (Number.isFinite(fromField) && fromField > 0) return fromField;
    const fromOrder = Number(section?.order_index);
    if (Number.isFinite(fromOrder) && fromOrder > 0) return fromOrder;
    return fallbackIndex + 1;
};

const getSectionOrderForMapping = (section: any, fallbackIndex: number = 0) => {
    const fromOrder = Number(section?.order_index);
    if (Number.isFinite(fromOrder) && fromOrder >= 0) return fromOrder;
    return getSectionNumberForMapping(section, fallbackIndex);
};

const normalizeSyncSectionName = (value: any) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const appliesSectionToInstanceType = (sectionNumber: number, instanceType: string) => {
    const normalizedType = normalizeVPType(instanceType || '');
    if (sectionNumber === 5 && (normalizedType === 'COMITE' || normalizedType === 'CONSEJERIA')) {
        return false;
    }
    return true;
};

const getSelectiveMappingTone = (status: SelectiveMappingStatus) => {
    switch (status) {
        case 'MATCH':
            return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
        case 'SUGERIDO':
            return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
        case 'NO_MATCH':
            return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
        default:
            return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
    }
};

const getSelectiveImpactLabel = (action: SelectiveMappingAction) => {
    switch (action) {
        case 'COPIAR_SECCION_COMPLETA':
            return 'Estructura, formato y metas candidatas';
        case 'SOLO_ESTRUCTURA':
            return 'Solo estructura candidata';
        case 'ESTRUCTURA_FORMATO':
            return 'Estructura + formato preparados';
        case 'ESTRUCTURA_METAS':
            return 'Estructura + metas preparadas';
        case 'SOLO_FORMATO_VISUAL':
            return 'Solo llaves visuales candidatas';
        case 'SOLO_METAS':
            return 'Solo metas candidatas';
        case 'CONSERVAR_DESTINO':
            return 'Destino preservado sin cambios';
        case 'EXCLUIR_SECCION':
            return 'Seccion fuera del sync';
        default:
            return 'Requiere seleccion manual';
    }
};

const getSelectiveActionFlags = (action: SelectiveMappingAction) => ({
    structure: action === 'SOLO_ESTRUCTURA' || action === 'ESTRUCTURA_FORMATO' || action === 'ESTRUCTURA_METAS' || action === 'COPIAR_SECCION_COMPLETA',
    visual: action === 'SOLO_FORMATO_VISUAL' || action === 'ESTRUCTURA_FORMATO' || action === 'COPIAR_SECCION_COMPLETA',
    goals: action === 'SOLO_METAS' || action === 'ESTRUCTURA_METAS' || action === 'COPIAR_SECCION_COMPLETA',
    protectedAdvances: true,
    protectedEvidence: true,
    protectedCapturedData: true
});

const getSelectiveRiskLabel = (row: SelectiveMappingRow, action: SelectiveMappingAction) => {
    if (!row.applies_to_instance || action === 'EXCLUIR_SECCION') return 'Bajo';
    if (row.status === 'NO_MATCH') return 'Alto';
    if (row.status === 'SUGERIDO' || action === 'MAPEO_MANUAL') return 'Medio';
    return 'Bajo';
};

const FULL_ALIGNMENT_ACTIONS = new Set<SelectiveMappingAction>(['ESTRUCTURA_FORMATO_METAS', 'COPIAR_SECCION_COMPLETA']);

const getInstanceTypeLabel = (type: any) => {
    const canonicalType = canonicalInstanceType(type || '');
    if (canonicalType === 'COMISION') return 'Comisión';
    if (canonicalType === 'COMITE') return 'Comité';
    if (canonicalType === 'CONSEJERIA') return 'Consejería';
    return type ? String(type) : 'Sin tipo';
};

const getSelectiveActionFromOperationalMode = (
    mode: SelectiveOperationalMode,
    row: SelectiveMappingRow
): SelectiveMappingAction => {
    if (!row.applies_to_instance || row.status === 'EXCLUIR') return 'EXCLUIR_SECCION';
    if (row.status === 'NO_MATCH') {
        const pref = SELECTIVE_OPERATIONAL_MODE_META[mode].preferredAction;
        if (pref === 'SOLO_ESTRUCTURA' || pref === 'ESTRUCTURA_FORMATO' || pref === 'ESTRUCTURA_METAS' || pref === 'ESTRUCTURA_FORMATO_METAS' || pref === 'COPIAR_SECCION_COMPLETA') {
            return pref === 'ESTRUCTURA_FORMATO_METAS' ? 'COPIAR_SECCION_COMPLETA' : pref;
        }
        return 'EXCLUIR_SECCION';
    }
    return SELECTIVE_OPERATIONAL_MODE_META[mode].preferredAction === 'ESTRUCTURA_FORMATO_METAS' ? 'COPIAR_SECCION_COMPLETA' : SELECTIVE_OPERATIONAL_MODE_META[mode].preferredAction;
};

const getDefaultSelectiveAction = (status: SelectiveMappingStatus, phase: RegularizationPhase | null, appliesToInstance: boolean): SelectiveMappingAction => {
    if (!appliesToInstance || status === 'EXCLUIR') return 'EXCLUIR_SECCION';
    if (status === 'MATCH') return 'COPIAR_SECCION_COMPLETA';
    if (status === 'SUGERIDO') return 'MAPEO_MANUAL';
    if (status === 'NO_MATCH') {
        return 'COPIAR_SECCION_COMPLETA';
    }
    if (phase === 'VISUAL_SCHEMA') return 'SOLO_FORMATO_VISUAL';
    return 'CONSERVAR_DESTINO';
};

const countSectionConfigKeys = (sections: any[] = []) => {
    return sections.reduce((acc: number, section: any) => {
        const content = section?.content;
        if (!content || typeof content !== 'object' || Array.isArray(content)) return acc;
        return acc + Object.keys(content).filter((key) => key.startsWith('_config_') || key === 'columns' || key.includes('columns_')).length;
    }, 0);
};

const detectRegularizationPhase = (params: {
    hasPlan: boolean;
    sectionCount: number;
    itemCount: number;
    hasConfigKeys: boolean;
    totalGoal: number;
    expectedSections: number;
    expectedItems: number;
}): RegularizationPhase => {
    if (!params.hasPlan) return 'PLAN_BASE';
    if (params.sectionCount < Math.max(1, params.expectedSections)) return 'SECTIONS';
    if (params.itemCount < Math.max(1, params.expectedItems)) return 'ITEMS';
    if (!params.hasConfigKeys) return 'VISUAL_SCHEMA';
    if (Number(params.totalGoal || 0) <= 0) return 'GOALS';
    return 'READY';
};

const getRegularizationActionMeta = (phase: RegularizationPhase) => {
    switch (phase) {
        case 'PLAN_BASE':
            return {
                label: 'Crear plan base en Planeacion',
                summary: 'El destino no tiene plan activo todavia.',
                supported: false,
                tone: 'text-sky-400 bg-sky-500/10 border-sky-500/20'
            };
        case 'SECTIONS':
            return {
                label: 'Regularizar secciones (2D)',
                summary: 'El destino tiene plan pero aun no completa la base estructural.',
                supported: false,
                tone: 'text-amber-400 bg-amber-500/10 border-amber-500/20'
            };
        case 'ITEMS':
            return {
                label: 'Regularizar items (2E)',
                summary: 'La estructura base existe pero faltan items del origen.',
                supported: true,
                tone: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
            };
        case 'VISUAL_SCHEMA':
            return {
                label: 'Sincronizar formato visual (2E2)',
                summary: 'Faltan llaves `_config_*` o columnas visuales necesarias.',
                supported: true,
                tone: 'text-amber-400 bg-amber-500/10 border-amber-500/20'
            };
        case 'GOALS':
            return {
                label: 'Sincronizar metas (2F-B)',
                summary: 'La estructura y el formato ya existen; restan las metas.',
                supported: true,
                tone: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
            };
        default:
            return {
                label: 'Destino listo',
                summary: 'Secciones, items, formato y metas detectados.',
                supported: false,
                tone: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
            };
    }
};

const STATUS_LABELS: Record<string, string> = {
    'BORRADOR': 'Borrador',
    'EN REVISION': 'En Revisión',
    'REVISADO': 'En Revisión',
    'APROBADO': 'Aprobado',
    'RECHAZADO': 'Rechazado',
    'draft': 'Borrador',
    'DRAFT': 'Borrador',
    'reviewed': 'En Revisión',
    'REVIEWED': 'En Revisión',
    'approved': 'Aprobado',
    'APPROVED': 'Aprobado',
    'rejected': 'Rechazado'
};

const MONTHS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MONTHS_FULL = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

// APP_VERSION centralizado arriba
const SYNC_MODAL_VERSION = 'v18.26.69-UI-TEXT-ENCODING-SURGICAL-FIX';





const fuzzyValue = (obj: any, patterns: string[]): any => {
    if (!obj || typeof obj !== 'object') return undefined;
    const keys = Object.keys(obj);
    const cleanPatterns = patterns.map(p => normalize(p).toLowerCase().replace(/[\s_]/g, ''));
    const foundKey = keys.find(k => {
        const cleanK = normalize(k).toLowerCase().replace(/[\s_]/g, '');
        return cleanPatterns.some(p => cleanK.includes(p) || p.includes(cleanK));
    });
    return foundKey ? obj[foundKey] : undefined;
};

const resolveKeyByPatterns = (cols: any[], patterns: string[]): string | null => {
    if (!Array.isArray(cols)) return null;
    const cleanPatterns = patterns.map(p => normalize(p).toLowerCase().replace(/[\s_]/g, ''));
    const col = cols.find(c => {
        const cleanLabel = normalize(c.label || "").toLowerCase().replace(/[\s_]/g, '');
        const cleanKey = normalize(c.key || "").toLowerCase().replace(/[\s_]/g, '');
        return cleanPatterns.some(p => cleanLabel.includes(p) || p.includes(cleanLabel) || cleanKey.includes(p) || p.includes(cleanKey));
    });
    return col ? col.key : null;
};



const countValidRows = (data: any) =>
    Array.isArray(data) ? data.filter(row =>
        Object.values(row).some(v => v !== null && v !== undefined && String(v).trim() !== '')
    ).length : 0;

const countValidRowsByHalfFilled = (data: any, columns: any[]) => {
    if (!Array.isArray(data)) return 0;
    return data.filter(row => {
        const filledCount = columns.filter(col => {
            const val = row[col.key];
            return val !== undefined && val !== null && String(val).trim() !== '';
        }).length;
        return filledCount >= Math.ceil(columns.length / 2);
    }).length;
};

const countValidRowsWithKey = (data: any, key: string) => {
    if (!Array.isArray(data)) return 0;
    return data.filter(row => {
        const val = row[key];
        return val !== undefined && val !== null && String(val).trim() !== '';
    }).length;
};

const getSwotPoints = (data: any) => {
    let pts = 0;
    if (data?.strengths && String(data.strengths).trim().length > 0) pts++;
    if (data?.weaknesses && String(data.weaknesses).trim().length > 0) pts++;
    if (data?.opportunities && String(data.opportunities).trim().length > 0) pts++;
    if (data?.threats && String(data.threats).trim().length > 0) pts++;
    return pts;
};

export const monthToNum = (m: string) => {
    const idx = MONTHS_FULL.indexOf(m.toUpperCase());
    if (idx !== -1) return idx + 1;
    const idxShort = MONTHS.indexOf(m.toUpperCase());
    return idxShort !== -1 ? idxShort + 1 : 1;
};

const INSTANCE_TYPE_LABELS: Record<string, string> = {
    [COMISION_TYPE]: 'Comisiones',
    'COMITE': 'Comités',
    'CONSEJERIA': 'Consejerías'
};
const INSTANCE_TYPE_SINGULAR_LABELS: Record<string, string> = {
    [COMISION_TYPE]: 'Comisión',
    'COMITE': 'Comité',
    'CONSEJERIA': 'Consejerías'
};

const getInstanceTypeSingularLabel = (type: string) => {
    switch (canonicalInstanceType(type)) {
        case COMISION_TYPE:
            return 'Comisión';
        case 'COMITE':
            return 'Comité';
        case 'CONSEJERIA':
            return 'Consejería';
        default:
            return 'Instancia';
    }
};

const getCurrentPeriod = (frec: string) => {
    const now = new Date();
    if (frec === 'Mensual') {
        return MONTHS[now.getMonth()];
    } else {
        const start = new Date(now.getFullYear(), 0, 1);
        const diff = now.getTime() - start.getTime();
        const oneDay = 1000 * 60 * 60 * 24;
        const day = Math.floor(diff / oneDay);
        return `SEM ${Math.ceil(day / 7)}`;
    }
};

const apiFetch = async (url: string, options: any = {}) => {
    const { data: { session } } = await db.supabase.auth.getSession();
    const token = session?.access_token;

    return fetch(url, {
        ...options,
        headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'x-user-id': session?.user?.id || ''
        }
    });
};

const invokeStructureSyncEdge = async (govSecureToken: string, payload: Record<string, unknown>) => {

    const { data, error } = await db.supabase.functions.invoke('apply-planning-structure-sync', {
        headers: {
            'x-gov-secure-token': govSecureToken
        },
        body: payload
    });

    if (error) {
        const httpStatus = (error as any).status || (error as any).context?.status || 500;
        let httpStatusText = error.message || 'Error en Edge Function';
        let sanitizedBody: any = null;
        let requestId = '';

        // Intentar extraer detalles adicionales de la respuesta HTTP original
        const response = (error as any).context;
        if (response && typeof response.clone === 'function') {
            try {
                requestId = response.headers.get('x-request-id')
                    || response.headers.get('cf-ray')
                    || response.headers.get('x-sb-project-ref')
                    || '';
                const rawText = await response.clone().text();
                sanitizedBody = rawText ? JSON.parse(rawText) : null;
            } catch (e) {
            }
        }

        // Si no se pudo obtener del context, pero el error tiene un mensaje que ya es JSON
        if (!sanitizedBody && error.message) {
            try {
                sanitizedBody = JSON.parse(error.message);
            } catch {}
        }

        const edgeError = new Error(
            sanitizedBody?.error
            || sanitizedBody?.message
            || error.message
            || `Edge Function HTTP ${httpStatus}`.trim()
        );

        (edgeError as any).httpStatus = httpStatus;
        (edgeError as any).httpStatusText = httpStatusText;
        (edgeError as any).sanitizedBody = sanitizedBody;
        (edgeError as any).requestId = requestId;

        throw edgeError;
    }

    return data;
};

const getStructureSyncErrorSummary = (error: any) => {
    const httpStatus = error?.httpStatus || error?.context?.status || null;
    const httpStatusText = error?.httpStatusText || error?.context?.statusText || '';
    const requestId = error?.requestId || '';
    const sanitizedBody = error?.sanitizedBody || null;
    const message = error?.message || 'Error desconocido';
    const compactBody = sanitizedBody
        ? typeof sanitizedBody === 'string'
            ? sanitizedBody
            : JSON.stringify(sanitizedBody)
        : '';

    return {
        httpStatus,
        httpStatusText,
        requestId,
        sanitizedBody,
        message,
        toastMessage: [
            httpStatus ? `HTTP ${httpStatus}` : null,
            httpStatusText || null,
            message || null,
            requestId ? `req ${requestId}` : null
        ].filter(Boolean).join(' | '),
        bodyMessage: compactBody
    };
};

const ensureHistoryConsistency = (ind: any) => {
    const count = ind.frec === 'Mensual' ? 12 : 52;
    if (!ind.history || ind.history.length === 0) {
        return Array(count).fill(0).map((_, i) => ({
            period: ind.frec === 'Mensual' ? MONTHS[i] : `SEM ${i + 1}`,
            meta: 0,
            real: 0,
            note: ''
        }));
    }
    if (ind.history.length !== count) {
        const newHistory = Array(count).fill(0).map((_, i) => {
            const periodLabel = ind.frec === 'Mensual' ? MONTHS[i] : `SEM ${i + 1}`;
            const existing = ind.history.find((h: any) => h.period === periodLabel);
            return existing || { period: periodLabel, meta: 0, real: 0, note: '' };
        });
        return newHistory;
    }
    return ind.history;
};
export const countActiveYTD = (indicadores: any[], periodIdx: number) => {
    if (!indicadores) return 0;
    return indicadores.filter(ind => {
        // REGLA SURGICA v18.11.08: Mes Vencido (Canonico)
        const canonicalCompletedIdx = getElapsedCompletedMonths(periodIdx);
        const expiredHistory = ind.history && canonicalCompletedIdx >= 0 ? ind.history.slice(0, canonicalCompletedIdx + 1) : [];
        return expiredHistory.some((h: any) => h.meta !== '' && h.meta !== null && h.meta !== undefined && Number(h.meta) > 0);
    }).length;
};


const WorkPlanSectionEditor = ({ section, onBack, onSave, readOnly, isAdmin, user }: { section: any, onBack: () => void, onSave: () => void, readOnly: boolean, isAdmin: boolean, user?: any }) => {
    const settings = useContext(SettingsContext);
    const canEditStructure = isAdmin;
    const canEditOperationalContent = !readOnly || isAdmin;
    const sectionCanEditGoals = canEditOperationalContent;
    const [content, setContent] = useState<any>(() => {
        try {
            const raw = section.content ? (typeof section.content === 'string' ? JSON.parse(section.content) : section.content) : null;
            return normalizeSectionContent(section, raw);
        } catch (e) {
            return null;
        }
    });
    const [sectionGoal, setSectionGoal] = useState<number>(section.goal || 0);
    const [sectionName, setSectionName] = useState(section.name || '');
    const sn = resolveCanonicalSectionNumber(section);
    const structType = detectStructuralType(section);



    const [isSavingSection, setIsSavingSection] = useState(false);

    const handleFinalShieldSave_965 = async () => {
        if (isSavingSection) return;
        setIsSavingSection(true);
        try {
            // v18.12.03: Pasar sectionGoal como manualGoal para que S7 y secciones con meta manual la preserven
            const metrics = computeSectionMetrics(sn, content, sectionGoal);
            const finalGoal = metrics.goal;
            const finalProgress = metrics.progress;

            // BLINDAJE v18.10.33


            if (!section.id) throw new Error("ID de sección no encontrado.");


            await db.updateWorkPlanSection(section.id, {
                content,
                goal: Number(finalGoal) || 0,
                meta_anual: Number(finalGoal) || 0,
                progress: Number(finalProgress) || 0,
                ...(isAdmin ? { name: sectionName } : {})
            });


            try {
                await db.syncWorkPlanItems(section.work_plan_id);
                await db.syncWorkPlanTotals(section.work_plan_id);
            } catch (e) {
                // Sync failed
            }

            console.log(`[S5_SHIELD_DEBUG] finalGoal=${finalGoal} finalProgress=${finalProgress} sn=${sn}`);
            try {
                await recordAuditEvent({
                    module: 'PLANEACION',
                    entity_type: 'WORK_PLAN_SECTION',
                    action: 'UPDATE',
                    source: 'UI',
                    metadata: {
                        planId: section.work_plan_id,
                        sectionId: section.id,
                        sectionTitle: sectionName,
                        trigger: 'planning_section_editor'
                    }
                });
            } catch (auditErr) {
                console.warn('[AUDIT] Fallo silencioso en handleFinalShieldSave_965', auditErr);
            }
            showToast(`PLAN ACTUALIZADO`, 'success');
            onSave();
            onBack();
        } catch (err: unknown) {
            console.error("SAVE ERROR:", err);
            showToast(db.getMutationErrorMessage(err), 'error');
        } finally {
            setIsSavingSection(false);
        }
    };

    // CRUD DINÁMICO v18.10.31
    // Resuelve columnas: prioriza configuraciones persistidas por tipo estructural
    const resolveColumns = (defaultCols: any[]): any[] => {
        const snValue = resolveCanonicalSectionNumber(section);
        let resolved = defaultCols;
        if (snValue !== null && content?.[`_config_columns_sec${snValue}`]) {
            resolved = content[`_config_columns_sec${snValue}`];
        }

        if (snValue === 1) {
            resolved = resolved.map((c: any) => {
                if (c.key === 'objetivo_agenda') {
                    return {
                        ...c,
                        type: 'select',
                        options: ['Crece Mi Negocio', 'Diálogo Social y Entorno Laboral', 'Seguridad, Justicia y Paz', 'Democracia, Libertades y Anticorrupción', 'T-MEC y Nearshoring']
                    };
                }
                return c;
            });
        }
        return resolved;
    };

    // CRUD DINÁMICO v18.10.86
    // Corrección 3: Permisos quirúrgicos. Edición de columnas habilitada SOLO para Admin en la sección 8 (Convenios).
    const canEditColumns = isAdmin;

    // Persiste cambios de columnas en content._config_columns_secN
    const handleColumnsChange = canEditColumns ? (newCols: any[]) => {
        const snValue = resolveCanonicalSectionNumber(section);
        if (snValue === null) {
            console.error("CRITICAL: Section number not found for column persistence", section);
            showToast("ERROR ESTRUCTURAL: No se puede identificar el número de sección.", "error");
            return;
        }
        const configKey = `_config_columns_sec${snValue}`;
        setContent((prev: any) => ({ ...(prev || {}), [configKey]: newCols }));
    } : undefined;

    const handleContentUpdate = (patch: any) => setContent((prev: any) => ({ ...(prev || {}), ...patch }));

    const renderEditor = () => {
        switch (sn) {
            case 1: // OBJETIVOS DE LA AGENDA
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-emerald-600/10 border border-emerald-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[100px] rounded-full -mr-32 -mt-32"></div>
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'no_oe', label: 'No. OE', className: 'w-20 text-center font-black bg-slate-900/50' },
                                    { key: 'objetivo_agenda', label: 'Objetivo de la Agenda', type: 'select', options: ['Crece Mi Negocio', 'Diálogo Social y Entorno Laboral', 'Seguridad, Justicia y Paz', 'Democracia, Libertades y Anticorrupción', 'T-MEC y Nearshoring'], className: 'font-bold w-64' },
                                    { key: 'no_ap', label: 'No. AP', className: 'w-20 text-center font-black bg-slate-900/50 text-emerald-400' },
                                    { key: 'accion_prioritaria', label: 'Acción Prioritaria', className: 'font-bold text-emerald-400' }
                                ])}
                                data={content?.sec1_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec1_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

            case 2: // PLAN ESTRATÃƒâ€°GICO
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-blue-600/10 border border-blue-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-[100px] rounded-full -mr-32 -mt-32"></div>
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'no_oe', label: 'No. OE', className: 'w-20 text-center font-black bg-slate-900/50' },
                                    { key: 'objetivo_estrategico', label: 'Objetivo Estratégico', className: 'font-bold' },
                                    { key: 'no_ap', label: 'No. AP', className: 'w-20 text-center font-black bg-slate-900/50 text-blue-400' },
                                    { key: 'accion_prioritaria', label: 'Acción Prioritaria', className: 'font-bold text-blue-400' }
                                ])}
                                data={content?.sec2_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec2_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

            case 3: // DIAGNÃƒâ€œSTICO
                return (
                    <DiagnosticoFormat
                        data={content?.sec3_data}
                        onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec3_data', val))}
                        readOnly={readOnly}
                        isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                        resolveColumns={resolveColumns}
                        handleColumnsChange={handleColumnsChange}
                    />
                );

            case 4: // ESTRUCTURA
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-emerald-600/10 border border-emerald-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'vicepresidencia', label: 'Vicepresidencia / Puesto', className: 'font-bold' },
                                    { key: 'nombre_responsable', label: 'Nombre Responsable' },
                                    { key: 'celular', label: 'Celular / Contacto' }
                                ])}
                                data={content?.sec4_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec4_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

            case 5: // COMISIONES
                return (
                    <ComisionesManager
                        data={content?.sec5_data}
                        onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec5_data', val))}
                        readOnly={readOnly}
                        isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                        resolveColumns={resolveColumns}
                        handleColumnsChange={handleColumnsChange}
                    />
                );

            case 6: // EVENTOS
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-indigo-600/10 border border-indigo-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'mes', label: 'Mes', className: 'bg-slate-900/50 font-black w-32' },
                                    { key: 'tema', label: 'Tema / Evento', className: 'font-bold' },
                                    { key: 'fecha', label: 'Fecha / Objetivo', className: 'font-bold text-indigo-400' },
                                    { key: 'evento', label: 'Detalle / Comentarios' }
                                ])}
                                data={content?.sec6_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec6_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

            case 7: // PROYECTOS (Layout de Tarjetas Restaurado)
                return (
                    <ProyectosCardsEditor
                        data={content?.sec7_data}
                        onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec7_data', val))}
                        readOnly={readOnly}
                        isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                        title={sectionName}
                        onTitleChange={setSectionName}
                    />
                );

            case 8: // CONVENIOS
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-amber-600/10 border border-amber-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'aliado', label: 'Aliado / Institución', className: 'font-bold text-amber-400' },
                                    { key: 'objetivo', label: 'Objetivo del Convenio' },
                                    { key: 'vencimiento', label: 'Fecha Vencimiento', className: 'w-32 text-center' },
                                    { key: 'estatus', label: 'Estatus', type: 'select', options: ['VIGENTE', 'POR VENCER', 'RENOVACIÃ“N', 'PROCESO'], className: 'w-32' }
                                ])}
                                data={content?.convenios_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'convenios_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

            case 9: // TEMAS DE INTERÃƒâ€°S
                return (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="bg-rose-600/10 border border-rose-500/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                            <div className="space-y-1 mb-8">
                                <h3 className="text-white font-black text-2xl uppercase tracking-tighter italic">TEMAS RELEVANTES</h3>
                                <p className="text-rose-400 text-[10px] font-black uppercase tracking-[0.3em]">Asuntos Críticos para Socios (S9)</p>
                            </div>
                            <GenericTableEditor
                                readOnly={!sectionCanEditGoals}
                                isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                columns={resolveColumns([
                                    { key: 'tema', label: 'Temas relevantes para socios directos', className: 'font-bold' },
                                    { key: 't1', label: 'T1' },
                                    { key: 't2', label: 'T2' },
                                    { key: 't3', label: 'T3' },
                                    { key: 't4', label: 'T4' }
                                ])}
                                data={content?.sec9_data || []}
                                onChange={val => setContent(prev => safeUpdateContent(section, prev, 'sec9_data', val))}
                                onColumnsChange={handleColumnsChange}
                                onTitleChange={setSectionName}
                                title={sectionName}
                            />
                        </div>
                    </div>
                );

             default: {
                 const canonicalKey = `sec${sn}_data`;
                 return (
                     <div className="space-y-8 animate-in fade-in duration-500">
                         <div className="bg-slate-800/10 border border-slate-700/20 p-10 rounded-[3rem] relative overflow-hidden shadow-2xl">
                             <GenericTableEditor
                                 readOnly={!sectionCanEditGoals}
                                 isAdmin={isAdmin} canEditRows={sectionCanEditGoals}
                                 columns={resolveColumns([
                                     { key: 'col_1', label: 'Campo 1' },
                                     { key: 'col_2', label: 'Campo 2' }
                                 ])}
                                 data={content?.[canonicalKey] || []}
                                 onChange={val => setContent(prev => safeUpdateContent(section, prev, canonicalKey, val))}
                                 onColumnsChange={handleColumnsChange}
                                 onTitleChange={setSectionName}
                                 title={sectionName}
                             />
                         </div>
                     </div>
                 );
             }
        }
    };


    const progressValue = calculateDetailedProgress(section, content);
    const goalValue = calculateDetailedGoal(section, content, sectionGoal);

    // Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â BLINDAJE MÃƒâ€°TRICO v18.12.03 Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â
    // Secciones con conteo dinámico SIEMPRE usan goalValue calculado.
    // Secciones con meta manual (S3 fija=6, S4 estructura) permiten input.
    const DYNAMIC_SECTIONS = [5]; // Solo Comisiones mantiene conteo 100% automático (S1, S2, S6, S8, S9 permiten meta manual)
    const isDynamicGoal = sn !== null && DYNAMIC_SECTIONS.includes(sn);
    // El valor REAL de meta que se muestra y se usa para compliance
    const displayGoal = isDynamicGoal ? goalValue : (sectionGoal > 0 ? sectionGoal : goalValue);
    const complianceValue = displayGoal > 0 ? Math.min(100, Math.round((progressValue / displayGoal) * 100)) : 0;

    return (
        <div className="w-full max-w-none mx-auto space-y-4 animate-in fade-in slide-in-from-bottom-8 duration-700">
            {/* STICKY EJECUTIVO REAL DEL HEADER DEL EDITOR */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-3 px-4 py-2 sticky top-0 bg-slate-950/95 backdrop-blur-xl z-40 border-b border-white/5 -mx-4 shadow-lg">
                <div className="space-y-1">
                    <div className="flex items-center gap-2.5">
                        <button onClick={onBack} className="w-8 h-8 bg-slate-900 hover:bg-slate-800 text-white rounded-lg flex items-center justify-center transition-all border border-white/5 shadow-xl active:scale-95 min-h-[36px] min-w-[36px]">
                            <ArrowLeft size={14} />
                        </button>
                        {user && (user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' || user.role === 'DIRECTOR_NACIONAL' || user.role === 'ADMIN') && (
                            <div className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
                                <span className="text-[8px] font-black text-emerald-500 uppercase tracking-[0.2em]">{APP_VERSION}</span>
                            </div>
                        )}
                    </div>
                    <div className="space-y-0.5">
                        {canEditStructure ? (
                            <input
                                className="text-lg sm:text-xl md:text-2xl font-black text-white uppercase tracking-tighter italic leading-none bg-transparent border-b border-transparent hover:border-emerald-500/30 focus:border-emerald-500 focus:outline-none w-full"
                                value={sectionName}
                                onChange={e => setSectionName(e.target.value)}
                            />
                        ) : (
                            <h2 className="text-lg sm:text-xl md:text-2xl font-black text-white uppercase tracking-tighter italic leading-none">{sectionName}</h2>
                        )}
                        <p className="text-slate-500 font-bold uppercase tracking-widest text-[8px] mt-0.5">Módulo de Planeación Estratégica Coparmex 2026</p>
                    </div>
                </div>

                {canEditOperationalContent && (
                    <button
                        onClick={handleFinalShieldSave_965}
                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-2 rounded-lg font-black text-[10px] flex items-center gap-2 shadow-2xl shadow-emerald-500/30 transition-all active:scale-95 group relative overflow-hidden min-h-[36px]"
                    >
                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                        <ShieldCheck size={16} className="relative z-10" />
                        <span className="relative z-10">GUARDAR CAMBIOS</span>
                    </button>
                )}
            </div>

            {/* MÃƒâ€°TRICAS COMPACTAS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 px-4">
                <div className="bg-slate-900 border border-white/5 p-2.5 sm:p-3 rounded-lg flex items-center gap-3 group hover:bg-emerald-500/5 transition-all">
                    <div className="w-9 h-9 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-500 group-hover:scale-110 transition-transform min-h-[36px] min-w-[36px]">
                        <Target size={16} />
                    </div>
                    <div>
                        <p className="text-[7.5px] text-slate-500 font-black uppercase tracking-widest">Meta de la Sección</p>
                        {isDynamicGoal ? (
                            /* BLINDAJE v18.12.03: Secciones dinámicas SIEMPRE muestran conteo calculado */
                            <h4 className="text-xl font-black text-white italic">{goalValue}</h4>
                        ) : ((sectionCanEditGoals) && structType !== 'COMISIONES') ? (
                            <input
                                type="number"
                                className="bg-transparent text-xl font-black text-white italic border-b border-white/10 focus:border-emerald-500 focus:outline-none w-16"
                                value={sectionGoal}
                                onChange={e => setSectionGoal(Number(e.target.value))}
                            />
                        ) : (
                            <h4 className="text-xl font-black text-white italic">{goalValue}</h4>
                        )}
                    </div>
                </div>
                <div className="bg-slate-900 border border-white/5 p-2.5 sm:p-3 rounded-lg flex items-center gap-3 group hover:bg-blue-500/5 transition-all">
                    <div className="w-9 h-9 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform min-h-[36px] min-w-[36px]">
                        <TrendingUp size={16} />
                    </div>
                    <div>
                        <p className="text-[7.5px] text-slate-500 font-black uppercase tracking-widest">Avance Reportado</p>
                        <h4 className="text-xl font-black text-white italic">{progressValue}</h4>
                    </div>
                </div>
                <div className={cn("bg-slate-950 border p-2.5 sm:p-3 rounded-lg flex items-center gap-3 relative overflow-hidden shadow-2xl", complianceValue >= (settings?.threshold_green ?? 90) ? 'border-emerald-500/20' : complianceValue >= (settings?.threshold_amber ?? 75) ? 'border-amber-500/20' : 'border-red-500/20')}>
                    <div className={cn("absolute top-0 right-0 w-32 h-32 blur-3xl -mr-16 -mt-16", complianceValue >= (settings?.threshold_green ?? 90) ? 'bg-emerald-500/5' : complianceValue >= (settings?.threshold_amber ?? 75) ? 'bg-amber-500/5' : 'bg-red-500/5')}></div>
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center text-slate-950 group-hover:rotate-12 transition-transform shadow-lg min-h-[36px] min-w-[36px]", complianceValue >= (settings?.threshold_green ?? 90) ? 'bg-emerald-500 shadow-emerald-500/20' : complianceValue >= (settings?.threshold_amber ?? 75) ? 'bg-amber-500 shadow-amber-500/20' : 'bg-red-500 shadow-red-500/20')}>
                        <Award size={16} />
                    </div>
                    <div>
                        <p className={cn("text-[7.5px] font-black uppercase tracking-widest", complianceValue >= (settings?.threshold_green ?? 90) ? 'text-emerald-500' : complianceValue >= (settings?.threshold_amber ?? 75) ? 'text-amber-500' : 'text-red-500')}>Cumplimiento Actual</p>
                        <h4 className={cn("text-xl font-black italic", complianceValue >= (settings?.threshold_green ?? 90) ? 'text-emerald-400' : complianceValue >= (settings?.threshold_amber ?? 75) ? 'text-amber-400' : 'text-red-400')}>{complianceValue}%</h4>
                    </div>
                </div>
            </div>

            <div className="px-4">
                {renderEditor()}
            </div>
        </div>
    );
};

const WorkPlanDetail = ({ planId, onBack, user, hierarchy, onPlanUpdated }: { planId: string, onBack: () => void, user: any, hierarchy: any, onPlanUpdated?: () => Promise<void> | void }) => {
    const settings = useContext(SettingsContext);
    const { isSA, isND, isVP, isUserAdmin, isPresident, canApproveAsVP, isAdmin, isSuperAdmin } = useRole(user);
    const canEditStructure = isAdmin;
    const detailCanEditGoals = isAdmin || canEditModule(user, 'Planeación');

    const [plan, setPlan] = useState<any>(null);
    const [hasError, setHasError] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    const [editingValues, setEditingValues] = useState<Record<string, any>>({});
    const [selectedSection, setSelectedSection] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [allCommissions, setAllCommissions] = useState<any[]>([]);
    const [isReplModalOpen, setIsReplModalOpen] = useState(false);
    const [showDisabledSections, setShowDisabledSections] = useState(false);

    // HELPER: Resolver meta para vista general respetando persistencia manual (v18.12.05)
    function getGeneralViewGoal(section: any, metrics: any) {
        const sn = resolveCanonicalSectionNumber(section);
        if (sn === 5) return metrics.goal;

        // v18.12.05: Respetar bloqueo explícito o valor persistido > 0
        const isLocked = (section as any).manual_goal_locked === true || section.content?.manual_goal_locked === true;
        const persistedGoal = Number(section.goal ?? section.meta_anual ?? 0);

        if (isLocked || persistedGoal > 0) return persistedGoal;
        return metrics.goal;
    }

    const handleDragStart = (index: number) => {
        setDragIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDrop = async (index: number) => {
        if (dragIndex === null || dragIndex === index) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        const sections = [...plan.sections];
        const [moved] = sections.splice(dragIndex, 1);
        sections.splice(index, 0, moved);
        setPlan({ ...plan, sections });
        setDragIndex(null);
        setDragOverIndex(null);
        try {
            const orderedIds = sections.map((s: any) => s.id);
            await db.reorderSections(planId, orderedIds);
        } catch (err: any) {
            console.error('[DND] Error:', err);
            fetchPlan();
        }
    };

    const fetchPlan = useCallback(async () => {
        try {
            setHasError(false);
            setLoadError(null);
            const data = await db.getWorkPlanDetail(planId);
            setPlan(data);
        } catch (e: unknown) {
            console.error("Failed to load plan", e);
            if (db.isScopedReadNotFound(e)) {
                setPlan(null);
            } else {
                setHasError(true);
                setLoadError(db.getScopedReadErrorMessage(e));
            }
        } finally {
            setLoading(false);
        }
    }, [planId]);

    const fetchCommissions = useCallback(async () => {
        try {
            const data = await db.getAllCommissions();
            setAllCommissions(data || []);
        } catch (e) {
            console.error("Failed to load commissions", e);
        }
    }, []);

    useEffect(() => {
        fetchPlan();
        fetchCommissions();
    }, [fetchPlan, fetchCommissions]);

    const handleEditChange = (sectionId: string, field: string, value: any) => {
        setEditingValues(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                [field]: value
            }
        }));
    };

    const saveChanges = async () => {
        try {
            const updates = Object.entries(editingValues).map(([id, values]) => {
                const payload: any = { ...values };
                const section = plan.sections.find((s: any) => s.id === id);

                // v18.12.05: Si se edita la meta, marcar como bloqueada manualmente en el content JSON
                if (payload.goal !== undefined) {
                    payload.meta_anual = Number(payload.goal) || 0;

                    // Asegurar que el flag persista en el JSON content para evitar que syncWorkPlanTotals lo pise
                    if (section) {
                        payload.content = {
                            ...(section.content || {}),
                            manual_goal_locked: true
                        };
                    }
                }
                return db.updateWorkPlanSection(id, payload);
            });
            await Promise.all(updates);
            await db.syncWorkPlanTotals(planId);
            await db.syncWorkPlanItems(planId);
            try {
                await recordAuditEvent({
                    module: 'PLANEACION',
                    entity_type: 'WORK_PLAN',
                    action: 'UPDATE',
                    source: 'UI',
                    metadata: {
                        planId,
                        updatedSections: updates.length,
                        trigger: 'planning_bulk_save'
                    }
                });
            } catch (auditErr) {
                console.warn('[AUDIT] Fallo silencioso en saveChanges', auditErr);
            }
            showToast('Cambios guardados y sincronizados exitosamente.', 'success');
            setIsEditing(false);
            setEditingValues({});
            fetchPlan();
        } catch (err: any) {
            console.error("Error saving changes", err);
            showToast('Error al guardar cambios: ' + (err.message || 'Desconocido'), 'error');
        }
    };

    const handleDeletePlan = async () => {
        if (!confirm('¿Estás seguro de eliminar este plan de trabajo por completo? Esta acción eliminará todas sus secciones y no se puede deshacer.')) return;
        try {
            await db.deleteWorkPlan(planId);
            onBack();
        } catch (err) {
            showToast('Error al eliminar el plan', 'error');
        }
    };

    const handleAddSection = async () => {
        const sectionName = prompt('Nombre de la nueva sección:');
        if (!sectionName) return;
        try {
            const nextNum = (plan.sections.length > 0 ? Math.max(...plan.sections.map((s: any) => s.section_number)) : 0) + 1;
            await db.addWorkPlanSection(planId, {
                section_number: nextNum,
                order_index: nextNum,
                name: sectionName,
                min_goal: 0,
                goal: 0,
                progress: 0,
                content: {
                     _format_type: 'CUSTOM',
                     _config_title: sectionName,
                     _config_columns: [],
                     _config_custom_section: true,
                     [`sec${nextNum}_data`]: []
                }
            });
            fetchPlan();
            try {
                await recordAuditEvent({
                    module: 'PLANEACION',
                    entity_type: 'WORK_PLAN_SECTION',
                    action: 'CREATE',
                    source: 'UI',
                    metadata: {
                        planId,
                        sectionTitle: sectionName,
                        trigger: 'planning_ui'
                    }
                });
            } catch (auditErr) {
                console.warn('[AUDIT] Fallo silencioso en handleAddSection', auditErr);
            }
        } catch (err) {
            showToast('Error al añadir sección', 'error');
        }
    };

    const handleDeleteSection = async (sectionId: string, name: string) => {
        if (!confirm(`¿Estás seguro de eliminar la sección "${name}"? Esta acción borrará también todos los ítems contenidos y no se puede deshacer.`)) return;

        try {
            await db.deleteWorkPlanSection(sectionId);
            if (plan?.id) {
                await db.syncWorkPlanTotals(plan.id);
            }
            await fetchPlan();
            try {
                await recordAuditEvent({
                    module: 'PLANEACION',
                    entity_type: 'WORK_PLAN_SECTION',
                    action: 'DELETE',
                    source: 'UI',
                    old_value: { sectionId, name },
                    metadata: {
                        planId: plan?.id || planId,
                        sectionId,
                        sectionTitle: name,
                        trigger: 'planning_ui'
                    }
                });
            } catch (auditErr) {
                console.warn('[AUDIT] Fallo silencioso en handleDeleteSection', auditErr);
            }
            showToast('Sección eliminada correctamente', 'success');
        } catch (err: any) {
            console.error('Error crítico en handleDeleteSection:', err);
            showToast(`Error crítico al eliminar: ${err.message || 'Error desconocido'}`, 'error');
        }
    };

    const getApprovalStep = () => {
        if (!plan) return 1;
        if (plan.indicators_president_approved && plan.vp_approved) return 4;
        if (plan.vp_approved) return 3;
        if (plan.status === 'EN REVISION' || plan.status === 'REVISADO') return 2;
        return 1;
    };

    const currentStep = getApprovalStep();

    const handleUpdatePlanApproval = async (id: string, value: boolean, source: 'vp' | 'president') => {
        if (source === 'vp' && !canAvalVpPlan(user, plan)) {
            showToast("No tienes permisos de Vicepresidente para avalar este plan.", "error");
            return;
        }
        if (source === 'president' && !(isAdmin || isSuperAdmin)) {
            showToast("Solo administradores nacionales pueden aprobar el plan.", "error");
            return;
        }

        const isRevoking = !value;
        const sourceLabel = source === "vp" ? "Aval de Vicepresidencia" : "Aprobación Nacional";
        const commissionName = plan?.gov_commission?.name || plan?.commission_name || "la Comisión";

        const msg = isRevoking
            ? `¿Estás seguro de REVOCAR el ${sourceLabel} para ${commissionName}?`
            : `¿Confirmas otorgar el ${sourceLabel} para ${commissionName}? El plan pasará a estado de revisión/aprobación.`;

        if (!confirm(msg)) return;

        try {
            setSyncing(true);
            const newVpApproved = source === 'vp' ? value : !!plan.vp_approved;
            const newCommitteeApproved = source === 'president' ? value : !!plan.indicators_president_approved;

            let newStatus = 'BORRADOR';
            if (newVpApproved && newCommitteeApproved) {
                newStatus = 'APROBADO';
            } else if (newVpApproved || newCommitteeApproved) {
                newStatus = 'EN REVISION';
            }

            await db.updateWorkPlan(id, { vp_approved: value });
            await fetchPlan();
            if (onPlanUpdated) await onPlanUpdated();
            showToast(`${sourceLabel} ${isRevoking ? 'revocado' : 'otorgado'} con éxito`, 'success');
        } catch (err: any) {
            showToast(`Error en sistema de gobernanza: ${err.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    if (loading) return <div className="py-20 text-center animate-pulse text-emerald-500 font-black tracking-widest text-lg">AUDITANDO PLAN DE TRABAJO...</div>;
    if (hasError) return (
        <div className="p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
            <AlertTriangle className="mx-auto text-red-400" size={32} />
            <p className="text-red-200 font-black uppercase tracking-widest text-xs">No se pudo cargar el detalle del plan</p>
            <p className="text-white/70 text-sm">{loadError}</p>
            <button onClick={onBack} className="px-5 py-2 rounded-xl bg-slate-800 text-white text-xs font-black uppercase tracking-widest">Volver a Planeacion</button>
        </div>
    );
    if (selectedSection) {
        return (
            <WorkPlanSectionEditor
                section={selectedSection}
                onBack={() => setSelectedSection(null)}
                onSave={() => {
                    setSelectedSection(null);
                    fetchPlan();
                }}
                readOnly={!canEditModule(user, 'Planeación')}
                isAdmin={isAdmin}
                user={user}
            />
        );
    }

    if (!plan) return <div className="text-white p-12 text-center text-[10px] font-black uppercase tracking-widest">PLAN NO ENCONTRADO</div>;
    // DETERMINISMO ABSOLUTO: El cálculo de métricas ahora depende EXCLUSIVAMENTE del contrato canónico S1-S9.

    const handleToggleSectionVisibility = async (sec: any, type: 'hide' | 'disable') => {
        let contentObj = {};
        try {
            contentObj = sec.content ? (typeof sec.content === 'string' ? JSON.parse(sec.content) : sec.content) : {};
            if (!contentObj || typeof contentObj !== 'object') contentObj = {};
        } catch (e) {}

        const parsedContent = contentObj as any;
        if (type === 'hide') {
            parsedContent._config_hidden = !parsedContent._config_hidden;
        } else if (type === 'disable') {
            parsedContent._config_disabled = !parsedContent._config_disabled;
            if (parsedContent._config_disabled) {
                parsedContent._config_hidden = true;
            }
        }

        try {
            await db.updateWorkPlanSection(sec.id, { content: parsedContent });
            showToast("Visibilidad de sección actualizada", "success");
            fetchPlan();
        } catch (err: any) {
            showToast("Error al actualizar sección: " + err.message, "error");
        }
    };

    const totals = (plan.sections || []).reduce((acc: any, s: any) => {
        let isConfigDisabled = false;
        try {
            const parsed = typeof s.content === 'string' ? JSON.parse(s.content) : s.content;
            isConfigDisabled = parsed?._config_disabled === true || parsed?._config_hidden === true || parsed?.active === false;
        } catch (e) {}

        if (isConfigDisabled) {
            return acc;
        }

        const snValue = resolveCanonicalSectionNumber(s);
        const normalizedContent = normalizeSectionContent(snValue, s.content);
        const metrics = computeSectionMetrics(snValue, normalizedContent, s.goal);

        // v18.12.05: Usar helper para respetar meta manual en totales
        const liveG = getGeneralViewGoal(s, metrics);
        const g = editingValues[s.id]?.goal !== undefined ? editingValues[s.id].goal : liveG;
        const p = editingValues[s.id]?.progress !== undefined ? editingValues[s.id].progress : metrics.progress;
        return {
            goal: acc.goal + g,
            progress: acc.progress + p
        };
    }, { goal: 0, progress: 0 });

    const totalGoal = totals.goal;
    const totalProgress = totals.progress;
    const globalPercentage = totalGoal > 0 ? Math.round((totalProgress / totalGoal) * 100) : 0;


    return (
        <div className="space-y-8 pb-32">
            {isReplModalOpen && (
                <PlanReplicaModal
                    sourcePlanId={plan.id}
                    sourceName={plan.gov_commission?.name || plan.commission_name}
                    onClose={() => setIsReplModalOpen(false)}
                    onSyncApplied={fetchPlan}
                    hierarchy={hierarchy}
                    isSuperAdmin={isSuperAdmin}
                    user={user}
                />
            )}            <div className="border-b border-white/5 pb-1.5 md:pb-2 sticky top-0 bg-slate-950/95 backdrop-blur-xl z-50 px-4 -mx-4 shadow-lg">
                <div className="flex items-center justify-between mb-1.5 md:mb-2">
                    <button
                        onClick={onBack}
                        className="group flex items-center gap-1.5 text-white/50 hover:text-emerald-400 transition-all"
                    >
                        <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-emerald-500/20 group-hover:text-emerald-400 transition-all">
                            <ChevronRight size={11} className="rotate-180" />
                        </div>
                        <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.1em]">Volver</span>
                    </button>

                    <div className="flex items-center gap-3">
                        {user && (user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' || user.role === 'DIRECTOR_NACIONAL' || user.role === 'ADMIN') && (
                            <span className="text-[7px] font-black text-white/30 uppercase tracking-[0.25em]">Governance {APP_VERSION}</span>
                        )}
                        <div className={cn(
                            "px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border shadow-xl bg-slate-900",
                            plan.status === 'APROBADO' ? "text-emerald-400 border-emerald-500/30" : "text-blue-400 border-blue-500/30"
                        )}>
                            {plan.status}
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto px-4">
                    <div className="flex items-center justify-between relative">
                        <div className="absolute top-2.5 left-0 w-full h-[2px] bg-slate-800 -z-10" />
                        <div
                            className="absolute top-2.5 left-0 h-[2px] bg-emerald-500 transition-all duration-1000 -z-10"
                            style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
                        />

                        {[
                            { id: 1, label: 'Borrador', icon: <Edit3 size={9} /> },
                            { id: 2, label: 'Revisión', icon: <Clock size={9} /> },
                            { id: 3, label: 'Aval VP', icon: <ShieldCheck size={9} /> },
                            { id: 4, label: 'Aprobado', icon: <Award size={9} /> }
                        ].map((step) => (
                            <div key={step.id} className="flex flex-col items-center gap-0.5 md:gap-1 z-10">
                                <div className={cn(
                                    "w-4.5 h-4.5 md:w-5.5 md:h-5.5 rounded-full flex items-center justify-center border-2 transition-all duration-500",
                                    currentStep >= step.id
                                        ? "bg-slate-950 border-emerald-500 text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.3)] scale-105"
                                        : "bg-slate-900 border-slate-800 text-slate-600"
                                )}>
                                    {step.id === 1 ? <Edit3 size={6} className="md:w-2.5 md:h-2.5" /> :
                                        step.id === 2 ? <Clock size={6} className="md:w-2.5 md:h-2.5" /> :
                                            step.id === 3 ? <ShieldCheck size={6} className="md:w-2.5 md:h-2.5" /> :
                                                <Award size={6} className="md:w-2.5 md:h-2.5" />}
                                </div>
                                <span className={cn(
                                    "text-[5px] md:text-[6.5px] font-black uppercase tracking-[0.03em] md:tracking-[0.1em] transition-colors duration-500 text-center whitespace-nowrap",
                                    currentStep >= step.id ? "text-white" : "text-slate-600"
                                )}>{step.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>


            <header className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl blur opacity-10 group-hover:opacity-20 transition duration-1000" />
                <div className="relative bg-slate-900 border border-white/5 p-3 sm:p-4 rounded-xl">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-3">
                        <div className="space-y-1 w-full">
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                                <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[8px] font-black uppercase tracking-[0.2em] text-emerald-400">
                                    Canal de Gobernación {user && (user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' || user.role === 'DIRECTOR_NACIONAL' || user.role === 'ADMIN') ? ` ${APP_VERSION}` : ''}
                                </span>
                            </div>
                            <h1 className="text-xl md:text-2.5xl font-black text-white uppercase tracking-tighter leading-none break-words">
                                {plan.gov_commission?.name || plan.commission_name}
                            </h1>
                            <div className="flex gap-3">
                                <p className="text-white/70 text-[8px] font-bold uppercase tracking-widest">{INSTANCE_TYPE_SINGULAR_LABELS[canonicalInstanceType(plan.commission_type || COMISION_TYPE)] || getInstanceTypeSingularLabel(plan.commission_type || COMISION_TYPE)} · Ciclo {plan.year}</p>
                                {plan.vp_approved && (
                                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1">
                                        <ShieldCheck size={10} /> Aval VP
                                    </span>
                                )}
                                {plan.indicators_president_approved && (
                                    <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1">
                                        <Award size={10} /> Aprobado
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col items-start md:items-end gap-2.5 w-full md:w-auto">
                            <div className="flex flex-wrap gap-1.5 justify-end">
                                {/* AVAL VP: Solo VPs responsables o Admins Nacionales */}
                                {canAvalVpPlan(user, plan) && (
                                    <>
                                        {!plan.vp_approved ? (
                                            <button
                                                onClick={() => handleUpdatePlanApproval(plan.id, true, 'vp')}
                                                disabled={syncing}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-500 transition-all shadow-lg active:scale-95"
                                            >
                                                <ShieldCheck size={13} /> AVAL VP
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleUpdatePlanApproval(plan.id, false, 'vp')}
                                                disabled={syncing}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-slate-800 text-red-500 border border-red-500/30 hover:bg-red-500 hover:text-white transition-all shadow-lg active:scale-95"
                                            >
                                                <ShieldX size={13} /> DESAPROBAR VP
                                            </button>
                                        )}
                                    </>
                                )}

                                {/* APROBACIÃƒâ€œN NACIONAL: Solo Admins Nacionales (SuperAdmin, Director Nacional) */}
                                {detailCanEditGoals && (isAdmin || isSuperAdmin) && (
                                    <button
                                        onClick={() => handleUpdatePlanApproval(plan.id, !plan.indicators_president_approved, 'president')}
                                        disabled={syncing}
                                        className={cn(
                                            "flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95",
                                            plan.indicators_president_approved
                                                ? "bg-slate-800 text-orange-500 border border-orange-500/30 hover:bg-orange-600 hover:text-white"
                                                : "bg-slate-700 text-white hover:bg-slate-600"
                                        )}
                                    >
                                        {plan.indicators_president_approved ? <ShieldAlert size={13} /> : <Award size={13} />}
                                        {plan.indicators_president_approved ? 'REVOCAR APROBACIÃ“N' : 'APROBAR PLAN'}
                                    </button>
                                )}

                                {isSuperAdmin && (
                                    <button
                                        onClick={() => setIsReplModalOpen(true)}
                                        disabled={syncing}
                                        className={cn(
                                            "flex items-center gap-2 px-4 py-2 bg-slate-800 text-blue-400 border border-blue-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all shadow-lg",
                                            syncing && "opacity-50 cursor-wait"
                                        )}
                                    >
                                        <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                                        {syncing ? 'Sincronizando...' : 'MOTOR DE SINCRONIZACIÓN'}
                                    </button>
                                )}
                            </div>

                            <div className="flex gap-2">
                                {(isAdmin || isSA) && (
                                    <button
                                        onClick={() => setShowDisabledSections(prev => !prev)}
                                        className={cn(
                                            "px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border",
                                            showDisabledSections
                                                ? "bg-slate-800 text-amber-400 border-amber-500/30"
                                                : "bg-slate-850/40 text-slate-400 border-slate-800/40 hover:bg-slate-800"
                                        )}
                                        title="Modo Debug: Mostrar u ocultar secciones inactivas/legacy"
                                    >
                                        {showDisabledSections ? 'Ocultar Inactivas' : 'Ver Inactivas'}
                                    </button>
                                )}

                                {detailCanEditGoals && (
                                    <>
                                        {isEditing ? (
                                            <>
                                                <button onClick={() => { setIsEditing(false); setEditingValues({}); }} className="bg-slate-800 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-700">Cancelar</button>
                                                <button onClick={saveChanges} className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 flex items-center gap-2">
                                                    <Database size={14} /> Guardar
                                                </button>
                                            </>
                                        ) : (
                                            <button onClick={() => setIsEditing(true)} className="bg-emerald-600/10 text-emerald-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white border border-emerald-500/20 flex items-center gap-2">
                                                <Calculator size={14} /> Editar
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-slate-800 mt-8">
                        <div>
                            <p className="text-white/70 text-[10px] font-bold uppercase mb-1">Meta Global</p>
                            <p className="text-3xl font-black text-white">{totalGoal}</p>
                        </div>
                        <div>
                            <p className="text-white/70 text-[10px] font-bold uppercase mb-1">Avance Acumulado</p>
                            <p className="text-3xl font-black text-emerald-500">{totalProgress}</p>
                        </div>
                        <div>
                            <p className="text-white/70 text-[10px] font-bold uppercase mb-1">% Cumplimiento</p>
                            <p className={cn("text-3xl font-black", globalPercentage >= (settings?.threshold_green ?? 90) ? 'text-emerald-500' : globalPercentage >= (settings?.threshold_amber ?? 75) ? 'text-amber-500' : 'text-red-500')}>{globalPercentage}%</p>
                        </div>
                    </div>
                </div>
            </header>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-800/80 text-white/60 text-[10px] uppercase font-black tracking-widest">
                            <th className="px-2 py-2.5 w-10"></th>
                            <th className="px-3 py-1.5 w-12 text-center">#</th>
                            <th className="px-4 py-1.5">SECCIÓN DEL PLAN</th>
                            <th className="px-4 py-1.5 text-center w-28">Meta</th>
                            <th className="px-4 py-1.5 text-center w-28">Avance</th>
                            <th className="px-4 py-1.5 text-center w-28">% Avance</th>
                            <th className="px-4 py-1.5 text-right">Detalle</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {plan.sections
                            .filter((s: any) => {
                                let isHiddenOrDisabled = false;
                                try {
                                    const parsed = typeof s.content === 'string' ? JSON.parse(s.content) : s.content;
                                    isHiddenOrDisabled = parsed?._config_hidden === true || parsed?._config_disabled === true || parsed?.active === false;
                                } catch (e) {}

                                if (isHiddenOrDisabled) {
                                    return (isAdmin || isSA) && showDisabledSections;
                                }
                                return true;
                            })
                            .map((section: any, index: number) => {
                            const snValue = resolveCanonicalSectionNumber(section);
                            // Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â REPARACIÃƒâ€œN CANÃƒâ€œNICA v18.12.04 Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â
                            const normalizedContent = normalizeSectionContent(snValue, section.content);
                            const metrics = computeSectionMetrics(snValue, normalizedContent, section.goal);

                            const visualSection = { ...section, section_number: snValue };
                            // v18.12.05: Usar helper para respetar meta manual en visualización
                            const liveG = getGeneralViewGoal(section, metrics);
                            const liveP = metrics.progress;

                            const currentMinGoal = editingValues[section.id]?.min_goal !== undefined ? editingValues[section.id].min_goal : section.min_goal;
                            const currentGoal = editingValues[section.id]?.goal !== undefined ? editingValues[section.id].goal : liveG;
                            const currentProgress = editingValues[section.id]?.progress !== undefined ? editingValues[section.id].progress : liveP;
                            const percentage = currentGoal > 0 ? Math.round((currentProgress / currentGoal) * 100) : 0;

                            return (
                                <tr
                                    key={section.id}
                                    draggable={canEditStructure}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                                    onDrop={() => handleDrop(index)}
                                    className={cn(
                                        "hover:bg-slate-800/40 transition-all group",
                                        dragIndex === index && "opacity-40 bg-slate-800",
                                        dragOverIndex === index && dragIndex !== index && "border-t-2 border-emerald-500"
                                    )}
                                >
                                    <td className="px-2 py-1.5 text-center cursor-grab active:cursor-grabbing">
                                        {canEditStructure && <GripVertical size={14} className="text-slate-600 hover:text-emerald-400 mx-auto" />}
                                    </td>
                                    <td className="px-3 py-1.5 text-center font-mono text-white/70 text-sm">{index + 1}</td>
                                    <td className="px-4 py-1.5">
                                        {isEditing && canEditStructure ? (
                                            <input
                                                type="text"
                                                value={editingValues[section.id]?.name !== undefined ? editingValues[section.id].name : section.name}
                                                onChange={(e) => handleEditChange(section.id, 'name', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                title="Editar nombre de la sección"
                                            />
                                        ) : (
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5">
                                                <span className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors whitespace-normal break-words leading-tight block">{section.name}</span>
                                                <div className="flex gap-1">
                                                    {(() => {
                                                        try {
                                                            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
                                                            return (
                                                                <>
                                                                    {parsed?._config_hidden === true && (
                                                                        <span className="px-1.5 py-0.5 bg-slate-850 text-amber-500 border border-amber-500/20 text-[8px] font-black rounded uppercase tracking-widest leading-none">Oculta</span>
                                                                    )}
                                                                    {parsed?._config_disabled === true && (
                                                                        <span className="px-1.5 py-0.5 bg-red-950/40 text-red-400 border border-red-900/40 text-[8px] font-black rounded uppercase tracking-widest leading-none">Desactivada</span>
                                                                    )}
                                                                </>
                                                            );
                                                        } catch (e) {
                                                            return null;
                                                        }
                                                    })()}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-1.5 text-center">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={currentGoal}
                                                onChange={(e) => handleEditChange(section.id, 'goal', parseInt(e.target.value) || 0)}
                                                disabled={detectStructuralType(visualSection) === 'COMISIONES'}
                                                className={cn(
                                                    "w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-center font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500",
                                                    detectStructuralType(visualSection) === 'COMISIONES' && "opacity-50 cursor-not-allowed"
                                                )}
                                            />
                                        ) : (
                                            <span className="text-white/60 font-mono">{currentGoal}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-1.5 text-center">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={currentProgress}
                                                onChange={(e) => handleEditChange(section.id, 'progress', parseInt(e.target.value) || 0)}
                                                disabled={detectStructuralType(visualSection) === 'COMISIONES'}
                                                className={cn(
                                                    "w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-center font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500",
                                                    detectStructuralType(visualSection) === 'COMISIONES' && "opacity-50 cursor-not-allowed"
                                                )}
                                            />
                                        ) : (
                                            <span className="text-white font-bold font-mono">{currentProgress}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-1.5">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${percentage}%` }}
                                                    className={cn(
                                                        "h-full rounded-full",
                                                        percentage >= (settings?.threshold_green || 90) ? "bg-emerald-500" :
                                                            percentage >= (settings?.threshold_amber || 75) ? "bg-amber-500" :
                                                                "bg-red-500"
                                                    )}
                                                />
                                            </div>
                                            <span className={cn(
                                                "text-[10px] font-black font-mono",
                                                percentage >= (settings?.threshold_green || 90) ? "text-emerald-500" :
                                                    percentage >= (settings?.threshold_amber || 75) ? "text-amber-500" :
                                                        "text-red-500"
                                            )}>{percentage}%</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-2.5 text-right">
                                        <div className="flex justify-end gap-2">
                                            {isSA ? (
                                                <>
                                                    {(() => {
                                                        try {
                                                            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
                                                            const isHidden = parsed?._config_hidden === true;
                                                            const isDisabled = parsed?._config_disabled === true;
                                                            return (
                                                                 <>
                                                                     <button
                                                                         onClick={(e) => { e.stopPropagation(); handleToggleSectionVisibility(section, 'hide'); }}
                                                                         className={cn(
                                                                             "p-2 hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center min-w-[36px] min-h-[36px]",
                                                                             isHidden ? "text-amber-500 hover:text-amber-400" : "text-white/40 hover:text-amber-400"
                                                                         )}
                                                                         title={isHidden ? "Hacer Visible a Usuarios" : "Ocultar a Usuarios"}
                                                                     >
                                                                         {isHidden ? <EyeOff size={18} /> : <Eye size={18} />}
                                                                     </button>
                                                                     <button
                                                                         onClick={(e) => { e.stopPropagation(); handleToggleSectionVisibility(section, 'disable'); }}
                                                                         className={cn(
                                                                             "p-2 hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center min-w-[36px] min-h-[36px]",
                                                                             isDisabled ? "text-red-500 hover:text-red-400" : "text-white/40 hover:text-red-400"
                                                                         )}
                                                                         title={isDisabled ? "Activar Sección (Habilitar cálculos)" : "Desactivar Sección (Excluir cálculos)"}
                                                                     >
                                                                         <AlertTriangle size={18} />
                                                                     </button>
                                                                 </>
                                                            );
                                                        } catch (e) {
                                                            return null;
                                                        }
                                                    })()}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newName = prompt('Nuevo nombre para la seccion:', section.name);
                                                            if (newName && newName !== section.name) {
                                                                db.updateWorkPlanSection(section.id, { name: newName })
                                                                    .then(() => {
                                                                        fetchPlan();
                                                                    });
                                                            }
                                                        }}
                                                        className="text-white/40 hover:text-blue-400 p-2 hover:bg-slate-800 rounded-lg transition-all"
                                                        title="Renombrar Sección"
                                                    >
                                                        <Edit3 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedSection(visualSection)}
                                                        className="text-white/70 hover:text-emerald-400 p-2 hover:bg-slate-800 rounded-lg transition-all"
                                                        title="Editar Contenido"
                                                    >
                                                        <Edit size={18} />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => setSelectedSection(visualSection)}
                                                    className="text-white/70 hover:text-indigo-400 p-2 hover:bg-slate-800 rounded-lg transition-all"
                                                    title="Ver Contenido"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            )}
                                            {isSA ? (
                                                <button
                                                    onClick={() => handleDeleteSection(section.id, section.name)}
                                                    className="text-slate-700 hover:text-red-400 p-2 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                    title="Eliminar Sección"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {isSA && (
                    <div className="p-6 bg-slate-900/50 border-t border-slate-800 flex justify-center">
                        <button
                            onClick={handleAddSection}
                            className="flex items-center gap-2 text-emerald-500 text-xs font-black uppercase tracking-widest hover:text-emerald-400 transition-all"
                        >
                            <Plus size={16} /> AÑADIR NUEVA SECCIÓN
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
const PlanReplicaModal = ({ sourcePlanId, sourceName, onClose, onSyncApplied, hierarchy, isSuperAdmin, user }: any) => {
    const BLOCK_SYNC = true; // DEFENSA ACTIVA POR SOFTWARE v18.21.41
    const [activeTab, setActiveTab] = useState<'LEGACY' | 'FLEXIBLE_SANDBOX'>('FLEXIBLE_SANDBOX');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [syncing, setSyncing] = useState(false);
    const [syncMode, setSyncMode] = useState<'ESTRUCTURA' | 'ESTRUCTURA_Y_METAS'>('ESTRUCTURA');
    const syncMetas = syncMode === 'ESTRUCTURA_Y_METAS';
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');

    // --- ESTADOS SANDBOX FLEXIBLE (v18.21.40) ---
    const [sandboxSourceId, setSandboxSourceId] = useState<string | null>(null);
    const [sandboxDestinations, setSandboxDestinations] = useState<string[]>([]);

    // Fallback inicial estático de 9 secciones (Hotfix v18.24.1)
    const [sandboxSections, setSandboxSections] = useState<any[]>([
        { id: 1, name: 'S1 Objetivos contribuir Agenda Presidente', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Seguro', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', enabled: true },
        { id: 2, name: 'S2 Objetivos de contribucion plan estrategico', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Seguro', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', enabled: true },
        { id: 3, name: 'S3 Diagnostico Sectorial y de Gestion', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Revisión', color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', enabled: true, note: 'Diagnóstico sectorial local se preserva' },
        { id: 4, name: 'S4 Estructura de la Comision', mode: 'SOLO_COLUMNAS_CONFIGURACION', status: 'Se fusionaría', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', enabled: true },
        { id: 5, name: 'S5 Comisiones Locales', mode: 'EXCLUIR_ELEMENTO', status: 'Excluido', color: 'text-red-400 bg-red-500/10 border-red-500/20', enabled: false, note: 'Excluido por defecto' },
        { id: 6, name: 'S6 Eventos de la Comision', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Se agregaría', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', enabled: true },
        { id: 7, name: 'S7 Proyectos Estrategicos', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Se agregaría', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', enabled: true },
        { id: 8, name: 'S8 Convenios y Vinculacion', mode: 'SOLO_COLUMNAS_CONFIGURACION', status: 'Se agregaría', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', enabled: true },
        { id: 9, name: 'S9 Temas Relevantes para Socios Directos', mode: 'SOLO_ESTRUCTURA_FORMATO', status: 'Se agregaría', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', enabled: true }
    ]);

    const [sandboxDryRunLogs, setSandboxDryRunLogs] = useState<string[]>([]);
    const [sandboxGlobalMode, setSandboxGlobalMode] = useState('CONSERVAR_Y_AGREGAR_DIFERENTES');
    const [sandboxIncludeMetas, setSandboxIncludeMetas] = useState(false);
    const [showDryRunModal, setShowDryRunModal] = useState(false);
    const [showPreflightDetailModal, setShowPreflightDetailModal] = useState(false);
    const [showAdvancedSyncControls, setShowAdvancedSyncControls] = useState(false);
    const [showSelectiveAdvancedEditor, setShowSelectiveAdvancedEditor] = useState(false);
    const [selectedOperationalMode, setSelectedOperationalMode] = useState<SelectiveOperationalMode>('SOLO_ESTRUCTURA_ITEMS');
    const [selectiveMappingFilter, setSelectiveMappingFilter] = useState<SelectiveMappingFilter>('TODAS');
    const [sourcePreflightBaseline, setSourcePreflightBaseline] = useState<{ expectedSections: number; expectedItems: number } | null>(null);
    const [destinationPreflight, setDestinationPreflight] = useState<Record<string, SyncDestinationPreflight>>({});
    const [sourceSectionCatalog, setSourceSectionCatalog] = useState<any[]>([]);
    const [selectedMappingTargetSections, setSelectedMappingTargetSections] = useState<any[]>([]);
    const [selectiveActionBySection, setSelectiveActionBySection] = useState<Record<string, SelectiveMappingAction>>({});
    const [manualTargetBySection, setManualTargetBySection] = useState<Record<string, string>>({});
    const [sessionExpiredDetected, setSessionExpiredDetected] = useState(false);

    // --- ESTADOS PARA EDGE FUNCTION PREVIEW ---
    const [isTestingEngine, setIsTestingEngine] = useState(false);
    const [enginePreviewResult, setEnginePreviewResult] = useState<any>(null);

    const applyPlanningStructureSyncPreview = async () => {
        const targetName = selectedDestinationComms.length === 1 ? selectedDestinationComms[0].name : 'el destino seleccionado';
        const confirmMsg = `Esta accion insertara unicamente items y herramientas estructurales en ${targetName}. No copiara metas, avances, evidencias ni borrara informacion. Deseas continuar?`;
        if (!confirm(confirmMsg)) return;

        setIsTestingEngine(true);
        setEnginePreviewResult(null);

        const token = localStorage.getItem('gov_secure_token');
        console.log("[STRUCTURE_SYNC_EDGE_UI] clicked real sync");

        try {
            if(!token) throw new Error("No hay token de sesión.");

            const payload = {
                source_plan_id: selectedSourcePlanId,
                target_plan_id: selectedDestinationComms.length === 1 ? selectedDestinationComms[0].plan_id : null,
                mode: "SOLO_ITEMS_ESTRUCTURA",
                preserve_target_data: true,
                replace_existing: false,
                copy_goals: false,
                copy_progress: false,
            copy_evidence: false,
                execute_real: true,
                phase: "2E_ITEMS_STRUCTURE_ONLY"
            };

            const data = await invokeStructureSyncEdge(token, payload);

            setSessionExpiredDetected(false);
            setEnginePreviewResult(data);
            if (data.success) {
                if (onSyncApplied) await onSyncApplied();
                showToast(`Ã‰xito: Se insertaron ${data.inserted_items || 0} items.`, "success");
            } else if (data.blocked) {
                showToast("Motor conectado, escritura bloqueada.", "success");
            }
        } catch (e: any) {
            console.error("[STRUCTURE_SYNC_EDGE_UI] fetch_error", e);
            const isSessionError = String(e.message || '').toLowerCase().includes('expired') || String(e.message || '').toLowerCase().includes('revoked');
            if (isSessionError) {
                showToast("Sesión expirada. Cierra sesión e ingresa nuevamente antes de sincronizar.", "error");
            } else {
                showToast(`Error al ejecutar motor: ${e.message}`, "error");
            }
        } finally {
            setIsTestingEngine(false);
        }
    };

    const applyVisualSchemaSync = async () => {
        const targetName = selectedDestinationComms.length === 1 ? selectedDestinationComms[0].name : 'el destino seleccionado';
        const confirmMsg = `Esta accion copiara unicamente la configuracion visual del formato desde ${selectedSource?.name || 'el origen'} hacia ${targetName}. No copiara metas, avances, evidencias ni datos capturados. Deseas continuar?`;
        if (!confirm(confirmMsg)) return;

        setIsTestingEngine(true);
        setEnginePreviewResult(null);

        const token = localStorage.getItem('gov_secure_token');
        console.log("[VISUAL_SCHEMA_SYNC_EDGE_UI] clicked real sync");

        try {
            if(!token) throw new Error("No hay token de sesión.");

            const payload = {
                source_plan_id: selectedSourcePlanId,
                target_plan_id: selectedDestinationComms.length === 1 ? selectedDestinationComms[0].plan_id : null,
                mode: "SOLO_SCHEMA_VISUAL",
                preserve_target_data: true,
                replace_existing: false,
                copy_visual_schema: true,
                copy_goals: false,
                copy_progress: false,
                copy_evidence: false,
                execute_real: true,
                phase: "2E2_VISUAL_SCHEMA_ONLY"
            };

            const data = await invokeStructureSyncEdge(token, payload);

            setEnginePreviewResult(data);
            if (data.success) {
                if (onSyncApplied) await onSyncApplied();
                showToast("Fase 2E2 ejecutada con éxito.", "success");
            } else if (data.blocked) {
                showToast("Motor conectado, escritura bloqueada.", "success");
            }

        } catch(error: any) {
            console.error("Sync Phase 2E2 Error:", error);
            const isSessionError2e2 = String(error.message || '').toLowerCase().includes('expired') || String(error.message || '').toLowerCase().includes('revoked');
            if (isSessionError2e2) {
                showToast("Sesión expirada. Cierra sesión e ingresa nuevamente antes de sincronizar.", "error");
            } else {
                showToast("Error en Sincronización Fase 2E2: " + error.message, "error");
            }
        } finally {
            setIsTestingEngine(false);
        }
    };

    const applyPlanningGoalsSyncPreview = async () => {
        const targetName = selectedDestinationComms.length === 1 ? selectedDestinationComms[0].name : 'el destino seleccionado';
        const confirmMsg = `Esta accion copiara unicamente metas desde ${selectedSource?.name || 'el origen'} hacia ${targetName}. No copiara avances, evidencias, cumplimiento ni resultados. Deseas continuar?`;
        if (!confirm(confirmMsg)) return;

        setIsTestingEngine(true);
        setEnginePreviewResult(null);

        const token = localStorage.getItem('gov_secure_token');
        console.log("[GOALS_SYNC_EDGE_UI] clicked real sync");

        try {
            if(!token) throw new Error("No hay token de sesión.");

            const payload = {
                source_plan_id: selectedSourcePlanId,
                target_plan_id: selectedDestinationComms.length === 1 ? selectedDestinationComms[0].plan_id : null,
                mode: "SOLO_METAS",
                preserve_target_data: true,
                replace_existing: false,
                copy_goals: true,
                copy_progress: false,
                copy_evidence: false,
                execute_real: true,
                phase: "2F_GOALS_ONLY"
            };

            const data = await invokeStructureSyncEdge(token, payload);

            setEnginePreviewResult(data);
            if (data.success) {
                if (onSyncApplied) await onSyncApplied();
                showToast(`Ã‰xito: Se actualizaron metas de ${data.sections_goals_updated || 0} secciones y ${data.item_goals_updated || 0} items.`, "success");
            } else if (data.blocked) {
                showToast("Motor conectado, escritura bloqueada.", "success");
            }
        } catch (e: any) {
            console.error("[GOALS_SYNC_EDGE_UI] fetch_error", e);
            const isSessionErrorGoals = String(e.message || '').toLowerCase().includes('expired') || String(e.message || '').toLowerCase().includes('revoked');
            if (isSessionErrorGoals) {
                showToast("Sesión expirada. Cierra sesión e ingresa nuevamente antes de sincronizar.", "error");
            } else {
                showToast(`Error al ejecutar motor: ${e.message}`, "error");
            }
        } finally {
            setIsTestingEngine(false);
        }
    };

    const [authCheck1, setAuthCheck1] = useState(false);
    const [authCheck2, setAuthCheck2] = useState(false);
    const [authCheck3, setAuthCheck3] = useState(false);
    const [authPhrase, setAuthPhrase] = useState('');
    const [authStatusText, setAuthStatusText] = useState('');
    const [preparedSignature, setPreparedSignature] = useState<any>(null);
    const [checkpointStatus, setCheckpointStatus] = useState<'DRAFT' | 'READY' | 'INVALIDATED' | 'EXPIRED' | 'BLOCKED'>('DRAFT');
    const [preparedCheckpoint, setPreparedCheckpoint] = useState<any>(null);
    const [validationResult, setValidationResult] = useState<any>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [backupSimulationResult, setBackupSimulationResult] = useState<any>(null);
    const [isSimulatingBackup, setIsSimulatingBackup] = useState(false);
    const [contingencyStatus, setContingencyStatus] = useState<string>('NORMAL');
    const [lastSafeEvent, setLastSafeEvent] = useState<any>({
        phase: 'PHASE_0_PREFLIGHT_READY',
        timestamp: new Date().toISOString(),
        event_type: 'SYSTEM_BOOT_SAFE',
        status: 'SUCCESS',
        recommended_action: 'Ninguna'
    });

    // FIX CRÍTICO v14.5.50: Cargar instancias directamente desde DB, independiente de hierarchy prop
    const [catalogComms, setCatalogComms] = useState<any[]>([]);
    const [loadingCatalog, setLoadingCatalog] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                setLoadingCatalog(true);
                const [commissions, plans] = await Promise.all([
                    db.getAllCommissions(),
                    db.getWorkPlans(user)
                ]);

                const latestPlanByCommission = new Map<string, any>();
                (plans || []).forEach((plan: any) => {
                    const key = String(plan.commission_id || '');
                    if (!key) return;
                    const current = latestPlanByCommission.get(key);
                    if (!current || Number(plan.year || 0) > Number(current.year || 0)) {
                        latestPlanByCommission.set(key, plan);
                    }
                });

                const hydrated = (commissions || []).map((c: any) => {
                    const plan = latestPlanByCommission.get(String(c.id));
                    const sectionsCount = Number(plan?.sections_count || 0);
                    const hasWorkPlan = Boolean(plan);
                    const isSourceEligible = hasWorkPlan && sectionsCount > 0;
                    const isTargetEligible = hasWorkPlan && c.type !== 'DEMO';

                    return {
                        ...c,
                        plan_id: plan?.id || null,
                        has_work_plan: hasWorkPlan,
                        sections_count: sectionsCount,
                        eligible_as_source: isSourceEligible,
                        eligible_as_target: isTargetEligible,
                        eligible: hasWorkPlan, // used generally
                        eligible_reason: !hasWorkPlan
                            ? 'Sin plan de trabajo'
                            : ''
                    };
                });

                setCatalogComms(hydrated.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
            } catch (e) {
                console.error('[PlanReplicaModal] Error cargando comisiones:', e);
                setCatalogComms([]);
            } finally {
                setLoadingCatalog(false);
            }
        };
        load();
    }, []);

    const selectedSource = useMemo(() => catalogComms.find((c: any) => c.id === sandboxSourceId) || null, [catalogComms, sandboxSourceId]);
    const selectedSourcePlanId = useMemo(() => selectedSource?.plan_id || sourcePlanId || null, [selectedSource, sourcePlanId]);
    const selectedDestinationComms = useMemo(() => catalogComms.filter((c: any) => sandboxDestinations.includes(c.id)), [catalogComms, sandboxDestinations]);
    const sourceFunctionalType = useMemo(() => canonicalInstanceType(selectedSource?.type || ''), [selectedSource?.type]);
    const destinationFunctionalTypes = useMemo(
        () => selectedDestinationComms
            .map((dest: any) => canonicalInstanceType(dest?.type || ''))
            .filter((type) => Boolean(type)),
        [selectedDestinationComms]
    );
    const hasCrossTypeDestination = useMemo(
        () => Boolean(
            sourceFunctionalType &&
            destinationFunctionalTypes.some((type) => type !== sourceFunctionalType)
        ),
        [destinationFunctionalTypes, sourceFunctionalType]
    );
    const hasMixedDestinationTypes = useMemo(
        () => new Set(destinationFunctionalTypes).size > 1,
        [destinationFunctionalTypes]
    );
    const refreshSelectedTargetState = useCallback(async (targetPlanId?: string | null) => {
        if (!targetPlanId) return;
        try {
            const planDetail = await db.getWorkPlanDetail(targetPlanId);

            setSelectedMappingTargetSections(planDetail?.sections || []);

            const selectedDest = selectedDestinationComms.find((dest: any) => dest.plan_id === targetPlanId);
            if (selectedDest) {
                const sectionCount = (planDetail?.sections || []).length;
                const itemCount = Number(planDetail?.item_count || 0);
                const hasConfigKeys = countSectionConfigKeys(planDetail?.sections || []) > 0;
                const totalGoal = Number(planDetail?.total_goal || 0);
                const totalProgress = Number(planDetail?.total_progress || 0);
                const phase = detectRegularizationPhase({
                    hasPlan: true,
                    sectionCount,
                    itemCount,
                    hasConfigKeys,
                    totalGoal,
                    expectedSections: sourcePreflightBaseline?.expectedSections || SYNC_EXPECTED_PLAN_SECTION_COUNT,
                    expectedItems: sourcePreflightBaseline?.expectedItems || 86
                });
                const action = getRegularizationActionMeta(phase);

                setDestinationPreflight((prev) => ({
                    ...prev,
                    [selectedDest.id]: {
                        commission_id: selectedDest.id,
                        name: selectedDest.name,
                        has_plan: true,
                        plan_id: targetPlanId,
                        section_count: sectionCount,
                        item_count: itemCount,
                        has_config_keys: hasConfigKeys,
                        total_goal: totalGoal,
                        total_progress: totalProgress,
                        missing_phase: phase,
                        next_allowed_action: action.label,
                        action_supported: action.supported,
                        snapshot_status: prev[selectedDest.id]?.snapshot_status || 'READY'
                    }
                }));
            }
        } catch (error) {
            console.error('[PlanReplicaModal] Error refreshing selected target state:', error);
        }
    }, [selectedDestinationComms, sourcePreflightBaseline]);

    useEffect(() => {
        if (catalogComms.length === 0) return;

        const sourceFromPlan = catalogComms.find((c: any) => c.plan_id === sourcePlanId) || catalogComms.find((c: any) => c.id === sourcePlanId);
        const eligibleFallback = catalogComms.find((c: any) => c.eligible) || catalogComms[0] || null;
        const nextSourceId = sourceFromPlan?.id || eligibleFallback?.id || null;

        setSandboxSourceId(prev => {
            if (prev && catalogComms.some((c: any) => c.id === prev)) return prev;
            return nextSourceId;
        });

        setSandboxDestinations(prev => {
            const valid = prev.filter(id => catalogComms.some((c: any) => c.id === id));
            return valid;
        });
    }, [catalogComms, sourcePlanId]);

    // EFECTO DE LECTURA DINÁMICA DE SECCIONES DESDE LA BASE DE DATOS (v18.24.1)
    useEffect(() => {
        const fetchSourceSections = async () => {
            const planId = selectedSourcePlanId;
            if (!planId) return;
            try {
                const planDetail = await db.getWorkPlanDetail(planId);
                if (planDetail && planDetail.sections) {
                    setSourceSectionCatalog(planDetail.sections || []);
                    const loadedSecs = planDetail.sections.map((s: any) => {
                        const sn = resolveCanonicalSectionNumber(s) || Number(s.section_number);

                        let initialMode = 'SOLO_ESTRUCTURA_FORMATO';
                        let initialStatus = 'Seguro';
                        let initialColor = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
                        let enabled = true;
                        let note = '';

                        // S3: Requiere revisión
                        if (sn === 3) {
                            initialMode = 'SOLO_ESTRUCTURA_FORMATO';
                            initialStatus = 'Revisión';
                            initialColor = 'text-purple-400 bg-purple-500/10 border-purple-500/20';
                            note = 'Diagnóstico sectorial local se preserva';
                        }
                        // S4: Estructura de la comisión
                        else if (sn === 4) {
                            initialMode = 'SOLO_COLUMNAS_CONFIGURACION';
                            initialStatus = 'Se fusionaría';
                            initialColor = 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
                        }
                        // S5: Comisiones Locales
                        else if (sn === 5) {
                            const selectedComms = selectedDestinationComms;

                            if (selectedComms.length === 0) {
                                initialMode = 'SOLO_ESTRUCTURA_FORMATO';
                                initialStatus = 'Seguro';
                                initialColor = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
                                enabled = true;
                                note = 'Disponible por defecto';
                            } else {
                                const hasUnknownType = selectedComms.some(c => !c.type);
                                const hasCommitteeOrConsejeria = selectedComms.some(c => {
                                    const t = normalizeVPType(c.type);
                                    return t === 'COMITE' || t === 'CONSEJERIA';
                                });
                                const hasCommission = selectedComms.some(c => {
                                    const t = normalizeVPType(c.type);
                                    return t === 'COMISION';
                                });

                                if (hasUnknownType) {
                                    initialMode = 'REQUIERE_APROBACION_MANUAL';
                                    initialStatus = 'Revisión';
                                    initialColor = 'text-purple-400 bg-purple-500/10 border-purple-500/20';
                                    enabled = false;
                                    note = 'Requiere revisión: Tipo de destino desconocido';
                                } else if (hasCommitteeOrConsejeria) {
                                    initialMode = 'EXCLUIR_ELEMENTO';
                                    initialStatus = 'Excluido';
                                    initialColor = 'text-red-400 bg-red-500/10 border-red-500/20';
                                    enabled = false;
                                    note = 'Excluido: S5 no aplica a Comités/Consejerías';
                                } else if (hasCommission) {
                                    initialMode = 'SOLO_ESTRUCTURA_FORMATO';
                                    initialStatus = 'Revisión';
                                    initialColor = 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
                                    enabled = true;
                                    note = 'Seleccionable con advertencia (Comisión)';
                                }
                            }
                        }
                        // S8: Convenios
                        else if (sn === 8) {
                            initialMode = 'SOLO_COLUMNAS_CONFIGURACION';
                            initialStatus = 'Se agregaría';
                            initialColor = 'text-blue-400 bg-blue-500/10 border-blue-500/20';
                        }
                        // S6, S7, S9
                        else if (sn === 6 || sn === 7 || sn === 9) {
                            initialMode = 'SOLO_ESTRUCTURA_FORMATO';
                            initialStatus = 'Se agregaría';
                            initialColor = 'text-blue-400 bg-blue-500/10 border-blue-500/20';
                        }

                        return {
                            id: sn,
                            name: `S${sn} ${s.name}`,
                            mode: initialMode,
                            status: initialStatus,
                            color: initialColor,
                            enabled: enabled,
                            note: note
                        };
                    });

                    loadedSecs.sort((a: any, b: any) => a.id - b.id);
                    setSandboxSections(loadedSecs);
                }
            } catch (e) {
                console.error('[PlanReplicaModal] Error loading source sections:', e);
            }
        };
        fetchSourceSections();
    // Solo re-ejecutar cuando cambia el plan origen â€” selectedDestinationComms se omite
    // intencionalmente para evitar bucle de re-render (es array recalculado en cada render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSourcePlanId]);

    useEffect(() => {
        const loadSourceBaseline = async () => {
            if (!selectedSourcePlanId) {
                setSourcePreflightBaseline(null);
                return;
            }
            try {
                const sourceDetail = await db.getWorkPlanDetail(selectedSourcePlanId);

                setSourcePreflightBaseline({
                    expectedSections: Math.max((sourceDetail?.sections || []).length, SYNC_EXPECTED_PLAN_SECTION_COUNT),
                    expectedItems: Math.max(Number(sourceDetail?.item_count || 0), 1)
                });
            } catch (error) {
                console.error('[PlanReplicaModal] Error loading source baseline:', error);
                setSourcePreflightBaseline({
                    expectedSections: SYNC_EXPECTED_PLAN_SECTION_COUNT,
                    expectedItems: 86
                });
            }
        };
        loadSourceBaseline();
    }, [selectedSourcePlanId]);

    useEffect(() => {
        const loadDestinationPreflight = async () => {
            if (selectedDestinationComms.length === 0) {
                setDestinationPreflight({});
                return;
            }

            const expectedSections = sourcePreflightBaseline?.expectedSections || SYNC_EXPECTED_PLAN_SECTION_COUNT;
            const expectedItems = sourcePreflightBaseline?.expectedItems || 86;

            try {
                const entries = await Promise.all(selectedDestinationComms.map(async (dest: any) => {
                    const snapshotRow = preflightRealData.snapshot_results_by_destination.find((row: any) => row.commission_id === dest.id);

                    if (!dest.has_work_plan || !dest.plan_id) {
                        const phase = detectRegularizationPhase({
                            hasPlan: false,
                            sectionCount: 0,
                            itemCount: 0,
                            hasConfigKeys: false,
                            totalGoal: 0,
                            expectedSections,
                            expectedItems
                        });
                        const action = getRegularizationActionMeta(phase);
                        return [dest.id, {
                            commission_id: dest.id,
                            name: dest.name,
                            has_plan: false,
                            plan_id: null,
                            section_count: 0,
                            item_count: 0,
                            has_config_keys: false,
                            total_goal: 0,
                            total_progress: 0,
                            missing_phase: phase,
                            next_allowed_action: action.label,
                            action_supported: action.supported,
                            snapshot_status: snapshotRow?.status || 'PENDING'
                        } satisfies SyncDestinationPreflight] as const;
                    }

                    const planDetail = await db.getWorkPlanDetail(dest.plan_id);

                    const sectionCount = (planDetail?.sections || []).length;
                    const itemCount = Number(planDetail?.item_count || 0);
                    const hasConfigKeys = countSectionConfigKeys(planDetail?.sections || []) > 0;
                    const totalGoal = Number(planDetail?.total_goal || 0);
                    const totalProgress = Number(planDetail?.total_progress || 0);
                    const phase = detectRegularizationPhase({
                        hasPlan: true,
                        sectionCount,
                        itemCount,
                        hasConfigKeys,
                        totalGoal,
                        expectedSections,
                        expectedItems
                    });
                    const action = getRegularizationActionMeta(phase);

                    return [dest.id, {
                        commission_id: dest.id,
                        name: dest.name,
                        has_plan: true,
                        plan_id: dest.plan_id,
                        section_count: sectionCount,
                        item_count: itemCount,
                        has_config_keys: hasConfigKeys,
                        total_goal: totalGoal,
                        total_progress: totalProgress,
                        missing_phase: phase,
                        next_allowed_action: action.label,
                        action_supported: action.supported,
                        snapshot_status: snapshotRow?.status || 'READY'
                    } satisfies SyncDestinationPreflight] as const;
                }));

                setDestinationPreflight(Object.fromEntries(entries));
            } catch (error) {
                console.error('[PlanReplicaModal] Error loading destination preflight:', error);
                setDestinationPreflight({});
            }
        };
        loadDestinationPreflight();
    }, [selectedDestinationComms, sourcePreflightBaseline]);

    useEffect(() => {
        const loadSelectiveTargetSections = async () => {
            const targetPlanId = selectedDestinationComms.length > 0 ? selectedDestinationComms[0]?.plan_id : null;
            if (!targetPlanId) {
                setSelectedMappingTargetSections([]);
                return;
            }
            try {
                const planDetail = await db.getWorkPlanDetail(targetPlanId);
                setSelectedMappingTargetSections(planDetail?.sections || []);
            } catch (error) {
                console.error('[PlanReplicaModal] Error loading selective mapping target sections:', error);
                setSelectedMappingTargetSections([]);
            }
        };
        loadSelectiveTargetSections();
    }, [selectedDestinationComms]);

    const assistantDestinations = selectedDestinationComms.map((dest: any) => {
        return destinationPreflight[dest.id] || {
            commission_id: dest.id,
            name: dest.name,
            has_plan: Boolean(dest.has_work_plan),
            plan_id: dest.plan_id || null,
            section_count: dest.sections_count || 0,
            item_count: 0,
            has_config_keys: false,
            total_goal: 0,
            total_progress: 0,
            missing_phase: detectRegularizationPhase({
                hasPlan: Boolean(dest.has_work_plan),
                sectionCount: Number(dest.sections_count || 0),
                itemCount: 0,
                hasConfigKeys: false,
                totalGoal: 0,
                expectedSections: sourcePreflightBaseline?.expectedSections || SYNC_EXPECTED_PLAN_SECTION_COUNT,
                expectedItems: sourcePreflightBaseline?.expectedItems || 86
            }),
            next_allowed_action: 'Calculando preflight...',
            action_supported: false,
            snapshot_status: 'PENDING'
        } as SyncDestinationPreflight;
    });

    const singleAssistantDestination = assistantDestinations.length === 1 ? assistantDestinations[0] : null;
    const assistantPrimaryMeta = singleAssistantDestination ? getRegularizationActionMeta(singleAssistantDestination.missing_phase) : null;
    const selectedSingleDestination = selectedDestinationComms.length === 1 ? selectedDestinationComms[0] : null;
    const selectedDestinationName = selectedSingleDestination?.name || 'destino seleccionado';
    const backendPilotOriginPlanId = 'c8367741-6076-46bf-87fa-8e1b8748e5f6';
    const isPilotOriginSelected = selectedSourcePlanId === backendPilotOriginPlanId;
    const hasSingleDestinationPlan = Boolean(selectedSingleDestination?.plan_id);
    const isSameFuncType = Boolean(
        selectedSource?.type &&
        selectedSingleDestination?.type &&
        selectedSource.type === selectedSingleDestination.type
    );
    const minimumManualSyncPreflightOk = (isPilotOriginSelected || isSameFuncType) && hasSingleDestinationPlan && selectedDestinationComms.length === 1 && sandboxSourceId !== selectedSingleDestination?.id;
    const selectiveMappingPhase = singleAssistantDestination?.missing_phase || null;
    const selectiveMappingTarget = selectedDestinationComms[0] || null;
    const selectiveMappingRows = useMemo<SelectiveMappingRow[]>(() => {
        if (!selectedSourcePlanId || !selectiveMappingTarget || sourceSectionCatalog.length === 0) return [];

        const targetOptions = selectedMappingTargetSections.map((section: any, index: number) => {
            const number = getSectionNumberForMapping(section, index);
            return {
                id: section.id,
                label: `S${number} ${section.name || `Seccion ${number}`}`
            };
        });

        return sourceSectionCatalog.map((section: any, index: number) => {
            const sourceNumber = getSectionNumberForMapping(section, index);
            const sourceOrder = getSectionOrderForMapping(section, index);
            const sourceName = String(section?.name || `Seccion ${sourceNumber}`);
            const normalizedSourceName = normalizeSyncSectionName(sourceName);
            const applicable = appliesSectionToInstanceType(sourceNumber, selectiveMappingTarget?.type || '');

            const exactNameMatch = selectedMappingTargetSections.find((target: any) =>
                normalizeSyncSectionName(target?.name || '') === normalizedSourceName
            );
            const orderMatch = (exactNameMatch || !isSameFuncType) ? null : selectedMappingTargetSections.find((target: any, targetIndex: number) => {
                const targetNumber = getSectionNumberForMapping(target, targetIndex);
                const targetOrder = getSectionOrderForMapping(target, targetIndex);
                return targetNumber === sourceNumber || targetOrder === sourceOrder;
            });
            const suggestedTarget = exactNameMatch || orderMatch || null;
            const status: SelectiveMappingStatus = !applicable
                ? 'EXCLUIR'
                : exactNameMatch
                    ? 'MATCH'
                    : suggestedTarget
                        ? 'SUGERIDO'
                        : 'NO_MATCH';
            const defaultAction = getDefaultSelectiveAction(status, selectiveMappingPhase, applicable);

            return {
                source_key: String(section?.id || sourceNumber),
                source_section_id: section?.id || null,
                source_section_number: sourceNumber,
                source_section_name: sourceName,
                source_order_index: sourceOrder,
                target_section_id: suggestedTarget?.id || null,
                target_section_number: suggestedTarget ? getSectionNumberForMapping(suggestedTarget) : null,
                target_section_name: suggestedTarget?.name || 'Sin sugerencia',
                target_order_index: suggestedTarget ? getSectionOrderForMapping(suggestedTarget) : null,
                match_by_name: Boolean(exactNameMatch),
                match_by_order: Boolean(!exactNameMatch && suggestedTarget),
                status,
                default_action: defaultAction,
                recommended_action: defaultAction,
                impact_label: getSelectiveImpactLabel(defaultAction),
                applies_to_instance: applicable,
                available_target_options: targetOptions
            };
        });
    }, [selectedSourcePlanId, selectiveMappingTarget, sourceSectionCatalog, selectedMappingTargetSections, selectiveMappingPhase]);
    const selectiveMappingRequiresAttention = selectiveMappingRows.some((row) => row.status === 'SUGERIDO' || row.status === 'NO_MATCH');
    const hasExplicitDestinationSelection = selectedDestinationComms.length > 0;
    const hasSingleExplicitDestinationSelection = selectedDestinationComms.length === 1;
    const showSelectiveMappingPanel = Boolean(
        selectedSourcePlanId &&
        hasSingleExplicitDestinationSelection
    );
    const selectiveBackendSupportsSectionMapping = true;
    const hasGovSecureToken = typeof window !== 'undefined' && Boolean(localStorage.getItem('gov_secure_token'));
    const selectiveExecutionFirstManualTargetRow = selectiveMappingRows.find((row) => {
        const action = selectiveActionBySection[row.source_key] || row.default_action;
        return action === 'MAPEO_MANUAL' && !manualTargetBySection[row.source_key];
    });
    // FIX v18.26.71: Guardias de NO_MATCH y target_section_id eliminadas del blocked reason.
    // Para modos globales (SOLO_ESTRUCTURA_ITEMS, ESTRUCTURA_FORMATO, ESTRUCTURA_METAS,
    // ESTRUCTURA_FORMATO_METAS) las secciones NO_MATCH son destinos validos que el motor
    // crea en el plan destino. Solo MAPEO_MANUAL sin destino explicito es un bloqueo real.
    const selectiveExecutionHasExecutableRows = selectiveMappingRows.some((row) => {
        const action = selectiveActionBySection[row.source_key] || row.default_action;
        return action !== 'CONSERVAR_DESTINO' && action !== 'EXCLUIR_SECCION';
    });
    const fullAlignmentDisabled = selectedDestinationComms.length > 0 && hasCrossTypeDestination;
    const fullAlignmentDisabledReason = fullAlignmentDisabled
        ? 'Solo disponible entre instancias del mismo tipo funcional.'
        : null;
    const resolveSelectiveRowAction = useCallback((row: SelectiveMappingRow) => {
        const action = selectiveActionBySection[row.source_key] || row.default_action;
        if (fullAlignmentDisabled && FULL_ALIGNMENT_ACTIONS.has(action)) {
            return 'ESTRUCTURA_FORMATO' as SelectiveMappingAction;
        }
        return action;
    }, [fullAlignmentDisabled, selectiveActionBySection]);
    const resolvedSelectiveExecutionRows = useMemo(() => (
        selectiveMappingRows
            .map((row) => {
                const action = resolveSelectiveRowAction(row);
                const manualTargetSectionId = manualTargetBySection[row.source_key] || null;
                const targetSectionId = action === 'MAPEO_MANUAL'
                    ? manualTargetSectionId
                    : (manualTargetSectionId || row.target_section_id || null);
                return {
                    source_key: row.source_key,
                    source_section_number: row.source_section_number,
                    source_section_name: row.source_section_name,
                    source_section_id: String(row.source_section_id || ''),
                    target_section_id: targetSectionId,
                    action,
                    mapping_status: row.status,
                    manual_target_section_id: manualTargetSectionId
                };
            })
            .filter((row) => row.action !== 'CONSERVAR_DESTINO' && row.action !== 'EXCLUIR_SECCION')
    ), [manualTargetBySection, resolveSelectiveRowAction, selectiveMappingRows]);
    const resolvedSelectiveExecutionSummary = useMemo(() => (
        resolvedSelectiveExecutionRows.reduce((acc, row) => {
            if (row.action === 'MAPEO_MANUAL') acc.manual += 1;
            if (row.action === 'SOLO_ESTRUCTURA' || row.action === 'ESTRUCTURA_FORMATO' || row.action === 'ESTRUCTURA_METAS' || row.action === 'ESTRUCTURA_FORMATO_METAS' || row.action === 'COPIAR_SECCION_COMPLETA') acc.structure += 1;
            if (row.action === 'SOLO_FORMATO_VISUAL' || row.action === 'ESTRUCTURA_FORMATO' || row.action === 'ESTRUCTURA_FORMATO_METAS' || row.action === 'COPIAR_SECCION_COMPLETA') acc.visual += 1;
            if (row.action === 'SOLO_METAS' || row.action === 'ESTRUCTURA_METAS' || row.action === 'ESTRUCTURA_FORMATO_METAS' || row.action === 'COPIAR_SECCION_COMPLETA') acc.goals += 1;
            return acc;
        }, { manual: 0, structure: 0, visual: 0, goals: 0 })
    ), [resolvedSelectiveExecutionRows]);
    const hasFullAlignmentRows = useMemo(
        () => resolvedSelectiveExecutionRows.some((row) => FULL_ALIGNMENT_ACTIONS.has(row.action)),
        [resolvedSelectiveExecutionRows]
    );
    const effectiveOperationalModeLabel = useMemo(() => {
        if (showSelectiveAdvancedEditor) return 'Edición por sección';
        return SELECTIVE_OPERATIONAL_MODE_META[selectedOperationalMode]?.label || 'Modo no disponible';
    }, [selectedOperationalMode, showSelectiveAdvancedEditor]);
    const effectivePayloadActionLabel = useMemo(() => {
        const actionSet = Array.from(new Set(resolvedSelectiveExecutionRows.map((row) => row.action)));
        if (actionSet.length === 0) return 'SIN_ACCIONES';
        if (actionSet.length === 1) return actionSet[0];
        return `MIXTO:${actionSet.join('+')}`;
    }, [resolvedSelectiveExecutionRows]);
    const selectiveExecutionBlockedReason = !selectiveBackendSupportsSectionMapping
        ? 'Ejecucion selectiva pendiente de backend. Usa modo avanzado para fases completas.'
        : !hasExplicitDestinationSelection
            ? 'Selecciona destino.'
        : selectedDestinationComms.length > 1 && selectedDestinationComms.some((c: any) => !c.plan_id)
            ? 'Todos los destinos en lote deben tener un plan valido.'
        : selectedDestinationComms.length === 1 && !hasSingleDestinationPlan
            ? 'El destino seleccionado debe tener un plan valido.'
        : sessionExpiredDetected || !hasGovSecureToken
            ? 'Sesion expirada o no disponible. Ingresa nuevamente antes de sincronizar.'
            : fullAlignmentDisabled && hasFullAlignmentRows
                ? 'La alineacion completa solo puede ejecutarse entre instancias del mismo tipo. Usa Estructura + formato para sincronizacion parcial cruzada.'
            : selectedDestinationComms.length === 1 && selectiveExecutionFirstManualTargetRow
                ? `Falta destino manual en S${selectiveExecutionFirstManualTargetRow.source_section_number}.`
                : resolvedSelectiveExecutionRows.length === 0 || !selectiveExecutionHasExecutableRows
                    ? 'No hay cambios aplicables para esta modalidad.'
                    : null;
    const canExecuteSelectiveMapping = Boolean(
        selectiveMappingRows.length > 0 &&
        !selectiveExecutionBlockedReason
    );
    const selectiveExecutableSectionCount = resolvedSelectiveExecutionRows.length;
    const selectiveCopySummary = useMemo(() => ({
        structure: resolvedSelectiveExecutionSummary.structure > 0,
        visual: resolvedSelectiveExecutionSummary.visual > 0,
        goals: resolvedSelectiveExecutionSummary.goals > 0
    }), [resolvedSelectiveExecutionSummary]);
    const selectiveFooterPrimaryText = selectedDestinationComms.length > 1
        ? 'Ejecuta la modalidad seleccionada para los destinos elegidos con proteccion de datos capturados.'
        : 'Ejecuta la modalidad seleccionada para este destino con proteccion de datos capturados.';
    const filteredSelectiveMappingRows = useMemo(() => {
        return selectiveMappingRows.filter((row) => {
            const action = resolveSelectiveRowAction(row);
            switch (selectiveMappingFilter) {
                case 'SUGERIDAS':
                    return row.status === 'SUGERIDO';
                case 'MATCH':
                    return row.status === 'MATCH';
                case 'EXCLUIDAS':
                    return action === 'EXCLUIR_SECCION' || row.status === 'EXCLUIR';
                case 'MANUALES':
                    return action === 'MAPEO_MANUAL';
                case 'RIESGO':
                    return getSelectiveRiskLabel(row, action) !== 'Bajo';
                case 'METAS':
                    return getSelectiveActionFlags(action).goals;
                case 'FORMATO':
                    return getSelectiveActionFlags(action).visual;
                default:
                    return true;
            }
        });
    }, [resolveSelectiveRowAction, selectiveMappingFilter, selectiveMappingRows]);
    const batchDestinationSummaries = useMemo(() => {
        return selectedDestinationComms.map((dest: any) => {
            const preflight = destinationPreflight[dest.id];
            return {
                id: dest.id,
                name: dest.name,
                phase: preflight?.missing_phase || 'PENDING',
                sectionCount: preflight?.section_count ?? dest.sections_count ?? 0,
                itemCount: preflight?.item_count ?? 0,
                totalGoal: preflight?.total_goal ?? 0,
                hasConfigKeys: preflight?.has_config_keys ?? false
            };
        });
    }, [destinationPreflight, selectedDestinationComms]);
    const selectiveProtectionSummary = useMemo(() => {
        const actions = resolvedSelectiveExecutionRows.map((row) => getSelectiveActionFlags(row.action));
        const countBy = (key: 'structure' | 'visual' | 'goals') => actions.filter((action) => action[key]).length;
        return {
            protectedLabels: ['current_value', 'progress', 'evidencias', 'resultados', 'datos_capturados'],
            structure: countBy('structure'),
            visual: countBy('visual'),
            goals: countBy('goals')
        };
    }, [resolvedSelectiveExecutionRows]);

    useEffect(() => {
        if (selectiveMappingRows.length === 0) {
            setSelectiveActionBySection({});
            setManualTargetBySection({});
            return;
        }

        setSelectiveActionBySection(() => {
            const next: Record<string, SelectiveMappingAction> = {};
            selectiveMappingRows.forEach((row) => {
                next[row.source_key] = row.default_action;
            });
            return next;
        });

        setManualTargetBySection((prev) => {
            const next: Record<string, string> = {};
            selectiveMappingRows.forEach((row) => {
                if ((prev[row.source_key] && row.available_target_options.some((option) => option.id === prev[row.source_key])) || row.target_section_id) {
                    next[row.source_key] = prev[row.source_key] || row.target_section_id || '';
                }
            });
            const sameKeys = Object.keys(prev).length === Object.keys(next).length && Object.keys(next).every((key) => prev[key] === next[key]);
            return sameKeys ? prev : next;
        });
    }, [selectiveMappingRows]);

    const handleSelectiveActionChange = useCallback((sourceKey: string, action: SelectiveMappingAction) => {
        if (fullAlignmentDisabled && FULL_ALIGNMENT_ACTIONS.has(action)) {
            showToast("La alineación completa solo puede ejecutarse entre instancias del mismo tipo. Usa Estructura + formato para sincronización parcial cruzada.", "warning");
            action = 'ESTRUCTURA_FORMATO';
        }
        setSelectiveActionBySection((prev) => {
            if (prev[sourceKey] === action) return prev;
            return {
                ...prev,
                [sourceKey]: action
            };
        });
    }, [fullAlignmentDisabled]);

    const handleSelectiveManualTargetChange = useCallback((sourceKey: string, targetId: string) => {
        setManualTargetBySection((prev) => {
            if (prev[sourceKey] === targetId) return prev;
            return {
                ...prev,
                [sourceKey]: targetId
            };
        });
    }, []);
    const applyOperationalModeToRows = useCallback((mode: SelectiveOperationalMode) => {
        const nextMode = fullAlignmentDisabled && mode === 'ESTRUCTURA_FORMATO_METAS'
            ? 'ESTRUCTURA_FORMATO'
            : mode;
        if (fullAlignmentDisabled && mode === 'ESTRUCTURA_FORMATO_METAS') {
            showToast("La alineación completa solo puede ejecutarse entre instancias del mismo tipo. Se ajustó automáticamente a Estructura + formato.", "warning");
        }
        setSelectedOperationalMode(nextMode);
        setSelectiveActionBySection((prev) => {
            const next = { ...prev };
            selectiveMappingRows.forEach((row) => {
                next[row.source_key] = getSelectiveActionFromOperationalMode(nextMode, row);
            });
            return next;
        });
    }, [fullAlignmentDisabled, selectiveMappingRows]);
    const handleBulkExcludeAll = useCallback(() => {
        setSelectiveActionBySection((prev) => {
            const next = { ...prev };
            selectiveMappingRows.forEach((row) => {
                next[row.source_key] = 'EXCLUIR_SECCION';
            });
            return next;
        });
    }, [selectiveMappingRows]);
    const handleBulkRestoreSuggested = useCallback(() => {
        setSelectiveActionBySection((prev) => {
            const next = { ...prev };
            selectiveMappingRows.forEach((row) => {
                next[row.source_key] = row.default_action;
            });
            return next;
        });
    }, [selectiveMappingRows]);
    const handleBulkConserveAll = useCallback(() => {
        setSelectiveActionBySection((prev) => {
            const next = { ...prev };
            selectiveMappingRows.forEach((row) => {
                next[row.source_key] = 'CONSERVAR_DESTINO';
            });
            return next;
        });
    }, [selectiveMappingRows]);

    useEffect(() => {
        if (!fullAlignmentDisabled) return;

        if (selectedOperationalMode === 'ESTRUCTURA_FORMATO_METAS') {
            setSelectedOperationalMode('ESTRUCTURA_FORMATO');
        }

        setSelectiveActionBySection((prev) => {
            let changed = false;
            const next = { ...prev };
            Object.entries(next).forEach(([sourceKey, action]) => {
                if (FULL_ALIGNMENT_ACTIONS.has(action)) {
                    next[sourceKey] = 'ESTRUCTURA_FORMATO';
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [fullAlignmentDisabled, selectedOperationalMode]);

    const handleExecuteSelectiveSelection = useCallback(async () => {
        if (!canExecuteSelectiveMapping) {
            showToast(selectiveExecutionBlockedReason || ADVANCED_SYNC_BUTTON_META.SELECTIVE.help, "warning");
            return;
        }
        if (!isSuperAdmin) {
            showToast("Solo SUPER_ADMIN puede ejecutar sincronizacion selectiva.", "error");
            return;
        }
        if (!selectedSourcePlanId || selectedDestinationComms.length === 0) {
            showToast("Selecciona un origen y al menos un destino con plan valido.", "warning");
            return;
        }
        if (hasFullAlignmentRows && hasCrossTypeDestination) {
            showToast("La alineación completa solo puede ejecutarse entre instancias del mismo tipo. Usa Estructura + formato para sincronización parcial cruzada.", "error");
            return;
        }

        const executableRows = resolvedSelectiveExecutionRows.map((row) => ({
            source_section_id: row.source_section_id,
            target_section_id: row.target_section_id,
            action: row.action,
            mapping_status: row.mapping_status,
            manual_target_section_id: row.manual_target_section_id
        }));

        if (executableRows.length === 0) {
            showToast("Selecciona al menos una accion ejecutable.", "warning");
            return;
        }

        console.info('[SELECTIVE_SYNC_UI] executable_rows', executableRows);

        const summaryLines = [
            `Modalidad seleccionada: ${effectiveOperationalModeLabel}`,
            `Origen: ${selectedSource?.name || 'No seleccionado'}`,
            `Tipo origen: ${getInstanceTypeLabel(selectedSource?.type)}`,
            `Tipo destino: ${selectedDestinationComms.length > 1 || hasMixedDestinationTypes ? 'Múltiples' : getInstanceTypeLabel(selectedDestinationComms[0]?.type)}`,
            `Es alineación completa: ${hasFullAlignmentRows ? 'Sí' : 'No'}`,
            `Destinos a sincronizar: ${selectedDestinationComms.length} (${selectedDestinationComms.map((c: any) => c.name).join(', ')})`,
            `Secciones a procesar por destino: ${executableRows.length}`,
            `Estructura/items: ${resolvedSelectiveExecutionSummary.structure}`,
            `Formato visual: ${resolvedSelectiveExecutionSummary.visual}`,
            `Metas a copiar: ${resolvedSelectiveExecutionSummary.goals}`,
            `Mapeo manual: ${resolvedSelectiveExecutionSummary.manual}`,
            `Avances modificados: 0`,
            `Evidencias copiadas: 0`,
            `Payload final: mode=SELECCION_POR_SECCION | action=${effectivePayloadActionLabel}`
        ];

        if (!confirm(`Se ejecutara la seleccion por seccion para ${selectedDestinationComms.length} destinos con proteccion de datos.\n\n${summaryLines.join('\n')}\n\nDeseas continuar?`)) {
            return;
        }

        setIsTestingEngine(true);
        setEnginePreviewResult(null);

        try {
            const token = localStorage.getItem('gov_secure_token');
            if (!token) throw new Error("No hay token de sesion.");

            let totalStructure = 0;
            let totalVisual = 0;
            let totalGoals = 0;
            let lastResult: any = null;

            for (const dest of selectedDestinationComms) {
                if (!dest.plan_id) continue;

                const payload = {
                    source_plan_id: selectedSourcePlanId,
                    target_plan_id: dest.plan_id,
                    phase: "SELECTIVE_SECTION_MAPPING",
                    mode: "SELECCION_POR_SECCION",
                    preserve_target_data: true,
                    replace_existing: false,
                    copy_progress: false,
                    copy_evidence: false,
                    execute_real: true,
                    section_mapping: executableRows
                };

                const data = await invokeStructureSyncEdge(token, payload);
                lastResult = data;

                if (data.success) {
                    totalStructure += (data.structure_sections_updated || 0);
                    totalVisual += (data.visual_sections_updated || 0);
                    totalGoals += (data.section_goals_updated || 0);
                    await refreshSelectedTargetState(dest.plan_id);
                }
            }

            setSessionExpiredDetected(false);
            if (lastResult) {
                setEnginePreviewResult({
                    ...lastResult,
                    structure_sections_updated: totalStructure,
                    visual_sections_updated: totalVisual,
                    section_goals_updated: totalGoals
                });
            }

            showToast(`Sincronización por lote finalizada. Totales - Estructura: ${totalStructure}, Formato: ${totalVisual}, Metas: ${totalGoals}, Avances: 0.`, "success");
            if (onSyncApplied && selectedDestinationComms.length > 0) {
                await onSyncApplied(selectedDestinationComms[0].plan_id);
            }
        } catch (error: any) {
            console.error("[SELECTIVE_SYNC_EDGE_UI] fetch_error", error);
            const errorSummary = getStructureSyncErrorSummary(error);
            setEnginePreviewResult({
                success: false,
                error: errorSummary.message,
                http_status: errorSummary.httpStatus,
                http_status_text: errorSummary.httpStatusText,
                sanitized_error_body: errorSummary.sanitizedBody,
                request_id: errorSummary.requestId
            });
            const isSessionError = String(error.message || '').toLowerCase().includes('expired') || String(error.message || '').toLowerCase().includes('revoked');
            setSessionExpiredDetected(isSessionError);
            if (isSessionError) {
                showToast("Sesion expirada. Cierra sesion e ingresa nuevamente antes de sincronizar.", "error");
            } else {
                showToast(`Error al ejecutar lote: ${errorSummary.toastMessage}`, "error");
            }
        } finally {
            setIsTestingEngine(false);
        }
    }, [canExecuteSelectiveMapping, effectiveOperationalModeLabel, effectivePayloadActionLabel, hasCrossTypeDestination, hasFullAlignmentRows, hasMixedDestinationTypes, isSuperAdmin, onSyncApplied, refreshSelectedTargetState, resolvedSelectiveExecutionRows, resolvedSelectiveExecutionSummary, selectiveExecutionBlockedReason, selectedDestinationComms, selectedSource?.name, selectedSource?.type, selectedSourcePlanId]);

    const selectiveMappingSummary = useMemo(() => {
        const rows = selectiveMappingRows;
        return rows.reduce((acc, row) => {
            acc.total += 1;
            acc[row.status] += 1;
            const action = resolveSelectiveRowAction(row);
            if (action === 'EXCLUIR_SECCION') acc.excluded += 1;
            if (action === 'CONSERVAR_DESTINO') acc.conserve += 1;
            if (action === 'MAPEO_MANUAL') acc.manual += 1;
            if (action === 'SOLO_ESTRUCTURA' || action === 'ESTRUCTURA_FORMATO' || action === 'ESTRUCTURA_METAS' || action === 'ESTRUCTURA_FORMATO_METAS' || action === 'COPIAR_SECCION_COMPLETA') acc.structure += 1;
            if (action === 'SOLO_FORMATO_VISUAL' || action === 'ESTRUCTURA_FORMATO' || action === 'ESTRUCTURA_FORMATO_METAS' || action === 'COPIAR_SECCION_COMPLETA') acc.visual += 1;
            if (action === 'SOLO_METAS' || action === 'ESTRUCTURA_METAS' || action === 'ESTRUCTURA_FORMATO_METAS' || action === 'COPIAR_SECCION_COMPLETA') acc.goals += 1;
            return acc;
        }, {
            total: 0,
            MATCH: 0,
            SUGERIDO: 0,
            NO_MATCH: 0,
            EXCLUIR: 0,
            excluded: 0,
            conserve: 0,
            manual: 0,
            structure: 0,
            visual: 0,
            goals: 0
        });
    }, [resolveSelectiveRowAction, selectiveMappingRows]);

    const handleAssistantExecute = async () => {
        if (!singleAssistantDestination) {
            showToast("Selecciona un solo destino para ejecutar el siguiente paso guiado.", "warning");
            return;
        }

        if (singleAssistantDestination.missing_phase === 'READY') {
            showToast("El destino ya esta listo. Usa Vista previa para revisar el preflight.", "success");
            return;
        }

        if (singleAssistantDestination.missing_phase === 'PLAN_BASE') {
            showToast("Esta fase MVP detecta PLAN_BASE, pero la creacion guiada del plan se habilitara en la siguiente iteracion.", "info");
            setShowPreflightDetailModal(true);
            return;
        }

        if (singleAssistantDestination.missing_phase === 'SECTIONS') {
            showToast("El destino necesita secciones base. La fase 2D sigue reservada para operacion controlada.", "info");
            setShowAdvancedSyncControls(true);
            return;
        }

        if (singleAssistantDestination.missing_phase === 'ITEMS') {
            await applyPlanningStructureSyncPreview();
            return;
        }

        if (singleAssistantDestination.missing_phase === 'VISUAL_SCHEMA') {
            await applyVisualSchemaSync();
            return;
        }

        if (singleAssistantDestination.missing_phase === 'GOALS') {
            await applyPlanningGoalsSyncPreview();
        }
    };

    const handleToggle = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleExecute = async () => {
        if (BLOCK_SYNC) {
            showToast("Sincronización bloqueada por BLOCK_SYNC en esta versión.", "error");
            return;
        }
        if (selectedIds.length === 0) return;
        const modeLabel = syncMode === 'ESTRUCTURA_Y_METAS'
            ? 'ESTRUCTURA + METAS (clonará también las metas anuales)'
            : 'SOLO ESTRUCTURA (metas quedarán en 0 en el destino)';
        if (!window.confirm(
            `TABULA RASA - MODO: ${modeLabel}\n\n` +
            `Este proceso BORRARÁ COMPLETAMENTE toda la información en ${selectedIds.length} instancia(s) seleccionada(s)\n` +
            `(indicadores, secciones, ítems y avances) y la reemplazará con el plan de:\n\n` +
            `"${sourceName}"\n\n` +
            `¿Confirmas la operación?`
        )) return;

        setSyncing(true);
        try {
            const result = await db.replicateWorkPlan(sourcePlanId, selectedIds, {
                syncStructure: true,
                syncMetas,
                syncMode
            });
            const msg = result.error > 0
                ? `${result.ok} instancias OK, ${result.error} con errores:\n${result.errors.join('\n')}`
                : `${result.ok} instancia(s) sincronizadas exitosamente con "${sourceName}".`;
            showToast(msg);
            if (result.ok > 0) onClose();
        } catch (e: any) {
            showToast(`ERROR GLOBAL: ${e?.message || "No se pudo completar la réplica masiva."}`);
        } finally {
            setSyncing(false);
        }
    };

    const runSandboxDryRun = async () => {
        setSandboxDryRunLogs(["[SANDBOX] Conectando con Supabase...", "[SANDBOX] Analizando de forma segura..."]);
        setShowDryRunModal(true);
        try {
            // Validaciones locales de preflight en la UI antes de proceder a la simulación
            const validationErrors: string[] = [];
            const selectedComms = selectedDestinationComms;

            const hasUnknownType = selectedComms.some(c => !c.type);
            const hasCommitteeOrConsejeria = selectedComms.some(c => {
                const t = normalizeVPType(c.type);
                return t === 'COMITE' || t === 'CONSEJERIA';
            });

            const s5Enabled = sandboxSections.find(s => s.id === 5)?.enabled;
            const hasS9 = sandboxSections.some(s => s.id === 9);

            if (!hasS9) {
                validationErrors.push("Falta la sección origen S9 (Temas Relevantes para Socios Directos).");
            }
            if (hasUnknownType) {
                validationErrors.push("Hay destinos seleccionados con tipo de instancia desconocido.");
            }
            if (s5Enabled && hasCommitteeOrConsejeria) {
                validationErrors.push("La sección 'Comisiones Locales' (S5) está habilitada para destinos incompatibles (Comité o Consejería).");
            }

            if (validationErrors.length > 0) {
                const errorLogs = [
                    "[BLOQUEO DE PREFLIGHT DETECTADO EN EL SANDBOX]",
                    ...validationErrors.map(err => `  - ${err}`),
                    "Operación de simulación cancelada por seguridad. Corrija las exclusiones o destinos."
                ];
                setSandboxDryRunLogs(errorLogs);
                showToast("Preflight bloqueado por inconsistencia de datos", "error");
                return;
            }

            // Encontrar comisiones correspondientes a sandboxDestinations
            const targets = selectedComms.map(c => c.id);
            if (targets.length === 0) {
                targets.push("53882ec0-1d96-4b02-aa00-cdc48a9d98c0");
            }

            const modeBySec: Record<string, string> = {};
            sandboxSections.forEach(s => {
                modeBySec[String(s.id)] = s.mode;
            });

            const result = await db.planningFlexibleSyncDryRun({
                sourcePlanId: selectedSourcePlanId || sourcePlanId,
                targetInstanceIds: targets,
                selectedSectionIds: sandboxSections.filter(s => s.enabled).map(s => s.id),
                modeBySection: modeBySec,
                preserveCapturedData: true,
                preserveDestinationOrphans: sandboxGlobalMode === 'CONSERVAR_Y_AGREGAR_DIFERENTES',
                includeMetas: sandboxIncludeMetas,
                dryRunOnly: true
            });

            if (result.success) {
                setSandboxDryRunLogs(result.logs || []);
                showToast("Dry-run calculado con éxito desde Supabase (solo lectura)", "success");
            } else {
                setSandboxDryRunLogs(result.logs || ["Error desconocido ejecutando el dry-run."]);
                showToast("Error en dry-run", "error");
            }
        } catch (e: any) {
            console.error(e);
            setSandboxDryRunLogs([
                `[ERROR SYSTEM] Falló el cálculo del dry-run real: ${e?.message || e}`,
                `[FALLBACK] Mostrando simulación segura offline...`,
                `[DRY-RUN Offline] Origen: ${selectedSource?.name || 'Sin origen'}`,
                `[DRY-RUN Offline] Destino: ${selectedDestinationComms.map(c => c.name).join(', ')}`,
                `[DRY-RUN Offline] Estado: Seguro (BLOCK_SYNC activo, cero escrituras)`
            ]);
            showToast("Ejecutando Dry-run en modo offline / fallback", "warning");
        }
    };

    const runSelectiveExecutionPreflight = async () => {
        setSandboxDryRunLogs(["[PREFLIGHT] Preparando payload real de vista previa..."]);
        setShowDryRunModal(true);
        setEnginePreviewResult(null);

        try {
            if (!selectedSourcePlanId || selectedDestinationComms.length !== 1 || !selectedDestinationComms[0]?.plan_id) {
                setSandboxDryRunLogs([
                    "[PREFLIGHT] Vista previa no disponible.",
                    "Selecciona exactamente un destino con plan valido para ejecutar el preflight real."
                ]);
                showToast("Selecciona un solo destino con plan valido para la vista previa.", "warning");
                return;
            }

            if (hasFullAlignmentRows && hasCrossTypeDestination) {
                const blockedMessage = "La alineacion completa solo puede ejecutarse entre instancias del mismo tipo. Usa Estructura + formato para sincronizacion parcial cruzada.";
                setSandboxDryRunLogs([
                    "[PREFLIGHT BLOQUEADO]",
                    blockedMessage,
                    `Modalidad seleccionada: ${effectiveOperationalModeLabel}`,
                    `Tipo origen: ${getInstanceTypeLabel(selectedSource?.type)}`,
                    `Tipo destino: ${getInstanceTypeLabel(selectedDestinationComms[0]?.type)}`
                ]);
                showToast(blockedMessage, "error");
                return;
            }

            const token = localStorage.getItem('gov_secure_token');
            if (!token) throw new Error("No hay token de sesion.");

            const payload = {
                source_plan_id: selectedSourcePlanId,
                target_plan_id: selectedDestinationComms[0].plan_id,
                phase: "SELECTIVE_SECTION_MAPPING",
                mode: "SELECCION_POR_SECCION",
                preserve_target_data: true,
                replace_existing: false,
                copy_progress: false,
                copy_evidence: false,
                execute_real: false,
                section_mapping: resolvedSelectiveExecutionRows.map((row) => ({
                    source_section_id: row.source_section_id,
                    target_section_id: row.target_section_id,
                    action: row.action,
                    mapping_status: row.mapping_status,
                    manual_target_section_id: row.manual_target_section_id
                }))
            };

            const result = await invokeStructureSyncEdge(token, payload);
            setEnginePreviewResult(result);

            if (result?.blocked) {
                const preview = result.preview || {};
                setSandboxDryRunLogs([
                    `[PREFLIGHT] Modalidad seleccionada: ${effectiveOperationalModeLabel}`,
                    `[PREFLIGHT] Tipo origen: ${getInstanceTypeLabel(selectedSource?.type)}`,
                    `[PREFLIGHT] Tipo destino: ${getInstanceTypeLabel(selectedDestinationComms[0]?.type)}`,
                    `[PREFLIGHT] Es alineacion completa: ${hasFullAlignmentRows ? 'Si' : 'No'}`,
                    `[PREFLIGHT] Metas a copiar: ${resolvedSelectiveExecutionSummary.goals}`,
                    `[PREFLIGHT] Avances modificados: 0`,
                    `[PREFLIGHT] Evidencias copiadas: 0`,
                    `[PREFLIGHT] Secciones procesadas: ${preview.sections_processed || 0}`,
                    `[PREFLIGHT] Estructura/items: ${preview.structure_sections_updated || 0}`,
                    `[PREFLIGHT] Formato visual: ${preview.visual_sections_updated || 0}`,
                    `[PREFLIGHT] Metas: ${preview.section_goals_updated || 0}`
                ]);
                showToast("Vista previa calculada con el mismo payload final de ejecucion.", "success");
            } else {
                setSandboxDryRunLogs(["[PREFLIGHT] Respuesta sin modo preview esperado.", JSON.stringify(result || {}, null, 2)]);
                showToast("La vista previa no devolvio el formato esperado.", "warning");
            }
        } catch (error: any) {
            console.error("[SELECTIVE_PREFLIGHT_UI] fetch_error", error);
            setSandboxDryRunLogs([
                `[ERROR SYSTEM] Fallo el calculo del preflight real: ${error?.message || error}`,
                `[PREFLIGHT] Origen: ${selectedSource?.name || 'Sin origen'}`,
                `[PREFLIGHT] Destino: ${selectedDestinationComms.map((c) => c.name).join(', ') || 'Sin destino'}`
            ]);
            showToast("Error al calcular la vista previa real.", "error");
        }
    };

    const filteredComms = catalogComms.filter(c => {
        const matchesSearch = normalize(c.name).includes(normalize(searchText));
        const matchesType = typeFilter === 'TODOS' || normalize(c.type) === normalize(typeFilter);
        return matchesSearch && matchesType;
    });

    return (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-[150] flex items-center justify-center p-2 md:p-6 overflow-hidden">
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 16 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                className="bg-slate-950 border border-slate-800 w-full max-w-[96vw] xl:max-w-[1500px] rounded-[2rem] overflow-hidden shadow-[0_0_80px_rgba(0,0,0,0.9)] flex flex-col max-h-[94vh] relative"
            >
                <header className="px-5 py-2.5 border-b border-slate-900 bg-slate-900/60 flex items-center justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-emerald-500/20 p-2 rounded-lg">
                            <Zap size={16} className="text-emerald-400" fill="currentColor" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-white uppercase tracking-tight leading-none">Réplica y Sincronización Estratégica <span className="text-emerald-500 px-2 py-0.5 bg-emerald-500/10 rounded ml-2 text-[8px] tracking-widest leading-none translate-y-[-1px] inline-block">{SYNC_MODAL_VERSION}</span></h3>
                            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-1">Gobernanza Anual Coparmex 2026</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* PESTAÃ‘AS DE ALTERNANCIA DE MOCKUP SANDBOX */}
                        <div className="flex bg-slate-900 p-0.5 rounded-lg border border-white/5">
                            <button
                                onClick={() => setActiveTab('LEGACY')}
                                className={cn(
                                    "px-3 py-1 rounded-md text-[8px] font-black uppercase tracking-wider transition-all",
                                    activeTab === 'LEGACY' ? "bg-slate-800 text-white shadow" : "text-slate-500 hover:text-white"
                                )}
                            >
                                Réplica Masiva
                            </button>
                            <button
                                onClick={() => setActiveTab('FLEXIBLE_SANDBOX')}
                                className={cn(
                                    "px-3 py-1 rounded-md text-[8px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5",
                                    activeTab === 'FLEXIBLE_SANDBOX' ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-white"
                                )}
                            >
                                <Sparkles size={8} className={activeTab === 'FLEXIBLE_SANDBOX' ? "animate-pulse text-white" : "text-slate-500"} />
                                Sincronizador Flexible (Sandbox)
                            </button>
                        </div>

                        <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full text-slate-500 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </header>

                {activeTab === 'LEGACY' ? (
                    // --- MÃ“DULO LEGACY (RÃ‰PLICA MASIVA CLÁSICA) ---
                    <>
                        <div className="px-5 py-2 bg-slate-900/30 border-b border-slate-900 flex items-center gap-3 shrink-0">
                            <div className="relative flex-1 group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-emerald-500 transition-colors" size={14} />
                                <input
                                    type="text"
                                    placeholder="Buscar instancia destino..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-800 rounded-lg pl-9 pr-4 py-2 text-[11px] font-bold text-white placeholder:text-slate-700 focus:outline-none focus:border-emerald-500/50 transition-all uppercase tracking-widest shadow-inner shadow-black/20"
                                />
                            </div>

                            <div className="flex bg-slate-950/50 rounded-lg border border-slate-800 p-0.5">
                                {(['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'] as const).map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(t)}
                                        className={cn(
                                            "px-3 py-1.5 rounded-md text-[8px] font-black uppercase tracking-tighter transition-all",
                                            typeFilter === t ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-400"
                                        )}
                                    >
                                        {t === 'TODOS' ? 'TODAS' : t.slice(0, 3)}
                                    </button>
                                ))}
                            </div>

                            <div className="h-6 w-[1px] bg-slate-800 mx-1" />

                            <button
                                onClick={() => setSelectedIds(selectedIds.length === filteredComms.length ? [] : filteredComms.map(c => c.id))}
                                className="px-4 py-2 rounded-lg border border-slate-800 text-[9px] font-black uppercase text-slate-500 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all flex items-center gap-2"
                            >
                                {selectedIds.length === filteredComms.length ? <RotateCcw size={12} /> : <CheckSquare size={12} />}
                                {selectedIds.length === filteredComms.length ? 'Limpiar' : 'Todo'}
                            </button>
                        </div>

                        {/* ADVERTENCIA DE BLINDAJE DE SEGURIDAD v18.21.41 */}
                        <div className="mx-5 mt-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-3 shrink-0">
                            <ShieldAlert className="text-amber-500 shrink-0" size={20} />
                            <div className="flex-1">
                                <h4 className="text-[10px] font-black uppercase text-amber-500 tracking-wider">Réplica Masiva Legacy Bloqueada</h4>
                                <p className="text-[9px] text-slate-400 font-bold uppercase mt-1 leading-normal">
                                    La réplica masiva destructiva legacy está temporalmente bloqueada por seguridad mediante <span className="text-amber-400 font-black">BLOCK_SYNC</span>. Use la pestaña superior <span className="text-emerald-400 font-black">Sincronizador Flexible (Sandbox)</span> para realizar simulaciones visuales seguras.
                                </p>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 bg-slate-950/40 scrollbar-hide content-start">
                            {loadingCatalog ? (
                                <div className="col-span-4 flex flex-col items-center justify-center py-16 gap-3">
                                    <div className="w-8 h-8 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                                    <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Cargando instancias...</span>
                                </div>
                            ) : filteredComms.length === 0 ? (
                                <div className="col-span-4 flex flex-col items-center justify-center py-16 gap-3">
                                    <Search size={32} className="text-slate-700" />
                                    <span className="text-slate-600 text-[10px] font-black uppercase tracking-widest">Sin resultados</span>
                                </div>
                            ) : null}
                            {!loadingCatalog && filteredComms.map(comm => {
                                const isSelected = selectedIds.includes(comm.id);
                                return (
                                    <label
                                        key={comm.id}
                                        className={cn(
                                            "p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col gap-2 relative overflow-hidden group",
                                            isSelected
                                                ? "bg-emerald-500/10 border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.1)]"
                                                : "bg-white/[0.02] border-white/8 hover:border-white/15 hover:bg-white/[0.04]"
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={isSelected}
                                            onChange={() => handleToggle(comm.id)}
                                        />
                                        <div className="flex justify-between items-start">
                                            <div className={cn(
                                                "w-7 h-7 rounded-lg flex items-center justify-center border transition-all shrink-0",
                                                isSelected ? "bg-emerald-500 border-emerald-400 shadow-md shadow-emerald-500/30" : "bg-slate-900 border-slate-800 group-hover:border-slate-600"
                                            )}>
                                                {isSelected ? (
                                                    <Check size={14} className="text-white" strokeWidth={3} />
                                                ) : (
                                                    <div className="w-3 h-3 rounded-sm border-2 border-slate-700" />
                                                )}
                                            </div>
                                            <span className="text-[7px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border bg-slate-900 border-slate-800 text-slate-500">
                                                {INSTANCE_TYPE_SINGULAR_LABELS[canonicalInstanceType(comm.type)] || comm.type || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "text-[11px] font-black uppercase leading-tight",
                                                isSelected ? "text-emerald-300" : "text-slate-300 group-hover:text-white"
                                            )}>
                                                {comm.name}
                                            </p>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        <footer className="px-5 py-3 border-t border-slate-900 bg-slate-900/60 flex justify-between items-center gap-4 shrink-0">
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black text-white/30 uppercase tracking-[0.3em]">Resumen de Selección</span>
                                <span className="text-sm font-black text-white">
                                    {selectedIds.length} <span className="text-emerald-500">Instancias Vinculadas</span>
                                    <span className="text-slate-600 text-[10px] font-normal ml-2">Â· Modo: {syncMode === 'ESTRUCTURA_Y_METAS' ? 'Estructura + Metas' : 'Solo Estructura'}</span>
                                </span>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-white hover:bg-slate-900 transition-all border border-transparent hover:border-slate-800">Cancelar</button>
                                <button
                                    onClick={handleExecute}
                                    disabled={BLOCK_SYNC || syncing || selectedIds.length === 0}
                                    className={cn(
                                        "px-8 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest text-white shadow-xl transition-all flex items-center gap-2 min-w-[180px] justify-center",
                                        BLOCK_SYNC
                                            ? "bg-slate-900 text-slate-500 border border-white/5 cursor-not-allowed"
                                            : "bg-emerald-600 shadow-emerald-900/30 hover:bg-emerald-500 disabled:opacity-30 disabled:grayscale"
                                    )}
                                >
                                    {BLOCK_SYNC ? (
                                        <>
                                            <ShieldAlert size={14} className="text-red-500 animate-pulse" />
                                            Bloqueado por BLOCK_SYNC
                                        </>
                                    ) : (
                                        <>
                                            {syncing ? <RefreshCw className="animate-spin" size={14} /> : <Zap size={14} fill="currentColor" />}
                                            Ejecutar Réplica Estratégica
                                        </>
                                    )}
                                </button>
                            </div>
                        </footer>
                    </>
                ) : (
                    // --- MÃ“DULO SANDBOX (SINCRONIZADOR FLEXIBLE MULTIDISPONIBLE v18.21.40) ---
                    <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-slate-950/80">
                        {/* BARRA LATERAL IZQUIERDA: CONFIGURACIÃ“N ORIGEN/DESTINO */}
                        <div className="w-full md:w-80 border-r border-slate-900 p-4 space-y-4 shrink-0 flex flex-col bg-slate-950">
                            <div>
                                <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">1. Seleccionar Instancia Origen</label>
                                <select
                                    value={sandboxSourceId || ''}
                                    onChange={(e) => setSandboxSourceId(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-[10px] font-bold text-white uppercase focus:outline-none focus:border-emerald-500"
                                >
                                    <option value="" disabled>Selecciona una instancia</option>
                                    {catalogComms.map((c: any) => (
                                        <option key={c.id} value={c.id} disabled={!c.eligible_as_source}>
                                            {c.name} ({INSTANCE_TYPE_SINGULAR_LABELS[canonicalInstanceType(c.type)] || c.type}){c.eligible_as_source ? '' : ` - ${c.eligible_reason}`}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-[7px] font-bold text-white/30 uppercase mt-1 block">
                                    {selectedSource
                                        ? `${selectedSource.plan_id ? `Plan ${String(selectedSource.plan_id).slice(0, 8)}` : 'Sin plan'} Â· ${selectedSource.sections_count || 0} secciones`
                                        : 'Catálogo cargado desde gov_commissions'}
                                </span>
                            </div>

                            <div className="flex-1 flex flex-col overflow-hidden">
                                <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">2. Instancias Destino Seleccionadas</label>
                                <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-hide">
                                    {catalogComms.map((c: any) => {
                                        const isSelected = sandboxDestinations.includes(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                disabled={!c.eligible_as_target || c.id === sandboxSourceId}
                                                onClick={() => {
                                                    setSandboxDestinations((prev: string[]) =>
                                                        prev.includes(c.id) ? prev.filter((x: string) => x !== c.id) : [...prev, c.id]
                                                    );
                                                }}
                                                className={cn(
                                                    "w-full text-left p-2.5 rounded-xl border transition-all text-[9.5px] font-bold uppercase tracking-tight flex items-center justify-between",
                                                    !c.eligible_as_target ? "opacity-50 cursor-not-allowed" : "",
                                                    isSelected ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400" : "bg-slate-900/40 border-slate-900 text-slate-500 hover:text-white"
                                                )}
                                            >
                                                <span>{c.name}</span>
                                                <span className="text-[7px] px-1.5 py-0.25 bg-slate-900/80 border border-white/5 rounded text-white/30">
                                                    {INSTANCE_TYPE_SINGULAR_LABELS[canonicalInstanceType(c.type)] || c.type || 'N/A'}
                                                    {c.id === sandboxSourceId ? ' Â· ORIGEN' : c.eligible_as_target ? (c.sections_count === 0 ? ' Â· DESTINO ELEGIBLE' : ' Â· COMISIÃ“N DESTINO') : ' Â· NO ELEGIBLE'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ADVERTENCIA DE MODO SANDBOX */}
                            <div className="bg-amber-500/5 border border-amber-500/20 p-3 rounded-2xl space-y-1">
                                <div className="flex items-center gap-1.5 text-amber-500">
                                    <AlertTriangle size={12} />
                                    <span className="text-[8.5px] font-black uppercase tracking-widest">Entorno Sandbox</span>
                                </div>
                                <p className="text-[7.5px] text-white/50 leading-relaxed uppercase tracking-tighter">Modo de simulación visual. Las inyecciones en base de datos están inhabilitadas en esta versión.</p>
                            </div>
                        </div>

                        {/* PANEL CENTRAL: MATRIZ DE SECCIONES, MODOS Y ESTADOS DE IMPACTO */}
                        <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden p-2 md:p-4 space-y-4 relative">
                            <div className="flex justify-between items-center border-b border-slate-900 pb-2">
                                <div>
                                    <h4 className="text-[10px] font-black text-white uppercase tracking-widest">3. Configurar Elementos y Modos de Sincronización</h4>
                                    <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">Define el comportamiento del Safe Merge por sección individual</p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-[7.5px] font-black text-white/40 uppercase">Estatus:</span>
                                    <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-black text-[7.5px] uppercase tracking-widest">Seguro</span>
                                    <span className="px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400 font-black text-[7.5px] uppercase tracking-widest">Revisión</span>
                                    <span className="px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-black text-[7.5px] uppercase tracking-widest">Excluido</span>
                                </div>
                            </div>

                            {/* --- CONTROLES GLOBALES DE MODO (MOCKUP) --- */}
                            {showSelectiveAdvancedEditor && (
                                <div className="bg-slate-950 border border-slate-900 rounded-2xl p-4 space-y-4">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="space-y-1">
                                            <h5 className="text-[9px] font-black uppercase text-white tracking-widest font-sans">Modo Global de Inyección</h5>
                                            <p className="text-[7.5px] text-white/50 tracking-wider">Deletes deshabilitados por defecto. Simulación sin escritura.</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setSandboxGlobalMode('CONSERVAR_Y_AGREGAR_DIFERENTES')}
                                                className={cn(
                                                    "px-3 py-1.5 rounded-lg border text-[8.5px] font-black uppercase tracking-widest transition-all",
                                                    sandboxGlobalMode === 'CONSERVAR_Y_AGREGAR_DIFERENTES'
                                                        ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                                                        : "bg-slate-900 border-slate-800 text-slate-500 hover:text-white"
                                                )}
                                            >
                                                Conservar Destino + Agregar Nuevos
                                            </button>
                                            <button
                                                onClick={() => setSandboxGlobalMode('REEMPLAZAR_ELEMENTOS_DESTINO')}
                                                className={cn(
                                                    "px-3 py-1.5 rounded-lg border text-[8.5px] font-black uppercase tracking-widest transition-all",
                                                    sandboxGlobalMode === 'REEMPLAZAR_ELEMENTOS_DESTINO'
                                                        ? "bg-red-500/20 border-red-500/50 text-red-400"
                                                        : "bg-slate-900 border-slate-800 text-slate-500 hover:text-white"
                                                )}
                                            >
                                                Reemplazar Existentes (Requiere Autorización)
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 border-t border-slate-900 pt-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={sandboxIncludeMetas}
                                                onChange={(e) => setSandboxIncludeMetas(e.target.checked)}
                                                className="w-3.5 h-3.5 rounded bg-slate-950 border-slate-800 text-indigo-500 focus:ring-indigo-500"
                                            />
                                            <span className="text-[8.5px] font-black text-white/70 uppercase tracking-widest">Incluir Metas (Estructura + Metas del Origen)</span>
                                        </label>
                                    </div>
                                    {sandboxGlobalMode === 'REEMPLAZAR_ELEMENTOS_DESTINO' && (
                                        <div className="bg-red-500/10 border border-red-500/20 p-2 rounded text-[7px] text-red-400 font-bold uppercase tracking-widest">
                                            Advertencia: Modo reemplazar aún no ejecutable. Requiere backup criptográfico y confirmación explícita.
                                        </div>
                                    )}
                                </div>
                            )}

                            <details className="bg-slate-950/95 border border-sky-500/20 rounded-2xl p-4 space-y-4">
                                <summary className="cursor-pointer list-none flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                    <div>
                                        <p className="text-[9px] font-black uppercase text-sky-400 tracking-widest">Ver diagnostico tecnico avanzado</p>
                                        <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider mt-1">Hashes, snapshot, sensores, bloqueo backend y lectura readonly.</p>
                                    </div>
                                    <span className="px-2 py-1 rounded bg-slate-900 border border-slate-800 text-[7px] font-black uppercase tracking-widest text-slate-300">
                                        Solo avanzado
                                    </span>
                                </summary>
                                <div className="mt-4 space-y-4">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                    <div>
                                        <h5 className="text-[9px] font-black uppercase text-sky-400 tracking-widest">Preflight de Backup / Snapshot de Seguridad</h5>
                                        <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider mt-1">Conectado a datos asíncronos reales de Supabase (v18.24.18A).</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const allSelectedCoveredOrNew = selectedDestinationComms.length > 0 && selectedDestinationComms.every(dest =>
                                                preflightRealData.snapshot_results_by_destination.some((r: any) => r.commission_id === dest.id) || !dest.has_work_plan
                                            );
                                            const globalUIStatus = allSelectedCoveredOrNew ? preflightRealData.global_status : 'PENDING';

                                            return (
                                                <span className={cn(
                                                    "px-2 py-1 rounded text-[7px] font-black uppercase tracking-widest border",
                                                    globalUIStatus === 'READY'
                                                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                        : "bg-amber-500/10 border-amber-500/20 text-amber-400"
                                                )}>
                                                    Estado: {globalUIStatus}
                                                </span>
                                            );
                                        })()}
                                        <span className="px-2 py-1 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-[7px] font-black uppercase tracking-widest">Sync real bloqueada: SÍ</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {selectedDestinationComms.length > 0 ? selectedDestinationComms.map((dest: any) => {
                                        const realDestData = preflightRealData.snapshot_results_by_destination.find(
                                            (r: any) => r.commission_id === dest.id
                                        );
                                        const status = realDestData ? realDestData.status : (dest.has_work_plan ? 'PENDING' : 'READY');
                                        const hash = realDestData ? realDestData.snapshot_hash : 'N/A';
                                        const sectionsCount = realDestData ? realDestData.validation.sectionsReadCount : (dest.sections_count || 0);
                                        const workPlanId = realDestData ? (preflightRealData.destinations_real.find((d: any) => d.commission_id === dest.id)?.work_plan_id || 'N/A') : 'N/A';
                                        const isCovered = !!realDestData;

                                        return (
                                            <div key={dest.id} className="rounded-xl border border-slate-800 bg-slate-950/70 p-3 space-y-2">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="text-[10px] font-black uppercase text-white tracking-tight">{dest.name}</p>
                                                        <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                                                            {INSTANCE_TYPE_SINGULAR_LABELS[canonicalInstanceType(dest.type)] || dest.type || 'N/A'} Â· ID: {dest.id.substring(0, 8)}...
                                                        </p>
                                                        {isCovered && (
                                                            <p className="text-[6.5px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                                                Plan ID: {workPlanId.substring(0, 8)}...
                                                            </p>
                                                        )}
                                                    </div>
                                                    <span className={cn(
                                                        "text-[7px] px-2 py-0.5 rounded font-black uppercase tracking-widest border",
                                                        status === 'READY'
                                                            ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                            : status === 'PARTIAL'
                                                                ? "bg-amber-500/10 border-amber-500/20 text-amber-400"
                                                                : "bg-red-500/10 border-red-500/20 text-red-400"
                                                    )}>
                                                        {status}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                                    <div className="rounded-lg bg-black/30 border border-white/5 p-2">
                                                        Secciones: {sectionsCount} reales
                                                    </div>
                                                    <div className="rounded-lg bg-black/30 border border-white/5 p-2">
                                                        includeMetas: true
                                                    </div>
                                                    <div className="rounded-lg bg-black/30 border border-white/5 p-2 col-span-2 truncate">
                                                        Hash: <span className="text-slate-300 font-mono select-all">{hash}</span>
                                                    </div>
                                                </div>
                                                {!isCovered && dest.has_work_plan && (
                                                    <p className="text-[6.5px] text-red-400 font-bold uppercase tracking-widest">
                                                        * No evaluado en el preflight asíncrono.
                                                    </p>
                                                )}
                                                {!isCovered && !dest.has_work_plan && (
                                                    <p className="text-[6.5px] text-sky-400 font-bold uppercase tracking-widest">
                                                        * Instancia sin plan activo. Se simulará la creación desde cero.
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    }) : (
                                        <div className="md:col-span-2 rounded-xl border border-slate-800 bg-black/30 p-4 text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                                            No hay destinos seleccionados para preflight.
                                        </div>
                                    )}
                                </div>

                                <div className="rounded-xl border border-slate-800 bg-black/30 p-3 space-y-2">
                                    <p className="text-[8px] font-black uppercase text-white tracking-widest">Reglas visibles de bloqueo</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[7px] text-slate-400 font-bold uppercase tracking-widest">
                                        <span>Sin snapshot READY no se puede sincronizar.</span>
                                        <span>Si cualquier destino no está READY, bloquear.</span>
                                        <span>Hash ausente bloquea.</span>
                                        <span>preserveCapturedData=false requiere doble confirmación.</span>
                                        <span>estimatedDeletes &gt; 0 requiere autorización reforzada.</span>
                                        <span>BLOCK_SYNC activo bloquea ejecución real.</span>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-1 border-t border-slate-800">
                                    <div className="text-[7px] font-bold uppercase tracking-widest text-slate-500 space-y-1">
                                        <p>BLOCK_SYNC activo: SÍ</p>
                                        <p>FLEXIBLE_SYNC_EXECUTION_ENABLED=false</p>
                                        <p className="text-emerald-500">Checkpoint Fase 2E controlado listo</p>
                                    </div>
                                    <button
                                        onClick={() => setShowPreflightDetailModal(true)}
                                        disabled={selectedDestinationComms.length === 0}
                                        className={cn(
                                            "px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all",
                                            selectedDestinationComms.length === 0
                                                ? "bg-slate-900 border border-slate-800 text-slate-500 cursor-not-allowed"
                                                : "bg-sky-600/10 text-sky-400 border border-sky-500/20 hover:bg-sky-600 hover:text-white"
                                        )}
                                    >
                                        Snapshot real-readonly disponible
                                    </button>
                                </div>
                            </div>

                            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 space-y-4">
                                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                                    <div className="space-y-1">
                                        <h5 className="text-[9px] font-black uppercase text-emerald-400 tracking-widest">Asistente de Regularizacion MVP</h5>
                                        <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">Preflight readonly guiado para detectar la fase actual del destino.</p>
                                    </div>
                                    {singleAssistantDestination && (
                                        <span className={cn(
                                            "px-2 py-1 rounded text-[7px] font-black uppercase tracking-widest border",
                                            assistantPrimaryMeta?.tone || "bg-slate-900 border-slate-800 text-slate-400"
                                        )}>
                                            {singleAssistantDestination.missing_phase}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {assistantDestinations.length > 0 ? assistantDestinations.map((dest) => {
                                        const actionMeta = getRegularizationActionMeta(dest.missing_phase);
                                        return (
                                            <div key={dest.commission_id} className="rounded-xl border border-slate-800 bg-black/30 p-3 space-y-2.5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="text-[10px] font-black uppercase text-white tracking-tight">{dest.name}</p>
                                                        <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                                                            has_plan={dest.has_plan ? 'SI' : 'NO'} Â· total_goal={dest.total_goal} Â· total_progress={dest.total_progress}
                                                        </p>
                                                    </div>
                                                    <span className={cn("px-2 py-0.5 rounded border text-[7px] font-black uppercase tracking-widest", actionMeta.tone)}>
                                                        {dest.missing_phase}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                                    <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Secciones: {dest.section_count}</div>
                                                    <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Items: {dest.item_count}</div>
                                                    <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Config: {dest.has_config_keys ? 'SI' : 'NO'}</div>
                                                    <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Snapshot: {dest.snapshot_status}</div>
                                                </div>
                                                <div className="rounded-lg border border-white/5 bg-slate-950/60 p-2.5">
                                                    <p className="text-[7px] text-slate-500 font-black uppercase tracking-widest">Siguiente accion recomendada</p>
                                                    <p className="text-[8px] text-white font-black uppercase tracking-wide mt-1">{dest.next_allowed_action}</p>
                                                    <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest mt-1">{actionMeta.summary}</p>
                                                </div>
                                            </div>
                                        );
                                    }) : (
                                        <div className="md:col-span-2 rounded-xl border border-slate-800 bg-black/30 p-4 text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                                            No hay destinos seleccionados para el asistente.
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1 border-t border-emerald-500/10">
                                    <div className="text-[7px] font-bold uppercase tracking-widest text-slate-500 space-y-1">
                                        <p>El asistente evalua PLAN_BASE, SECTIONS, ITEMS, VISUAL_SCHEMA, GOALS y READY.</p>
                                        <p>La Edge Function conserva el origen Bienestar y desbloquea destinos con plan para 2E, 2E2 y 2F.</p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2.5 w-full lg:w-auto">
                                        <button
                                            onClick={handleAssistantExecute}
                                            disabled={!singleAssistantDestination || isTestingEngine}
                                            className={cn(
                                                "flex-1 lg:flex-initial px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 border",
                                                !singleAssistantDestination || isTestingEngine
                                                    ? "bg-slate-900 border-slate-800 text-slate-500 cursor-not-allowed"
                                                    : "bg-emerald-600/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-600 hover:text-white"
                                            )}
                                        >
                                            {isTestingEngine ? <RefreshCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                                            {isTestingEngine ? "Ejecutando..." : "Ejecutar siguiente paso"}
                                        </button>
                                        <button
                                            onClick={() => setShowPreflightDetailModal(true)}
                                            disabled={selectedDestinationComms.length === 0}
                                            className={cn(
                                                "flex-1 lg:flex-initial px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 border",
                                                selectedDestinationComms.length === 0
                                                    ? "bg-slate-900 border border-slate-800 text-slate-500 cursor-not-allowed"
                                                    : "bg-sky-600/10 text-sky-400 border border-sky-500/20 hover:bg-sky-600 hover:text-white"
                                            )}
                                        >
                                            <Eye size={14} />
                                            Vista previa
                                        </button>
                                        {isSuperAdmin && (
                                            <label className="flex-1 lg:flex-initial px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 border bg-slate-900/80 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={showAdvancedSyncControls}
                                                    onChange={(e) => setShowAdvancedSyncControls(e.target.checked)}
                                                    className="w-4 h-4 rounded border-slate-700 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-950 bg-slate-950"
                                                />
                                                <Shield size={14} />
                                                Mostrar modo avanzado SUPER_ADMIN
                                            </label>
                                        )}
                                    </div>
                                </div>
                                </div>
                            </details>

                            {!hasExplicitDestinationSelection && (
                                <div className="rounded-3xl border border-slate-800 bg-black/20 p-4">
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                                        Selecciona una instancia destino para calcular el mapeo o preparar el lote.
                                    </p>
                                </div>
                            )}

                            <div className="rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-3 space-y-3">
                                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                                    <div className="space-y-1">
                                        <h5 className="text-[9px] font-black uppercase text-cyan-400 tracking-widest font-sans">Mapeo selectivo de secciones</h5>
                                        <p className="text-[7.5px] text-slate-400 font-bold uppercase tracking-wider">
                                            Vista simple para elegir una modalidad global y revisar protecciones antes de ejecutar.
                                        </p>
                                        <p className="text-[7px] text-amber-300 font-black uppercase tracking-widest">
                                            Esta fase prepara reglas visibles. No copia avances, evidencias, resultados, valor de avance ni datos capturados.
                                        </p>
                                    </div>
                                    <div className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2 text-[7px] font-bold uppercase tracking-widest text-slate-400 space-y-1">
                                        <p>Origen: {selectedSource?.name || sourceName || 'Sin origen'}</p>
                                        <p>Destino analizado: {showSelectiveMappingPanel ? (selectiveMappingTarget?.name || 'Sin destino') : 'Modo lote / múltiples destinos'}</p>
                                        {showSelectiveAdvancedEditor && <p>Modo global: {SELECTIVE_OPERATIONAL_MODE_META[selectedOperationalMode].shortLabel}</p>}
                                        {!hasSingleExplicitDestinationSelection && hasExplicitDestinationSelection && (
                                            <p className="text-amber-400">El mapeo detallado se calcula por destino. El lote conserva reglas globales y preflight visible.</p>
                                        )}
                                    </div>
                                </div>

                                {showSelectiveAdvancedEditor && (
                                <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-9 gap-2 text-[7px] font-black uppercase tracking-widest">
                                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-2 text-emerald-300">Match: {selectiveMappingSummary.MATCH}</div>
                                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-2 text-amber-300">Sugerido: {selectiveMappingSummary.SUGERIDO}</div>
                                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-2 text-rose-300">No match: {selectiveMappingSummary.NO_MATCH}</div>
                                    <div className="rounded-xl border border-slate-500/20 bg-slate-500/10 p-2 text-slate-300">Excluir: {selectiveMappingSummary.excluded}</div>
                                    <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 p-2 text-sky-300">Conservar: {selectiveMappingSummary.conserve}</div>
                                    <div className="rounded-xl border border-violet-500/20 bg-violet-500/10 p-2 text-violet-300">Manual: {selectiveMappingSummary.manual}</div>
                                    <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/10 p-2 text-indigo-300">Estructura: {selectiveMappingSummary.structure}</div>
                                    <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-2 text-yellow-300">Formato: {selectiveMappingSummary.visual}</div>
                                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-2 text-emerald-300">Metas: {selectiveMappingSummary.goals}</div>
                                </div>
                                )}

                                <div className="grid grid-cols-1 xl:grid-cols-[1.45fr_0.9fr] gap-4">
                                    <div className="space-y-3">
                                        <div className="rounded-2xl border border-slate-800 bg-black/30 p-3 space-y-3">
                                            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-[8px] font-black uppercase tracking-widest text-white">Modos operativos por lote</p>
                                                    <p className="text-[7px] font-bold uppercase tracking-widest text-slate-500 mt-1">
                                                        Aplica una regla base a todas las secciones visibles sin exigir edicion fila por fila.
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {Object.entries(SELECTIVE_OPERATIONAL_MODE_META).map(([mode, meta]) => {
                                                        const isDisabledFullMode = mode === 'ESTRUCTURA_FORMATO_METAS' && fullAlignmentDisabled;
                                                        return (
                                                            <button
                                                                key={mode}
                                                                disabled={isDisabledFullMode}
                                                                onClick={() => applyOperationalModeToRows(mode as SelectiveOperationalMode)}
                                                                className={cn(
                                                                    "px-3 py-2 rounded-xl border text-[7px] font-black uppercase tracking-widest transition-all",
                                                                    selectedOperationalMode === mode
                                                                        ? "bg-cyan-500/20 border-cyan-400/40 text-cyan-200"
                                                                        : "bg-slate-950/80 border-slate-800 text-slate-400 hover:border-cyan-500/30 hover:text-cyan-200",
                                                                    isDisabledFullMode && "cursor-not-allowed border-slate-800 text-slate-600 hover:border-slate-800 hover:text-slate-600"
                                                                )}
                                                                title={isDisabledFullMode ? fullAlignmentDisabledReason || '' : meta.description}
                                                            >
                                                                {meta.shortLabel}
                                                                <span className="block mt-1 text-[6px] text-slate-500">{meta.description}</span>
                                                                {isDisabledFullMode && (
                                                                    <span className="block mt-1 text-[6px] text-amber-300">{fullAlignmentDisabledReason}</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            {fullAlignmentDisabledReason && (
                                                <p className="text-[7px] font-bold uppercase tracking-widest text-amber-300">
                                                    {fullAlignmentDisabledReason}
                                                </p>
                                            )}
                                            {showSelectiveAdvancedEditor && (
                                            <div className="flex flex-wrap gap-2">
                                                <button onClick={handleBulkExcludeAll} className="px-3 py-2 rounded-xl border border-rose-500/20 bg-rose-500/10 text-rose-300 text-[7px] font-black uppercase tracking-widest">Excluir todo</button>
                                                <button onClick={handleBulkConserveAll} className="px-3 py-2 rounded-xl border border-sky-500/20 bg-sky-500/10 text-sky-300 text-[7px] font-black uppercase tracking-widest">Conservar destino</button>
                                                <button onClick={handleBulkRestoreSuggested} className="px-3 py-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 text-[7px] font-black uppercase tracking-widest">Restaurar sugerido</button>
                                            </div>
                                            )}
                                            {showSelectiveAdvancedEditor && (
                                            <div className="flex flex-wrap gap-2">
                                                {Object.entries(SELECTIVE_MAPPING_FILTER_LABELS).map(([filter, label]) => (
                                                    <button
                                                        key={filter}
                                                        onClick={() => setSelectiveMappingFilter(filter as SelectiveMappingFilter)}
                                                        className={cn(
                                                            "px-3 py-1.5 rounded-lg border text-[7px] font-black uppercase tracking-widest transition-all",
                                                            selectiveMappingFilter === filter
                                                                ? "bg-slate-200 text-slate-950 border-slate-100"
                                                                : "bg-slate-950/80 border-slate-800 text-slate-400 hover:text-white"
                                                        )}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            )}
                                            {!showSelectiveAdvancedEditor && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                                    <div className="rounded-xl border border-white/5 bg-slate-950/70 p-3 space-y-1">
                                                        <p className="text-white">Secciones detectadas</p>
                                                        <p>{selectiveMappingRows.length}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-white/5 bg-slate-950/70 p-3 space-y-1">
                                                        <p className="text-white">Secciones a sincronizar</p>
                                                        <p>{selectiveExecutableSectionCount}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-white/5 bg-slate-950/70 p-3 space-y-1">
                                                        <p className="text-white">Destinos seleccionados</p>
                                                        <p>{selectedDestinationComms.length}</p>
                                                    </div>
                                                    <div className="rounded-xl border border-white/5 bg-slate-950/70 p-3 space-y-1">
                                                        <p className="text-white">Se copiara</p>
                                                        <p>{[
                                                            selectiveCopySummary.structure ? 'estructura' : null,
                                                            selectiveCopySummary.visual ? 'formato' : null,
                                                            selectiveCopySummary.goals ? 'metas' : null
                                                        ].filter(Boolean).join(' + ') || 'nada'}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {hasExplicitDestinationSelection && !showSelectiveMappingPanel && (
                                            <details className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3 group" open={selectedDestinationComms.length <= 2}>
                                                <summary className="cursor-pointer list-none flex items-center justify-between">
                                                    <p className="text-[8px] font-black uppercase tracking-widest text-amber-300">
                                                        El mapeo detallado se edita destino por destino. En selección múltiple conservamos preflight y modo global para preparar el lote.
                                                    </p>
                                                    <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-[6.5px] font-black uppercase tracking-widest text-amber-400 group-open:hidden">
                                                        Ver destinos ({selectedDestinationComms.length})
                                                    </span>
                                                    <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-[6.5px] font-black uppercase tracking-widest text-amber-400 hidden group-open:inline">
                                                        Ocultar destinos
                                                    </span>
                                                </summary>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                                                    {batchDestinationSummaries.map((dest) => (
                                                        <div key={dest.id} className="rounded-xl border border-white/5 bg-slate-950/70 p-3 text-[7px] font-bold uppercase tracking-widest text-slate-400 space-y-1">
                                                            <p className="text-white">{dest.name}</p>
                                                            <p>Fase: {dest.phase}</p>
                                                            <p>Secciones: {dest.sectionCount} · Items: {dest.itemCount}</p>
                                                            <p>Config: {dest.hasConfigKeys ? 'SÍ' : 'NO'} · Metas: {dest.totalGoal}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                        )}

                                        {showSelectiveMappingPanel && showSelectiveAdvancedEditor && (
                                            <div className="rounded-2xl border border-slate-800 bg-black/30 overflow-hidden">
                                                <div className="grid grid-cols-[78px_minmax(220px,1.2fr)_minmax(220px,1fr)_minmax(210px,1fr)_140px_128px] gap-0 border-b border-slate-800 bg-slate-950/80 text-[7px] font-black uppercase tracking-widest text-slate-500">
                                                    <div className="px-3 py-2">Seccion</div>
                                                    <div className="px-3 py-2">Origen</div>
                                                    <div className="px-3 py-2">Destino</div>
                                                    <div className="px-3 py-2">Accion</div>
                                                    <div className="px-3 py-2">Impacto</div>
                                                    <div className="px-3 py-2">Riesgo</div>
                                                </div>
                                                <div className="max-h-[46vh] overflow-auto scrollbar-hide">
                                                    {filteredSelectiveMappingRows.length > 0 ? filteredSelectiveMappingRows.map((row) => {
                                                        const currentAction = resolveSelectiveRowAction(row);
                                                        const currentImpact = getSelectiveImpactLabel(currentAction);
                                                        const currentRisk = getSelectiveRiskLabel(row, currentAction);
                                                        const actionFlags = getSelectiveActionFlags(currentAction);
                                                        return (
                                                            <div key={row.source_key} className="grid grid-cols-[78px_minmax(220px,1.2fr)_minmax(220px,1fr)_minmax(210px,1fr)_140px_128px] gap-0 border-b border-slate-900/80 text-[7px]">
                                                                <div className="px-3 py-3 space-y-2">
                                                                    <span className="inline-flex px-2 py-1 rounded border border-white/10 bg-slate-950 text-slate-300 font-black uppercase tracking-widest">S{row.source_section_number}</span>
                                                                    <span className={cn("inline-flex px-2 py-1 rounded border font-black uppercase tracking-widest", getSelectiveMappingTone(row.status))}>{row.status}</span>
                                                                </div>
                                                                <div className="px-3 py-3 space-y-2 min-w-0">
                                                                    <p className="text-[8px] font-black uppercase tracking-tight text-white">{row.source_section_name}</p>
                                                                    <p className="text-slate-500 font-bold uppercase tracking-widest">Nombre={row.match_by_name ? 'SI' : 'NO'} Â· Orden={row.match_by_order ? 'SI' : 'NO'}</p>
                                                                    {!row.applies_to_instance && <p className="text-rose-300 font-black uppercase tracking-widest">No aplica al tipo</p>}
                                                                </div>
                                                                <div className="px-3 py-3 space-y-2">
                                                                    <p className="text-white font-black uppercase tracking-wide">{row.target_section_id ? `S${row.target_section_number} ${row.target_section_name}` : 'Sin sugerencia'}</p>
                                                                    <p className="text-slate-500 font-bold uppercase tracking-widest">Destino sugerido: {row.target_section_name}</p>
                                                                    {showAdvancedSyncControls && (
                                                                        <div className="text-[6px] text-slate-500 font-bold uppercase tracking-widest break-all">
                                                                            <p>source_section_id: {String(row.source_section_id || 'null')}</p>
                                                                            <p>target_section_id: {String((manualTargetBySection[row.source_key] || row.target_section_id || 'null'))}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="px-3 py-3 space-y-2">
                                                                    <select
                                                                        value={currentAction}
                                                                        onChange={(e) => handleSelectiveActionChange(row.source_key, e.target.value as SelectiveMappingAction)}
                                                                        className="w-full bg-slate-900 border border-slate-800 rounded-lg px-2 py-2 text-[7px] font-black uppercase tracking-widest text-white focus:outline-none focus:border-cyan-500"
                                                                    >
                                                                        {Object.entries(SELECTIVE_MAPPING_ACTION_LABELS).map(([value, label]) => (
                                                                            <option
                                                                                key={value}
                                                                                value={value}
                                                                                disabled={fullAlignmentDisabled && FULL_ALIGNMENT_ACTIONS.has(value as SelectiveMappingAction)}
                                                                            >
                                                                                {label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <p className="text-[6px] text-slate-400 font-bold uppercase tracking-wider">{SELECTIVE_MAPPING_ACTION_HELP[currentAction]}</p>
                                                                    {currentAction === 'MAPEO_MANUAL' && (
                                                                        <select
                                                                            value={manualTargetBySection[row.source_key] || ''}
                                                                            onChange={(e) => handleSelectiveManualTargetChange(row.source_key, e.target.value)}
                                                                            className="w-full bg-slate-950 border border-violet-500/30 rounded-lg px-2 py-2 text-[7px] font-black uppercase tracking-widest text-white focus:outline-none focus:border-violet-400"
                                                                        >
                                                                            <option value="">Selecciona seccion destino</option>
                                                                            {row.available_target_options.map((option) => (
                                                                                <option key={option.id} value={option.id}>{option.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    )}
                                                                </div>
                                                                <div className="px-3 py-3 space-y-2">
                                                                    <p className="text-cyan-300 font-black uppercase tracking-wide">{currentImpact}</p>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {actionFlags.structure && <span className="px-1.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-black uppercase tracking-widest">Items</span>}
                                                                        {actionFlags.visual && <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 font-black uppercase tracking-widest">Formato</span>}
                                                                        {actionFlags.goals && <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-black uppercase tracking-widest">Metas</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="px-3 py-3 space-y-2">
                                                                    <p className={cn(
                                                                        "font-black uppercase tracking-widest",
                                                                        currentRisk === 'Alto' ? "text-rose-300" : currentRisk === 'Medio' ? "text-amber-300" : "text-emerald-300"
                                                                    )}>{currentRisk}</p>
                                                                    <p className="text-[6px] text-slate-500 font-bold uppercase tracking-widest">Protege avance y evidencia</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    }) : (
                                                        <div className="p-4 text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                                                            No hay filas para el filtro actual.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <div className="rounded-2xl border border-slate-800 bg-black/30 p-3 space-y-3">
                                            <p className="text-[8px] font-black uppercase tracking-widest text-white">Resumen operativo</p>
                                            <div className="grid grid-cols-2 gap-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Filas visibles: {showSelectiveAdvancedEditor ? filteredSelectiveMappingRows.length : selectiveMappingRows.length}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Destino(s): {selectedDestinationComms.length}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Modalidad: {effectiveOperationalModeLabel}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Tipo origen: {getInstanceTypeLabel(selectedSource?.type)}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Tipo destino: {selectedDestinationComms.length > 1 || hasMixedDestinationTypes ? 'Multiples' : getInstanceTypeLabel(selectedDestinationComms[0]?.type)}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Alineacion completa: {hasFullAlignmentRows ? 'Si' : 'No'}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Items: {resolvedSelectiveExecutionSummary.structure}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Formato: {resolvedSelectiveExecutionSummary.visual}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Metas: {resolvedSelectiveExecutionSummary.goals}</div>
                                                <div className="rounded-lg bg-slate-950/70 border border-white/5 p-2">Protegidos: {selectiveProtectionSummary.protectedLabels.length}</div>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-800 bg-black/30 p-3 space-y-2">
                                            <p className="text-[8px] font-black uppercase tracking-widest text-white">Protecciones vigentes</p>
                                            {selectiveProtectionSummary.protectedLabels.map((label) => (
                                                <div key={label} className="rounded-lg bg-slate-950/70 border border-white/5 px-2.5 py-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                                    {label}: protegido
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => setShowSelectiveAdvancedEditor((prev) => !prev)}
                                            className={cn(
                                                "w-full rounded-2xl border px-3 py-2.5 text-[8px] font-black uppercase tracking-widest transition-all",
                                                showSelectiveAdvancedEditor
                                                    ? "border-cyan-400/30 bg-cyan-500/10 text-cyan-200"
                                                    : "border-slate-800 bg-slate-950/70 text-slate-300 hover:border-cyan-500/30 hover:text-cyan-200"
                                            )}
                                        >
                                            {showSelectiveAdvancedEditor ? 'Ocultar edicion por seccion / avanzado' : 'Editar por seccion / avanzado'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* LISTADO DE SECCIONES CON SELECTORES */}
                            {isSuperAdmin && showAdvancedSyncControls && (
                            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1.5 scrollbar-hide">
                                {sandboxSections.map(sec => (
                                    <div
                                        key={sec.id}
                                        className={cn(
                                            "p-2 md:p-3 rounded-2xl border flex flex-col md:flex-row md:items-center justify-between gap-3 transition-all",
                                            sec.enabled ? "bg-slate-900/50 border-slate-800" : "bg-slate-950/20 border-slate-900 opacity-40"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={sec.enabled}
                                                onChange={() => {
                                                    setSandboxSections(prev =>
                                                        prev.map(x => x.id === sec.id ? { ...x, enabled: !x.enabled } : x)
                                                    );
                                                }}
                                                className="w-4 h-4 rounded border-slate-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-950 bg-slate-950 cursor-pointer"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-[10px] sm:text-xs font-black text-white uppercase tracking-tight">{sec.name}</span>
                                                {sec.note && (
                                                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">{sec.note}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 self-end md:self-auto">
                                            {/* SELECTOR DE MODO POR SECCIÃ“N */}
                                            <select
                                                value={sec.mode}
                                                disabled={!sec.enabled}
                                                onChange={(e) => {
                                                    const newMode = e.target.value;
                                                    let newStatus = 'Seguro';
                                                    if (newMode === 'EXCLUIR_ELEMENTO') newStatus = 'Excluido';
                                                    if (newMode === 'REQUIERE_APROBACION_MANUAL') newStatus = 'Bloqueado';
                                                    setSandboxSections(prev =>
                                                        prev.map(x => x.id === sec.id ? { ...x, mode: newMode, status: newStatus } : x)
                                                    );
                                                }}
                                                className="bg-slate-950 border border-slate-900 rounded px-2.5 py-1 text-[8.5px] font-black uppercase text-white tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer"
                                            >
                                                <option value="SOLO_ESTRUCTURA_FORMATO">Solo Estructura</option>
                                                <option value="ESTRUCTURA_Y_METAS">Estructura + Metas</option>
                                                <option value="SOLO_COLUMNAS_CONFIGURACION">Solo Columnas</option>
                                                <option value="PRESERVAR_DESTINO">Preservar Destino</option>
                                                <option value="EXCLUIR_ELEMENTO">Excluir Sección</option>
                                                <option value="REQUIERE_APROBACION_MANUAL">Revisión Manual</option>
                                            </select>

                                            {/* ESTATUS VISUAL DE IMPACTO */}
                                            <span className={cn("px-2 py-0.5 rounded border text-[7.5px] font-black uppercase tracking-widest min-w-[90px] text-center", sec.color)}>
                                                {sec.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            )}

                            {/* BOTONERA Y RESUMEN FINAL */}
                            <div className="sticky bottom-0 z-20 bg-slate-900 border border-slate-900 rounded-3xl p-3 md:p-4 flex flex-col gap-4 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">

                                {/* MODO NORMAL */}
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                                    <div className="space-y-0.5 self-start sm:self-auto">
                                        <span className="text-[7.5px] font-black text-white/30 uppercase tracking-widest">Modo normal</span>
                                        <p className="text-white text-xs font-black uppercase tracking-tight">
                                            {selectiveFooterPrimaryText}
                                        </p>
                                        {selectiveExecutionBlockedReason && (
                                            <p className="text-[7px] text-amber-400 font-bold uppercase tracking-widest mt-1">
                                                {selectiveExecutionBlockedReason}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
                                        <button
                                            onClick={runSelectiveExecutionPreflight}
                                            disabled={!hasSingleExplicitDestinationSelection}
                                            className={cn(
                                                "flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 border",
                                                hasSingleExplicitDestinationSelection
                                                    ? "bg-indigo-600/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-600 hover:text-white"
                                                    : "bg-slate-900 border-slate-800 text-slate-500 cursor-not-allowed"
                                            )}
                                            title={ADVANCED_SYNC_BUTTON_META.DRY_RUN.help}
                                        >
                                            <History size={14} />
                                            Vista previa
                                        </button>
                                        <button
                                            onClick={handleExecuteSelectiveSelection}
                                            disabled={!canExecuteSelectiveMapping}
                                            className={cn(
                                                "flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 border transition-all",
                                                canExecuteSelectiveMapping
                                                    ? "bg-emerald-600/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-600 hover:text-white"
                                                    : "bg-emerald-600/50 text-white/50 border-emerald-500/20 cursor-not-allowed"
                                            )}
                                            title={ADVANCED_SYNC_BUTTON_META.SELECTIVE.help}
                                        >
                                            <Zap size={14} />
                                            {ADVANCED_SYNC_BUTTON_META.SELECTIVE.label}
                                        </button>
                                        <button
                                            onClick={onClose}
                                            className="flex-1 sm:flex-initial bg-slate-800 text-slate-300 hover:bg-slate-700 px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>

                                {/* MODO AVANZADO SUPER_ADMIN */}
                                {isSuperAdmin && showAdvancedSyncControls && (
                                    <div className="pt-3 border-t border-slate-800/50 flex flex-col gap-2">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle size={12} className="text-amber-500" />
                                            <span className="text-[7px] text-amber-500 font-bold uppercase tracking-widest">Modo avanzado / fases completas</span>
                                        </div>
                                        <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest">
                                            Estas fases ignoran la seleccion por seccion y operan como pruebas controladas de fase completa.
                                        </p>
                                        <div className="flex flex-wrap gap-2.5">
                                            <button
                                                onClick={runSandboxDryRun}
                                                disabled={isTestingEngine || !minimumManualSyncPreflightOk}
                                                className="flex-1 sm:flex-initial bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-600 hover:text-white px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={ADVANCED_SYNC_BUTTON_META.DRY_RUN.help}
                                            >
                                                {isTestingEngine ? <RefreshCw size={12} className="animate-spin" /> : <History size={12} />}
                                                {ADVANCED_SYNC_BUTTON_META.DRY_RUN.label}
                                            </button>
                                            <button
                                                onClick={applyPlanningStructureSyncPreview}
                                                disabled={isTestingEngine || !minimumManualSyncPreflightOk || sandboxSections.filter(x => x.enabled).length === 0}
                                                className="flex-1 sm:flex-initial bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-600 hover:text-white px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={ADVANCED_SYNC_BUTTON_META.STRUCTURE.help}
                                            >
                                                {isTestingEngine ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                                                {ADVANCED_SYNC_BUTTON_META.STRUCTURE.label}
                                            </button>
                                            <button
                                                onClick={applyVisualSchemaSync}
                                                disabled={isTestingEngine || !minimumManualSyncPreflightOk}
                                                className="flex-1 sm:flex-initial bg-amber-600/10 text-amber-400 border border-amber-500/20 hover:bg-amber-600 hover:text-white px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={ADVANCED_SYNC_BUTTON_META.VISUAL.help}
                                            >
                                                {isTestingEngine ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                                                {ADVANCED_SYNC_BUTTON_META.VISUAL.label}
                                            </button>
                                            <button
                                                id="btn-sync-goals-2f-b"
                                                onClick={applyPlanningGoalsSyncPreview}
                                                disabled={isTestingEngine || !minimumManualSyncPreflightOk}
                                                className="flex-1 sm:flex-initial bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-600 hover:text-white px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={ADVANCED_SYNC_BUTTON_META.GOALS.help}
                                            >
                                                {isTestingEngine ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                                                {ADVANCED_SYNC_BUTTON_META.GOALS.label}
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[7px] font-bold uppercase tracking-widest text-slate-400">
                                            <div className="rounded-xl border border-slate-800 bg-black/20 px-3 py-2">{ADVANCED_SYNC_BUTTON_META.DRY_RUN.help}</div>
                                            <div className="rounded-xl border border-slate-800 bg-black/20 px-3 py-2">{ADVANCED_SYNC_BUTTON_META.STRUCTURE.help}</div>
                                            <div className="rounded-xl border border-slate-800 bg-black/20 px-3 py-2">{ADVANCED_SYNC_BUTTON_META.VISUAL.help}</div>
                                            <div className="rounded-xl border border-slate-800 bg-black/20 px-3 py-2">{ADVANCED_SYNC_BUTTON_META.GOALS.help}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* MODAL DE PREFLIGHT, AUDITORÍA Y ROLLBACK (MOCKUP INERTE v18.21.73) */}
                        {showDryRunModal && (
                            <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[200] flex items-center justify-center p-4">
                                <div className="bg-slate-950 border border-slate-800 w-full max-w-5xl h-[90vh] rounded-3xl flex flex-col overflow-hidden shadow-2xl">
                                    {/* HEADER CON ADVERTENCIA */}
                                    <div className="flex justify-between items-center bg-red-950/30 border-b border-red-500/20 p-4 shrink-0">
                                        <div className="flex items-center gap-3">
                                            <ShieldAlert size={20} className="text-red-500 animate-pulse" />
                                            <div>
                                                <h4 className="text-red-400 text-sm font-black uppercase tracking-widest leading-none">Simulación sin escritura. BLOCK_SYNC activo.</h4>
                                                <p className="text-[10px] text-red-500/60 uppercase tracking-widest mt-1">Modo Sandbox - Checkpoint real deshabilitado - Rollback no ejecutable</p>
                                            </div>
                                        </div>
                                        <button onClick={() => setShowDryRunModal(false)} className="text-slate-500 hover:text-white p-2">
                                            <X size={20} />
                                        </button>
                                    </div>

                                    {/* CONTENIDO SCROLLEABLE */}
                                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                        {/* PANEL 1: DRY RUN */}
                                        <div className="border border-slate-800 rounded-2xl overflow-hidden">
                                            <div className="bg-slate-900/50 p-3 border-b border-slate-800 flex items-center gap-2">
                                                <History size={16} className="text-indigo-400" />
                                                <h5 className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">1. Resultado del Dry-Run</h5>
                                            </div>
                                            <div className="p-4 bg-slate-950/50 font-mono text-[9px] text-indigo-300 max-h-[150px] overflow-y-auto">
                                                {sandboxDryRunLogs.length > 0 ? sandboxDryRunLogs.map((log, idx) => (
                                                    <div key={idx} className={log.includes('EXCLUDE') || log.includes('EXCLUIDO') ? 'text-red-400' : log.includes('VERDICT') || log.includes('COMPLETADO') ? 'text-emerald-400 font-bold' : ''}>
                                                        {log}
                                                    </div>
                                                )) : "Ejecutando cálculos en memoria..."}
                                            </div>
                                        </div>

                                        {/* PANEL 2: RIESGOS Y BLOCKERS */}
                                        <div className="border border-orange-500/20 rounded-2xl overflow-hidden">
                                            <div className="bg-orange-950/20 p-3 border-b border-orange-500/20 flex items-center gap-2">
                                                <AlertTriangle size={16} className="text-orange-400" />
                                                <h5 className="text-[10px] font-black uppercase text-orange-400 tracking-widest">2. Riesgos y Blockers Detectados</h5>
                                            </div>
                                            <div className="p-4 bg-slate-950/50 space-y-2">
                                                <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 p-3 rounded-xl">
                                                    <AlertTriangle size={14} className="text-orange-400 shrink-0 mt-0.5" />
                                                    <p className="text-[10px] text-orange-300"><strong>WARNING:</strong> Si no preserva datos capturados, 3 metas podrían quedar huérfanas en "Comisión de Salud".</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* PANEL 3: PREFLIGHT PREVIEW Y RESUMEN ESCRITURAS */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="border border-slate-800 rounded-2xl overflow-hidden">
                                                <div className="bg-slate-900/50 p-3 border-b border-slate-800 flex items-center gap-2">
                                                    <Eye size={16} className="text-blue-400" />
                                                    <h5 className="text-[10px] font-black uppercase text-blue-400 tracking-widest">3. Preflight Preview</h5>
                                                </div>
                                                <div className="p-4 bg-slate-950/50 space-y-3">
                                                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                                        <span className="text-[10px] text-slate-400">Destinos Afectados</span>
                                                        <span className="text-[10px] font-bold text-white">{sandboxDestinations.length} Instancias</span>
                                                    </div>
                                                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                                        <span className="text-[10px] text-slate-400">Preservar Datos</span>
                                                        <span className="text-[10px] font-bold text-emerald-400">SÍ</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border border-slate-800 rounded-2xl overflow-hidden">
                                                <div className="bg-slate-900/50 p-3 border-b border-slate-800 flex items-center gap-2">
                                                    <Database size={16} className="text-emerald-400" />
                                                    <h5 className="text-[10px] font-black uppercase text-emerald-400 tracking-widest">Resumen Escrituras Estimadas</h5>
                                                </div>
                                                <div className="p-4 bg-slate-950/50 space-y-3">
                                                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                                        <span className="text-[10px] text-slate-400">Nuevos Inserts</span>
                                                        <span className="text-[10px] font-bold text-emerald-400">~ 24 Filas</span>
                                                    </div>
                                                    <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                                        <span className="text-[10px] text-slate-400">Actualizaciones (Updates)</span>
                                                        <span className="text-[10px] font-bold text-blue-400">~ 18 Filas</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* PANEL 4: CHECKPOINT PREVIEW */}
                                        <div className="border border-slate-800 rounded-2xl overflow-hidden">
                                            <div className="bg-slate-900/50 p-3 border-b border-slate-800 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Save size={16} className="text-purple-400" />
                                                    <h5 className="text-[10px] font-black uppercase text-purple-400 tracking-widest">4. Checkpoint Preview (Simulado)</h5>
                                                </div>
                                                <span className="text-[9px] text-slate-500 bg-slate-900 px-2 py-0.5 rounded">~ 124 KB Estimado</span>
                                            </div>
                                            <div className="p-4 bg-slate-950/50">
                                                <p className="text-[10px] text-slate-400 mb-2">Mostrando resumen estructural. Ver detalles (Inhabilitado en Sandbox).</p>
                                                <pre className="text-[9px] text-slate-500 font-mono bg-black p-3 rounded-xl border border-white/5 overflow-hidden">
{JSON.stringify({
    entity_type: "FLEXIBLE_SYNC_CHECKPOINT",
    action: "CHECKPOINT_CREATED_ONLY_TEST",
    timestamp: new Date().toISOString(),
    total_progress: 100,
    total_goal: 100,
    snapshot: "[PAYLOAD_TRUNCADO_POR_SEGURIDAD]"
}, null, 2)}
                                                </pre>
                                            </div>
                                        </div>

                                        {/* PANEL 5: ROLLBACK PREVIEW */}
                                        <div className="border border-slate-800 rounded-2xl overflow-hidden relative group">
                                            <div className="bg-slate-900/50 p-3 border-b border-slate-800 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <RotateCcw size={16} className="text-pink-400" />
                                                    <h5 className="text-[10px] font-black uppercase text-pink-400 tracking-widest">5. Rollback Preview</h5>
                                                </div>
                                                <span className="text-[9px] text-pink-500/50 uppercase tracking-widest animate-pulse">ESTADO: SIMULACIÃ“N</span>
                                            </div>
                                            <div className="p-4 bg-slate-950/50 overflow-x-auto">
                                                <table className="w-full text-left text-[10px]">
                                                    <thead>
                                                        <tr className="border-b border-white/10 text-slate-500 uppercase">
                                                            <th className="pb-2 font-normal">Campo Restaurable</th>
                                                            <th className="pb-2 font-normal">Valor Actual</th>
                                                            <th className="pb-2 font-normal">Valor Post-Rollback</th>
                                                            <th className="pb-2 font-normal">Impacto</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="py-2 text-slate-300">Meta Enero (K1)</td>
                                                            <td className="py-2 text-red-400 font-mono">15</td>
                                                            <td className="py-2 text-emerald-400 font-mono">10</td>
                                                            <td className="py-2 text-slate-400">Riesgo Bajo</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            {/* OVERLAY BLOQUEO EDICION ROLLBACK */}
                                            <div className="absolute inset-0 bg-black/40 hidden group-hover:flex items-center justify-center backdrop-blur-[1px] transition-all">
                                                <span className="bg-black/80 px-3 py-1 rounded text-[10px] text-red-400 border border-red-500/20">Edición / Copiado deshabilitado por seguridad</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* PANEL 6: CONFIRMACIÃ“N Y BOTONERA */}
                                    <div className="bg-slate-900/80 border-t border-slate-800 p-5 shrink-0 space-y-4">
                                        <div className="max-w-md mx-auto space-y-2">
                                            <label className="text-[9px] text-slate-500 uppercase tracking-widest text-center block">Confirmación de Seguridad (Mockup)</label>
                                            <input
                                                type="text"
                                                disabled
                                                placeholder="AUTORIZO CREAR CHECKPOINT DE SINCRONIZACION FLEXIBLE"
                                                className="w-full bg-black border border-slate-800 rounded-lg px-4 py-2.5 text-xs text-center font-mono opacity-50 cursor-not-allowed"
                                            />
                                        </div>

                                        <div className="flex justify-center gap-3">
                                            <button disabled className="bg-slate-800 text-slate-500 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-not-allowed">
                                                Solicitar Checkpoint Futuro
                                            </button>
                                            <button disabled className="bg-slate-800 text-slate-500 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 cursor-not-allowed" title="Bloqueado por seguridad / pendiente de autorización futura">
                                                <ShieldAlert size={14} className="text-red-500" />
                                                Bloqueado por seguridad / pendiente de autorización futura
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* VISOR DE DETALLES DE SNAPSHOT PREFLIGHT REAL */}
                        {showPreflightDetailModal && (
                            <div className="absolute inset-0 bg-black/90 backdrop-blur-md rounded-3xl p-6 flex flex-col z-[100] border border-sky-500/30 overflow-hidden">
                                <div className="flex justify-between items-center border-b border-slate-800 pb-3 mb-4">
                                    <div>
                                        <h4 className="text-[11px] font-black uppercase text-sky-400 tracking-widest flex items-center gap-2">
                                            <ShieldCheck className="text-emerald-400" size={16} />
                                            Visor del Preflight de Snapshot Real (v18.24.18A)
                                        </h4>
                                        <p className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                            Metadata determinística generada por el motor asíncrono
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowPreflightDetailModal(false)}
                                        className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:text-white transition-all text-slate-400"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-4 pr-1.5 scrollbar-hide text-[9px] text-slate-300 font-medium">
                                    <div className="bg-slate-950/60 border border-slate-900 rounded-xl p-3.5 space-y-2">
                                        <p className="text-[9px] font-black uppercase text-white tracking-widest">Resumen General</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 uppercase text-[8px] font-bold text-slate-400">
                                            <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                                <span className="block text-[7.5px] text-slate-500">Status Global:</span>
                                                <span className="text-emerald-400 font-black text-[10px]">{preflightRealData.global_status}</span>
                                            </div>
                                            <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                                <span className="block text-[7.5px] text-slate-500">READY Count:</span>
                                                <span className="text-white font-black text-[10px]">{preflightRealData.ready_count} / 6</span>
                                            </div>
                                            <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                                <span className="block text-[7.5px] text-slate-500">BLOCK_SYNC status:</span>
                                                <span className="text-red-400 font-black text-[10px]">ACTIVO (SÍ)</span>
                                            </div>
                                            <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                                <span className="block text-[7.5px] text-slate-500">Writes en DB:</span>
                                                <span className="text-white font-black text-[10px]">{preflightRealData.supabase_writes}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-950/60 border border-slate-900 rounded-xl p-3.5 space-y-2.5">
                                        <p className="text-[9px] font-black uppercase text-white tracking-widest">Datos del Snapshot de Origen</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[8px] text-slate-400 uppercase">
                                            <p>Comisión: <span className="text-white font-black">{preflightRealData.origin_real.name}</span></p>
                                            <p>Tipo: <span className="text-white font-black">{preflightRealData.origin_real.type}</span></p>
                                            <p>Comisión ID: <span className="text-white font-mono select-all">{preflightRealData.origin_real.commission_id}</span></p>
                                            <p>Plan de Trabajo 2026: <span className="text-white font-mono select-all">{preflightRealData.origin_real.work_plan_id}</span></p>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="text-[9px] font-black uppercase text-white tracking-widest px-1">Snapshots por Destino Seleccionado</p>
                                        <div className="space-y-2">
                                            {selectedDestinationComms.map((dest: any) => {
                                                const realDest = preflightRealData.snapshot_results_by_destination.find(
                                                    (r: any) => r.commission_id === dest.id
                                                );

                                                if (!realDest) {
                                                    return (
                                                        <div key={dest.id} className="bg-red-950/20 border border-red-900/30 rounded-xl p-3 flex flex-col gap-1 uppercase">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-[9.5px] font-black text-white">{dest.name}</span>
                                                                <span className="text-[8px] px-2 py-0.5 rounded border border-red-500/20 text-red-400 bg-red-500/10 font-black">NO CUBIERTO</span>
                                                            </div>
                                                            <p className="text-[7.5px] text-slate-500">Destino fuera del preflight criptográfico asíncrono actual.</p>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={dest.id} className="bg-slate-950/60 border border-slate-900 rounded-xl p-3 space-y-2 uppercase text-[8px]">
                                                        <div className="flex justify-between items-center border-b border-slate-900 pb-1.5">
                                                            <span className="text-[9.5px] font-black text-white">{dest.name}</span>
                                                            <span className="text-[8px] px-2 py-0.5 rounded border border-emerald-500/20 text-emerald-400 bg-emerald-500/10 font-black">
                                                                {realDest.status}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400 font-bold">
                                                            <p>ID Comisión: <span className="text-white font-mono select-all">{realDest.commission_id}</span></p>
                                                            <p>Plan de Trabajo: <span className="text-white font-mono select-all">
                                                                {preflightRealData.destinations_real.find((d: any) => d.commission_id === dest.id)?.work_plan_id || 'N/A'}
                                                            </span></p>
                                                            <p>Secciones en DB: <span className="text-white font-black">{realDest.validation.sectionsReadCount}</span></p>
                                                            <p>includeMetas: <span className="text-emerald-400 font-black">SÍ</span></p>
                                                            <p>preserveCapturedData: <span className="text-emerald-400 font-black">SÍ</span></p>
                                                            <p>Valid Type: <span className="text-white font-black">{String(realDest.validation.validType)}</span></p>
                                                        </div>
                                                        <div className="bg-black/40 border border-white/5 rounded p-2 text-slate-500 text-[7px] truncate font-mono">
                                                            SHA-256: <span className="text-slate-300 select-all">{realDest.snapshot_hash}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* PANEL DE AUTORIZACIÃ“N CONTROLADA INERTE (v18.24.21) */}
                                    <div className="bg-slate-950/80 border border-sky-500/20 rounded-2xl p-4 mt-2 space-y-4">
                                        <div className="flex justify-between items-center border-b border-slate-900 pb-2">
                                            <div>
                                                <h4 className="text-[10px] font-black text-sky-400 uppercase tracking-widest flex items-center gap-1.5">
                                                    <ShieldCheck size={14} className="text-sky-400" />
                                                    Autorización Controlada de Preflight
                                                </h4>
                                                <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">
                                                    Verificación inerte de seguridad y precondiciones
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-400 font-mono text-[7px] font-bold">
                                                    v18.24.21
                                                </span>
                                            </div>
                                        </div>

                                        {/* 1. ESTADO GENERAL */}
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-900/40 p-2.5 rounded-xl border border-white/5 uppercase text-[7.5px] font-bold text-slate-400">
                                            <div>
                                                <span className="block text-[6.5px] text-slate-500">Auth Status:</span>
                                                <span className="text-amber-400 font-black">DESIGN_ONLY / UI_INERT</span>
                                            </div>
                                            <div>
                                                <span className="block text-[6.5px] text-slate-500">Sync Real Autorizada:</span>
                                                <span className="text-red-400 font-black">NO</span>
                                            </div>
                                            <div>
                                                <span className="block text-[6.5px] text-slate-500">Sync Real Bloqueada:</span>
                                                <span className="text-emerald-400 font-black">SÍ</span>
                                            </div>
                                            <div>
                                                <span className="block text-[6.5px] text-slate-500">Motivo:</span>
                                                <span className="text-white font-black truncate block" title="BLOCK_SYNC activo y FLEXIBLE_SYNC_EXECUTION_ENABLED=false">BLOCK_SYNC ACTIVO</span>
                                            </div>
                                        </div>

                                        {/* 2. PRECONDICIONES */}
                                        <div className="space-y-1.5">
                                            <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider block">Precondiciones de Seguridad (Verificadas)</span>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[7px] text-slate-400 font-bold uppercase">
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>Snapshot Global: <span className="text-emerald-400 font-black">READY</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>6 Destinos: <span className="text-emerald-400 font-black">READY</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>6 Hashes: <span className="text-emerald-400 font-black">PRESENTES</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>Mocks Detectados: <span className="text-emerald-400 font-black">FALSE</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>preserveCapturedData: <span className="text-emerald-400 font-black">TRUE</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>includeMetas: <span className="text-emerald-400 font-black">TRUE</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>Rol Requerido: <span className="text-emerald-400 font-black">{hierarchy?.current_user?.role || 'ADMINISTRADOR'}</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>Checkpoint: <span className="text-emerald-400 font-black">REQUERIDO</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-900 px-2 py-1 rounded">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    <span>gov_audit_log: <span className="text-emerald-400 font-black">NO ESCRITO (INERT)</span></span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 3. DOBLE CONFIRMACIÃ“N */}
                                        <div className="space-y-2 bg-slate-900/30 p-3 rounded-xl border border-white/5">
                                            <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider block">Doble Confirmación Requerida</span>
                                            <div className="space-y-1.5">
                                                <label className="flex items-center gap-2 cursor-pointer text-[7.5px] font-bold uppercase text-slate-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={authCheck1}
                                                        onChange={(e) => setAuthCheck1(e.target.checked)}
                                                        className="w-3.5 h-3.5 rounded bg-slate-950 border-slate-800 text-sky-500 focus:ring-sky-500"
                                                    />
                                                    <span>Confirmo que revisé origen, destinos, secciones y hashes.</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-[7.5px] font-bold uppercase text-slate-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={authCheck2}
                                                        onChange={(e) => setAuthCheck2(e.target.checked)}
                                                        className="w-3.5 h-3.5 rounded bg-slate-950 border-slate-800 text-sky-500 focus:ring-sky-500"
                                                    />
                                                    <span>Confirmo que preserveCapturedData está activo.</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-[7.5px] font-bold uppercase text-slate-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={authCheck3}
                                                        onChange={(e) => setAuthCheck3(e.target.checked)}
                                                        className="w-3.5 h-3.5 rounded bg-slate-950 border-slate-800 text-sky-500 focus:ring-sky-500"
                                                    />
                                                    <span>Entiendo que esta autorización no ejecuta sincronización real.</span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* 4. FRASE MANUAL OBLIGATORIA */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <label className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider">
                                                    Escribe la frase de validación manual exactamente:
                                                </label>
                                                <span className="text-[7px] text-slate-500 font-mono">
                                                    "AUTORIZO PREFLIGHT, NO SYNC REAL"
                                                </span>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={authPhrase}
                                                    onChange={(e) => setAuthPhrase(e.target.value)}
                                                    placeholder="Escribe la frase de autorización aquí..."
                                                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[9px] font-bold text-white uppercase placeholder:text-slate-700 focus:outline-none focus:border-sky-500/50 transition-all font-mono"
                                                />
                                                {authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL" && (
                                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-400 text-[8px] font-black uppercase tracking-widest bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                        âœ“ COINCIDE
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* 5. FIRMA LÃ“GICA PREPARADA EN MEMORIA */}
                                        {preparedSignature && (
                                            <div className="space-y-2 bg-black/60 border border-emerald-500/20 rounded-xl p-3.5 animate-fadeIn">
                                                <div className="flex justify-between items-center border-b border-emerald-500/10 pb-1.5">
                                                    <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1">
                                                        <ShieldCheck size={12} />
                                                        Firma Lógica Preparada en Memoria
                                                    </span>
                                                    <span className="text-[7px] text-slate-500 font-bold uppercase">
                                                        {authStatusText}
                                                    </span>
                                                </div>
                                                <pre className="text-[7.5px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed select-all">
                                                    {JSON.stringify(preparedSignature, null, 2)}
                                                </pre>
                                            </div>
                                        )}

                                        {/* BOTONERA CONTROLADA */}
                                        <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                            <button
                                                onClick={() => {
                                                    if (authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL") {
                                                        setAuthStatusText("Autorización preparada localmente / no ejecutada");
                                                        setPreparedSignature({
                                                            actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                            timestamp: new Date().toISOString(),
                                                            snapshot_hash_global: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                            target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                            authorization_intent: 'PREFLIGHT_ONLY',
                                                            version: 'v18.24.21',
                                                            status: 'AUTHORIZATION_PREPARED'
                                                        });
                                                    }
                                                }}
                                                disabled={!(authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL")}
                                                className={cn(
                                                    "flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                                    (authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL")
                                                        ? "bg-sky-600 text-white hover:bg-sky-500 shadow-lg shadow-sky-950/50"
                                                        : "bg-slate-900 border border-slate-800 text-slate-600 cursor-not-allowed"
                                                )}
                                            >
                                                <Save size={12} />
                                                Preparar autorización controlada
                                            </button>

                                            <button
                                                disabled
                                                className="flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-red-950/20 border border-red-500/20 text-red-400 flex items-center justify-center gap-2 cursor-not-allowed"
                                            >
                                                <ShieldAlert size={12} />
                                                Sincronización real bloqueada por seguridad
                                            </button>
                                        </div>
                                    </div>

                                    {/* PANEL DEL CHECKPOINT DE AUTORIZACIÃ“N (v18.24.23) */}
                                    <div className="bg-slate-950/80 border border-indigo-500/20 rounded-2xl p-4 mt-2 space-y-4">
                                        <div className="flex justify-between items-center border-b border-slate-900 pb-2">
                                            <div>
                                                <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1.5">
                                                    <Save size={14} className="text-indigo-400" />
                                                    Checkpoint de Autorización
                                                </h4>
                                                <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">
                                                    Simulación inerte del ciclo de vida de la auditoría de sincronización
                                                </p>
                                            </div>
                                            <div className="text-right flex items-center gap-2">
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-widest border font-mono",
                                                    checkpointStatus === 'READY'
                                                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                        : checkpointStatus === 'INVALIDATED'
                                                            ? "bg-red-500/10 border-red-500/20 text-red-400"
                                                            : checkpointStatus === 'EXPIRED'
                                                                ? "bg-amber-500/10 border-amber-500/20 text-amber-400 animate-pulse"
                                                                : checkpointStatus === 'BLOCKED'
                                                                    ? "bg-rose-500/15 border-rose-500/20 text-rose-400"
                                                                    : "bg-slate-900 border border-slate-800 text-slate-400"
                                                )}>
                                                    Estado: {checkpointStatus}
                                                </span>
                                            </div>
                                        </div>

                                        {/* 1. SELECCIONADOR DE ESTADO DEMOSTRATIVO */}
                                        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900/30 p-2.5 rounded-xl border border-white/5 uppercase text-[7.5px] font-bold text-slate-400">
                                            <span className="text-white/60">Simular Estados del Checkpoint:</span>
                                            <div className="flex gap-1.5 flex-wrap">
                                                {(['DRAFT', 'READY', 'INVALIDATED', 'EXPIRED', 'BLOCKED'] as const).map(st => (
                                                    <button
                                                        key={st}
                                                        onClick={() => setCheckpointStatus(st)}
                                                        className={cn(
                                                            "px-2 py-1 rounded border text-[7px] font-black uppercase tracking-widest transition-all",
                                                            checkpointStatus === st
                                                                ? "bg-indigo-600 border-indigo-500 text-white"
                                                                : "bg-slate-900 border-slate-800 text-slate-500 hover:text-white"
                                                        )}
                                                    >
                                                        {st}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* 2. EXPIRACIÃ“N TEMPORAL DEMOSTRATIVA */}
                                        <div className="bg-slate-900/40 p-2.5 rounded-xl border border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[7.5px] uppercase font-bold text-slate-400">
                                            <div>
                                                <span className="text-slate-500 block text-[6.5px]">Expiración Recomendada:</span>
                                                <span className="text-white font-black">4 Horas (Límite Estricto de Seguridad)</span>
                                            </div>
                                            <div>
                                                <span className="text-slate-500 block text-[6.5px]">Vigencia Local Demostrativa:</span>
                                                {checkpointStatus === 'READY' ? (
                                                    <span className="text-emerald-400 font-black flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                                                        VIGENTE (3h 59m restantes)
                                                    </span>
                                                ) : checkpointStatus === 'EXPIRED' ? (
                                                    <span className="text-amber-400 font-black flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                                        CADUCADO (Expiración excedida)
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500">N/A</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* 3. CONDICIONES DE INVALIDACIÃ“N */}
                                        <div className="space-y-1.5 bg-slate-900/30 p-3 rounded-xl border border-white/5">
                                            <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider block">Condiciones de Invalidación Reactiva</span>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[7px] text-slate-400 font-bold uppercase">
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>Cambio de Hash Global o Hashes Destino</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>Cambio en Comisión de Origen o Destinos</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>preserveCapturedData = false</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>Detección de Datos Mocks (mocks_detected = true)</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>Usuario Administrador No Autorizado / No Logueado</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <AlertTriangle size={10} className="text-amber-500" />
                                                    <span>BLOCK_SYNC = false o FLEXIBLE_SYNC_EXECUTION_ENABLED = true</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 4. PREVISUALIZACIÃ“N DEL PAYLOAD FUTURO PREPARADO */}
                                        {preparedCheckpoint && (
                                            <div className="space-y-2 bg-black/60 border border-indigo-500/20 rounded-xl p-3.5 animate-fadeIn">
                                                <div className="flex justify-between items-center border-b border-indigo-500/10 pb-1.5">
                                                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1">
                                                        <Save size={12} />
                                                        Payload del Checkpoint Local Preparado
                                                    </span>
                                                    <span className="text-[7.5px] text-emerald-400 font-bold uppercase">
                                                        STATUS: READY_LOCAL
                                                    </span>
                                                </div>
                                                <pre className="text-[7.5px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed select-all">
                                                    {JSON.stringify(preparedCheckpoint, null, 2)}
                                                </pre>
                                            </div>
                                        )}

                                        {/* BOTONERA CHECKPOINT */}
                                        <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                            <button
                                                onClick={() => {
                                                    const allPreconditionsMet = authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL";
                                                    if (allPreconditionsMet) {
                                                        setCheckpointStatus('READY');
                                                        setPreparedCheckpoint({
                                                            event_type: "SYNC_PREFLIGHT_AUTHORIZATION_CHECKPOINT",
                                                            sync_run_id: "sync-run-v18.24.23-readonly-local-test",
                                                            actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                            actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                            timestamp: new Date().toISOString(),
                                                            source_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                            mode: sandboxGlobalMode,
                                                            includeMetas: sandboxIncludeMetas,
                                                            preserveCapturedData: true,
                                                            snapshot_global_status: preflightRealData.global_status,
                                                            snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                            target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                            ready_count: preflightRealData.ready_count,
                                                            partial_count: preflightRealData.partial_count,
                                                            failed_count: preflightRealData.failed_count,
                                                            mocks_detected: false,
                                                            manual_phrase: authPhrase,
                                                            checkbox_confirmations: {
                                                                confirmo_revision: authCheck1,
                                                                confirmo_preservacion: authCheck2,
                                                                entiendo_no_ejecucion: authCheck3
                                                            },
                                                            authorization_intent: 'PREFLIGHT_ONLY',
                                                            sync_real_authorized: false,
                                                            block_sync_status: true,
                                                            execution_enabled: false,
                                                            app_version: "v18.24.23-SYNC-ENGINE-AUTHORIZATION-CHECKPOINT-UI-INERT",
                                                            git_tag: "v18.24.23-SYNC-ENGINE-AUTHORIZATION-CHECKPOINT-UI-INERT",
                                                            status: "CHECKPOINT_PREPARED_LOCAL"
                                                        });
                                                    }
                                                }}
                                                disabled={!(authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL")}
                                                className={cn(
                                                    "flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                                    (authCheck1 && authCheck2 && authCheck3 && authPhrase === "AUTORIZO PREFLIGHT, NO SYNC REAL")
                                                        ? "bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-950/50"
                                                        : "bg-slate-900 border border-slate-800 text-slate-600 cursor-not-allowed"
                                                )}
                                            >
                                                <Save size={12} />
                                                Preparar checkpoint local
                                            </button>

                                            <button
                                                disabled
                                                className="flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-red-950/20 border border-red-500/20 text-red-400 flex items-center justify-center gap-2 cursor-not-allowed"
                                            >
                                                <ShieldAlert size={12} />
                                                Sincronización real bloqueada por seguridad
                                            </button>
                                        </div>
                                    </div>

                                    {/* PANEL DE VALIDACIÃ“N CRIPTOGRÁFICA (v18.24.26) */}
                                    <div className="bg-slate-950/80 border border-emerald-500/20 rounded-2xl p-4 mt-2 space-y-4">
                                        <div className="flex justify-between items-center border-b border-slate-900 pb-2">
                                            <div>
                                                <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
                                                    <ShieldCheck size={14} className="text-emerald-400" />
                                                    Validación Criptográfica del Checkpoint
                                                </h4>
                                                <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">
                                                    Motor asíncrono de comparación de firmas en memoria
                                                </p>
                                            </div>
                                            <div className="text-right flex items-center gap-2">
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-widest border font-mono",
                                                    validationResult?.validation_status === 'VALIDATION_READY'
                                                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                        : validationResult?.validation_status === 'VALIDATION_FAILED'
                                                            ? "bg-red-500/10 border-red-500/20 text-red-400"
                                                            : validationResult?.validation_status === 'VALIDATION_EXPIRED'
                                                                ? "bg-amber-500/10 border-amber-500/20 text-amber-400"
                                                                : validationResult?.validation_status === 'VALIDATION_INVALIDATED'
                                                                    ? "bg-rose-500/15 border-rose-500/20 text-rose-400"
                                                                    : validationResult?.validation_status === 'VALIDATION_BLOCKED'
                                                                        ? "bg-red-950/20 border-red-500/30 text-red-400 animate-pulse"
                                                                        : "bg-slate-900 border border-slate-800 text-slate-500"
                                                )}>
                                                    Veredicto: {validationResult?.validation_status || 'PENDIENTE'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* ESCENARIOS MOCK/DEMO PARA COMPROBAR BLOQUEOS */}
                                        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900/30 p-2.5 rounded-xl border border-white/5 uppercase text-[7.5px] font-bold text-slate-400 font-sans">
                                            <span className="text-white/60">Simular Fallas de Validación (Lectura inerte):</span>
                                            <div className="flex gap-1.5 flex-wrap">
                                                <button
                                                    onClick={async () => {
                                                        setIsValidating(true);
                                                        const res = await db.validateAuthorizationCheckpointReadOnly({
                                                            checkpoint_payload: { ...preparedCheckpoint, target_hashes: [] }, // hash faltante
                                                            snapshot_global_hash: "",
                                                            target_hashes: [],
                                                            origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                            mode: sandboxGlobalMode,
                                                            includeMetas: sandboxIncludeMetas,
                                                            preserveCapturedData: true,
                                                            actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                            actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                            timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                            app_version: "v18.24.26",
                                                            git_tag: "v18.24.26"
                                                        });
                                                        setValidationResult(res);
                                                        setIsValidating(false);
                                                    }}
                                                    className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-red-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                >
                                                    1. Hash Faltante
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        setIsValidating(true);
                                                        const res = await db.validateAuthorizationCheckpointReadOnly({
                                                            checkpoint_payload: preparedCheckpoint,
                                                            snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                            target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                            origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                            mode: sandboxGlobalMode,
                                                            includeMetas: sandboxIncludeMetas,
                                                            preserveCapturedData: true,
                                                            actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                            actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                            timestamp: new Date(Date.now() - 5 * 3600 * 1000).toISOString(), // hace 5 horas
                                                            app_version: "v18.24.26",
                                                            git_tag: "v18.24.26"
                                                        });
                                                        setValidationResult(res);
                                                        setIsValidating(false);
                                                    }}
                                                    className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-amber-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                >
                                                    2. Expirado (&gt;4h)
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        setIsValidating(true);
                                                        const res = await db.validateAuthorizationCheckpointReadOnly({
                                                            checkpoint_payload: preparedCheckpoint,
                                                            snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                            target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                            origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                            mode: sandboxGlobalMode,
                                                            includeMetas: sandboxIncludeMetas,
                                                            preserveCapturedData: true,
                                                            actor_id: '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                            actor_email: 'invitado@coparmex.org.mx',
                                                            actor_role: 'EDITOR', // no autorizado
                                                            timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                            app_version: "v18.24.26",
                                                            git_tag: "v18.24.26"
                                                        });
                                                        setValidationResult(res);
                                                        setIsValidating(false);
                                                    }}
                                                    className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-rose-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                >
                                                    3. Actor No Autorizado
                                                </button>
                                            </div>
                                        </div>

                                        {/* 1. ANÁLISIS DE INTEGRIDAD Y COMPATIBILIDAD */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest bg-slate-900/30 p-3 rounded-xl border border-white/5">
                                            <div className="space-y-1">
                                                <span className="text-slate-500 block text-[6.5px]">Hash Global del Snapshot:</span>
                                                <span className="text-white font-mono block truncate select-all">
                                                    {preparedCheckpoint?.snapshot_global_hash || "PENDIENTE"}
                                                </span>
                                                <span className="text-slate-500 block text-[6.5px] mt-1.5">Hashes por Destino:</span>
                                                <span className="text-white font-mono block">
                                                    {preparedCheckpoint?.target_hashes?.length || 0} hashes detectados
                                                </span>
                                            </div>
                                            <div className="space-y-1">
                                                <span className="text-slate-500 block text-[6.5px]">Coincidencia de Firmas:</span>
                                                {validationResult ? (
                                                    <span className={cn(
                                                        "font-black block",
                                                        validationResult.hash_match_summary.global_hash_match ? "text-emerald-400" : "text-red-400"
                                                    )}>
                                                        Global: {validationResult.hash_match_summary.global_hash_match ? "COINCIDE (READY)" : "DISCREPANCIA DETECTADA"}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 block font-bold">Pendiente de verificación</span>
                                                )}

                                                <span className="text-slate-500 block text-[6.5px] mt-1">Estatus del Snapshot:</span>
                                                <span className="text-emerald-400 font-black block">
                                                    {preparedCheckpoint?.snapshot_global_status || "READY_LOCAL"}
                                                </span>
                                            </div>
                                        </div>

                                        {/* 2. VALIDACIÃ“N DE ACTOR Y TIEMPO */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest bg-slate-900/30 p-3 rounded-xl border border-white/5">
                                            <div className="space-y-1">
                                                <span className="text-slate-500 block text-[6.5px]">Actor Resolvido:</span>
                                                <span className="text-white font-black block">
                                                    {validationResult?.actor_validation.resolved_role || preparedCheckpoint?.actor_role || "PENDIENTE"}
                                                </span>
                                                <span className="text-slate-500 block text-[6.5px] mt-1">Autorización del Actor:</span>
                                                {validationResult ? (
                                                    <span className={cn(
                                                        "font-black block",
                                                        validationResult.actor_validation.is_authorized ? "text-emerald-400" : "text-red-400"
                                                    )}>
                                                        {validationResult.actor_validation.is_authorized ? "AUTORIZADO (ADMIN)" : "NO AUTORIZADO"}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 block font-bold">Pendiente</span>
                                                )}
                                            </div>
                                            <div className="space-y-1">
                                                <span className="text-slate-500 block text-[6.5px]">Vigencia de Firma (Max 4 Horas):</span>
                                                {validationResult ? (
                                                    <span className={cn(
                                                        "font-black block",
                                                        validationResult.time_validation.is_expired ? "text-amber-400" : "text-emerald-400"
                                                    )}>
                                                        {validationResult.time_validation.is_expired
                                                            ? "EXPIRADO"
                                                            : `VIGENTE (${validationResult.time_validation.elapsed_minutes}m transcurridos)`}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 block font-bold">Pendiente</span>
                                                )}
                                                <span className="text-slate-500 block text-[6.5px] mt-1">Banderas del Sistema:</span>
                                                {validationResult ? (
                                                    <span className={cn(
                                                        "font-black block",
                                                        (validationResult.flag_validation.block_sync_ok && validationResult.flag_validation.execution_enabled_ok) ? "text-emerald-400" : "text-red-400"
                                                    )}>
                                                        {(validationResult.flag_validation.block_sync_ok && validationResult.flag_validation.execution_enabled_ok) ? "CONTROL DE SEGURIDAD OK" : "ESTADO ALTERADO"}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 block font-bold">Pendiente</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* 3. RAZONES DE BLOQUEO Y DETALLES */}
                                        {validationResult && (validationResult.validation_reasons.length > 0 || validationResult.blocking_reasons.length > 0) && (
                                            <div className="bg-red-950/20 border border-red-500/20 rounded-xl p-3 space-y-2 uppercase text-[7.5px] font-bold text-red-400">
                                                <span className="block text-[8px] font-black tracking-widest text-red-300">Alertas de Validación / Bloqueos:</span>
                                                <ul className="list-disc pl-4 space-y-1">
                                                    {validationResult.validation_reasons.map((r: string, idx: number) => (
                                                        <li key={idx}>{r}</li>
                                                    ))}
                                                    {validationResult.blocking_reasons.map((r: string, idx: number) => (
                                                        <li key={idx} className="font-black">{r}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* BOTONERA VALIDACIÃ“N */}
                                        <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                            <button
                                                onClick={() => {
                                                    setIsValidating(true);
                                                    setTimeout(async () => {
                                                        try {
                                                            const res = await db.validateAuthorizationCheckpointReadOnly({
                                                                checkpoint_payload: preparedCheckpoint || {
                                                                    source_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                    target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                    target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                    mode: sandboxGlobalMode,
                                                                    preserveCapturedData: true,
                                                                    includeMetas: sandboxIncludeMetas,
                                                                    snapshot_global_status: preflightRealData.global_status,
                                                                    snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                    ready_count: preflightRealData.ready_count,
                                                                    partial_count: preflightRealData.partial_count,
                                                                    failed_count: preflightRealData.failed_count,
                                                                    mocks_detected: false,
                                                                    authorization_intent: 'PREFLIGHT_ONLY'
                                                                },
                                                                snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                mode: sandboxGlobalMode,
                                                                includeMetas: sandboxIncludeMetas,
                                                                preserveCapturedData: true,
                                                                actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                                actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                                timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                                app_version: "v18.24.26-SYNC-ENGINE-CHECKPOINT-CRYPTO-VALIDATION-UI-INERT",
                                                                git_tag: "v18.24.26-SYNC-ENGINE-CHECKPOINT-CRYPTO-VALIDATION-UI-INERT"
                                                            });
                                                            setValidationResult(res);
                                                        } catch (err) {
                                                            console.error(err);
                                                        } finally {
                                                            setIsValidating(false);
                                                        }
                                                    }, 300);
                                                }}
                                                disabled={isValidating}
                                                className="flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 transition-all font-sans font-bold"
                                            >
                                                {isValidating ? (
                                                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin block" />
                                                ) : (
                                                    <ShieldAlert size={12} />
                                                )}
                                                Validar checkpoint local
                                            </button>

                                            <button
                                                disabled
                                                className="flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-red-950/20 border border-red-500/20 text-red-400 flex items-center justify-center gap-2 cursor-not-allowed font-sans font-bold"
                                            >
                                                <ShieldAlert size={12} />
                                                Sincronización real bloqueada por seguridad
                                            </button>
                                        </div>

                                        {/* PANEL DE SIMULACIÃ“N DE BACKUP TRANSACCIONAL (v18.24.29) */}
                                        <div className="mt-5 border-t border-white/5 pt-5 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Database size={14} className="text-emerald-400" />
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-white">
                                                        Simulación de Backup Transaccional
                                                    </span>
                                                </div>
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-widest border font-mono",
                                                    !backupSimulationResult
                                                        ? "bg-slate-900 border-slate-800 text-slate-500"
                                                        : backupSimulationResult.blocking_reasons.some((r: string) => !r.includes("PHASE_3"))
                                                            ? "bg-red-500/10 border-red-500/20 text-red-400"
                                                            : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                )}>
                                                    Estado: {!backupSimulationResult ? 'SIN EJECUTAR' : backupSimulationResult.blocking_reasons.some((r: string) => !r.includes("PHASE_3")) ? 'BLOQUEADO' : 'READY'}
                                                </span>
                                            </div>

                                            {/* Parámetros de Simulación e Interruptores */}
                                            <div className="bg-slate-950/60 p-3 rounded-xl border border-white/5 space-y-3">
                                                <div className="flex flex-wrap items-center justify-between gap-2 uppercase text-[7.5px] font-bold text-slate-400">
                                                    <span className="text-white/60">Configurar Parámetros del Backup Simulador:</span>
                                                    <div className="flex gap-2">
                                                        <label className="flex items-center gap-1 cursor-pointer hover:text-white">
                                                            <input
                                                                type="checkbox"
                                                                checked={sandboxIncludeMetas}
                                                                onChange={(e) => setSandboxIncludeMetas(e.target.checked)}
                                                                className="rounded border-slate-850 bg-slate-900 text-emerald-600 focus:ring-0 focus:ring-offset-0 w-3 h-3 cursor-pointer"
                                                            />
                                                            <span>includeMetas</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="flex gap-2 flex-wrap pt-0.5">
                                                    <button
                                                        onClick={async () => {
                                                            setIsSimulatingBackup(true);
                                                            const res = await db.simulateTransactionalBackupReadOnly({
                                                                checkpoint_payload: preparedCheckpoint || {
                                                                    source_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                    target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                    target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                    mode: sandboxGlobalMode,
                                                                    preserveCapturedData: true,
                                                                    includeMetas: sandboxIncludeMetas,
                                                                    snapshot_global_status: preflightRealData.global_status,
                                                                    snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                    ready_count: preflightRealData.ready_count,
                                                                    partial_count: preflightRealData.partial_count,
                                                                    failed_count: preflightRealData.failed_count,
                                                                    mocks_detected: false,
                                                                    authorization_intent: 'PREFLIGHT_ONLY'
                                                                },
                                                                snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                mode: sandboxGlobalMode,
                                                                includeMetas: sandboxIncludeMetas,
                                                                preserveCapturedData: true,
                                                                actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                                actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                                timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                                estimatedDeletes: 0,
                                                                mocks_detected: false,
                                                                snapshot_global_status: preflightRealData.global_status || 'READY'
                                                            });
                                                            setBackupSimulationResult(res);
                                                            setIsSimulatingBackup(false);
                                                        }}
                                                        className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-emerald-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                    >
                                                        Simulación Correcta (Fases Completas)
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            setIsSimulatingBackup(true);
                                                            const res = await db.simulateTransactionalBackupReadOnly({
                                                                checkpoint_payload: preparedCheckpoint,
                                                                snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                mode: sandboxGlobalMode,
                                                                includeMetas: sandboxIncludeMetas,
                                                                preserveCapturedData: false,
                                                                actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                                actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                                timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                                estimatedDeletes: 0,
                                                                mocks_detected: false,
                                                                snapshot_global_status: preflightRealData.global_status || 'READY'
                                                            });
                                                            setBackupSimulationResult(res);
                                                            setIsSimulatingBackup(false);
                                                        }}
                                                        className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-rose-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                    >
                                                        Fuerza preserveCapturedData = false
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            setIsSimulatingBackup(true);
                                                            const res = await db.simulateTransactionalBackupReadOnly({
                                                                checkpoint_payload: preparedCheckpoint,
                                                                snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                mode: sandboxGlobalMode,
                                                                includeMetas: sandboxIncludeMetas,
                                                                preserveCapturedData: true,
                                                                actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                                actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                                timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                                estimatedDeletes: 3,
                                                                mocks_detected: false,
                                                                snapshot_global_status: preflightRealData.global_status || 'READY'
                                                            });
                                                            setBackupSimulationResult(res);
                                                            setIsSimulatingBackup(false);
                                                        }}
                                                        className="px-2 py-1 rounded border border-slate-800 bg-slate-900 text-amber-400 text-[6.5px] hover:text-white transition-all font-bold"
                                                    >
                                                        Fuerza estimatedDeletes &gt; 0
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Estado Físico y Blindaje */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest bg-slate-900/30 p-2.5 rounded-xl border border-white/5 font-sans">
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">Escrituras Supabase:</span>
                                                    <span className="text-red-400 font-black block">0 (BLOQUEADO)</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">gov_audit_log escrito:</span>
                                                    <span className="text-red-400 font-black block">NO</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">BLOCK_SYNC activo:</span>
                                                    <span className="text-emerald-400 font-black block">SÍ</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">FLEXIBLE_SYNC:</span>
                                                    <span className="text-red-400 font-black block">FALSE</span>
                                                </div>
                                            </div>

                                            {backupSimulationResult && (
                                                <>
                                                    {/* Resumen del Backup Transaccional Creado */}
                                                    {backupSimulationResult.backup_id && (
                                                        <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest">
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                <div>
                                                                    <span className="text-slate-500 block text-[6.5px]">backup_id generado:</span>
                                                                    <span className="text-white block font-mono select-all truncate">{backupSimulationResult.backup_id}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-slate-500 block text-[6.5px]">sync_run_id generado:</span>
                                                                    <span className="text-white block font-mono select-all truncate">{backupSimulationResult.sync_run_id}</span>
                                                                </div>
                                                            </div>
                                                            <div className="pt-1.5 border-t border-white/5">
                                                                <span className="text-slate-500 block text-[6.5px] mb-1">Resúmenes por Comisión Destino (Target Backups):</span>
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                                                    {backupSimulationResult.target_backup_summaries?.map((s: any, idx: number) => (
                                                                        <div key={idx} className="bg-slate-950/40 p-2 rounded-lg border border-white/5 space-y-1">
                                                                            <span className="text-emerald-400 font-black block truncate">{s.target_commission_name}</span>
                                                                            <span className="text-white font-mono block text-[6px] truncate select-all">{s.target_commission_id}</span>
                                                                            <div className="flex justify-between text-[6px] text-slate-500 font-mono pt-1">
                                                                                <span>Plan: {s.backed_up_records.gov_work_plans}</span>
                                                                                <span>Secc: {s.backed_up_records.gov_work_plan_sections}</span>
                                                                                <span>Resp: {s.backed_up_records.gov_user_responses}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Fases de Ejecución */}
                                                    <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 uppercase text-[7.5px] font-bold text-slate-400 font-sans">
                                                        <span className="block text-[8px] font-black tracking-widest text-slate-300">Monitoreo de Fases del Pipeline:</span>
                                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                            {['PHASE_0_PREFLIGHT_READY', 'PHASE_1_BACKUP_CREATED', 'PHASE_2_DRY_RUN_REVALIDATED', 'PHASE_3_DML_PENDING_AUTHORIZATION'].map((phase, idx) => {
                                                                const isPassed = backupSimulationResult.local_logger.some((l: any) => l.phase === phase && l.status === 'SUCCESS');
                                                                const isBlocked = backupSimulationResult.local_logger.some((l: any) => l.phase === phase && l.status === 'BLOCKED');
                                                                return (
                                                                    <div key={idx} className={cn(
                                                                        "p-2 rounded-lg border text-center space-y-1 flex flex-col justify-center",
                                                                        isPassed
                                                                            ? "bg-emerald-950/10 border-emerald-500/20 text-emerald-400"
                                                                            : isBlocked
                                                                                ? "bg-red-950/20 border-red-500/30 text-red-400 animate-pulse"
                                                                                : "bg-slate-950/40 border-slate-800 text-slate-500"
                                                                    )}>
                                                                        <span className="text-[6.5px] block font-mono font-black">{phase.split('_')[1]}</span>
                                                                        <span className="text-[7.5px] font-black block truncate">{phase.replace('PHASE_', '')}</span>
                                                                        <span className="text-[6.5px] block">
                                                                            {isPassed ? 'âœ“ COMPLETADO' : isBlocked ? 'âš  BLOQUEADO' : 'PENDIENTE'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {/* Logger Local (Bitácora en Memoria) */}
                                                    <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 uppercase text-[7.5px] font-bold text-slate-400">
                                                        <span className="block text-[8px] font-black tracking-widest text-slate-300">Logger Local in-memory:</span>
                                                        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                                                            {backupSimulationResult.local_logger.map((l: any, idx: number) => (
                                                                <div key={idx} className="bg-slate-950/50 p-2 rounded border border-white/5 font-mono space-y-1">
                                                                    <div className="flex justify-between items-center text-[7px]">
                                                                        <span className={cn(
                                                                            "font-black",
                                                                            l.status === 'SUCCESS' ? "text-emerald-400" : "text-red-400"
                                                                        )}>
                                                                            [{l.status}] {l.event_type}
                                                                        </span>
                                                                        <span className="text-slate-500">{l.timestamp.split('T')[1].substr(0, 8)}</span>
                                                                    </div>
                                                                    <div className="text-[6.5px] text-slate-400">
                                                                        Fase: {l.phase}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Errores y Condiciones de Bloqueo */}
                                                    {backupSimulationResult.blocking_reasons?.length > 0 && (
                                                        <div className="bg-red-950/20 border border-red-500/20 rounded-xl p-3 space-y-2 uppercase text-[7.5px] font-bold text-red-400">
                                                            <span className="block text-[8px] font-black tracking-widest text-red-300">Razones del Bloqueo / Safeguard reasons:</span>
                                                            <ul className="list-disc pl-4 space-y-1">
                                                                {backupSimulationResult.blocking_reasons.map((r: string, idx: number) => (
                                                                    <li key={idx} className="font-black">{r}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Botonera de Control de Simulación */}
                                            <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                                <button
                                                    onClick={async () => {
                                                        setIsSimulatingBackup(true);
                                                        try {
                                                            const res = await db.simulateTransactionalBackupReadOnly({
                                                                checkpoint_payload: preparedCheckpoint || {
                                                                    source_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                    target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                    target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                    mode: sandboxGlobalMode,
                                                                    preserveCapturedData: true,
                                                                    includeMetas: sandboxIncludeMetas,
                                                                    snapshot_global_status: preflightRealData.global_status,
                                                                    snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                    ready_count: preflightRealData.ready_count,
                                                                    partial_count: preflightRealData.partial_count,
                                                                    failed_count: preflightRealData.failed_count,
                                                                    mocks_detected: false,
                                                                    authorization_intent: 'PREFLIGHT_ONLY'
                                                                },
                                                                snapshot_global_hash: "d7a8f3b20d3a5a4cf13a69a23cdc48d3c162e8b014ed9600c2d9845e9f1b4e6c9",
                                                                target_hashes: preflightRealData.snapshot_results_by_destination.map((r: any) => r.snapshot_hash),
                                                                origin_commission_id: selectedSource?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                target_commission_ids: selectedDestinationComms.map((c: any) => c.id),
                                                                mode: sandboxGlobalMode,
                                                                includeMetas: sandboxIncludeMetas,
                                                                preserveCapturedData: true,
                                                                actor_id: hierarchy?.current_user?.id || '48bdf7be-b0d5-4024-8298-ed2c38bf3125',
                                                                actor_email: hierarchy?.current_user?.email || 'admin@coparmex.org.mx',
                                                                actor_role: hierarchy?.current_user?.role || 'ADMINISTRADOR',
                                                                timestamp: preparedCheckpoint?.timestamp || new Date().toISOString(),
                                                                estimatedDeletes: 0,
                                                                mocks_detected: false,
                                                                snapshot_global_status: preflightRealData.global_status || 'READY'
                                                            });
                                                            setBackupSimulationResult(res);
                                                        } catch (err) {
                                                            console.error(err);
                                                        } finally {
                                                            setIsSimulatingBackup(false);
                                                        }
                                                    }}
                                                    disabled={isSimulatingBackup}
                                                    className="flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 transition-all font-sans font-bold"
                                                >
                                                    {isSimulatingBackup ? (
                                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin block" />
                                                    ) : (
                                                        <Database size={12} />
                                                    )}
                                                    Simular backup transaccional
                                                </button>
                                            </div>
                                        </div>

                                        {/* PANEL DE MONITOREO DE CONTINGENCIAS DE INFRAESTRUCTURA (v18.24.32) */}
                                        <div className="mt-5 border-t border-white/5 pt-5 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Activity size={14} className={cn(
                                                        contingencyStatus === 'NORMAL' ? "text-emerald-400" : "text-red-400 animate-pulse"
                                                    )} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-white">
                                                        Monitoreo de Contingencias de Infraestructura
                                                    </span>
                                                </div>
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-widest border font-mono",
                                                    contingencyStatus === 'NORMAL'
                                                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                        : "bg-red-500/15 border-red-500/20 text-red-400 animate-pulse"
                                                )}>
                                                    Estado: {contingencyStatus}
                                                </span>
                                            </div>

                                            {/* Alerta de Contingencia Activa */}
                                            {contingencyStatus !== 'NORMAL' && (
                                                <div className="bg-red-950/20 border border-red-500/30 rounded-xl p-3 space-y-1.5 uppercase text-[7.5px] font-bold text-red-400 flex items-start gap-2.5 font-sans">
                                                    <ShieldAlert size={16} className="text-red-400 shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="block text-[8px] font-black tracking-widest text-red-300">
                                    Operación bloqueada por contingencia. Revisión requerida.
                                                        </span>
                                                        <p className="text-red-400/80 leading-normal normal-case font-normal mt-0.5">
                                                            El motor seguro ha entrado en estado de protección fail-closed debido a una simulación de fallo de infraestructura ({contingencyStatus}). Toda operación DML de escritura o replica real en Supabase permanece inhabilitada.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Estado del Motor y Políticas */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest bg-slate-900/30 p-2.5 rounded-xl border border-white/5 font-sans">
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">Sincronización Real:</span>
                                                    <span className="text-red-400 font-black block">NO (BLOQUEADO)</span>
                                                </div>
                                                <div>
                                                <span className="text-slate-500 block text-[6.5px]">Estatus de Bloqueo:</span>
                                                <span className="text-red-400 font-black block">SÍ (ACTIVADO)</span>
                                                </div>
                                                <div>
                                                <span className="text-slate-500 block text-[6.5px]">Política Global:</span>
                                                    <span className="text-emerald-400 font-black block">FAIL_CLOSED</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500 block text-[6.5px]">Idempotencia Fases:</span>
                                                    <span className="text-emerald-400 font-black block">ENFORCED</span>
                                                </div>
                                            </div>

                                            {/* Grilla de Indicadores de Infraestructura */}
                                            <div className="bg-slate-950/60 p-3 rounded-xl border border-white/5 space-y-2.5 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest font-sans">
                                                <span className="text-slate-500 block text-[6.5px] mb-1 font-bold">Estado de Sensores y Canales:</span>
                                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                                                    {[
                                                        { label: "Red / Conectividad", key: "network", errStatus: ['NETWORK_INTERRUPTED'] },
                                                        { label: "Servidor Supabase", key: "supabase", errStatus: ['READ_TIMEOUT', 'BACKUP_TIMEOUT', 'WRITE_TIMEOUT', 'PARTIAL_WRITE_DETECTED'] },
                                                        { label: "Firebase / Hosting", key: "firebase", errStatus: [] },
                                                        { label: "Sesión / Admin", key: "session", errStatus: ['SESSION_EXPIRED', 'PERMISSION_CHANGED'] },
                                                        { label: "Integridad Hashes", key: "data", errStatus: ['HASH_MISMATCH', 'TARGET_CHANGED', 'SOURCE_CHANGED'] },
                                                        { label: "Ejecución Futura", key: "execution", errStatus: ['PARTIAL_WRITE_DETECTED'] },
                                                        { label: "Rollback Futuro", key: "rollback", errStatus: ['ROLLBACK_FAILED'] }
                                                    ].map((ind, idx) => {
                                                        const isFailed = ind.errStatus.includes(contingencyStatus) || (contingencyStatus === 'MANUAL_REVIEW_REQUIRED');
                                                        return (
                                                            <div key={idx} className={cn(
                                                                "bg-slate-900/60 p-2 rounded-lg border text-center space-y-1",
                                                                isFailed
                                                                    ? "border-red-500/30 text-red-400 bg-red-950/5 animate-pulse"
                                                                    : "border-white/5 text-emerald-400"
                                                            )}>
                                                                <span className="block text-[6px] text-slate-500 font-mono">SENSOR {idx + 1}</span>
                                                                <span className="block text-[7.5px] truncate font-black text-white/90">{ind.label}</span>
                                                                <span className="block text-[6.5px] font-bold font-mono">
                                                                    {isFailed ? "âš  FALLO" : "âœ“ NORMAL"}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Ãšltimo Evento Seguro */}
                                            <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 text-[7.5px] font-bold uppercase text-slate-400 tracking-widest font-sans">
                                                <div className="flex items-center gap-1.5 text-white">
                                                    <Info size={12} className="text-emerald-400" />
                                                    <span>Ãšltimo Evento Seguro Registrado:</span>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-slate-950/40 p-2.5 rounded-lg border border-white/5 font-mono">
                                                    <div>
                                                        <span className="text-slate-500 block text-[6px]">Fase:</span>
                                                        <span className="text-white block truncate">{lastSafeEvent.phase}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[6px]">Marca Temporal:</span>
                                                        <span className="text-white block truncate">{lastSafeEvent.timestamp.split('T')[1]?.substr(0, 8) || lastSafeEvent.timestamp}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[6px]">Código Evento:</span>
                                                        <span className="text-white block truncate">{lastSafeEvent.event_type}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 block text-[6px]">Recomendación:</span>
                                                        <span className="text-emerald-400 block truncate">{lastSafeEvent.recommended_action}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Simulador Local de Fallas de Infraestructura */}
                                            <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 uppercase text-[7.5px] font-bold text-slate-400 font-sans">
                                                <span className="block text-[8px] font-black tracking-widest text-slate-300">Simulador de Fallos de Infraestructura (Local Inerte):</span>
                                                <div className="flex gap-2 flex-wrap">
                                                    {[
                                                        { label: "Desconexión Red", val: "NETWORK_INTERRUPTED", action: "Verificar cable de red y reintentar lectura" },
                                                        { label: "Timeout Lectura", val: "READ_TIMEOUT", action: "Revalidar latencia con servidor Supabase" },
                                                        { label: "Sesión Expirada", val: "SESSION_EXPIRED", action: "Iniciar sesión de Administrador de nuevo" },
                                                        { label: "Permisos Cambiados", val: "PERMISSION_CHANGED", action: "Solicitar re-auditoría de roles RBAC" },
                                                        { label: "Hash Mismatch", val: "HASH_MISMATCH", action: "Regenerar preflight y firmar nuevo checkpoint" },
                                                        { label: "Destino Mutado", val: "TARGET_CHANGED", action: "Reiniciar preflight con targets originales" },
                                                        { label: "Escritura Parcial", val: "PARTIAL_WRITE_DETECTED", action: "Ejecutar reversión de emergencia/rollback" },
                                                        { label: "Fallo Rollback", val: "ROLLBACK_FAILED", action: "ATENCIÃ“N: Solicitar soporte técnico de infraestructura" },
                                                        { label: "Revisión Manual", val: "MANUAL_REVIEW_REQUIRED", action: "SuperAdmin debe auditar manualmente el estado" }
                                                    ].map((item, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                setContingencyStatus(item.val);
                                                                setLastSafeEvent({
                                                                    phase: 'PHASE_3_DML_PENDING_AUTHORIZATION',
                                                                    timestamp: new Date().toISOString(),
                                                                    event_type: `SIMULATED_${item.val}`,
                                                                    status: 'BLOCKED',
                                                                    recommended_action: item.action
                                                                });
                                                            }}
                                                            className={cn(
                                                                "px-2 py-1 rounded border text-[6px] hover:text-white transition-all font-bold font-mono",
                                                                contingencyStatus === item.val
                                                                    ? "bg-red-500/20 border-red-500 text-red-300"
                                                                    : "bg-slate-950/60 border-slate-800 text-slate-400"
                                                            )}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Reglas de Blindaje Fail-Closed */}
                                            <div className="bg-slate-900/30 p-3 rounded-xl border border-white/5 space-y-2 uppercase text-[7.5px] font-bold text-slate-400 font-sans">
                                                <span className="block text-[8px] font-black tracking-widest text-slate-300">Reglas de Blindaje de Infraestructura (Fail-Closed):</span>
                                                <ul className="list-disc pl-4 space-y-1 font-mono text-[6.5px] text-slate-500">
                                                    <li><span className="text-slate-400 font-bold font-sans">FAIL-CLOSED POR DEFECTO:</span> Ante cualquier excepción o duda del sensor, se bloquea la transacción.</li>
                                                    <li><span className="text-slate-400 font-bold font-sans">SIN REINTENTOS DML AUTOMÁTICOS:</span> Las escrituras fallidas nunca se reintentan solas.</li>
                                                    <li><span className="text-slate-400 font-bold font-sans">REINTENTAR SOLO LECTURAS:</span> Solo los pings de red y select-queries admiten reintento.</li>
                                                    <li><span className="text-slate-400 font-bold font-sans">REVALIDAR INTEGRIDAD HASHES:</span> No se puede reanudar sin re-calcular firmas de destinos.</li>
                                                    <li><span className="text-slate-400 font-bold font-sans">CONTROL DE VERSIÃ“N DE APP:</span> Se prohíbe operar si el cliente corre un bundle de caché desactualizado.</li>
                                                </ul>
                                            </div>

                                            {/* Acciones de Contingencia */}
                                            <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                                <button
                                                    onClick={() => {
                                                        setContingencyStatus('NORMAL');
                                                        setLastSafeEvent({
                                                            phase: 'PHASE_0_PREFLIGHT_READY',
                                                            timestamp: new Date().toISOString(),
                                                            event_type: 'REVALIDATION_SUCCESS',
                                                            status: 'SUCCESS',
                                                            recommended_action: 'Ninguna'
                                                        });
                                                    }}
                                                    className="flex-1 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-slate-900 hover:bg-slate-800 border border-emerald-500/30 hover:border-emerald-500 text-emerald-400 flex items-center justify-center gap-1.5 transition-all font-sans font-bold"
                                                >
                                                    <RefreshCw size={10} />
                                                    Revalidar lectura
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setContingencyStatus('NORMAL');
                                                        setBackupSimulationResult(null);
                                                        setLastSafeEvent({
                                                            phase: 'PHASE_0_PREFLIGHT_READY',
                                                            timestamp: new Date().toISOString(),
                                                            event_type: 'SAFE_ABORT_RESET',
                                                            status: 'SUCCESS',
                                                            recommended_action: 'Iniciar nuevo preflight'
                                                        });
                                                    }}
                                                    className="flex-1 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-white/30 text-white flex items-center justify-center gap-1.5 transition-all font-sans font-bold"
                                                >
                                                    <RotateCcw size={10} />
                                                    Abortar operación
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setContingencyStatus('MANUAL_REVIEW_REQUIRED');
                                                        setLastSafeEvent({
                                                            phase: 'PHASE_3_DML_PENDING_AUTHORIZATION',
                                                            timestamp: new Date().toISOString(),
                                                            event_type: 'MANUAL_REVIEW_REQUESTED',
                                                            status: 'BLOCKED',
                                                            recommended_action: 'SuperAdmin auditando base de datos'
                                                        });
                                                    }}
                                                    className="flex-1 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-slate-900 hover:bg-slate-800 border border-red-500/30 hover:border-red-500 text-red-400 flex items-center justify-center gap-1.5 transition-all font-sans font-bold"
                                                >
                                                    <AlertTriangle size={10} />
                                                    Solicitar revisión manual
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-3.5 mt-4 text-[7.5px] text-slate-500 font-bold uppercase tracking-widest space-y-1">
                                    <div className="flex items-center gap-1 text-red-400">
                                        <ShieldAlert size={12} className="animate-pulse" />
                                        <span>BLOCK_SYNC e Inyección en base de datos deshabilitados por protección de software.</span>
                                    </div>
                                    <p>No se realizaron escrituras en gov_audit_log ni en tablas de datos. Modo consulta readonly completado.</p>
                                </div>
                            </div>
                        )}
                        {/* RESULTADO EDGE FUNCTION PREVIEW */}
                        {enginePreviewResult && (
                            <div className="absolute inset-0 bg-black/90 backdrop-blur-md rounded-3xl p-6 flex flex-col z-[100] border border-emerald-500/30 overflow-hidden">
                                <div className="flex justify-between items-center border-b border-slate-800 pb-3 mb-4 shrink-0">
                                    <div>
                                        <h4 className="text-[11px] font-black uppercase text-emerald-400 tracking-widest flex items-center gap-2">
                                            <Zap className="text-emerald-400" size={16} fill="currentColor" />
                                            Vista Previa del Motor en la Nube
                                        </h4>
                                        <p className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                            Respuesta en tiempo real desde apply-planning-structure-sync
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setEnginePreviewResult(null)}
                                        className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:text-white transition-all text-slate-400"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto space-y-4 pr-1.5 scrollbar-hide text-[9px] text-slate-300 font-medium">
                                    {enginePreviewResult.error && (
                                        <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl space-y-2">
                                            <p className="text-[9px] font-black uppercase text-red-400 tracking-widest leading-none">Error Edge Sanitizado</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 uppercase text-[8px] font-bold text-slate-300">
                                                <div className="p-2 rounded bg-black/30 border border-white/5">
                                                    <span className="block text-[7px] text-slate-500">HTTP Status</span>
                                                    <span className="text-red-300">{String(enginePreviewResult.http_status || 'N/A')}</span>
                                                </div>
                                                <div className="p-2 rounded bg-black/30 border border-white/5">
                                                    <span className="block text-[7px] text-slate-500">Status Text</span>
                                                    <span className="text-red-300">{String(enginePreviewResult.http_status_text || 'N/A')}</span>
                                                </div>
                                                <div className="p-2 rounded bg-black/30 border border-white/5">
                                                    <span className="block text-[7px] text-slate-500">Request Id</span>
                                                    <span className="text-red-300 break-all">{String(enginePreviewResult.request_id || 'N/A')}</span>
                                                </div>
                                            </div>
                                            <div className="p-2 rounded bg-black/30 border border-white/5">
                                                <span className="block text-[7px] text-slate-500 uppercase">Mensaje</span>
                                                <span className="text-red-200 break-words">{String(enginePreviewResult.error)}</span>
                                            </div>
                                            <div className="p-2 rounded bg-black/30 border border-white/5">
                                                <span className="block text-[7px] text-slate-500 uppercase">Body Sanitizado</span>
                                                <pre className="mt-1 whitespace-pre-wrap break-words text-[8px] text-red-200">{typeof enginePreviewResult.sanitized_error_body === 'string' ? enginePreviewResult.sanitized_error_body : JSON.stringify(enginePreviewResult.sanitized_error_body || {}, null, 2)}</pre>
                                            </div>
                                        </div>
                                    )}
                                    {enginePreviewResult.blocked && (
                                        <div className="bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl flex items-start gap-2">
                                            <ShieldAlert size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-[9px] font-black uppercase text-emerald-400 tracking-widest leading-none">Motor conectado, escritura bloqueada.</p>
                                                <p className="text-[8px] text-emerald-500/60 uppercase tracking-widest mt-1">
                                                    write_enabled: {String(enginePreviewResult.preview?.write_enabled || false)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 uppercase text-[8px] font-bold text-slate-400">
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Secciones Origen:</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.source_sections || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Secciones Destino (Actuales):</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.target_sections || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Secciones Faltantes:</span>
                                            <span className="text-amber-400 font-black text-[10px]">{enginePreviewResult.preview?.missing_sections || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Estimación Inserts:</span>
                                            <span className="text-blue-400 font-black text-[10px]">{enginePreviewResult.preview?.estimated_inserts || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Estimación Updates:</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.estimated_updates || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Estimación Deletes:</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.estimated_deletes || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Metas Copiadas:</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.goals_copied || 0}</span>
                                        </div>
                                        <div className="p-2.5 rounded bg-black/40 border border-white/5">
                                            <span className="block text-[7.5px] text-slate-500">Avance Modificado:</span>
                                            <span className="text-white font-black text-[10px]">{enginePreviewResult.preview?.progress_modified || 0}</span>
                                        </div>
                                    </div>
                                    {enginePreviewResult.preview?.missing_section_names && enginePreviewResult.preview.missing_section_names.length > 0 && (
                                        <div className="bg-slate-950/60 border border-slate-900 rounded-xl p-3.5 space-y-2.5 mt-4">
                                            <p className="text-[9px] font-black uppercase text-white tracking-widest">Nombres de Secciones Faltantes</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {enginePreviewResult.preview.missing_section_names.map((name: string, i: number) => (
                                                    <span key={i} className="px-2 py-1 bg-slate-900 border border-slate-800 rounded text-[7px] text-slate-400 font-bold uppercase">{name}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {enginePreviewResult.preview?.risk_warning && (
                                        <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl flex items-start gap-2 mt-4">
                                            <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-[9px] font-black uppercase text-amber-400 tracking-widest leading-none">Advertencia de Riesgo</p>
                                                <p className="text-[8px] text-amber-500/60 uppercase tracking-widest mt-1">
                                                    {enginePreviewResult.preview.risk_warning}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        </div>
    );
};


// --- Other Modules ---

const PlanningNavbar = ({
    search,
    setSearch,
    vpFilterId,
    setVpFilterId,
    typeFilter,
    setTypeFilter,
    hierarchy,
    isCollapsed,
    onBack,
    instanceName
}: any) => {
    return (
        <div className={cn(
            "sticky top-0 z-[50] transition-all duration-500 bg-slate-950/40 backdrop-blur-md border-b border-white/5 px-6",
            isCollapsed ? "py-3" : "py-8"
        )}>
            <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="w-10 h-10 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl flex items-center justify-center transition-all border border-white/10 hover:border-emerald-500/50 shadow-lg shadow-black/40 group"
                        >
                            <ChevronLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
                        </button>
                    )}
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className={cn("font-black text-white uppercase tracking-tighter italic leading-none transition-all", isCollapsed ? "text-xl" : "text-3xl md:text-4xl")}>
                                {isCollapsed ? (
                                    <>PLAN <span className="text-emerald-500">2026</span></>
                                ) : (
                            <>Módulo de <span className="text-emerald-500">Planeación</span></>
                                )}
                            </h2>
                            {isCollapsed && (
                                <div className="h-4 w-px bg-white/20 mx-2" />
                            )}
                            {isCollapsed && (
                                <span className="text-[10px] font-black text-white/40 uppercase tracking-widest whitespace-normal break-words leading-tight max-w-[300px] md:max-w-none">
                                    {instanceName}
                                </span>
                            )}
                        </div>
                        {!isCollapsed && <p className="text-emerald-500 text-[9px] font-black uppercase tracking-[0.4em] ml-1">Inteligencia Estratégica & Gobernanza</p>}
                    </div>
                </div>

                {!isCollapsed && (
                    <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                        <div className="relative w-full md:w-80 group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-emerald-500 transition-colors" size={16} />
                            <input
                                type="text"
                                placeholder="BUSCAR INSTANCIA..."
                                className="w-full bg-slate-900/60 border border-slate-800/50 rounded-2xl py-3.5 pl-11 pr-5 text-[11px] font-black text-white uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all shadow-xl placeholder:text-white/10"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center gap-3 bg-slate-900/40 p-1.5 rounded-[1.25rem] border border-white/5">
                            <select
                                className="bg-slate-900 border border-slate-800 text-[10px] font-black text-white uppercase tracking-widest px-4 py-2.5 rounded-xl focus:outline-none focus:border-emerald-500/50 cursor-pointer hover:bg-slate-800 transition-colors"
                                value={vpFilterId || ''}
                                onChange={(e) => setVpFilterId(e.target.value || null)}
                            >
                                <option value="">TODAS LAS VP</option>
                                {hierarchy?.vicepresidencies?.map((vp: any) => (
                                    <option key={vp.id} value={vp.id}>{vp.name}</option>
                                ))}
                            </select>

                            <div className="flex bg-slate-950/50 rounded-xl p-1 gap-1">
                                {['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(t as any)}
                                        className={cn(
                                            "px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all",
                                            typeFilter === t ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/20" : "text-white/30 hover:text-white hover:bg-white/5"
                                        )}
                                    >
                                        {t === 'TODOS' ? 'TODOS' : t === 'COMISION' ? 'COM' : t === 'COMITE' ? 'CMT' : 'CNS'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const StatCard = ({ title, value, subtext, icon: Icon, color, trend }: any) => (
    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2rem] shadow-2xl hover:border-emerald-500/50 transition-all group overflow-hidden relative">
        <div className={cn("absolute -top-12 -right-12 w-32 h-32 blur-3xl opacity-10 rounded-full", color)}></div>
        <div className="flex justify-between items-start mb-6 relative z-10">
            <div className={cn("p-4 rounded-[1.25rem] group-hover:scale-110 transition-transform shadow-xl text-white", color)}>
                <Icon size={24} strokeWidth={2.5} />
            </div>
            {trend && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                    <TrendingUp size={12} className="text-emerald-500" />
                    <span className="text-[10px] font-black text-emerald-500">{trend}</span>
                </div>
            )}
        </div>
        <div className="relative z-10">
            <p className="text-white/70 text-[10px] uppercase font-black tracking-widest mb-1">{title}</p>
            <h3 className="text-4xl font-black text-white tracking-tighter mb-2">{value}</h3>
            <p className="text-slate-600 text-[10px] font-black uppercase tracking-wider">{subtext}</p>
        </div>
    </div>
);


const PlanningModule = ({
    user,
    selectedInstancia,
    setSelectedInstancia,
    vpFilterId,
    setVpFilterId
}: {
    user: any,
    selectedInstancia: string | null,
    setSelectedInstancia: (id: string | null) => void,
    vpFilterId: string | null,
    setVpFilterId: (id: string | null) => void
}) => {
    const settings = useContext(SettingsContext);
    const { isSA, isND, isVP, isUserAdmin, isGlobal, isSuperAdmin } = useRole(user);
    const isSAOrAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR' || user?.role === 'DIRECTOR_NACIONAL';
    const isVPScoped = !isSAOrAdmin && (user?.org_scope === 'VP' || !!user?.vicepresidency_id);
    const isInstanceScoped = !isSAOrAdmin && (user?.org_scope === 'INSTANCE' || !!user?.commission_id || !!user?.instance_id);

    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [readError, setReadError] = useState<string | null>(null);

    const [hierarchy, setHierarchy] = useState<any>(null);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');

    const [noPlanInstanceModal, setNoPlanInstanceModal] = useState<any>(null);
    const [isCreatingBasePlan, setIsCreatingBasePlan] = useState(false);

    const handleCreateBasePlan = async (comm: any) => {
        try {
            if (import.meta.env.DEV) {
                console.log("[CREATE_BASE_PLAN_UI] clicked", comm.id);
            }
            setIsCreatingBasePlan(true);
            const token = localStorage.getItem('gov_secure_token');
            if (import.meta.env.DEV) {
                console.log("[CREATE_BASE_PLAN_UI] secure_token_exists", !!token);
            }
            if(!token) throw new Error("No hay token de sesión.");

            const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-planning-base-work-plan`;
            if (import.meta.env.DEV) {
                console.log("[CREATE_BASE_PLAN_UI] endpoint", endpoint);
                console.log("[CREATE_BASE_PLAN_UI] commission_id", comm.id);
            }

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-gov-secure-token': token,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`
                },
                body: JSON.stringify({ commission_id: comm.id })
            });

            if (import.meta.env.DEV) {
                console.log("[CREATE_BASE_PLAN_UI] response_status", res.status);
            }
            const data = await res.json();
            if (import.meta.env.DEV) {
                console.log("[CREATE_BASE_PLAN_UI] response_body", data);
            }

            if (!data.success) throw new Error(data.error || "Error desconocido");

            showToast("Plan base creado exitosamente", "success");
            setNoPlanInstanceModal(null);
            fetchData();
            setSelectedInstancia(comm.id);
        } catch (e: any) {
            console.error(e);
            showToast(`Error al crear plan base: ${e.message}`, "error");
        } finally {
            setIsCreatingBasePlan(false);
        }
    };




    const fetchData = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const data = await db.getWorkPlans(user);
            setPlans(data);
            setReadError(null);
        } catch (e: unknown) {
            console.error('[PLANNING_SCOPED_READ_ERROR]', e);
            setReadError(db.getScopedReadErrorMessage(e));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData();
        db.getHierarchy(user).then(setHierarchy);
    }, [fetchData, user]);

    useEffect(() => {
        if (!isSAOrAdmin) {
            if (isVPScoped && user?.vicepresidency_id) {
                if (vpFilterId !== user.vicepresidency_id) {
                    setVpFilterId(user.vicepresidency_id);
                }
            } else if (isInstanceScoped) {
                const targetInst = user?.commission_id || user?.instance_id;
                if (targetInst && selectedInstancia !== targetInst) {
                    setSelectedInstancia(targetInst);
                }
            }
        }
    }, [user, isSAOrAdmin, isVPScoped, isInstanceScoped, vpFilterId, setVpFilterId, selectedInstancia, setSelectedInstancia]);

    const handleCreatePlan = async (commId: string) => {
        try {
            setLoading(true);
            const planId = await db.createWorkPlan(user, 2026, commId);
            await fetchData();
        } catch (err: any) {
            if (err.messag?.includes('Ya existe')) {
                const existing = plans.find(p => p.commission_id === commId && p.year === 2026);
                if (existing) return;
            }
            showToast("No se pudo crear. Intenta nuevamente.", 'success');
        } finally {
            setLoading(false);
        }
    };

    const [replicaModalOpen, setReplicaModalOpen] = useState(false);
    const [sourcePlanId, setSourcePlanId] = useState<string | null>(null);

    const handleSync = async (planId: string) => {
        setSourcePlanId(planId);
        setReplicaModalOpen(true);
    };

    const getVisibleInstances = useCallback(() => {
        if (!hierarchy) return [];
        const commissions = (hierarchy.commissions || []).map((c: any) => ({
            ...c,
            type: c.type || 'COMISION',
            typeLabel: 'Comisiones',
            vpName: c.vicepresident_name || 'Sin VP',
            vpId: c.vicepresidency_id || 'indep'
        }));
        const independents = (hierarchy.independents || []).map((c: any) => ({
            ...c,
            type: c.type || (normalize(c.name).includes('COMITE') ? 'COMITE' : 'CONSEJERIA'),
            typeLabel: canonicalInstanceType(c.type) === 'COMITE' ? 'Comités' : canonicalInstanceType(c.type) === 'CONSEJERIA' ? 'Consejería' : 'Directo',
            vpName: 'Sin Vicepresidencia',
            vpId: 'indep'
        }));
        let all = [...commissions, ...independents];

        // RBAC VISUAL HARDLOCK
        if (!isSAOrAdmin) {
            if (isInstanceScoped) {
                const targetInst = user.commission_id || user.instance_id;
                all = all.filter((c: any) => c && String(c.id) === String(targetInst));
            } else if (isVPScoped) {
                const targetVp = user.vicepresidency_id;
                all = all.filter((c: any) => c && String(c.vpId) === String(targetVp));
            }
        }
        return all;
    }, [hierarchy, user, isSAOrAdmin, isInstanceScoped, isVPScoped]);

    /* ESTABLE Ã¢â‚¬â€ NO MODIFICAR SIN VALIDACIÃƒâ€œN COMPLETA (PROTECCIÃƒâ€œN ANTI-REGRESIÃƒâ€œN FASE 4 - FILTROS COMBINADOS) */
    const filteredInstances = useMemo(() => {
        let all = getVisibleInstances() || [];

        // 1. Filtro por Vicepresidencia (combinable y robusto a null/undefined)
        if (vpFilterId && vpFilterId !== 'all' && vpFilterId !== 'null') {
            all = all.filter((c: any) => c && String(c.vpId || '') === String(vpFilterId));
        }

        // 2. Filtro por Tipo (Tabs) con Normalización Robusta
        if (typeFilter !== 'TODOS') {
            const target = normalize(typeFilter || '').replace(/[_]/g, "");
            all = all.filter((c: any) => {
                if (!c) return false;
                const current = normalize(c.type || COMISION_TYPE).replace(/[_]/g, "");
                if (target === 'COMISION') return current === 'COMISION' || current === 'COMISIONESTRATEGICA';
                return current === target;
            });
        }

        // 3. Búsqueda por Texto (Nombre, VP, Responsable o Tipo) robusta ante null/undefined
        if (search) {
            const s = normalize(search || '');
            all = all.filter((c: any) => {
                if (!c) return false;
                return normalize(c.name || "").includes(s) ||
                    normalize(c.vpName || "").includes(s) ||
                    normalize(c.president_name || "").includes(s) ||
                    normalize(c.type || "").includes(s);
            });
        }

        return all.sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'es', { sensitivity: 'base' }));
    }, [getVisibleInstances, search, typeFilter, vpFilterId]);

    const groupedInstances = useMemo(() => {
        const groups: Record<string, any[]> = { [COMISION_TYPE]: [], 'COMITE': [], 'CONSEJERIA': [] };
        filteredInstances.forEach(inst => {
            const t = inst.type || COMISION_TYPE;
            if (groups[t]) groups[t].push(inst);
            else groups[COMISION_TYPE].push(inst);
        });
        return groups;
    }, [filteredInstances]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
            <p className="text-emerald-500 font-black uppercase tracking-widest animate-pulse">Cargando Planeación...</p>
        </div>
    );

    if (readError && plans.length === 0) return (
        <div className="min-h-[420px] flex items-center justify-center p-6">
            <div className="max-w-xl w-full p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
                <AlertTriangle className="mx-auto text-red-400" size={36} />
                <h2 className="text-red-200 font-black uppercase tracking-widest">Fallo de lectura de Planeacion</h2>
                <p className="text-white/70 text-sm">{readError}</p>
                <p className="text-white/40 text-xs">No se mostraran estados SIN PLAN hasta confirmar una ausencia real.</p>
                <button onClick={() => fetchData()} className="px-5 py-2 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-widest">Reintentar</button>
            </div>
        </div>
    );

    const currentInstance = getVisibleInstances().find(i => i.id === selectedInstancia);
    const currentPlan = plans.find(p => p.commission_id === selectedInstancia && p.year === 2026);
    const isPlanOpen = Boolean(selectedInstancia && currentPlan);

    return (
        <div className="min-h-screen bg-black text-white p-4 sm:p-6 lg:p-8 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(2rem+env(safe-area-inset-bottom))]">
            {readError && (
                <div className="max-w-[1600px] mx-auto mb-4 p-4 rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 text-sm font-bold">
                    {readError} Se conservan los ultimos datos visibles.
                </div>
            )}
            {isPlanOpen ? (
                <div className="animate-in fade-in zoom-in-95 duration-500">
                    <WorkPlanDetail
                        planId={currentPlan.id}
                        onBack={() => {
                            setSelectedInstancia(null);
                            fetchData(true);
                        }}
                        user={user}
                        hierarchy={hierarchy}
                        onPlanUpdated={fetchData}
                    />
                </div>
            ) : (
                <div className="max-w-[1600px] mx-auto space-y-4">
                    {/* Header Principal Compacto */}
                    <div className="space-y-1 sm:space-y-2">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white uppercase tracking-tighter italic leading-none">
                            PLANEACIÓN <span className="text-emerald-500">ESTRATÉGICA</span> 2026
                        </h1>
                        <p className="text-white/40 text-[8px] sm:text-[9px] font-black uppercase tracking-[0.3em] ml-1">
                            GESTIÓN DE PLANES DE TRABAJO ANUALES
                        </p>
                    </div>

                    {/* Controles de Filtrado Avanzado Sticky */}
                    {!isInstanceScoped && (
                        <div className="sticky top-0 z-30 bg-black/95 backdrop-blur-md py-3 border-b border-slate-900 px-4 -mx-4 flex flex-col xl:flex-row items-center justify-between gap-4 w-full">
                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-1.5 bg-slate-900/50 p-1.5 rounded-2xl sm:rounded-full border border-white/5">
                                {['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(t as any)}
                                        className={cn(
                                            "px-4 sm:px-6 py-2 rounded-xl sm:rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all min-w-[90px] sm:min-w-[110px]",
                                            typeFilter === t
                                                ? "bg-emerald-600 text-white shadow-xl shadow-emerald-500/20"
                                                : "text-white/40 hover:text-white hover:bg-white/5"
                                        )}
                                    >
                                        {t === 'TODOS' ? 'TODOS' : INSTANCE_TYPE_LABELS[t === 'COMISION' ? COMISION_TYPE : t]}
                                    </button>
                                ))}
                            </div>

                            <div className="flex flex-col md:flex-row items-center gap-3 w-full xl:w-auto">
                                {/* Filtro por Vicepresidencia */}
                                <div className="relative w-full md:w-60 group">
                                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-emerald-500 pointer-events-none">
                                        <Filter size={14} />
                                    </div>
                                    <select
                                        className="w-full bg-slate-900/60 border border-white/5 rounded-full py-3 pl-11 pr-10 text-[10px] font-black text-white uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all appearance-none cursor-pointer hover:bg-slate-800/80"
                                        value={vpFilterId || ''}
                                        onChange={(e) => setVpFilterId(e.target.value || null)}
                                    >
                                        {!isVPScoped && <option value="">TODAS LAS VP</option>}
                                        {!isVPScoped && <option value="indep">SIN VICEPRESIDENCIA</option>}
                                        {hierarchy?.vicepresidencies?.filter((vp: any) => {
                                            if (isVPScoped) {
                                                return String(vp.id) === String(user?.vicepresidency_id);
                                            }
                                            return vp.name && vp.id;
                                        }).map((vp: any) => (
                                            <option key={vp.id} value={vp.id}>{vp.name}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-5 top-1/2 -translate-y-1/2 text-white/20 pointer-events-none">
                                        <ChevronDown size={14} />
                                    </div>
                                </div>

                                {/* Buscador de Texto */}
                                <div className="relative w-full md:w-80 group">
                                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-emerald-500 transition-colors" size={16} />
                                    <input
                                        type="text"
                                        placeholder="BUSCAR INSTANCIA..."
                                        className="w-full bg-slate-900/60 border border-white/5 rounded-full py-3 pl-12 pr-10 text-[10px] font-black text-white uppercase tracking-[0.2em] focus:outline-none focus:border-emerald-500/50 transition-all shadow-2xl placeholder:text-white/10"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                    {search && (
                                        <button
                                            onClick={() => setSearch('')}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Grid de Tarjetas Optimizado (UX001 Compliant) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 pt-8">
                        {/* ESTABLE Ã¢â‚¬â€ NO MODIFICAR SIN VALIDACIÃƒâ€œN COMPLETA (PROTECCIÃƒâ€œN ANTI-REGRESIÃƒâ€œN FASE 4 - CARDS & CUMPLIMIENTO) */}
                        {filteredInstances.map(inst => {
                            if (!inst) return null;
                            const plan = (plans || []).find(p => p && p.commission_id === inst.id && p.year === 2026);
                            const hasPlan = !!plan;
                            const pct = hasPlan && plan && plan.total_goal > 0
                                ? Math.min(100, Math.round(((plan.total_progress || 0) / plan.total_goal) * 100))
                                : 0;

                            return (
                                <button
                                    key={inst.id}
                                    onClick={() => {
                                        if (hasPlan) {
                                            setSelectedInstancia(inst.id);
                                        } else {
                                            setNoPlanInstanceModal(inst);
                                        }
                                    }}
                                    className="bg-slate-900/40 border border-slate-800 hover:border-emerald-500/50 rounded-2xl p-3.5 sm:p-4 text-left transition-all duration-500 group relative overflow-hidden shadow-2xl hover:shadow-emerald-500/10 active:scale-[0.98]"
                                >
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></div>

                                    <div className="flex justify-between items-start mb-2">
                                        <div className="w-8 h-8 bg-slate-950 border border-white/5 rounded-xl flex items-center justify-center text-white/20 group-hover:text-emerald-500 transition-colors">
                                            <FileText size={15} />
                                        </div>
                                        <div className={cn(
                                            "px-2 py-0.5 rounded-md border text-[6.5px] font-black uppercase tracking-widest",
                                            hasPlan ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" : "bg-slate-950 border-white/5 text-white/20"
                                        )}>
                                            {hasPlan ? 'ACTIVO' : 'SIN PLAN'}
                                        </div>
                                    </div>

                                    <div className="space-y-0.5">
                                        <h3 className="text-sm sm:text-base font-black text-white uppercase tracking-tight leading-snug group-hover:text-emerald-500 transition-colors min-h-[2.5rem] sm:min-h-[2.75rem] line-clamp-2">
                                            {inst.name}
                                        </h3>
                                        <p className="text-white/30 text-[7.5px] sm:text-[8px] font-black uppercase tracking-widest truncate">
                                            {inst.president_name || 'SIN ASIGNAR'}
                                        </p>
                                    </div>

                                    <div className="mt-2.5 pt-2 border-t border-white/5 flex flex-col gap-1.5">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[8px] font-black text-white/10 uppercase tracking-[0.2em]">
                                                {hasPlan && plan ? (STATUS_LABELS[plan.status as keyof typeof STATUS_LABELS] || 'PENDIENTE') : 'PENDIENTE DE INICIO'}
                                            </span>
                                            <ChevronRight size={14} className="text-white/10 group-hover:text-emerald-500 transition-colors" />
                                        </div>

                                        {hasPlan && plan && (
                                            <div className="flex gap-1.5">
                                                <div className={cn(
                                                    "flex items-center gap-1 px-1.25 py-0.25 rounded-md text-[6px] font-black uppercase tracking-widest border transition-all",
                                                    plan.vp_approved
                                                        ? "bg-indigo-500/10 border-indigo-500/20 text-indigo-400"
                                                        : "bg-slate-950 border-white/5 text-white/10"
                                                )}>
                                                    <ShieldCheck size={8} className={plan.vp_approved ? "text-indigo-400" : "text-white/10"} />
                                                    VP
                                                </div>
                                                <div className={cn(
                                                    "flex items-center gap-1 px-1.25 py-0.25 rounded-md text-[6px] font-black uppercase tracking-widest border transition-all",
                                                    plan.indicators_president_approved
                                                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                                                        : "bg-slate-950 border-white/5 text-white/10"
                                                )}>
                                                    <Award size={8} className={plan.indicators_president_approved ? "text-emerald-400" : "text-white/10"} />
                                                    NAC
                                                </div>
                                            </div>
                                        )}

                                        {hasPlan && plan && (() => {
                                            const greenTh = settings?.threshold_green ?? 90;
                                            const amberTh = settings?.threshold_amber ?? 75;
                                            const colorClass = pct >= greenTh ? 'text-emerald-500' : pct >= amberTh ? 'text-amber-500' : 'text-red-500';
                                            const bgClass = pct >= greenTh ? 'bg-emerald-500' : pct >= amberTh ? 'bg-amber-500' : 'bg-red-500';
                                            const progressColorClass = pct >= greenTh ? 'text-emerald-400' : pct >= amberTh ? 'text-amber-400' : 'text-red-400';
                                            return (
                                                <div className="mt-2 pt-1.5 border-t border-white/5 space-y-1">
                                                    <div className="flex justify-between items-center text-[8px] font-bold text-white/40 uppercase tracking-wider">
                                                        <span>Avance Real</span>
                                                        <span className={cn("font-black", progressColorClass)}>{plan.total_progress || 0} / {plan.total_goal || 0}</span>
                                                    </div>
                                                    <div className="w-full bg-slate-950 rounded-full h-[3px] border border-white/5 overflow-hidden">
                                                        <div
                                                            className={cn("h-full rounded-full transition-all duration-500", bgClass)}
                                                            style={{ width: `${pct}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="flex justify-between items-center text-[10px] font-black text-white uppercase tracking-tight">
                                                        <span>Cumplimiento</span>
                                                        <span className={cn("font-black", colorClass)}>{pct}%</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </button>
                            );
                        })}

                        {/* Estado Vacío UX */}
                        {filteredInstances.length === 0 && !loading && (
                            <div className="col-span-full py-32 flex flex-col items-center justify-center text-center space-y-6 bg-slate-900/20 border border-dashed border-white/10 rounded-[3rem] animate-in fade-in duration-700">
                                <div className="w-24 h-24 bg-slate-900 rounded-full flex items-center justify-center text-white/10 border border-white/5 shadow-2xl">
                                    <Search size={40} />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight italic">No se encontraron instancias</h3>
                                    <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">Intenta ajustar los filtros o la búsqueda de texto</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setTypeFilter('TODOS');
                                        setVpFilterId(null);
                                    }}
                                    className="px-10 py-4 bg-emerald-600/10 hover:bg-emerald-600 text-emerald-500 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-500/20 transition-all active:scale-95 flex items-center gap-3 shadow-xl"
                                >
                                    <RotateCcw size={14} />
                                    LIMPIAR FILTROS
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {noPlanInstanceModal && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-[500] flex items-center justify-center p-6">
                    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-950 border border-slate-800 w-full max-w-xl rounded-[3rem] p-12 relative overflow-hidden shadow-[0_0_100px_rgba(16,185,129,0.1)]">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                        <div className="flex justify-between items-start mb-6 relative z-10">
                            <div className="w-16 h-16 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500 shadow-xl shadow-amber-500/10">
                                <AlertTriangle size={32} />
                            </div>
                            <button onClick={() => setNoPlanInstanceModal(null)} className="text-slate-500 hover:text-white transition-colors"><X size={24} /></button>
                        </div>
                        <h3 className="text-3xl font-black text-white uppercase tracking-tighter mb-4 relative z-10">
                            Instancia sin plan activo
                        </h3>
                        <p className="text-slate-400 text-sm mb-10 relative z-10">
                            Esta instancia existe en la estructura organizacional, pero todavía no tiene un plan de trabajo activo.
                        </p>
                        <div className="space-y-4 relative z-10 flex flex-col gap-3">
                            <button
                                onClick={() => handleCreateBasePlan(noPlanInstanceModal)}
                                disabled={user?.role !== 'SUPER_ADMIN' || isCreatingBasePlan}
                                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-xl transition-all flex items-center justify-center gap-2"
                            >
                                {isCreatingBasePlan ? <RefreshCw size={16} className="animate-spin" /> : <FilePlus size={16} />}
                                Crear plan base
                            </button>
                            {/* Option 2 removed as we want to trigger Sandbox with target selected. But the user said:
                            2. â€œUsar como destino en Dry Runâ€
                               - abre Sincronizador Flexible con esta instancia como destino. */}
                            <button
                                onClick={() => {
                                    setNoPlanInstanceModal(null);
                                    // Normally we would set it in App_V3 state, but this component only sets selectedInstancia
                                    // To open Sandbox, we need App_V3 state logic. For MVP phase 2B, this button might just trigger an alert if not wired:
                                    alert("Para usar como destino, dirígete a un Plan Origen y abre la Réplica Masiva, y luego escoge Sincronizador Flexible.");
                                }}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-xl transition-all flex items-center justify-center gap-2 border border-slate-800"
                            >
                                <Zap size={16} />
                                Usar como destino en Dry Run
                            </button>
                            <button
                                onClick={() => setNoPlanInstanceModal(null)}
                                className="w-full bg-transparent hover:bg-white/5 text-slate-500 hover:text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-xl transition-all"
                            >
                                Cancelar
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {replicaModalOpen && sourcePlanId && (
                <PlanReplicaModal
                    sourcePlanId={sourcePlanId}
                    sourceName={plans.find(p => p.id === sourcePlanId)?.commission_name || 'Instancia Origen'}
                    onClose={() => setReplicaModalOpen(false)}
                    hierarchy={hierarchy}
                    isSuperAdmin={isSuperAdmin}
                    user={user}
                />
            )}
        </div>
    );
};



const Dashboard = ({ user }: { user: any }) => {
    const [hasError, setHasError] = useState(false);
    const [hierarchy, setHierarchy] = useState<any>(null);

    const settings = useContext(SettingsContext);
    const navigate = useNavigate();
    const [stats, setStats] = useState<any>(null);
    const [chartData, setChartData] = useState<any[]>([]);
    const [instanceDetail, setInstanceDetail] = useState<any>(null);
    const [indicators, setIndicators] = useState<any[]>([]);
    const [selectedMonth, setSelectedMonth] = useState(MONTHS[Math.max(0, getElapsedCompletedMonths())]);
    const vpWeights = useMemo(() => ({
        COMISION_TYPE: settings?.weight_comisiones || 50,
        COMITE: settings?.weight_comites || 30,
        CONSEJERIA: settings?.weight_consejerias || 20
    }), [settings]);

    const { isND, isVP, isUserAdmin, isPresident, isGlobal } = useRole(user);
    const [showVpConfig, setShowVpConfig] = useState(false);
    const [searchParams] = useLocation().search ? [new URLSearchParams(useLocation().search)] : [new URLSearchParams()];
    const urlVPId = searchParams.get('vpId');

    const [activeVPId, setActiveVPId] = useState<string | null>(() => {
        if (isVP) return user?.instance_id || user?.vicepresidency_id || null;
        return urlVPId || null;
    });

    const [loading, setLoading] = useState(true);
    const [vpCompliance, setVpCompliance] = useState(0);

    const isVPView = isVP || (isND && activeVPId);
    const isInstanceScope = isPresident;
    const isGlobalScope = isGlobal;

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {

                const monthIdx = MONTHS.indexOf(selectedMonth);

                if (isVPView && activeVPId) {
                    // --- MODO VICEPRESIDENCIA (Real-Time Compliance Engine) ---
                    const { instances } = await db.getVPImpactData(activeVPId);
                    // Sincronizar estados de planes para conteo de validaciones
                    const plans = await db.getWorkPlans({ role: 'VICE_PRESIDENT', vicepresidency_id: activeVPId, org_scope: 'VP' } as any);

                    const enrichedInstances = await Promise.all(instances.map(async (inst: any) => {
                        const inds = await db.getIndicators(inst.id);
                        const kpiResult = calculateProratedCompliance(inds, getElapsedCompletedMonths(monthIdx));
                        const kpiPct = kpiResult.cumplimiento;

                        const planForInst = plans.find(p => p.commission_id === inst.id);
                        const plan_status = planForInst?.status || 'SIN PLAN';
                        const planPct = (planForInst?.total_goal > 0)
                            ? Math.round((planForInst.total_progress / planForInst.total_goal) * 100)
                            : 0;
                        const hasOperationalPlan = isVpPlanEvaluable({ total_goal: planForInst?.total_goal });
                        const hasCanonicalKpis = isVpKpiEvaluable({
                            hasActiveKpis: kpiResult.cumplimiento !== null,
                            totalMeta: kpiResult.totalMeta
                        });

                        // Threshold evaluation for KPI dimension
                        const tGreen = settings?.threshold_green || 90;
                        const tAmber = settings?.threshold_amber || 75;
                        const status = kpiPct >= tGreen ? 'green' : kpiPct >= tAmber ? 'amber' : 'red';

                        return {
                            ...inst,
                            kpiCompliance: kpiPct,
                            planProgress: planPct,
                            planEligible: hasOperationalPlan,
                            kpiEligible: hasCanonicalKpis,
                            hasActiveKpis: hasCanonicalKpis,
                            hasActivePlan: hasOperationalPlan,
                            status,
                            plan_status
                        };
                    }));

                    const vpPerf = calculateVPPerformance(
                        enrichedInstances,
                        buildVpPerformanceSettings(enrichedInstances, settings)
                    );

                    setChartData(enrichedInstances.map(r => ({
                        name: r.name,
                        val: r.kpiCompliance === null ? 0 : Math.round(r.kpiCompliance),
                        type: r.type
                    })));

                    // Aggregate KPI statuses
                    const kpiStatus = { green: 0, amber: 0, red: 0 };
                    enrichedInstances.forEach(inst => {
                        if (inst.status === 'green') kpiStatus.green++;
                        else if (inst.status === 'amber') kpiStatus.amber++;
                        else kpiStatus.red++;
                    });

                    const pendingValidations = enrichedInstances.filter(r => r.plan_status === 'EN REVISION').length;

                    setVpCompliance(vpPerf.global);
                    setStats({
                        totalCommissions: enrichedInstances.length,
                        activeWorkPlans: vpPerf.activePlans,
                        pct_cumplimiento: Math.round(vpPerf.global),
                        globalProgress: Math.round(vpPerf.global),
                        vpImpact: Math.round(vpPerf.kpi),
                        vpGestion: Math.round(vpPerf.plan),
                        kpiStatus,
                        pendingValidations
                    });
                } else {
                    // --- MODO ANALÍTICO / GLOBAL / INSTANCIA ---
                    const results = await db.getDashboardByCommission(user);

                    if (isInstanceScope && results.length > 0) {
                        setInstanceDetail(results[0]);
                    }

                    setChartData(results.map((r: any) => ({
                        name: r.commission_name || r.name,
                        val: r.pct_cumplimiento || 0,
                        type: r.commission_type || r.type
                    })));

                    const totalWeight = results.length || 1;
                    const avgCompliance = results.reduce((sum: number, r: any) => sum + (r.pct_cumplimiento || 0), 0) / totalWeight;

                    // Aggregating statuses from DB view data
                    const kpiStatus = {
                        green: results.reduce((acc, r) => acc + (r.kpi_green || 0), 0),
                        amber: results.reduce((acc, r) => acc + (r.kpi_amber || 0), 0),
                        red: results.reduce((acc, r) => acc + (r.kpi_red || 0), 0)
                    };

                    // Count Global Pending Validations
                    const pendingValidations = results.filter(r => r.plan_status === 'EN REVISION').length;

                    setStats({
                        totalCommissions: results.length,
                        activeWorkPlans: results.filter(r => (r.plan_status || 'SIN PLAN') !== 'SIN PLAN').length,
                        pct_cumplimiento: Math.round(avgCompliance),
                        globalProgress: Math.round(avgCompliance),
                        vpImpact: Math.round(avgCompliance),
                        vpGestion: Math.round(avgCompliance),
                        kpiStatus,
                        pendingValidations
                    });
                }
            } catch (e) {
                console.error("Dashboard Sync Error:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [user, activeVPId, selectedMonth, vpWeights, isVPView, settings?.weight_dimension_kpis, settings?.weight_dimension_plans]);

    if (loading) return (
        <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
            <p className="text-white/40 font-black uppercase text-[10px] tracking-widest">Sincronizando Inteligencia Estratégica...</p>
        </div>
    );

    if (!stats) return (
        <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4 text-center">
            <div className="bg-red-500/10 p-6 rounded-[2rem] border border-red-500/20 max-w-lg">
                <AlertTriangle className="text-red-500 w-12 h-12 mx-auto mb-4" />
                <h3 className="text-red-400 font-black uppercase tracking-tight text-xl mb-2">Error de Sincronización</h3>
                <p className="text-white/70 text-sm mb-4">No se pudo cargar el tablero de impacto. Esto puede deberse a que faltan vistas en la base de datos (ej. gov_v_commission_dashboard) o errores de red.</p>
                <p className="text-white/50 text-[10px] font-bold uppercase tracking-widest bg-black/40 p-3 rounded-xl border border-white/5">
                    Por favor presiona F12, ve a la pestaña "Console" y captura el error que dice "Dashboard Sync Error:" para enviarlo a soporte.
                </p>
            </div>
        </div>
    );

    // --- DASHBOARD FOR INSTANCE PRESIDENTS: Clean, focused view ---
    if (isInstanceScope) {
        const detail = instanceDetail;
        const planStatus = detail?.plan_status || 'SIN PLAN';
        const statusColors: Record<string, string> = {
            'APROBADO': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'BORRADOR': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
            'EN REVISION': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'REVISADO': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
            'SIN PLAN': 'bg-slate-500/20 text-white/60 border-slate-500/30',
        };
        const pct = stats.globalProgress || 0;
        const barColor = pct >= (settings?.threshold_green || 90) ? 'bg-emerald-500' : pct >= (settings?.threshold_amber || 75) ? 'bg-amber-500' : 'bg-red-500';

        return (
            <div className="space-y-8">
                <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                    <div>
                        <span className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.3em] mb-2 block">Mi Tablero de Gestión</span>
                        <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter uppercase leading-none">{detail?.commission_name || 'Mi Instancia'}</h1>
                        <p className="text-white/70 text-sm font-bold mt-4 max-w-xl leading-relaxed">
                            Resumen de avance y estado de aprobacion de tu plan de trabajo 2026.
                        </p>
                    </div>
                    <div className={cn("px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border", statusColors[planStatus] || statusColors['SIN PLAN'])}>
                        Plan: {planStatus}
                    </div>
                </header>

                {/* Cumplimiento card + KPI summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Main compliance card */}
                    <div className="md:col-span-2 bg-slate-900 border border-slate-800 p-10 rounded-[2.5rem] relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                        <div className="relative z-10">
                            <h3 className="text-sm font-black text-white/70 uppercase tracking-widest mb-6">Cumplimiento Global</h3>
                            {planStatus === 'SIN PLAN' ? (
                                <div className="bg-slate-950/80 border border-sky-500/30 p-6 rounded-2xl relative z-20">
                                    <h4 className="text-sky-400 font-black uppercase text-[11px] tracking-widest mb-2 flex items-center gap-2">
                                        <Target size={14} /> Estructura Nueva
                                    </h4>
                                    <p className="text-slate-300 text-[10px] font-bold uppercase tracking-wider leading-relaxed">
                                        Esta instancia aún no tiene plan activo. Puedes usarla en Dry Run como destino de simulación, pero para captura real requiere crear o sincronizar un plan.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-end gap-6 mb-6">
                                        <span className={cn("text-7xl font-black tracking-tighter", pct >= (settings?.threshold_green || 90) ? 'text-emerald-400' : pct >= (settings?.threshold_amber || 75) ? 'text-amber-400' : 'text-red-400')}>{pct}%</span>
                                        <span className="text-slate-600 text-sm font-bold uppercase pb-3">de avance anual</span>
                                    </div>
                                    <div className="w-full bg-slate-800 rounded-full h-4">
                                        <div className={cn("h-full rounded-full transition-all duration-1000", barColor)} style={{ width: `${Math.min(pct, 100)}%` }} />
                                    </div>
                                </>
                            )}
                            <div className="grid grid-cols-3 gap-6 mt-8">
                                <div className="text-center">
                                    <p className="text-emerald-400 text-2xl font-black">{stats.kpiStatus.green}</p>
                                    <p className="text-[9px] text-white/70 font-black uppercase tracking-widest">Elem. Óptimos</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-amber-400 text-2xl font-black">{stats.kpiStatus.amber}</p>
                                    <p className="text-[9px] text-white/70 font-black uppercase tracking-widest">En Riesgo</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-red-400 text-2xl font-black">{stats.kpiStatus.red}</p>
                                    <p className="text-[9px] text-white/70 font-black uppercase tracking-widest">Críticos</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick actions panel */}
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem]">
                        <h3 className="text-lg font-black text-white uppercase tracking-tight mb-6 font-serif italic">Acciones</h3>
                        <div className="space-y-4">
                            {canViewAuthorizedModule(user, 'Planeación') && (
                                <Link to="/planning" className="flex items-center gap-4 bg-slate-800/50 p-5 rounded-2xl hover:bg-emerald-600 transition-all group">
                                    <div className="w-10 h-10 bg-slate-950 rounded-xl flex items-center justify-center text-emerald-500 group-hover:text-white transition-colors"><Target size={20} /></div>
                                    <div>
                                        <p className="text-white font-black text-xs uppercase tracking-tight">Mi Plan</p>
                                        <p className="text-white/70 text-[8px] font-bold uppercase group-hover:text-emerald-100">Ver / Gestionar</p>
                                    </div>
                                </Link>
                            )}
                            {canViewAuthorizedModule(user, 'Seguimiento') && (
                                <Link to="/tracking" className="flex items-center gap-4 bg-slate-800/50 p-5 rounded-2xl hover:bg-indigo-600 transition-all group">
                                    <div className="w-10 h-10 bg-slate-950 rounded-xl flex items-center justify-center text-indigo-500 group-hover:text-white transition-colors"><BarChart3 size={20} /></div>
                                    <div>
                                        <p className="text-white font-black text-xs uppercase tracking-tight">Captura KPI</p>
                                        <p className="text-white/70 text-[8px] font-bold uppercase group-hover:text-indigo-100">Monitor de Avance</p>
                                    </div>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- GLOBAL / VP DASHBOARD (Admin, VP, etc.) ---
    return (
        <div className="space-y-12">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <span className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.3em] mb-2 block">
                        {isVPView ? 'Resumen de Impacto Vicepresidencia' : 'Resumen Ejecutivo de Impacto 2026'}
                    </span>
                    <h1 className="text-3xl md:text-4xl font-black text-white tracking-tighter uppercase leading-none">
                        {isVPView ? (
                            <>Tablero de<br /><span className="text-emerald-500">Vicepresidencia</span></>
                        ) : (
                            <>Tablero de<br /><span className="text-emerald-500">GESTIÓN NACIONAL</span></>
                        )}
                    </h1>
                    <p className="text-white/70 text-xs font-bold mt-4 max-w-xl leading-relaxed">
                        {isVPView ? 'Consolidado de inteligencia estratégica y avance de las instancias correspondientes a esta Vicepresidencia.' : 'Consolidado de inteligencia estratégica y avance de gobernanza 2026. Este tablero integra la visión nacional con el desempeño táctico de cada instancia.'}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-3">
                    <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-2xl flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-[9px] font-black text-white/70 uppercase tracking-widest">Estado del Ecosistema</p>
                            <p className="text-sm font-black text-white uppercase mt-0.5">Operativo • {stats.totalCommissions} Instancias</p>
                        </div>
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                        <TrendingUp size={14} className="text-emerald-500" />
                        <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{stats.globalProgress > 0 ? `+${stats.globalProgress}%` : '0%'} ALCANCE ESTRATÉGICO</span>
                    </div>
                </div>
            </header>

            {/* MEJORA B: Doble Semáforo para Vicepresidencias */}
            {isVPView && (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                        <div className="absolute -top-12 -right-12 w-48 h-48 blur-3xl opacity-10 rounded-full bg-emerald-500 group-hover:scale-150 transition-transform duration-1000"></div>
                        <div className="flex justify-between items-start mb-8 relative z-10">
                            <div className="p-5 rounded-[1.5rem] bg-gradient-to-br from-emerald-500 to-teal-600 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)] text-white">
                                <Activity size={28} strokeWidth={2.5} />
                            </div>
                            <button
                                onClick={() => setShowVpConfig(!showVpConfig)}
                                className={cn(
                                    "p-3 rounded-2xl transition-all border font-black text-[10px] flex items-center gap-2",
                                    showVpConfig ? "bg-emerald-500 border-emerald-400 text-white" : "bg-slate-800 border-slate-700 text-white/50 hover:bg-slate-700 hover:text-white"
                                )}
                            >
                                <Settings size={16} />
                                {showVpConfig ? 'GUARDAR' : 'CONFIGURAR'}
                            </button>
                        </div>
                        <div className="relative z-10">
                            <p className="text-white/40 text-[11px] uppercase font-black tracking-[0.2em] mb-2">Semáforo de Impacto</p>
                            <div className="flex items-baseline gap-3">
                                <h3 className="text-6xl font-black text-white tracking-tighter leading-none">{stats.vpImpact}%</h3>
                                <div className={cn("px-2 py-0.5 rounded text-[10px] font-black", stats.vpImpact >= (settings?.threshold_green || 90) ? "bg-emerald-500/20 text-emerald-500" : stats.vpImpact >= (settings?.threshold_amber || 75) ? "bg-amber-500/20 text-amber-500" : "bg-red-500/20 text-red-500")}>
                                    {stats.vpImpact >= (settings?.threshold_green || 90) ? 'ÓPTIMO' : stats.vpImpact >= (settings?.threshold_amber || 75) ? 'ESTABLE' : 'CRÍTICO'}
                                </div>
                            </div>
                            <p className="text-slate-600 text-[10px] font-bold uppercase tracking-wider mt-4">Cumplimiento Estratégico Ponderado (Impacto Real)</p>
                        </div>

                        <AnimatePresence>
                            {showVpConfig && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                    className="mt-8 pt-8 border-t border-slate-800/50 overflow-hidden"
                                >
                                    <p className="text-white/30 text-[9px] font-black uppercase mb-4 tracking-widest">Ajuste de Ponderacion por Tipo de Instancia</p>
                                    <div className="grid grid-cols-3 gap-4">
                                        {Object.entries(vpWeights).map(([type, weight]) => (
                                            <div key={type} className="bg-slate-800/30 p-4 rounded-2xl border border-slate-800">
                                                <label className="block text-[8px] font-black text-white/40 uppercase mb-2 leading-none">{type}</label>
                                                <div className="flex items-center gap-2">
                                                    <div className="bg-transparent text-white text-xl font-black w-full">
                                                        {weight}
                                                    </div>
                                                    <span className="text-white/20 font-black">%</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                        <div className="absolute -top-12 -right-12 w-48 h-48 blur-3xl opacity-10 rounded-full bg-blue-500 group-hover:scale-150 transition-transform duration-1000"></div>
                        <div className="flex justify-between items-start mb-8 relative z-10">
                            <div className="p-5 rounded-[1.5rem] bg-gradient-to-br from-blue-500 to-indigo-600 shadow-[0_10px_30px_-10px_rgba(59,130,246,0.5)] text-white">
                                <Target size={28} strokeWidth={2.5} />
                            </div>
                        </div>
                        <div className="relative z-10">
                            <p className="text-white/40 text-[11px] uppercase font-black tracking-[0.2em] mb-2">Semaforo de Gestion</p>
                            <div className="flex items-baseline gap-3">
                                <h3 className="text-6xl font-black text-white tracking-tighter leading-none">{stats.vpGestion}%</h3>
                                <div className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-[10px] font-black uppercase">PONDERADO</div>
                            </div>
                            <p className="text-slate-600 text-[10px] font-bold uppercase tracking-wider mt-4">Cumplimiento del Plan de Trabajo (Gestion Operativa)</p>
                        </div>
                    </div>
                </section>
            )}

            {/* Insights de Inteligencia Estrategica */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-slate-900 border border-slate-800 p-6 rounded-3xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-emerald-500/10 transition-all"></div>
                    <div className="relative z-10">
                        <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-3">
                            <Sparkles size={18} className="text-emerald-500" />
                            Insights de Inteligencia Estratégica
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="flex gap-4">
                                <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 shrink-0 border border-emerald-500/20">
                                    <PieChartIcon size={20} />
                                </div>
                                <div>
                                    <p className="text-white font-black text-sm uppercase mb-1">Concentracion de Impacto</p>
                                    <p className="text-white/60 text-xs leading-relaxed">Las instancias con mayor cumplimiento lideran con {stats.globalProgress}% de avance promedio nacional.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500 shrink-0 border border-amber-500/20">
                                    <Zap size={20} />
                                </div>
                                <div>
                                    <p className="text-white font-black text-sm uppercase mb-1">Oportunidad de Sincronización</p>
                                    <p className="text-white/60 text-xs leading-relaxed">{stats.kpiStatus.green} elementos en zona optima. {stats.kpiStatus.red > 0 ? `${stats.kpiStatus.red} elementos en riesgo requieren atencion.` : 'Sin elementos en riesgo.'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {getUserModules(user).includes('Reportes') && (
                    <div className="bg-emerald-600 p-6 rounded-3xl shadow-2xl shadow-emerald-500/20 flex flex-col justify-between relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                        <div className="relative z-10">
                            <span className="text-white/60 text-[10px] font-black uppercase tracking-widest mb-1 block">Logro Destacado</span>
                            <h4 className="text-2xl font-black text-white uppercase leading-tight">Reporte de Cumplimiento</h4>
                        </div>
                        <div className="relative z-10 pt-4 border-t border-white/10 mt-4">
                            <p className="text-white/80 text-[10px] font-bold leading-relaxed mb-4">La integracion de Juntas Nacionales ha alcanzado un quorum record del 92%.</p>
                            <button
                                onClick={() => navigate('/reports')}
                                className="group/btn flex items-center gap-2 text-white font-black text-[10px] uppercase tracking-widest hover:text-white/80 transition-all"
                            >
                                Ver Reporte <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <StatCard title="Instancias" value={stats.totalCommissions} subtext="Nivel de Operación" icon={Building2} color="bg-emerald-600" trend="+12%" />
                <StatCard title="Planes" value={stats.activeWorkPlans} subtext="En Ejecución" icon={Shield} color="bg-blue-600" />
                <StatCard title="Cumplimiento" value={`${stats.globalProgress}%`} subtext="Promedio Nacional" icon={Target} color="bg-amber-600" trend="+2.5%" />
                <StatCard title="Revisiones" value={stats.pendingValidations} subtext="Flujo Pendiente" icon={FileText} color="bg-purple-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-slate-900 border border-slate-800 p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-10">
                        <BarChart3 className="text-slate-800" size={120} />
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-8">Curva de Avance por {isGlobalScope ? 'Vicepresidencia' : 'Instancia'}</h3>
                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData.length > 0 ? chartData : [{ name: 'Sin datos', val: 0 }]}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10, fontWeight: 800 }} dy={10} interval={0} angle={-35} textAnchor="end" height={80} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10, fontWeight: 800 }} />
                                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '12px', color: '#fff' }} />
                                    <Bar dataKey="val" radius={[8, 8, 0, 0]} barSize={40}>
                                        {(chartData.length > 0 ? chartData : [{ name: 'Sin datos', val: 0 }]).map((entry: any, idx: number) => (
                                            <Cell key={idx} fill={entry.val >= (settings?.threshold_green || 90) ? '#10b981' : entry.val >= (settings?.threshold_amber || 75) ? '#f59e0b' : entry.val > 0 ? '#ef4444' : '#334155'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 p-10 rounded-[3rem] shadow-2xl">
                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-8 font-serif italic">Acciones Rápidas</h3>
                    <div className="space-y-4">
                        {canViewAuthorizedModule(user, 'Planeación') && (
                            <Link to="/planning" className="flex items-center gap-4 bg-slate-800/50 p-6 rounded-[2rem] hover:bg-emerald-600 transition-all group">
                                <div className="w-12 h-12 bg-slate-950 rounded-2xl flex items-center justify-center text-emerald-500 group-hover:text-white transition-colors">
                                    <Target size={24} />
                                </div>
                                <div>
                                    <p className="text-white font-black text-xs uppercase tracking-tight">Planes de Trabajo</p>
                                    <p className="text-white/70 text-[8px] font-bold uppercase group-hover:text-emerald-100 transition-colors">Gestionar Metas 2026</p>
                                </div>
                            </Link>
                        )}
                        {canViewAuthorizedModule(user, 'Seguimiento') && (
                            <Link to="/tracking" className="flex items-center gap-4 bg-slate-800/50 p-6 rounded-[2rem] hover:bg-indigo-600 transition-all group">
                                <div className="w-12 h-12 bg-slate-950 rounded-2xl flex items-center justify-center text-indigo-500 group-hover:text-white transition-colors">
                                    <BarChart3 size={24} />
                                </div>
                                <div>
                                    <p className="text-white font-black text-xs uppercase tracking-tight">Captura KPI</p>
                                    <p className="text-white/70 text-[8px] font-bold uppercase group-hover:text-indigo-100 transition-colors">Monitor de Avance</p>
                                </div>
                            </Link>
                        )}
                        {getUserModules(user).includes('Talento') && (
                            <Link to="/talent" className="flex items-center gap-4 bg-slate-800/50 p-6 rounded-[2rem] hover:bg-amber-600 transition-all group">
                                <div className="w-12 h-12 bg-slate-950 rounded-2xl flex items-center justify-center text-amber-500 group-hover:text-white transition-colors">
                                    <Users size={24} />
                                </div>
                                <div>
                                    <p className="text-white font-black text-xs uppercase tracking-tight">Talento Exp.</p>
                                    <p className="text-white/70 text-[8px] font-bold uppercase group-hover:text-amber-100 transition-colors">Perfiles de Liderazgo</p>
                                </div>
                            </Link>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

/** Explorador de VP e Instancias - Panel navegable */
const VPInstanceExplorer = ({ user }: { user: any }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [hierarchy, setHierarchy] = useState<any>(null);
    const [openVPs, setOpenVPs] = useState<string[]>([]);

    const selectedVP = searchParams.get('vpId');
    const selectedInstancia = searchParams.get('instanciaId');

    const isUserVP = user?.org_scope === 'VP' || user?.role === 'VICE_PRESIDENT';
    const isUserCoordinator = user?.org_scope === 'COORDINATOR' || user?.role === 'COORDINADOR';
    const myVPId = user?.vicepresidency_id || user?.instance_id;

    useEffect(() => {
        db.getHierarchy(user).then(setHierarchy);
    }, [user]);

    useEffect(() => {
        if (selectedVP && !openVPs.includes(selectedVP)) {
            setOpenVPs([...openVPs, selectedVP]);
        }
    }, [selectedVP]);

    if (!hierarchy) return null;

    const allVPs = isUserVP
        ? (hierarchy.vicepresidencies || []).filter((v: any) => v.id === myVPId)
        : isUserCoordinator
            ? (hierarchy.vicepresidencies || []).filter((v: any) => (user.accessible_vps || []).includes(v.id))
            : (hierarchy.vicepresidencies || []);

    const allComms_ = [...(hierarchy.commissions || []), ...(hierarchy.independents || [])];

    const getCommsForVP = (vpId: string) => {
        return allComms_.filter((c: any) => c.vicepresidency_id === vpId);
    };

    const toggleVP = (vpId: string) => {
        setOpenVPs(prev => prev.includes(vpId) ? prev.filter(id => id !== vpId) : [...prev, vpId]);
    };

    return (
        <section className="bg-slate-900/50 border border-slate-800/50 rounded-[2.5rem] overflow-hidden shadow-2xl backdrop-blur-sm">
            <div className="p-8 border-b border-slate-800/50 flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <Layers className="text-emerald-500" size={20} />
                        Estructura de <span className="text-emerald-400">Gobernanza</span>
                    </h3>
                    <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mt-1">Exploracion Jerárquica y Estados de Cumplimiento</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{allVPs.length} Vicepresidencias</span>
                </div>
            </div>

            <div className="p-4 space-y-2">
                {allVPs.map((vp: any) => {
                    const isOpen = openVPs.includes(vp.id);
                    const vpComms = getCommsForVP(vp.id);
                    const isSelected = selectedVP === vp.id;

                    return (
                        <div key={vp.id} className={cn(
                            "rounded-2xl border transition-all duration-300 overflow-hidden",
                            isOpen ? "bg-slate-800/40 border-slate-700/60 shadow-lg" : "bg-transparent border-transparent hover:bg-slate-800/20"
                        )}>
                            <button
                                onClick={() => toggleVP(vp.id)}
                                className="w-full flex items-center justify-between p-4 px-6 group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={cn(
                                        "w-8 h-8 rounded-xl flex items-center justify-center transition-all",
                                        isOpen ? "bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]" : "bg-slate-950 text-slate-500 group-hover:text-emerald-400"
                                    )}>
                                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </div>
                                    <div className="text-left">
                                        <span className={cn(
                                            "text-xs font-black uppercase tracking-widest transition-colors",
                                            isSelected ? "text-emerald-400" : "text-white group-hover:text-emerald-400"
                                        )}>
                                            {vp.name?.replace('VP ', '').replace('Vicepresidencia de ', '')}
                                        </span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[8px] font-bold text-slate-500 uppercase tracking-tighter">{vpComms.length} Instancias</span>
                                            {vp.pct_cumplimiento !== undefined && (
                                                <div className="flex items-center gap-1.5 ml-2">
                                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: vp.pct_cumplimiento >= 90 ? '#10b981' : vp.pct_cumplimiento >= 75 ? '#f59e0b' : '#ef4444' }}></div>
                                                    <span className="text-[10px] font-black text-white/60">{Math.round(vp.pct_cumplimiento)}%</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    {/* Boton para ver Dashboard de VP */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`?vpId=${vp.id}`);
                                        }}
                                        className="px-3 py-1.5 rounded-lg bg-slate-950/50 hover:bg-emerald-500/20 text-[8px] font-black text-white/40 hover:text-emerald-400 uppercase tracking-widest border border-slate-800 transition-all"
                                    >
                                        Ver Tablero VP
                                    </button>
                                </div>
                            </button>

                            <AnimatePresence>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden bg-slate-950/30"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-6 pt-2">
                                            {vpComms.map((comm: any) => {
                                                const isInstanceSelected = selectedInstancia === comm.id;
                                                return (
                                                    <button
                                                        key={comm.id}
                                                        onClick={() => navigate(`/tracking?instanciaId=${comm.id}&vpId=${comm.vicepresidency_id}`)}
                                                        className={cn(
                                                            "flex flex-col p-4 rounded-xl border transition-all group relative",
                                                            isInstanceSelected
                                                                ? "bg-emerald-500/10 border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.1)]"
                                                                : "bg-slate-900 border-slate-800/50 hover:border-emerald-500/30"
                                                        )}
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest bg-slate-950 px-2 py-0.5 rounded-full border border-slate-800">
                                                                {comm.type || 'Instancia'}
                                                            </span>
                                                            {comm.pct_cumplimiento !== undefined && (
                                                                <span className={cn(
                                                                    "text-[10px] font-black",
                                                                    comm.pct_cumplimiento >= 90 ? "text-emerald-400" : comm.pct_cumplimiento >= 75 ? "text-amber-400" : "text-red-400"
                                                                )}>
                                                                    {Math.round(comm.pct_cumplimiento)}%
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className={cn(
                                                            "text-[11px] font-black leading-tight uppercase transition-colors mr-6",
                                                            isInstanceSelected ? "text-emerald-400" : "text-white group-hover:text-emerald-400"
                                                        )}>
                                                            {comm.name}
                                                        </span>
                                                        <div className="mt-3 flex items-center justify-between">
                                                            <span className="text-[8px] text-slate-600 font-bold truncate max-w-[120px]">
                                                                {comm.president || 'Sin asignar'}
                                                            </span>
                                                            {comm.plan_status === 'APPROVED' && (
                                                                <CheckCircle2 size={12} className="text-emerald-500" />
                                                            )}
                                                        </div>

                                                        {isInstanceSelected && (
                                                            <div className="absolute top-2 right-2">
                                                                <Activity size={12} className="text-emerald-500 animate-pulse" />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// --- Módulos de Administración ---

const AdminSettingsModule = ({ user }: { user: any }) => {
    const { isAdmin } = useRole(user);
    const [settings, setSettings] = useState<SystemSettings>({ ...DEFAULT_SETTINGS });
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'system' | 'operational-control' | 'cardex'>('operational-control');

    useEffect(() => {
        db.getSystemSettings().then(data => setSettings({ ...DEFAULT_SETTINGS, ...data }));
    }, []);

    const updateField = (key: string, value: any) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        const kpiWeight = Number(settings.weight_dimension_kpis ?? DEFAULT_SETTINGS.weight_dimension_kpis);
        const planWeight = Number(settings.weight_dimension_plans ?? DEFAULT_SETTINGS.weight_dimension_plans);
        if (kpiWeight + planWeight !== 100) {
            showToast("KPI Weight + Plan Weight debe ser 100%", "error");
            return;
        }

        setSaving(true);
        try {
            await db.updateSystemSettings(settings);
            showToast("Configuraciones actualizadas");
        } catch (e) {
            showToast("Error al guardar", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto py-12">
            <header className="mb-12">
                <h1 className="text-3xl font-black text-white uppercase mb-2">Umbrales de Semáforo</h1>
                <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.3em]">Solo para Administradores de Gobernanza</p>
            </header>

            <div className="flex flex-wrap gap-3 border-b border-slate-800 pb-3 mb-8">
                <button
                    type="button"
                    onClick={() => setActiveTab('operational-control')}
                    className={`px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'operational-control' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white/50 hover:text-white'}`}
                >
                    Control Operativo
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('system')}
                    className={`px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'system' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white/50 hover:text-white'}`}
                >
                    Sistema
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('cardex')}
                    className={`px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'cardex' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white/50 hover:text-white'}`}
                >
                    Cardex / Bitácora
                </button>
            </div>

            {activeTab === 'operational-control' && (
                <div className="space-y-8">
                    <OperationalControlSettingsTab
                        localSettings={settings as any}
                        updateField={updateField}
                        isAdmin={isAdmin}
                    />
                    <div className="flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2"
                        >
                            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                            {saving ? 'Guardando...' : 'Guardar Configuracion'}
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'system' && (
            <div className="bg-slate-900 border border-slate-800 p-12 rounded-[3rem] space-y-8">
                <div className="space-y-4">
                    <label className="text-[10px] font-black text-emerald-500 uppercase tracking-widest block">Límite Superior (Verde)</label>
                    <div className="flex items-center gap-6">
                        <input
                            type="range" min="0" max="100"
                            className="flex-1 accent-emerald-500"
                            value={settings.threshold_green}
                            onChange={e => setSettings({ ...settings, threshold_green: parseInt(e.target.value) })}
                        />
                        <span className="text-2xl font-black text-white w-16 text-right">{settings.threshold_green}%</span>
                    </div>
                </div>

                <div className="space-y-4">
                    <label className="text-[10px] font-black text-amber-500 uppercase tracking-widest block">Límite de Alerta (Ámbar)</label>
                    <div className="flex items-center gap-6">
                        <input
                            type="range" min="0" max="100"
                            className="flex-1 accent-amber-500"
                            value={settings.threshold_amber}
                            onChange={e => setSettings({ ...settings, threshold_amber: parseInt(e.target.value) })}
                        />
                        <span className="text-2xl font-black text-white w-16 text-right">{settings.threshold_amber}%</span>
                    </div>
                </div>

                <div className="pt-8 border-t border-slate-800">
                    <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-2">
                        <Calculator size={16} className="text-sky-400" /> Ponderacion General del Score
                    </h3>
                    <p className="text-[10px] text-white/40 font-bold mb-6">
                        Define el peso general del cumplimiento KPI y del avance del plan en el calculo del score operativo.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">KPI Weight</label>
                            <input
                                type="number"
                                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white font-black"
                                value={settings.weight_dimension_kpis}
                                onChange={e => {
                                    const val = parseInt(e.target.value) || 0;
                                    setSettings(prev => ({ ...prev, weight_dimension_kpis: val, kpi_weight: val }));
                                }}
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">Plan Weight</label>
                            <input
                                type="number"
                                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white font-black"
                                value={settings.weight_dimension_plans}
                                onChange={e => {
                                    const val = parseInt(e.target.value) || 0;
                                    setSettings(prev => ({ ...prev, weight_dimension_plans: val, plan_weight: val }));
                                }}
                            />
                        </div>
                    </div>
                    <p className={`mt-4 text-[9px] font-black uppercase tracking-widest ${(Number(settings.weight_dimension_kpis ?? 0) + Number(settings.weight_dimension_plans ?? 0)) === 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                        Total KPI + Plan: {Number(settings.weight_dimension_kpis ?? 0) + Number(settings.weight_dimension_plans ?? 0)}%
                    </p>
                </div>

                <div className="pt-8 border-t border-slate-800">
                    <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center gap-2">
                        <Layers size={16} className="text-emerald-500" /> Ponderación por Instancia
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="space-y-3">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">COMISIONES</label>
                            <input type="number" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white font-black" value={settings.weight_comisiones} onChange={e => setSettings({ ...settings, weight_comisiones: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">COMITÉS</label>
                            <input type="number" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white font-black" value={settings.weight_comites} onChange={e => setSettings({ ...settings, weight_comites: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">Consejerías</label>
                            <input type="number" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white font-black" value={settings.weight_consejerias} onChange={e => setSettings({ ...settings, weight_consejerias: parseInt(e.target.value) || 0 })} />
                        </div>
                    </div>
                    <p className="mt-4 text-[8px] text-white/30 font-bold uppercase tracking-widest">Nota: El sistema prorrateará automáticamente al 100% si una categoría no existe en la Vicepresidencia.</p>
                </div>
                <div className="pt-8 border-t border-slate-800 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2"
                    >
                        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </button>
                </div>
            </div>
            )}

            {activeTab === 'cardex' && (
                <AuditCardexPanel />
            )}
        </div>
    );
};


// --- USER MANAGEMENT (MIGRADO A COMPONENTES) ---

/**
 * Componente Ficha de Expertise (Talento Estratégico)
 * Basado en el estándar estratégico de IAPRIORI
 */
const ExpertiseSheet = ({ talent, onClose, onRefresh, user }: { talent: db.TalentRecord, onClose: () => void, onRefresh: (silent: boolean) => void, user?: any }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState<db.TalentRecord>(talent);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const updateIdempotencyKeyRef = useRef<string | null>(null);
    const deleteIdempotencyKeyRef = useRef<string | null>(null);

    const EXPERTISE_OPTIONS = [
        'Estrategia', 'Finanzas', 'Legal', 'Tecnología', 'TTHH',
        'Comercial', 'Operaciones', 'Marketing', 'Sostenibilidad', 'RRPP', 'Otra'
    ];

    const handleToggleArea = (area: string) => {
        const areas = data.areas || [];
        if (areas.includes(area)) {
            setData({ ...data, areas: areas.filter(a => a !== area) });
        } else {
            setData({ ...data, areas: [...areas, area] });
        }
    };

    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);

        try {
            const updates = {
                name: data.name,
                email: data.email,
                education: data.education || '',
                experience: data.experience || '',
                interests: data.interests || '',
                areas: data.areas || [],
                experience_level: data.experience_level || 0,
                other_expertise: data.other_expertise || '',
                projects: data.projects || ''
            };

            const idempotencyKey = updateIdempotencyKeyRef.current || db.createMutationIdempotencyKey();
            updateIdempotencyKeyRef.current = idempotencyKey;
            await db.updateTalent(talent.user_id, updates, { idempotencyKey });
            updateIdempotencyKeyRef.current = null;


            // Fuerza a la vista local a retener los cambios más recientes que acaban de viajar a la BD
            setData(prev => ({ ...prev, ...updates }));

            setIsEditing(false);

            // onRefresh recarga la DB subyacente para el grid de forma silenciosa.
            if (onRefresh) await onRefresh(true);

            showToast('Perfil actualizado con éxito');
            onClose();

        } catch (err: unknown) {
            console.error('[AUDIT] Failed to save talent data:', err);
            showToast(db.getMutationErrorMessage(err), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            const idempotencyKey = deleteIdempotencyKeyRef.current || db.createMutationIdempotencyKey();
            deleteIdempotencyKeyRef.current = idempotencyKey;
            await db.removeTalent(talent.id, { idempotencyKey });
            deleteIdempotencyKeyRef.current = null;
            await onRefresh?.(true);
            onClose();
        } catch (err: unknown) {
            console.error(err);
            showToast(db.getMutationErrorMessage(err), 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[9999] flex items-center justify-center p-6 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <motion.div initial={{ y: 50, scale: 0.95, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} className="bg-slate-950 border border-slate-800 w-full max-w-4xl rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(16,185,129,0.1)] p-12 relative my-10" onClick={e => e.stopPropagation()}>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                    }}
                    className="absolute top-10 right-10 text-white/70 hover:text-white transition-all z-50 p-4"
                >
                    <X size={32} />
                </button>

                <header className="flex flex-col md:flex-row items-center gap-8 mb-16">
                    <div className="w-32 h-32 rounded-full bg-slate-900 border-4 border-emerald-500/20 flex items-center justify-center overflow-hidden shrink-0 shadow-2xl">
                        <Users size={64} className="text-emerald-500/30" />
                    </div>
                    <div className="text-center md:text-left flex-1">
                        <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                            <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest border border-emerald-500/20">Perfil Directivo Nacional</span>
                            {canEditModule(user, 'Talento') && (
                                <button
                                    onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                                    disabled={isSaving}
                                    className={cn(
                                        "px-6 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all",
                                        isEditing ? "bg-emerald-600 text-white" : "bg-slate-900 text-white/60 hover:text-white"
                                    )}
                                >
                                    {isSaving ? 'Guardando...' : (isEditing ? 'Confirmar Cambios' : 'Modificar Perfil')}
                                </button>
                            )}
                            {canEditModule(user, 'Talento') && (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isDeleting}
                                    className="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all"
                                >
                                    Eliminar
                                </button>
                            )}
                        </div>
                        {isEditing ? (
                            <div className="space-y-4">
                                <input
                                    className="bg-slate-900 border border-slate-800 p-4 rounded-2xl text-2xl font-bold text-white w-full outline-none focus:border-emerald-500 shadow-inner"
                                    value={data.name}
                                    onChange={e => setData({ ...data, name: e.target.value })}
                                    placeholder="Nombre Completo"
                                />
                                <input
                                    className="bg-slate-900 border border-slate-800 p-4 rounded-2xl text-[11px] font-bold text-white/70 w-full outline-none focus:border-emerald-500 uppercase tracking-widest"
                                    value={data.email}
                                    onChange={e => setData({ ...data, email: e.target.value })}
                                    placeholder="CORREO ELECTRÃƒâ€œNICO DIRECTO"
                                />
                            </div>
                        ) : (
                            <>
                                <h2 className="text-4xl font-bold text-white uppercase tracking-tight mb-2 leading-none">{data.name}</h2>
                                <p className="text-white/70 text-[11px] font-bold uppercase tracking-[0.2em]">{data.role} | {data.email}</p>
                            </>
                        )}
                    </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div className="space-y-12">
                        <div>
                            <h4 className="flex items-center gap-3 text-white text-[10px] font-black uppercase tracking-[0.4em] mb-6">
                                <LayoutDashboard size={14} className="text-emerald-500" /> Áreas de Expertise
                            </h4>
                            <div className="flex flex-wrap gap-2.5">
                                {EXPERTISE_OPTIONS.map(area => {
                                    const isSelected = (data.areas || []).includes(area);
                                    return (
                                        <button
                                            key={area}
                                            disabled={!isEditing}
                                            onClick={() => handleToggleArea(area)}
                                            className={cn(
                                                "px-4 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all border",
                                                isSelected
                                                    ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-500"
                                                    : "bg-slate-900/50 border-slate-800 text-white/70 opacity-50",
                                                isEditing && "hover:border-emerald-500/30 cursor-pointer"
                                            )}
                                        >
                                            {area}
                                        </button>
                                    );
                                })}
                            </div>
                            {(isEditing || data.areas?.includes('Otra')) && (
                                <div className="mt-4">
                                    <input
                                        disabled={!isEditing}
                                        placeholder="Especifique otra área de expertise..."
                                        className="w-full bg-slate-900/50 border border-slate-800 p-4 rounded-2xl text-white text-xs font-bold outline-none focus:border-emerald-500/30 transition-all"
                                        value={data.other_expertise || ''}
                                        onChange={e => setData({ ...data, other_expertise: e.target.value })}
                                    />
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex justify-between items-end mb-4">
                                <h4 className="flex items-center gap-3 text-white text-[10px] font-black uppercase tracking-[0.4em]">
                                    <Zap size={14} className="text-emerald-500" /> Nivel de Experiencia
                                </h4>
                                <span className="text-emerald-500 font-black text-[10px] uppercase">
                                    {data.experience_level === 100 ? 'Experto / Líder' : data.experience_level && data.experience_level > 70 ? 'Senior / Avanzado' : 'Especialista'}
                                </span>
                            </div>
                            <div className="h-6 bg-slate-900 rounded-full border border-slate-800 overflow-hidden p-1 relative">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${data.experience_level || 85}%` }}
                                    className="h-full bg-gradient-to-r from-emerald-600 to-cyan-500 rounded-full shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                                />
                                {isEditing && (
                                    <input
                                        type="range"
                                        min="0" max="100"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        value={data.experience_level || 85}
                                        onChange={e => setData({ ...data, experience_level: parseInt(e.target.value) })}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-12">
                        <div>
                            <h4 className="flex items-center gap-3 text-white text-[10px] font-black uppercase tracking-[0.4em] mb-6">
                                <Target size={14} className="text-emerald-500" /> Información Académica
                            </h4>
                            <div className="grid grid-cols-1 gap-6">
                                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-[2rem] hover:bg-slate-900 transition-all group">
                                    <span className="text-slate-600 text-[8px] font-black uppercase tracking-widest block mb-2">Formación</span>
                                    {isEditing ? (
                                        <textarea
                                            className="w-full bg-transparent text-slate-300 text-sm font-bold leading-relaxed outline-none resize-none"
                                            rows={2}
                                            value={data.education}
                                            onChange={e => setData({ ...data, education: e.target.value })}
                                        />
                                    ) : (
                                        <p className="text-slate-300 text-sm font-bold leading-relaxed">{data.education || 'Información académica'}</p>
                                    )}
                                </div>
                                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-[2rem] hover:bg-slate-900 transition-all group">
                                    <span className="text-slate-600 text-[8px] font-black uppercase tracking-widest block mb-2">Trayectoria Estratégica</span>
                                    {isEditing ? (
                                        <textarea
                                            className="w-full bg-transparent text-slate-300 text-sm font-bold leading-relaxed outline-none resize-none"
                                            rows={3}
                                            value={data.experience}
                                            onChange={e => setData({ ...data, experience: e.target.value })}
                                        />
                                    ) : (
                                        <p className="text-slate-300 text-sm font-bold leading-relaxed">{data.experience || 'Historial profesional'}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-[9px] font-black text-white/70 uppercase tracking-widest flex items-center gap-2 mb-3">
                                <FileText size={14} className="text-emerald-500" /> Proyectos Destacados
                            </h4>
                            {isEditing ? (
                                <textarea
                                    className="w-full bg-slate-900 border border-slate-800 p-5 rounded-2xl text-white text-xs font-bold leading-relaxed focus:border-emerald-500/50 outline-none transition-all resize-none"
                                    rows={3}
                                    placeholder="Describa proyectos o contribuciones clave..."
                                    value={data.projects || ''}
                                    onChange={e => setData({ ...data, projects: e.target.value })}
                                />
                            ) : (
                                <p className="text-slate-300 text-[11px] leading-relaxed font-medium bg-slate-900/30 p-4 rounded-xl border border-slate-800/50">{data.projects || 'Contribuciones especiales'}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-10 border-t border-slate-900 flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-slate-700">
                    <span>ID ESTRATÃƒâ€°GICO: {data.id.slice(0, 8)}</span>
                    <span className="text-emerald-900/50 flex items-center gap-2">
                        <Shield size={12} /> Validado por IAPRIORI AI
                    </span>
                </div>

                {/* Modal de confirmación de eliminación */}
                {showDeleteConfirm && (
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-[3rem] flex items-center justify-center z-50">
                        <div className="bg-slate-900 border border-red-500/30 p-10 rounded-3xl text-center space-y-6 max-w-sm">
                            <Trash2 size={40} className="text-red-500 mx-auto" />
                            <h3 className="text-white font-black text-lg uppercase">Eliminar Perfil</h3>
                            <p className="text-white/60 text-sm">¿Estás seguro de eliminar el perfil de <strong className="text-white">{data.name}</strong> Esta acción no se puede deshacer.</p>
                            <div className="flex gap-4">
                                <button disabled={isDeleting} onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-slate-800 text-white py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all disabled:opacity-40">Cancelar</button>
                                <button disabled={isDeleting} onClick={handleDelete} className="flex-1 bg-red-600 text-white py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed">{isDeleting ? 'Eliminando...' : 'Eliminar'}</button>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

/**
 * Componente Organizador de Reuniones Mensuales
 * Formato flexible para VP e Instancias
 */
const MeetingOrganizerModule = ({ user }: { user: any }) => {
    const [meetings, setMeetings] = useState<db.Meeting[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [newMeeting, setNewMeeting] = useState<Partial<db.Meeting>>({
        title: '',
        date: new Date().toISOString().split('T')[0],
        agenda: [
            { title: 'Revisión de avances', duration: 15, responsible: 'Presidente', type: 'AVANCES' },
            { title: 'Análisis de indicadores', duration: 20, responsible: 'Equipo Técnico', type: 'KPI' },
            { title: 'Propuestas de mejora', duration: 15, responsible: 'Pleno', type: 'IDEAS' },
            { title: 'Definición de acciones', duration: 10, responsible: 'Secretario', type: 'Acción' }
        ],
        status: 'PLANNED'
    });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await db.getMeetings({
                vp_id: user.vicepresidency_id,
                comm_id: user.commission_id
            });
            setMeetings(data);
        } catch (e) {
            console.error("Error loading meetings", e);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleAddMeeting = async () => {
        try {
            await db.createMeeting({
                ...newMeeting,
                owner_id: user.id,
                vicepresidency_id: user.vicepresidency_id || null,
                commission_id: user.commission_id || null
            });
            setShowAdd(false);
            refresh();
        } catch (e) {
            showToast("Error al programar sesión");
        }
    };

    const isVP = user.role === 'VICE_PRESIDENT' || user.role === 'ADMINISTRADOR';

    return (
        <div className="space-y-12">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <h1 className="text-4xl font-black text-white uppercase mb-2 tracking-tighter">Agenda de Sesiones</h1>
                    <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em]">Estructura Organizativa de Reuniones Mensuales</p>
                </div>
                <button onClick={() => setShowAdd(true)} className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-3 hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-900/20">
                    <Calendar size={16} /> Programar Sesión
                </button>
            </header>

            {isVP && (
                <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-12 -mt-12 blur-xl"></div>
                        <span className="text-[8px] font-black text-white/70 uppercase tracking-widest block mb-4">Eficiencia Sesiones</span>
                        <div className="flex items-end gap-3">
                            <span className="text-4xl font-black text-white leading-none">92%</span>
                            <TrendingUp size={16} className="text-emerald-500 mb-1" />
                        </div>
                        <p className="text-[10px] text-emerald-500/50 font-bold mt-2 uppercase">Meta: 85%</p>
                    </div>
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] relative overflow-hidden">
                        <span className="text-[8px] font-black text-white/70 uppercase tracking-widest block mb-4">Sesiones Ejecutadas</span>
                        <div className="flex items-end gap-3">
                            <span className="text-4xl font-black text-white leading-none">{meetings.filter(m => m.status === 'COMPLETED').length}</span>
                            <CheckCircle2 size={16} className="text-emerald-500 mb-1" />
                        </div>
                        <p className="text-[10px] text-slate-600 font-bold mt-2 uppercase">Ciclo 2026</p>
                    </div>
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] relative overflow-hidden">
                        <span className="text-[8px] font-black text-white/70 uppercase tracking-widest block mb-4">Acuerdos Generados</span>
                        <div className="flex items-end gap-3">
                            <span className="text-4xl font-black text-white leading-none">148</span>
                            <Zap size={16} className="text-cyan-500 mb-1" />
                        </div>
                        <p className="text-[10px] text-cyan-500/50 font-bold mt-2 uppercase">+12 esta semana</p>
                    </div>
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] relative overflow-hidden">
                        <span className="text-[8px] font-black text-white/70 uppercase tracking-widest block mb-4">Captura indicadores</span>
                        <div className="flex items-end gap-3">
                            <span className="text-4xl font-black text-white leading-none">85%</span>
                            <PieChartIcon size={16} className="text-emerald-500 mb-1" />
                        </div>
                        <div className="w-full bg-slate-800 h-1 rounded-full mt-4 overflow-hidden">
                            <div className="bg-emerald-500 h-full w-[85%]"></div>
                        </div>
                    </div>
                </section>
            )}

            <div className="grid grid-cols-1 gap-8">
                {loading ? (
                    <div className="h-64 flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                    </div>
                ) : meetings.length === 0 ? (
                    <div className="bg-slate-900/30 border-2 border-dashed border-slate-800 rounded-[3rem] p-24 text-center">
                        <div className="w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-8 border border-slate-700 shadow-inner">
                            <Calendar size={40} className="text-slate-600" />
                        </div>
                        <h3 className="text-white font-black uppercase text-xl mb-3">No hay sesiones programadas</h3>
                        <p className="text-slate-600 text-sm font-bold uppercase tracking-widest max-w-sm mx-auto">Inicia la gestión de tu agenda mensual para seguimiento estratégico de objetivos</p>
                    </div>
                ) : (
                    meetings.map(m => (
                        <div key={m.id} className="bg-slate-950 border border-slate-900 rounded-[3rem] p-10 flex flex-col md:flex-row gap-10 hover:border-slate-800 transition-all group overflow-hidden relative shadow-2xl">
                            <div className="absolute top-0 left-0 w-1 h-full bg-emerald-600/30 group-hover:bg-emerald-600 transition-all"></div>
                            <div className="md:w-64 md:border-r border-slate-900 md:pr-10">
                                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-6 mb-6 text-center group-hover:bg-emerald-500/10 transition-all">
                                    <span className="text-emerald-500 text-[10px] font-black uppercase tracking-widest block mb-2">FECHA DE SESIÃƒâ€œN</span>
                                    <span className="text-white text-2xl font-black tracking-tighter">{new Date(m.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}</span>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-white/70 px-2">
                                        <span>Duración</span>
                                        <span className="text-white">60 MIN</span>
                                    </div>
                                    <div className="px-4 py-2 bg-slate-900 rounded-xl border border-slate-800 text-[9px] font-black text-emerald-500 uppercase tracking-widest text-center shadow-inner">{m.status}</div>
                                </div>
                            </div>
                            <div className="flex-1 space-y-10">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-3xl font-black text-white uppercase tracking-tighter leading-none">{m.title}</h3>
                                    <button className="p-2 text-slate-700 hover:text-white transition-colors"><MoreVertical size={20} /></button>
                                </div>
                                <div className="space-y-3">
                                    {m.agenda?.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-6 group/item">
                                            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-[10px] font-black text-white/70 border border-slate-800 group-hover/item:bg-emerald-600 group-hover/item:text-white group-hover/item:border-emerald-500 transition-all">{idx + 1}</div>
                                            <div className="flex-1 bg-slate-900/40 border border-slate-900 px-6 py-3 rounded-2xl flex justify-between items-center group-hover/item:border-slate-800 group-hover/item:bg-slate-900/60 transition-all">
                                                <div>
                                                    <span className="text-slate-300 text-[11px] font-bold uppercase tracking-tight group-hover/item:text-white transition-colors">{item.title}</span>
                                                    <span className="text-slate-600 text-[7px] font-black uppercase block tracking-widest mt-0.5">{item.responsible}</span>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-[9px] font-black text-slate-600 uppercase">{item.duration} MIN</span>
                                                    <div className={`w-2 h-2 rounded-full shadow-[0_0_8px] ${item.type === 'KPI' ? 'bg-cyan-500 shadow-cyan-500/50' : 'bg-emerald-500 shadow-emerald-500/50'}`}></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <AnimatePresence>
                {showAdd && (
                    <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-[500] flex items-center justify-center p-6">
                        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-slate-950 border border-slate-800 w-full max-w-2xl rounded-[4rem] p-16 shadow-[0_0_100px_rgba(16,185,129,0.1)] relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                            <h3 className="text-3xl font-black text-white uppercase mb-10 tracking-tighter relative z-10">Agenda Estratégica Mensual</h3>
                            <div className="space-y-8 relative z-10">
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-white/70 uppercase tracking-widest ml-1">Propósito de la Sesión</label>
                                    <input className="w-full bg-slate-900/50 border border-slate-800 px-8 py-5 rounded-[2rem] text-white outline-none font-bold focus:border-emerald-500/50 transition-all shadow-inner" placeholder="Ej: Tercera Sesión Ordinaria VP" value={newMeeting.title} onChange={e => setNewMeeting({ ...newMeeting, title: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-white/70 uppercase tracking-widest ml-1">Calendario</label>
                                    <input type="date" className="w-full bg-slate-900/50 border border-slate-800 px-8 py-5 rounded-[2rem] text-white outline-none font-bold focus:border-emerald-500/50 transition-all shadow-inner [color-scheme:dark]" value={newMeeting.date} onChange={e => setNewMeeting({ ...newMeeting, date: e.target.value })} />
                                </div>

                                <div className="pt-10 flex gap-6">
                                    <button onClick={() => setShowAdd(false)} className="flex-1 border-2 border-slate-800 text-white/70 py-5 rounded-[2rem] font-black text-[11px] uppercase tracking-widest hover:bg-slate-900 transition-all">Descartar</button>
                                    <button onClick={handleAddMeeting} className="flex-1 bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-[11px] uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-2xl shadow-emerald-900/40">Programar Sesión</button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
/**
 * Módulo de Estructura Organizacional (Jerarquía)
 * Control de Vicepresidencias e Instancias Nacionales
 */
const HierarchyModule = ({ user }: { user: any }) => {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [showAddVP, setShowAddVP] = useState(false);
    const [showAddComm, setShowAddComm] = useState(false);
    const [editingItem, setEditingItem] = useState<{ type: 'VP' | 'COMM', data: any } | null>(null);
    const [filter, setFilter] = useState('');
    const [expandedVPs, setExpandedVPs] = useState<string[]>([]);

    const [newVP, setNewVP] = useState({ name: '', president: '' });
    const [newComm, setNewComm] = useState({ name: '', type: COMISION_TYPE as string, vp_id: '', president: '' });

    const labels: Record<string, string> = {
        [COMISION_TYPE]: 'Comisiones',
        'COMITE': 'Comités',
        'CONSEJERIA': 'Consejerías'
    };

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            setError(null);
            const result = await db.getHierarchy(user);

            // NORMALIZACIÃƒâ€œN QUIRÃƒÅ¡RGICA (v18.11.09)
            // Aseguramos compatibilidad entre llaves plural/singular detectadas en auditoría
            const normalized = {
                ...result,
                commissions: result.commissions || (result as any).commission || [],
                commission: result.commissions || (result as any).commission || [],
                independents: result.independents || (result as any).independent || [],
                independent: result.independents || (result as any).independent || []
            };

            setData(normalized);
        } catch (err: any) {
            console.error("Hierarchy load error", err);
            setError(STRUCTURE_RESPONSIBLES_LOAD_ERROR);
        }
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleAddVP = async () => {
        if (!newVP.name) return;
        try {
            if (editingItem && editingItem.type === 'VP') {
                await db.updateVicepresidency(editingItem.data.id, { name: newVP.name, president_name: newVP.president });
            } else {
                await db.createVicepresidency(newVP.name, newVP.president);
            }
            setShowAddVP(false);
            setEditingItem(null);
            setNewVP({ name: '', president: '' });
            refresh();
        } catch (e) { showToast("Error al procesar VP"); }
    };

    const handleDeleteVP = async (id: string) => {
        alert("Eliminación de Vicepresidencias deshabilitada por política de seguridad.");
    };

    // Commission Actions
    const handleAddComm = async () => {
        if (!newComm.name) return;
        try {
            if (editingItem && editingItem.type === 'COMM') {
                await db.updateCommission(editingItem.data.id, {
                    name: newComm.name,
                    type: newComm.type as "COMISION" | "COMITE" | "CONSEJERIA",
                    vicepresidency_id: newComm.vp_id || null,
                    president_name: newComm.president
                });
            } else {
                await db.createCommission({
                    name: newComm.name,
                    type: newComm.type as "COMISION" | "COMITE" | "CONSEJERIA",
                    vicepresidency_id: newComm.vp_id || null,
                    president_name: newComm.president
                });
            }
            setShowAddComm(false);
            setEditingItem(null);
            setNewComm({ name: '', type: COMISION_TYPE, vp_id: '', president: '' });
            refresh();
        } catch (e) { showToast("Error al procesar instancia"); }
    };

    const handleDeleteComm = async (commission: any) => {
        const id = commission?.id;
        const typeLabel = getInstanceTypeSingularLabel(commission?.type || COMISION_TYPE);
        const nameLabel = commission?.name || 'esta instancia';
        const confirmationMessage = [
            `¿Eliminar ${typeLabel.toLowerCase()} "${nameLabel}"?`,
            '',
            'Esta acción solo debe ejecutarse si la instancia no tiene planeación, usuarios o información relacionada.'
        ].join('\n');

        if (!confirm(confirmationMessage)) return;
        try {
            await db.deleteCommission(id, { actor: user });
            showToast('Instancia eliminada correctamente.', 'success');
            refresh();
            return;
        } catch (e: any) {
            console.error("[DELETE_COMM_ERROR]", e);
            const rawMessage = String(e?.message || '');
            const message =
                rawMessage.includes('No tienes permiso')
                    ? 'No tienes permiso para eliminar esta instancia.'
                    : rawMessage.includes('planeaci')
                        ? 'No se puede eliminar porque tiene planeación, usuarios o información relacionada.'
                        : rawMessage.includes('Error del servidor') || rawMessage.includes('internal') ? rawMessage : 'No fue posible eliminar la instancia. Verifica permisos o contacta al administrador.';
            showToast(message, 'error');
            return;
        }
        if (!confirm("¿Eliminar esta instancia organizacional?")) return;
        try {
            return;
            refresh();
        } catch (e: any) {
            console.error("[DELETE_COMM_ERROR]", e);
            showToast(`Error al eliminar: ${e.message || 'Existen registros (usuarios, reportes) vinculados a esta instancia.'}`);
        }
    };

    const openEditVP = (vp: any) => {
        setEditingItem({ type: 'VP', data: vp });
        setNewVP({ name: vp.name, president: vp.president_name });
        setShowAddVP(true);
    };

    const openEditComm = (c: any) => {
        setEditingItem({ type: 'COMM', data: c });
        setNewComm({ name: c.name, type: c.type, vp_id: c.vicepresidency_id || '', president: c.president_name });
        setShowAddComm(true);
    };

    const openAddCommToVP = (vpId: string) => {
        setNewComm({ name: '', type: COMISION_TYPE, vp_id: vpId, president: '' });
        setShowAddComm(true);
    };

    const toggleVP = (id: string) => {
        setExpandedVPs(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
    };

    // Perfil Debug & God Mode
    useEffect(() => {

    }, [user]);

    // Tolerant Admin Check + God Mode for Initial setup
    const isSA = user?.role === 'SUPER_ADMIN';
    const canEdit = canEditModule(user, 'Estructura');
    const canDeleteStructure = (commissionId: string) => {
        const role = user?.role || '';
        if (role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR' || role === 'DIRECTOR_NACIONAL') return true;
        if (role === 'EDITOR' || role === 'COORDINADOR') {
            const scope = user?.org_scope || (role === 'COORDINADOR' ? 'COORDINATOR' : 'INSTANCE');
            if (scope === 'INSTANCE' && (user?.commission_id === commissionId || user?.instance_id === commissionId)) return true;
            if (scope === 'COORDINATOR') {
                const instIds = user?.accessible_instances || [];
                if (instIds.includes(commissionId)) return true;
            }
        }
        return false;
    };
    if (error) return (
        <div className="p-12 text-center space-y-4">
            <p className="text-red-400 font-bold uppercase tracking-widest">{error}</p>
            <button onClick={refresh} className="bg-slate-800 text-white px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-700">Reintentar</button>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
            <p className="text-emerald-500 font-black uppercase tracking-widest animate-pulse">Cargando Estructura...</p>
        </div>
    );

    const hasContent = (data.vicepresidencies?.length > 0) || (data.independents?.length > 0) || (data.independent?.length > 0);

    return (
        <div className="space-y-8 pb-12">
            <header className="flex flex-col gap-6">
                <div className="w-full">
                    <h1 className="text-[22px] md:text-3xl leading-[1.1] font-black text-white uppercase tracking-tighter w-full mb-6 text-center md:text-left">Estructura Organizacional</h1>
                    <div className="flex flex-col md:flex-row gap-4 w-full items-center">
                        <div className="relative w-full md:w-80 shrink-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70" size={16} />
                            <input
                                type="text"
                                placeholder="Buscar instancia o VP..."
                                className="bg-slate-900 border border-slate-800 rounded-2xl h-[48px] py-2 pl-10 pr-4 text-white text-[12px] font-medium placeholder:text-white/70 focus:outline-none focus:border-emerald-500/50 w-full"
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full justify-center md:justify-start items-center mt-6">
                        {canEdit && (
                            <>
                                <button
                                    onClick={() => { setEditingItem(null); setNewVP({ name: '', president: '' }); setShowAddVP(true); }}
                                    className="bg-slate-900 border border-slate-800 text-white px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:border-emerald-500/50 transition-all flex items-center gap-2"
                                >
                                    <Plus size={14} /> Nueva VP
                                </button>
                                <button
                                    onClick={() => { setEditingItem(null); setNewComm({ name: '', type: COMISION_TYPE, vp_id: '', president: '' }); setShowAddComm(true); }}
                                    className="bg-emerald-600 text-white px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-500 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                                >
                                    <Plus size={14} /> Nueva Instancia
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => {
                                refresh();
                                const btn = document.activeElement as HTMLButtonElement;
                                if (btn) {
                                    const originalText = btn.innerHTML;
                                    btn.innerHTML = '<span class="text-emerald-500 flex items-center gap-2"><div class="w-3 h-3 bg-emerald-500 rounded-full animate-ping"></div> Actualizado</span>';
                                    setTimeout(() => { btn.innerHTML = originalText; }, 2000);
                                }
                            }}
                            className="bg-slate-800 text-white/60 px-5 h-[44px] rounded-2xl hover:bg-slate-700 transition-all flex items-center justify-center gap-2 shrink-0 bg-opacity-80 backdrop-blur-sm"
                        >
                            <Database size={16} />
                            <span className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap">Sincronizar</span>
                        </button>
                    </div>
                </div>
            </header>

            {!hasContent ? (
                <div className="py-32 flex flex-col items-center justify-center text-center space-y-8 bg-slate-900/30 rounded-[4rem] border-2 border-dashed border-slate-800/50">
                    <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center text-white/20 animate-pulse">
                        <FolderOpen size={48} strokeWidth={1} />
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-2xl font-black text-white uppercase tracking-tighter">No hay documentos registrados</h3>
                        <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">El repositorio nacional de la instancia se encuentra vacío</p>
                    </div>
                    <button onClick={() => showToast('Opción habilitada en la sección de Memoria.')} className="bg-white text-slate-950 px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-2xl">
                        Subir Primer Documento
                    </button>
                </div>
            ) : (
                <>
                    {/* Secciones de Vicepresidencias */}
                    {data.vicepresidencies
                        .filter(vp =>
                            vp.name.toLowerCase().includes(filter.toLowerCase()) ||
                            vp.president_name?.toLowerCase().includes(filter.toLowerCase()) ||
                            data.commission?.some(c => c.vicepresidency_id === vp.id && c.name.toLowerCase().includes(filter.toLowerCase()))
                        )
                        .map((vp: any) => {
                            const isExpanded = expandedVPs.includes(vp.id) || filter.length > 0;
                            const comms = data.commission?.filter((c: any) => c.vicepresidency_id === vp.id) || [];
                            const grouped: Record<string, any[]> = {
                                [COMISION_TYPE]: comms.filter((c: any) => c.type === COMISION_TYPE),
                                'COMITE': comms.filter((c: any) => c.type === 'COMITE'),
                                'CONSEJERIA': comms.filter((c: any) => c.type === 'CONSEJERIA')
                            };

                            return (
                                <div key={vp.id} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group transition-all duration-500">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-emerald-500/10 transition-colors"></div>

                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center relative z-10 cursor-pointer gap-4 w-full" onClick={() => toggleVP(vp.id)}>
                                        <div className="flex items-start md:items-center gap-3 w-full md:w-auto flex-1">
                                            <div className={cn(
                                                "w-10 h-10 shrink-0 rounded-xl flex items-center justify-center transition-all bg-slate-800 mt-1 md:mt-0",
                                                isExpanded ? "rotate-90 text-emerald-500" : "text-white/70"
                                            )}>
                                                <ChevronRight size={20} />
                                            </div>

                                            <div className="flex flex-col gap-1 w-full md:w-auto flex-1 min-w-0">
                                                <div className="flex items-start md:items-center justify-between w-full gap-2">
                                                    <h2 className="text-[16px] md:text-2xl font-black text-white uppercase tracking-tighter leading-tight group-hover:text-emerald-400 transition-colors pt-0.5 md:pt-0 pr-2 break-words flex-1 min-w-0">{vp.name}</h2>
                                                    {/* Desktop Edit/Trash */}
                                                    {canEdit && (
                                                        <div className="hidden md:flex gap-1 shrink-0 ml-4" onClick={e => e.stopPropagation()}>
                                                            <button onClick={() => openEditVP(vp)} className="p-1.5 text-white/70 hover:text-emerald-500 transition-colors"><Edit size={16} /></button>

                                                        </div>
                                                    )}
                                                </div>

                                                {/* Mobile Badge + Actions */}
                                                <div className="flex md:hidden items-center justify-between mt-1">
                                                    <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-3 py-1 rounded-full border border-emerald-500/20 uppercase tracking-widest inline-block">
                                                        Vicepresidencia
                                                    </span>
                                                    {canEdit && (
                                                        <div className="flex gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                                                            <button onClick={() => openEditVP(vp)} className="p-2 text-white/70 hover:text-emerald-500 transition-colors bg-slate-950 border border-slate-800 rounded-lg"><Edit size={16} /></button>

                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Desktop Right Side Badge */}
                                        <div className="hidden md:flex items-center gap-3 shrink-0">
                                            <span className="bg-emerald-500/10 text-emerald-500 text-[9px] font-black px-3 py-1.5 rounded-full border border-emerald-500/20 uppercase tracking-widest whitespace-nowrap">Vicepresidencia</span>
                                            <div className="text-right">
                                                <p className="text-xl font-black text-emerald-500 leading-none mb-1">{getStructureResponsibleLabel(vp.president_name)}</p>
                                                <p className="text-[10px] text-white/70 font-bold uppercase tracking-widest">VP Nacional</p>
                                            </div>
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-8 space-y-8">
                                                    {/* Titular Mobile or Detailed */}
                                                    <div className="flex items-center gap-4 px-5 py-4 bg-slate-950/50 rounded-2xl border border-slate-800/50 relative z-10">
                                                        <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center font-black text-white text-xl shadow-lg shadow-emerald-900/40">
                                                            {vp.president_name?.[0] || 'V'}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-black text-white">{getStructureResponsibleLabel(vp.president_name)}</p>
                                                            <p className="text-[9px] text-white/70 font-black uppercase tracking-widest">Responsable de la Vicepresidencia Nacional</p>
                                                        </div>
                                                    </div>

                                                    {/* Grupos de Instancias */}
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 relative z-10">
                                                        {([COMISION_TYPE, 'COMITE', 'CONSEJERIA'] as const).map(type => {
                                                            const items = grouped[type];
                                                            if (items.length === 0 && !isSA) return null;

                                                            return (
                                                                <div key={type} className="space-y-4">
                                                                    <div className="flex items-center gap-3 px-2">
                                                                        <div className="h-1 w-6 bg-emerald-500 rounded-full"></div>
                                                                        <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{labels[type]}</span>
                                                                        <span className="bg-slate-800 text-white/70 text-[8px] font-black px-2 py-0.5 rounded-md border border-slate-700">{items.length}</span>
                                                                    </div>

                                                                    <div className="space-y-3">
                                                                        {items.map((c: any) => (
                                                                            <div key={c.id} className="group/item flex flex-col px-[20px] py-[16px] bg-slate-950/40 rounded-2xl border border-slate-800 hover:border-emerald-500/30 hover:bg-slate-800/20 transition-all">
                                                                                <div className="flex justify-between items-start mb-2">
                                                                                    <p className="text-sm font-black text-slate-200 group-hover/item:text-emerald-400 transition-colors leading-tight line-clamp-2">{c.name}</p>
                                                                                    <div className="flex items-center gap-1.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                                                                        {canEdit && (
                                                                                            <>
                                                                                                <button onClick={() => openEditComm(c)} className="p-1 text-white/70 hover:text-emerald-500"><Edit size={12} /></button>
                                                                                                {canDeleteStructure(c.id) && (
                                                                                                    <button onClick={() => handleDeleteComm(c)} className="p-1 text-white/70 hover:text-red-500"><Trash2 size={12} /></button>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex justify-between items-end">
                                                                                    <div>
                                                                                        <p className="text-[10px] text-white/70 font-black uppercase tracking-widest">Presidente:</p>
                                                                                        <p className="text-xs text-slate-300 font-bold">{getStructureResponsibleLabel(c.president_name, 'N/A')}</p>
                                                                                    </div>
                                                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                                                </div>
                                                                            </div>
                                                                        ))}

                                                                        {canEdit && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setNewComm({ name: '', type: type, vp_id: vp.id, president: '' });
                                                                                    setShowAddComm(true);
                                                                                }}
                                                                                className="w-full border-2 border-dashed border-slate-800 rounded-2xl py-3 flex items-center justify-center gap-2 text-[8px] font-black text-slate-600 uppercase tracking-widest hover:border-emerald-500/20 hover:text-emerald-500 transition-all bg-slate-900/20"
                                                                            >
                                                                                <Plus size={12} /> Agregar {labels[type]}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}

                    {/* Comisiones Independientes */}
                    {(data.independents?.length > 0 || data.independent?.length > 0) && (
                        <section className="pt-12 border-t border-slate-800 space-y-10">
                            <div className="outline-none">
                                <h2 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-4">
                                    <div className="w-8 h-1 bg-emerald-500 rounded-full"></div>
                                    Instancias Independientes
                                </h2>
                                <p className="text-white/70 text-[10px] font-bold uppercase tracking-[0.3em] mt-1 ml-12">Autónomas (Sin vinculación a VP específica)</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {([COMISION_TYPE, 'COMITE', 'CONSEJERIA'] as const).map(type => {
                                    const allIndeps = data.independents || data.independent || [];
                                    const items = allIndeps.filter((c: any) => c.type === type && (
                                        c.name.toLowerCase().includes(filter.toLowerCase()) ||
                                        c.president_name?.toLowerCase().includes(filter.toLowerCase())
                                    ));
                                    if (items.length === 0) return null;

                                    return (
                                        <div key={type} className="space-y-4">
                                            <div className="flex items-center gap-3 px-2">
                                                <div className="h-1 w-4 bg-slate-700 rounded-full"></div>
                                                <span className="text-[9px] font-black text-white/70 uppercase tracking-widest">{labels[type]}</span>
                                                <span className="bg-slate-900 text-slate-600 text-[8px] font-black px-2 py-0.5 rounded-md border border-slate-800">{items.length}</span>
                                            </div>
                                            <div className="space-y-3">
                                                {items.map((c: any) => (
                                                    <div key={c.id} className="bg-slate-900 border border-slate-800 p-5 rounded-2xl hover:border-emerald-500/30 transition-all group relative overflow-hidden">
                                                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full -mr-8 -mt-8 blur-lg"></div>
                                                        <div className="flex justify-between items-start mb-3 relative z-10">
                                                            <div className="w-8 h-8 bg-slate-950 rounded-lg border border-slate-800 flex items-center justify-center text-slate-600 group-hover:text-emerald-500 group-hover:border-emerald-500/20 transition-all">
                                                                <Building2 size={16} />
                                                            </div>
                                                            {canEdit && (
                                                                <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button onClick={() => openEditComm(c)} className="p-1 text-white/70 hover:text-emerald-500 transition-colors"><Edit size={12} /></button>
                                                                    {canDeleteStructure(c.id) && (
                                                                        <button onClick={() => handleDeleteComm(c)} className="p-1 text-white/70 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <h3 className="text-[11px] font-black text-white uppercase mb-1 leading-tight relative z-10">{c.name}</h3>
                                                        <p className="text-white/70 text-[8px] font-black uppercase tracking-widest relative z-10">
                                                            TITULAR: <span className="text-slate-300 ml-1">{getStructureResponsibleLabel(c.president_name)}</span>
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                </>
            )}

            {/* Modales Provisorios (Simple implementation) */}
            <AnimatePresence>
                {showAddVP && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-900 border border-slate-800 p-10 rounded-[2.5rem] w-full max-w-md shadow-2xl">
                            <h2 className="text-2xl font-black text-white uppercase mb-8">{editingItem ? 'Editar' : 'Nueva'} Vicepresidencia</h2>
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Nombre de la VP</label>
                                    <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newVP.name} onChange={e => setNewVP({ ...newVP, name: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Titular (Presidente de VP)</label>
                                    <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newVP.president} onChange={e => setNewVP({ ...newVP, president: e.target.value })} />
                                </div>
                                <div className="flex gap-4 pt-4">
                                    <button onClick={() => { setShowAddVP(false); setEditingItem(null); }} className="flex-1 bg-slate-800 text-white/60 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest">Cancelar</button>
                                    <button onClick={handleAddVP} className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest">{editingItem ? 'Guardar' : 'Crear'} VP</button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {showAddComm && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-900 border border-slate-800 p-10 rounded-[2.5rem] w-full max-w-lg shadow-2xl">
                            <h2 className="text-2xl font-black text-white uppercase mb-8">{editingItem ? 'Editar' : 'Nueva'} Instancia</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Nombre</label>
                                    <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newComm.name} onChange={e => setNewComm({ ...newComm, name: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Tipo</label>
                                    <select className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500 appearance-none" value={newComm.type} onChange={e => setNewComm({ ...newComm, type: e.target.value })}>
                                        <option value={COMISION_TYPE}>Comisión</option>
                                        <option value="COMITE">Comité</option>
                                        <option value="CONSEJERIA">Consejería</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Depende de VP</label>
                                    <select className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500 appearance-none" value={newComm.vp_id} onChange={e => setNewComm({ ...newComm, vp_id: e.target.value })}>
                                        <option value="">INDEPENDIENTE</option>
                                        {data.vicepresidencies.map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-[10px] font-black text-white/70 uppercase tracking-widest">Titular (Presidente de Instancia)</label>
                                    <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newComm.president} onChange={e => setNewComm({ ...newComm, president: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-8">
                                <button onClick={() => { setShowAddComm(false); setEditingItem(null); }} className="flex-1 bg-slate-800 text-white/60 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest">Cancelar</button>
                                <button onClick={handleAddComm} className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest">{editingItem ? 'Guardar' : 'Crear'} Instancia</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div >
    );
};

const TalentModule = ({ user }: { user?: any }) => {
    const [talents, setTalents] = useState<db.TalentRecord[]>([]);
    const [selectedTalent, setSelectedTalent] = useState<db.TalentRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTalent, setNewTalent] = useState({ name: '', email: '', other_expertise: '', education: '', experience: '', interests: '' });
    const [isCreating, setIsCreating] = useState(false);
    const [deletingTalentIds, setDeletingTalentIds] = useState<Set<string>>(new Set());
    const createIdempotencyKeyRef = useRef<string | null>(null);
    const deleteIdempotencyKeysRef = useRef<Map<string, string>>(new Map());

    const loadTalents = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setLoadError(null);
        const timeoutPromise = new Promise<db.TalentRecord[]>((_, reject) =>
            setTimeout(() => reject(new db.GovScopedReadError('Talent directory timeout')), 8000)
        );
        try {
            const data = await Promise.race([db.getTalent(), timeoutPromise]);
            setTalents(data);
        } catch (err: unknown) {
            console.error('Error loading talent:', err);
            setLoadError(db.getScopedReadErrorMessage(err));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => { loadTalents(); }, [loadTalents]);

    const handleCreate = async () => {
        if (isCreating) return;
        if (!canEditModule(user, 'Talento')) { showToast('No tienes permisos para crear talento', 'error'); return; }
        if (!newTalent.name.trim()) { showToast('El nombre es obligatorio'); return; }
        setIsCreating(true);
        try {
            const idempotencyKey = createIdempotencyKeyRef.current || db.createMutationIdempotencyKey();
            createIdempotencyKeyRef.current = idempotencyKey;
            await db.addTalent(newTalent, { idempotencyKey });
            createIdempotencyKeyRef.current = null;
            setShowCreateModal(false);
            setNewTalent({ name: '', email: '', other_expertise: '', education: '', experience: '', interests: '' });
            await loadTalents();
        } catch (err: unknown) {
            showToast(db.getMutationErrorMessage(err), 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!canEditModule(user, 'Talento')) { showToast('No tienes permisos para eliminar talento', 'error'); return; }
        if (deletingTalentIds.has(id)) return;
        if (!confirm('¿Eliminar este perfil de talento?')) return;
        setDeletingTalentIds((current) => new Set(current).add(id));
        try {
            const idempotencyKey = deleteIdempotencyKeysRef.current.get(id) || db.createMutationIdempotencyKey();
            deleteIdempotencyKeysRef.current.set(id, idempotencyKey);
            await db.removeTalent(id, { idempotencyKey });
            deleteIdempotencyKeysRef.current.delete(id);
            await loadTalents();
        } catch (err: unknown) {
            showToast(db.getMutationErrorMessage(err), 'error');
        } finally {
            setDeletingTalentIds((current) => {
                const next = new Set(current);
                next.delete(id);
                return next;
            });
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
            <p className="text-emerald-500 font-black uppercase tracking-widest animate-pulse">Consultando Archivo de Talento...</p>
        </div>
    );

    if (loadError && talents.length === 0) return (
        <div className="min-h-[420px] flex items-center justify-center p-6">
            <div className="max-w-xl w-full p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
                <AlertTriangle className="mx-auto text-red-400" size={36} />
                <h2 className="text-red-200 font-black uppercase tracking-widest">No se pudo cargar Talento</h2>
                <p className="text-white/70 text-sm">{loadError}</p>
                <button onClick={() => loadTalents()} className="px-5 py-2 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-widest">Reintentar</button>
            </div>
        </div>
    );

    return (
        <div className="space-y-12">
            {loadError && (
                <div className="p-4 rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 text-sm font-bold">
                    {loadError} Se conservan los últimos perfiles visibles.
                </div>
            )}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white uppercase tracking-tight mb-2">Talento y Perfiles</h1>
                    <p className="text-white/70 text-[10px] font-bold uppercase tracking-[0.3em]">Directorio Estratégico Nacional</p>
                </div>
                {canEditModule(user, 'Talento') && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-2xl font-bold text-[11px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                    >
                        <Plus size={16} /> Nuevo Perfil Estratégico
                    </button>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {talents.map((t) => (
                    <div key={t.id} onClick={() => setSelectedTalent(t)} className="bg-slate-900 border border-slate-800 p-8 rounded-[2rem] hover:border-emerald-500/40 transition-all flex flex-col justify-between h-56 cursor-pointer group relative overflow-hidden">
                        <div className="absolute inset-0 bg-emerald-500/0 group-hover:bg-emerald-500/5 transition-colors"></div>
                        <div className="flex justify-between items-start relative z-10">
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                <Users size={24} />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="bg-slate-950 text-white/60 text-[8px] uppercase font-bold px-3 py-1.5 rounded-full border border-slate-800 tracking-widest group-hover:border-emerald-500/30 transition-all">Ver Ficha</span>
                                {canEditModule(user, 'Talento') && (
                                    <button
                                        disabled={deletingTalentIds.has(t.id)}
                                        onClick={(e) => handleDelete(t.id, e)}
                                        className="p-1.5 text-slate-600 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="relative z-10">
                            <h3 className="text-white font-bold uppercase text-xl item-name font-black tracking-tight group-hover:text-emerald-400 transition-colors">{t.name}</h3>
                            <p className="text-xs font-bold uppercase tracking-widest mt-1 item-name font-black tracking-tight text-white/70">{t.role}</p>
                            <div className="flex gap-2 mt-4">
                                <div className="h-1 flex-1 bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500" style={{ width: `${t.experience_level || 80}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {talents.length === 0 && (
                    <div className="md:col-span-2 lg:col-span-3 py-20 text-center text-white/40 font-black uppercase tracking-widest text-xs">
                        El directorio no contiene perfiles para tu alcance.
                    </div>
                )}
            </div>

            {/* Modal Crear Talento */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[200] flex items-center justify-center p-6">
                    <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-slate-950 border border-slate-800 w-full max-w-lg rounded-[2.5rem] p-10 relative">
                        <button onClick={() => setShowCreateModal(false)} className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={24} /></button>
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-8">Nuevo Talento</h2>
                        <div className="space-y-4">
                            <div><label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Nombre *</label>
                                <input className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-emerald-500/50" value={newTalent.name} onChange={e => setNewTalent({ ...newTalent, name: e.target.value })} placeholder="Nombre completo" /></div>
                            <div><label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Email</label>
                                <input className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-emerald-500/50" value={newTalent.email} onChange={e => setNewTalent({ ...newTalent, email: e.target.value })} placeholder="correo@ejemplo.com" /></div>
                            <div><label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Cargo / Especialidad</label>
                                <input className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-emerald-500/50" value={newTalent.other_expertise} onChange={e => setNewTalent({ ...newTalent, other_expertise: e.target.value })} placeholder="Presidente, VP, Director..." /></div>
                            <div><label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Formación Académica</label>
                                <textarea className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-emerald-500/50 resize-none" rows={2} value={newTalent.education} onChange={e => setNewTalent({ ...newTalent, education: e.target.value })} /></div>
                        </div>
                        <div className="flex gap-4 mt-8">
                            <button disabled={isCreating} onClick={() => setShowCreateModal(false)} className="flex-1 bg-slate-800 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all disabled:opacity-40">Cancelar</button>
                            <button disabled={isCreating} onClick={handleCreate} className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed">{isCreating ? 'Creando...' : 'Crear Perfil'}</button>
                        </div>
                    </motion.div>
                </div>
            )}

            <AnimatePresence>
                {selectedTalent && <ExpertiseSheet talent={selectedTalent} onClose={() => setSelectedTalent(null)} onRefresh={loadTalents} user={user} />}
            </AnimatePresence>
        </div>
    );
};

/**
 * Tablero Nacional COPARMEX Ã¢â‚¬â€ Gobernanza Articulada
 */
const NationalDashboardPanel = ({ hierarchy, settings, dashboardData, livePlanTotals, selectedMonth, onSelectVP }: any) => {
    const [selectedVPs, setSelectedVPs] = useState<string[]>([]);
    const vpData = hierarchy?.vicepresidencies || [];

    const toggleVP = (id: string) => {
        if (id === 'ALL') {
            setSelectedVPs(selectedVPs.length === vpData.length ? [] : vpData.map((v: any) => v.id));
            return;
        }
        setSelectedVPs(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
    };

    const getVPScore = (vp: any): number | null => {
        const instances = [
            ...(vp.comisiones || []),
            ...(vp.comites || []),
            ...(vp.consejerias || [])
        ].map(inst => {
            const metrics = dashboardData?.find((d: any) => d.commission_id === inst.id);
            const livePlan = livePlanTotals?.find((p: any) => p.commission_id === inst.id);
            const planGoal = livePlan?.total_goal ?? metrics?.total_meta_plan ?? metrics?.total_goal ?? 0;
            const planProgressValue = livePlan?.total_progress ?? metrics?.plan_total_progress ?? metrics?.total_progress ?? metrics?.plan_progress ?? metrics?.pct_cumplimiento ?? 0;
            const kpiGoal = metrics?.total_meta_kpi || (metrics?.total_meta || 0) || (metrics?.meta_anual || 0) || 0;
            const kpiReal = metrics?.total_real || metrics?.real_anual || metrics?.kpi_real || metrics?.kpi_progress || 0;
            const hasPlan = planGoal > 0;
            const hasKpis = kpiGoal > 0 && (kpiReal > 0 || parseNumberSafe(metrics?.kpi_count || metrics?.total_indicators, 0) > 0);
            const kpiCompliance = hasKpis ? parseNumberSafe(metrics?.kpi_compliance, 0) : null;

            return {
                ...inst,
                planProgress: hasPlan ? Math.min(100, Math.round((planProgressValue / planGoal) * 100)) : 0,
                kpiCompliance,
                planEligible: hasPlan,
                kpiEligible: hasKpis,
                hasActivePlan: hasPlan,
                hasActiveKpis: hasKpis
            };
        });

        if (instances.length === 0) return null;

        // v18.10.19: Alineación con Lógica Centralizada (calculateVPPerformance)
        const perf = calculateVPPerformance(instances, buildVpPerformanceSettings(instances, settings));
        return perf.global;
    };

    const activeVPs = selectedVPs.length > 0 ? vpData.filter((v: any) => selectedVPs.includes(v.id)) : vpData;

    // v8.8.0 PLATINUM (RESTORE): Ponderación dinámica prorrateada de VPs
    const integralScore = useMemo(() => {
        const applicableVPs = activeVPs
            .map((v: any) => ({ vp: v, score: getVPScore(v) }))
            .filter(({ score }) => typeof score === 'number' && Number.isFinite(score));
        if (applicableVPs.length === 0) return null;

        const totalSpecificWeight = applicableVPs.reduce((sum: number, { vp }: any) => sum + (vp.peso || 10), 0);
        if (totalSpecificWeight <= 0) return null;

        const weightedSum = applicableVPs.reduce((sum: number, { vp, score }: any) => {
            const p = (vp.peso || 10) / totalSpecificWeight;
            return sum + ((score as number) * p);
        }, 0);
        return Math.round(weightedSum);
    }, [activeVPs, dashboardData, livePlanTotals, settings]);

    // Ã°Å¸Å¡Â§ ESTABLE Ã¢â‚¬â€ NO MODIFICAR SIN VALIDACIÃƒâ€œN VISUAL COMPLETA
    // Motor canónico: getComplianceVisualState() en shared-logic.tsx
    const getSemaphoreColor = (val: number) => getComplianceVisualState(val, settings).textClass;
    const getSemaphoreBg   = (val: number) => getComplianceVisualState(val, settings).bgClass;

    return (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
            {/* Hero Nacional */}
            <div className="bg-slate-950 border border-slate-800 p-4 rounded-2xl relative overflow-hidden group shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-32 -mt-32 blur-[80px]"></div>
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-indigo-700 p-0.5 shadow-lg">
                            <div className="w-full h-full bg-slate-950 rounded-[0.7rem] flex items-center justify-center text-emerald-500">
                                <ShieldCheck size={16} strokeWidth={2} />
                            </div>
                        </div>
                        <div>
                            <p className="text-emerald-500 text-[9px] font-black uppercase tracking-[0.2em] mb-0.5 leading-none">Presidencia Nacional</p>
                            <h1 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Tablero Integral 2026</h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 bg-slate-900/40 p-2 px-5 rounded-xl border border-slate-800/50 backdrop-blur-md">
                        <div className="text-right">
                            <h3 className={`text-4xl font-black tracking-tighter leading-none ${typeof integralScore === 'number' && Number.isFinite(integralScore) ? getSemaphoreColor(integralScore) : 'text-slate-500'}`}>
                                {typeof integralScore === 'number' && Number.isFinite(integralScore) ? `${integralScore}%` : 'N/A'}
                            </h3>
                        </div>
                        <div className="flex gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${typeof integralScore === 'number' && Number.isFinite(integralScore) && integralScore >= (settings?.threshold_green || 90) ? 'bg-emerald-500 shadow-emerald-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${typeof integralScore === 'number' && Number.isFinite(integralScore) && integralScore >= (settings?.threshold_amber || 75) && integralScore < (settings?.threshold_green || 90) ? 'bg-amber-500 shadow-amber-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${typeof integralScore === 'number' && Number.isFinite(integralScore) && integralScore < (settings?.threshold_amber || 75) ? 'bg-red-500 shadow-red-500/50' : 'bg-slate-800'}`}></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filtros Nacionales */}
            <div className="bg-slate-900/40 border border-slate-800 p-2 rounded-xl flex items-center gap-2 overflow-x-auto scrollbar-hide">
                <button
                    onClick={() => toggleVP('ALL')}
                    className={cn(
                        "px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all",
                        selectedVPs.length === vpData.length || selectedVPs.length === 0 ? "bg-emerald-600 text-white" : "bg-slate-800 text-white/40 hover:text-white"
                    )}
                >
                    {selectedVPs.length === vpData.length || selectedVPs.length === 0 ? 'TODAS LAS VICEPRESIDENCIAS' : 'Seleccionar TODAS'}
                </button>
                <div className="w-px h-4 bg-slate-800 mx-2"></div>
                {vpData.map((v: any) => (
                    <button
                        key={v.id}
                        onClick={() => toggleVP(v.id)}
                        className={cn(
                            "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap",
                            selectedVPs.includes(v.id) ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30" : "bg-slate-800/40 text-white/40 border border-transparent hover:border-slate-700"
                        )}
                    >
                        {v.name}
                    </button>
                ))}
            </div>

            {/* Grid de Vicepresidencias */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                {activeVPs.map((v: any) => {
                    const score = getVPScore(v);
                    const isApplicableScore = typeof score === 'number' && Number.isFinite(score);
                    return (
                        <div
                            key={v.id}
                            onClick={() => onSelectVP && onSelectVP(v.id)}
                            className="bg-slate-900/40 border border-slate-800 p-4 rounded-2xl group relative overflow-hidden transition-all hover:scale-[1.02] cursor-pointer hover:border-emerald-500/50"
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className="max-w-[75%]">
                                    <p className="text-emerald-500 text-[9px] font-black uppercase tracking-widest mb-1">Vicepresidencia</p>
                                    <h4 className="text-sm font-black text-white uppercase tracking-tight group-hover:text-emerald-400 transition-colors line-clamp-1">{v.name}</h4>
                                </div>
                                <div className={`p-1.5 rounded-lg bg-slate-950/80 ${isApplicableScore ? getSemaphoreColor(score as number) : 'text-slate-500'} shadow-inner`}>
                                    <Activity size={12} />
                                </div>
                            </div>

                            <div className="flex justify-between items-end">
                                <div>
                                    <p className="text-[10px] font-black text-white/30 uppercase tracking-widest">Cumplimiento</p>
                                    <span className={`text-xl font-black ${isApplicableScore ? 'text-white' : 'text-slate-500'}`}>{isApplicableScore ? `${score}%` : 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1 items-end">
                                    <div className="h-1 w-24 bg-slate-950 rounded-full overflow-hidden">
                                        <div className={`h-full ${isApplicableScore ? getSemaphoreBg(score as number) : 'bg-slate-700'}`} style={{ width: `${isApplicableScore ? score : 0}%` }}></div>
                                    </div>
                                    <span className="text-[9px] font-bold text-white/20 uppercase tracking-widest">Integral 2026</span>
                                </div>
                            </div>

                            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-slate-800 group-hover:bg-emerald-500/50 transition-colors"></div>
                        </div>
                    );
                })}
            </div>
        </motion.div>
    );
};

/**
 * Tablero consolidado de Vicepresidencias.
 */
const VPDashboardPanel = ({ vpId, hierarchy, settings, selectedMonth, dashboardData, livePlanTotals, onSelectInstance, user }: any) => {
    const { canApproveAsVP, isAdmin, isSuperAdmin } = useRole(user);
    const isUserAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR';
    const [plans, setPlans] = useState<any[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(false);

    const handleApprovePlan = async (commissionId: string) => {
        try {
            const plan = plans.find(p => p.commission_id === commissionId);
            if (!plan) return;
            await db.approveWorkPlan(plan.id, user.id);
            setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: 'APROBADO' } : p));
        } catch (err) {
            console.error("Error approving plan:", err);
        }
    };

    useEffect(() => {
        const fetchPlans = async () => {
            if (!vpId) return;
            setLoadingPlans(true);
            try {
                // Obtenemos los IDs de las instancias de esta VP
                const vp = hierarchy?.vicepresidencie?.find((v: any) => v.id === vpId);
                if (!vp) return;
                const instanceIds = [
                    ...(vp.comisiones || []).map((c: any) => c.id),
                    ...(vp.comites || []).map((c: any) => c.id),
                    ...(vp.consejerias || []).map((c: any) => c.id)
                ];

                const scopedPlans = await db.getWorkPlans({ role: 'VICE_PRESIDENT', vicepresidency_id: vpId, org_scope: 'VP' } as any);
                const processedPlans = (scopedPlans || []).filter((plan: any) =>
                    instanceIds.includes(plan.commission_id) && Number(plan.year) === 2026
                );

                setPlans(processedPlans);
            } catch (err) {
                console.error("Error fetching VP plans:", err);
            } finally {
                setLoadingPlans(false);
            }
        };
        fetchPlans();
    }, [vpId, hierarchy]);

    const monthIdx = MONTHS.indexOf(selectedMonth);
    const vp = hierarchy?.vicepresidencie?.find((v: any) => v.id === vpId);

    if (!vp) return (
        <div className="p-20 text-center bg-slate-950/50 rounded-[3rem] border border-slate-800 border-dashed">
            <AlertTriangle className="mx-auto text-amber-500/20 mb-4" size={48} />
            <p className="text-white/20 font-black uppercase tracking-[0.3em]">Seleccione una Vicepresidencia para visualizar el Tablero Consolidado</p>
        </div>
    );

    // v18.9.24: Estado local para KPIs en vivo (Evita el 0%)
    const [liveKpiResults, setLiveKpiResults] = useState<Record<string, { compliance: number, eligible: boolean, totalMeta: number, totalReal: number }>>({});

    const vpInstances = useMemo(() => {
        if (!vp) return [];
        return [
            ...(vp.comisiones || []).map((c: any) => ({ ...c, type: COMISION_TYPE })),
            ...(vp.comites || []).map((c: any) => ({ ...c, type: 'COMITE' })),
            ...(vp.consejerias || []).map((c: any) => ({ ...c, type: 'CONSEJERIA' }))
        ];
    }, [vp]);

    useEffect(() => {
        const fetchLiveKpis = async () => {
            if (!vpId || !vpInstances.length) return;
            try {
                const ids = vpInstances.map((i: any) => i.id);
                // resolveIndicatorsForInstance Logic (v18.9.24)
                const indicators = await db.getIndicators(ids);
                const results: Record<string, { compliance: number, eligible: boolean, totalMeta: number, totalReal: number }> = {};
                const mIdx = MONTHS.indexOf(selectedMonth);

                ids.forEach(id => {
                    const instInds = indicators.filter((i: any) => (i.instance_id || i.commission_id) === id);
                    const { cumplimiento, totalMeta, totalReal } = calculateProratedCompliance(instInds, getElapsedCompletedMonths(mIdx), false, true);
                    results[id] = {
                        compliance: cumplimiento || 0,
                        eligible: cumplimiento !== null,
                        totalMeta: totalMeta || 0,
                        totalReal: totalReal || 0
                    };
                });


                setLiveKpiResults(results);
            } catch (err) {
                console.error("[FETCH_LIVE_KPIS_ERROR]", err);
            }
        };
        fetchLiveKpis();
    }, [vpId, selectedMonth, vpInstances]);

    const instancesWithData = useMemo(() => {
        return vpInstances.map(inst => {
            const data = dashboardData?.find((d: any) => d.commission_id === inst.id);
            const liveKpi = liveKpiResults[inst.id];

            // Integración de Totales en Vivo (v18.9.28 GOLD)
            const livePlan = livePlanTotals?.find((p: any) => p.commission_id === inst.id);
            const livePlanDbObj = plans?.find((p: any) => p.commission_id === inst.id);
            const planGoal = livePlan?.total_goal || 0;
            const planProgressRaw = livePlan?.total_progress || 0;
            const planPct = planGoal > 0 ? Math.round((planProgressRaw / planGoal) * 100) : 0;
            const planStatus = livePlanDbObj?.status || livePlan?.status || 'SIN_PLAN';

            // Elegibilidad ejecutiva: operación real, independiente del workflow de aprobación.
            const planEligible = planGoal > 0;

            // KPI es elegible si tiene indicadores configurados y meta aplicable.
            const kpiMeta = liveKpi ? liveKpi.totalMeta : ((data?.total_meta || 0) || (data?.meta_anual || 0));
            const kpiReal = liveKpi ? liveKpi.totalReal : ((data?.total_real || 0) || (data?.real_anual || 0));
            const kpiCount = parseNumberSafe(data?.kpi_count || data?.total_indicators, 0);
            const fallbackCompliance = parseNumberSafe(data?.total_compliance || data?.kpi_compliance, 0);
            const kpiEligible = liveKpi
                ? (liveKpi.eligible && kpiMeta > 0)
                : (kpiMeta > 0 && (kpiCount > 0 || kpiReal > 0));
            const kpiCompliance = liveKpi ? liveKpi.compliance : (kpiEligible ? fallbackCompliance : null);

            return {
                ...inst,
                planGoal,
                planProgress: planPct, // Se usa el porcentaje calculado del plan vivo
                planEligible,
                kpiCompliance,
                kpiEligible,
                hasActivePlan: planEligible, // Para calculateVPPerformance
                hasActiveKpis: kpiEligible, // Para calculateVPPerformance
                hasData: true,
                planProgressValue: planProgressRaw,
                planStatus
            };
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [vpInstances, dashboardData, plans, livePlanTotals, liveKpiResults]);


    // v18.9.28: Uso de Lógica Centralizada de shared-logic para evitar discrepancias
    const vpPerformance = useMemo(() => {
        return calculateVPPerformance(instancesWithData, buildVpPerformanceSettings(instancesWithData, settings));
    }, [instancesWithData, settings]);

    const weights = {
        [COMISION_TYPE]: settings?.weight_comisiones || 50,
        COMITE: settings?.weight_comites || 30,
        CONSEJERIA: settings?.weight_consejerias || 20
    };

    const finalPlanProgress = vpPerformance.plan;
    const finalKpiCompliance = vpPerformance.kpi;
    const totalScore = vpPerformance.global;

    // TRACE: VP_PERFORMANCE_MODEL_V18.9.8_STABLE_IDENTIFIED


    // Ã°Å¸Å¡Â§ ESTABLE Ã¢â‚¬â€ NO MODIFICAR SIN VALIDACIÃƒâ€œN VISUAL COMPLETA
    // Motor canónico: getComplianceVisualState() en shared-logic.tsx
    const getSemaphoreColor = (val: number) => getComplianceVisualState(val, settings).textClass;
    const getSemaphoreBg   = (val: number) => getComplianceVisualState(val, settings).bgClass;

    return (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
            <div className="bg-slate-900 border-2 border-emerald-500/50 p-4 rounded-2xl relative overflow-hidden group shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-[80px]"></div>
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 p-0.5 shadow-lg">
                            <div className="w-full h-full bg-slate-950 rounded-[0.7rem] flex items-center justify-center text-emerald-500">
                                <Crown size={18} strokeWidth={2} />
                            </div>
                        </div>
                        <div>
                            <p className="text-emerald-500 text-[8px] font-black uppercase tracking-[0.2em] mb-0.5 leading-none">Vicepresidencia Estratégica</p>
                            <h1 className="text-xl font-black text-white tracking-tighter uppercase leading-none">{vp.name}</h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 bg-slate-900/40 p-2 px-5 rounded-xl border border-slate-800/50 backdrop-blur-md">
                        <div className="text-right">
                            <h3 className={`text-3xl font-black tracking-tighter leading-none ${getSemaphoreColor(totalScore)}`}>
                                {totalScore}%
                            </h3>
                        </div>
                        <div className="flex gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${totalScore >= (settings?.threshold_green || 90) ? 'bg-emerald-500 shadow-emerald-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${totalScore >= (settings?.threshold_amber || 75) && totalScore < (settings?.threshold_green || 90) ? 'bg-amber-500 shadow-amber-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${totalScore < (settings?.threshold_amber || 75) ? 'bg-red-500 shadow-red-500/50' : 'bg-slate-800'}`}></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-2xl group">
                    <div className="flex justify-between items-center mb-2">
                        <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500 border border-blue-500/20"><Briefcase size={14} /></div>
                        <div className="text-right">
                            <span className="text-lg font-black text-white">{finalPlanProgress}%</span>
                            <p className="text-[7px] font-black uppercase text-white/30 tracking-widest">Gestión</p>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] transition-all duration-1000" style={{ width: `${finalPlanProgress}%` }}></div>
                    </div>
                </div>

                <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-2xl group">
                    <div className="flex justify-between items-center mb-3">
                        <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500 border border-indigo-500/20"><TrendingUp size={16} /></div>
                        <div className="text-right">
                            <span className="text-xl font-black text-white">{finalKpiCompliance}%</span>
                            <p className="text-[8px] font-black uppercase text-white/30 tracking-widest">Impacto</p>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-1000" style={{ width: `${finalKpiCompliance}%` }}></div>
                    </div>
                </div>
            </div>

            {/* RESUMEN DE PONDERACIÃƒâ€œN (Solo visible para Admin) Ã¢â‚¬â€œ BUG 4 FIX */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR') && (
                <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-2xl">
                    <h3 className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-4 px-2">Resumen por Categoría</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                            { label: 'Comisiones', val: settings?.weight_comisiones || 50, icon: <Building2 />, color: 'text-emerald-500', type: COMISION_TYPE },
                            { label: 'COMITÃ‰S', val: settings?.weight_comites || 30, icon: <Users />, color: 'text-blue-500', type: 'COMITE' },
                            { label: 'CONSEJERÍAS', val: settings?.weight_consejerias || 20, icon: <Target />, color: 'text-amber-500', type: 'CONSEJERIA' }
                        ].filter(cat => instancesWithData.some(i => i.type === cat.type))
                            .map((cat, i) => {
                                const normType = normalizeVPType(cat.type);
                                const normalizedWeight = vpPerformance.settings?.activeBaseTotal > 0
                                    ? Math.round((vpPerformance.settings.typeBaseWeights[normType] / vpPerformance.settings.activeBaseTotal) * 100)
                                    : cat.val;

                                return (
                                    <div key={i} className="bg-slate-950/50 p-6 rounded-2xl border border-white/5 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={cn("p-3 rounded-xl bg-white/5", cat.color)}>{cat.icon}</div>
                                            <div>
                                                <p className="text-[10px] font-black text-white uppercase tracking-widest">{cat.label}</p>
                                                <p className="text-[8px] text-white/30 uppercase font-bold tracking-widest">Peso Relativo</p>
                                            </div>
                                        </div>
                                        <span className="text-2xl font-black text-white">{normalizedWeight}%</span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}



            <div className="space-y-4">
                <div className="flex justify-between items-end px-2">
                    <h3 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em]">Instancias Operativas</h3>
                    <p className="text-[8px] text-white/20 font-bold uppercase tracking-widest">{instancesWithData.length} Organismos</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {instancesWithData.map(inst => (
                        <div key={inst.id}
                            onClick={() => onSelectInstance && onSelectInstance(inst.id)}
                            className="bg-slate-900/40 border border-slate-800/80 p-4 rounded-2xl hover:border-emerald-500/30 transition-all group overflow-hidden relative cursor-pointer hover:scale-[1.02] active:scale-[0.98]">
                            <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity">
                                {inst.type === COMISION_TYPE ? <Building2 size={40} /> : inst.type === 'COMITE' ? <Users size={40} /> : <Target size={40} />}
                            </div>

                            <div className="flex justify-between items-start mb-3 relative z-10">
                                <div className="max-w-[70%]">
                                    <div className="flex items-center gap-2 mb-1 leading-none">
                                        <p className="text-emerald-500 text-[10px] font-black uppercase tracking-widest">{INSTANCE_TYPE_LABELS[inst.type as keyof typeof INSTANCE_TYPE_LABELS]}</p>
                                        <span className="bg-slate-800 text-white/40 text-[8px] font-black px-1.5 py-0.5 rounded border border-white/5" title="Peso Relativo en VP">
                                            {(() => {
                                                const catWeight = weights[inst.type as keyof typeof weights] || 0;
                                                const availableTypes = [
                                                    { type: COMISION_TYPE, items: instancesWithData.filter(i => i.type === COMISION_TYPE) },
                                                    { type: 'COMITE', items: instancesWithData.filter(i => i.type === 'COMITE') },
                                                    { type: 'CONSEJERIA', items: instancesWithData.filter(i => i.type === 'CONSEJERIA') }
                                                ].filter(t => t.items.length > 0);
                                                const currentTotalWeight = availableTypes.reduce((acc, t) => acc + (weights[t.type as keyof typeof weights] || 0), 0);
                                                const normalizedCatWeight = catWeight / currentTotalWeight;
                                                const countInCat = instancesWithData.filter(i => i.type === inst.type).length;
                                                return `${((normalizedCatWeight / countInCat) * 100).toFixed(1)}%`;
                                            })()}
                                        </span>
                                    </div>
                                    <h4 className="text-base font-black text-white leading-tight uppercase tracking-tight group-hover:text-emerald-400 transition-colors line-clamp-2 h-10">{inst.name}</h4>
                                </div>
                                <div className="flex gap-1.5 relative z-10">
                                    <div className={`p-1.5 rounded-lg bg-slate-950/80 ${getSemaphoreColor(
                                        inst.planEligible && inst.kpiEligible
                                            ? Math.round((inst.planProgress * 0.3) + (inst.kpiCompliance * 0.7))
                                            : inst.planEligible ? inst.planProgress : inst.kpiCompliance
                                    )} shadow-inner border border-white/5`}>
                                        <Activity size={12} />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 relative z-10">
                                <div className="bg-slate-950/50 p-2 rounded-xl border border-white/5">
                                    <p className="text-[9px] font-black text-white/30 uppercase tracking-widest mb-0.5">Avance Plan</p>
                                    <span className="text-xs font-black text-white">{inst.planProgress || 0}%</span>
                                </div>
                                <div className="bg-slate-950/50 p-2 rounded-xl border border-white/5">
                                    <p className="text-[9px] font-black text-white/30 uppercase tracking-widest mb-0.5">Avance KPIs</p>
                                    <span className="text-xs font-black text-white">{inst.kpiCompliance || 0}%</span>
                                </div>
                            </div>

                            {/* BOTÃƒâ€œN APROBAR REPOSICIONADO PARA EVITAR RUIDO VISUAL */}
                            {inst.planStatus !== 'APROBADO' && inst.planStatus !== 'SIN PLAN' && (canApproveAsVP || isAdmin || isSuperAdmin) && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleApprovePlan(inst.id); }}
                                    className="mt-4 w-full py-2 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-500 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl border border-emerald-500/30 transition-all flex items-center justify-center gap-2 group/btn"
                                >
                                    <Check size={14} className="group-hover/btn:scale-125 transition-transform" />
                                    APROBAR PLAN ESTRATÃƒâ€°GICO
                                </button>
                            )}

                            <div className="mt-3 pt-3 border-t border-slate-800/50 flex justify-between items-center">
                                <div className="flex items-center gap-1.5">
                                    <div className={`w-1.5 h-1.5 rounded-full ${getSemaphoreBg(
                                        inst.planEligible && inst.kpiEligible
                                            ? Math.round((inst.planProgress * 0.3) + (inst.kpiCompliance * 0.7))
                                            : inst.planEligible ? inst.planProgress : inst.kpiCompliance
                                    )} animate-pulse shadow-[0_0_5px] shadow-current`}></div>
                                    <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Activo 2026</span>
                                </div>
                                <ChevronRight size={12} className="text-white/20 group-hover:translate-x-1 group-hover:text-emerald-500 transition-all" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Sección Ã¢â‚¬â€œ Planes de Trabajo de la Vicepresidencia */}
            <div className="space-y-6">
                <div className="flex justify-between items-end px-4">
                    <h3 className="text-sm font-black text-white/40 uppercase tracking-[0.4em]">Planes de Trabajo de Instancias en {vp.name}</h3>
                    <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest">{plans.length} Planes Identificados</p>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-[3rem] overflow-hidden shadow-2xl">
                    {loadingPlans ? (
                        <div className="p-20 text-center animate-pulse text-emerald-500 font-black uppercase tracking-[0.3em]">Cargando Planes de la Vicepresidencia...</div>
                    ) : plans.length > 0 ? (
                        <div className="overflow-x-auto custom-scrollbar w-full">
                            <table className="text-left border-separate border-spacing-0" style={{ minWidth: '1000px', width: 'max-content', tableLayout: 'auto' }}>
                                <thead>
                                    <tr className="bg-slate-950/50 text-white/30 text-[9px] uppercase font-black tracking-[0.2em] border-b border-white/5">
                                        <th className="px-4 md:px-8 py-4 md:py-6 whitespace-nowrap">Instancia</th>
                                        <th className="px-4 md:px-8 py-4 md:py-6 text-center whitespace-nowrap">Estado</th>
                                        <th className="px-4 md:px-8 py-4 md:py-6 text-center whitespace-nowrap">Meta Total</th>
                                        <th className="px-4 md:px-8 py-4 md:py-6 text-center whitespace-nowrap">Avance</th>
                                        <th className="px-4 md:px-8 py-4 md:py-6 text-center whitespace-nowrap">% Cumplimiento</th>
                                        <th className="px-4 md:px-8 py-4 md:py-6 text-right whitespace-nowrap">Aval VP</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {plans.map((p: any) => {
                                        const inst = vpInstances.find(i => i.id === p.commission_id);
                                        return (
                                            <tr key={p.id} className="hover:bg-white/[0.02] transition-colors group">
                                                <td className="px-6 py-6" style={{ whiteSpace: 'nowrap', minWidth: '400px', width: '400px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
                                                        <p className="text-white font-black uppercase text-sm md:text-base tracking-tight" style={{ whiteSpace: 'nowrap' }}>{inst?.name || 'Desconocida'}</p>
                                                        <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest mt-0.5" style={{ whiteSpace: 'nowrap' }}>{inst?.type || getInstanceTypeSingularLabel(COMISION_TYPE)}</p>
                                                    </div>
                                                </td>
                                                <td className="px-4 md:px-8 py-4 md:py-6 text-center" style={{ whiteSpace: 'nowrap', minWidth: '200px', width: '200px' }}>
                                                    <div className="flex justify-center items-center" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className={cn(
                                                            "px-5 py-2 rounded-full text-[10px] font-black border uppercase tracking-widest shadow-md transition-all",
                                                            (p.status === 'APROBADO' || p.status === 'approved') ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.1)]" :
                                                                (p.status === 'REVISADO' || p.status === 'reviewed' || p.status === 'EN REVISION') ? "bg-indigo-500/20 text-indigo-500 border-indigo-500/30" :
                                                                    "bg-amber-500/20 text-amber-500 border-amber-500/30"
                                                        )} style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' }}>
                                                            {(STATUS_LABELS[p.status as keyof typeof STATUS_LABELS] || p.status).replace(/ /g, '\u00A0')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-center font-mono text-xs text-white/60">{p.total_goal || 0}</td>
                                                <td className="px-8 py-6 text-center font-mono text-xs text-emerald-500/80 font-black">{p.total_progress || 0}</td>
                                                <td className="px-8 py-6 text-center">
                                                    <div className="flex flex-col items-center gap-1.5">
                                                        <span className={cn("text-xs font-black font-mono", getSemaphoreColor(p.global_percentage || 0))}>
                                                            {p.global_percentage || 0}%
                                                        </span>
                                                        <div className="w-20 h-1 bg-slate-950 rounded-full overflow-hidden">
                                                            <div className={cn("h-full transition-all duration-1000", getSemaphoreBg(p.global_percentage || 0))} style={{ width: `${p.global_percentage || 0}%` }}></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 md:px-8 py-4 md:py-6 text-right">
                                                    {p.status === 'APROBADO' ? (
                                                        <div className="flex items-center justify-end gap-2 text-emerald-400 whitespace-nowrap">
                                                            <span className="text-[10px] font-black uppercase tracking-widest">Plan Aprobado</span>
                                                            <ShieldCheck size={16} />
                                                        </div>
                                                    ) : (user.role === 'VICE_PRESIDENT' || isUserAdmin) ? (
                                                        <button
                                                            onClick={() => handleApprovePlan(inst?.id)}
                                                            className="px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-500 hover:text-white rounded-xl border border-emerald-500/30 font-black text-[10px] uppercase tracking-widest transition-all shadow-lg flex items-center gap-2 ml-auto whitespace-nowrap"
                                                        >
                                                            <Check size={14} />
                                                            Aprobar Plan
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center justify-end gap-2 text-white/10 whitespace-nowrap">
                                                            <span className="text-[10px] font-black uppercase tracking-widest italic">Pendiente de Aval</span>
                                                            <Shield size={16} />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-20 text-center text-white/20 font-black uppercase tracking-[0.3em] italic">No hay planes registrados para esta Vicepresidencia en 2026</div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

/**
 * Módulo principal de Seguimiento e indicadores.
 * Provee la interfaz para visualizar el tablero global, configurar ponderaciones,
 * y capturar el avance "real" vs "meta" por periodo en una jerarquía de instancias.
 * Cuenta con sincronización en tiempo real vía Supabase.
 * @param {Object} props
 * @param {any} props.user - El objeto usuario actual.
 */
/**
 * TrackingModule has been moved to src/components/tracking/TrackingModule.tsx
 * for module isolation and system stability (v18.9.93).
 */

const MemoryModule = ({ user }: { user: any }) => {
    const [docs, setDocs] = useState<db.DocRecord[]>([]);
    const [hierarchy, setHierarchy] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [newDoc, setNewDoc] = useState({ title: '', type: 'ACTA', url: '', commission_id: '', category: 'DOCUMENTO' });
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [viewMode, setViewMode] = useState<'REPOSITORY' | 'FORMATS'>('REPOSITORY');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('TODOS');
    const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
    const [openingDocId, setOpeningDocId] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState('ACTIVE');
    const [editingDoc, setEditingDoc] = useState<db.DocRecord | null>(null);
    const [aclDoc, setAclDoc] = useState<db.DocRecord | null>(null);
    const [viewer, setViewer] = useState<{ document: db.DocRecord; access: db.DocumentAccess } | null>(null);
    const memoryMutationKeysRef = useRef(new Map<string, string>());
    const memoryMutationOptions = (action: string, payload: unknown): db.GovMutationOptions => {
        const fingerprint = JSON.stringify([action, payload]);
        if (!memoryMutationKeysRef.current.has(fingerprint)) memoryMutationKeysRef.current.set(fingerprint, db.createMutationIdempotencyKey());
        return { idempotencyKey: memoryMutationKeysRef.current.get(fingerprint) };
    };
    const uploadReservationRef = useRef<{ fingerprint: string; uploadId: string } | null>(null);
    const canEditMemory = getModulePermission(user, 'Memoria') === 'EDITOR';
    const canAdminMemory = ['SUPER_ADMIN', 'ADMINISTRADOR'].includes(String(user?.role || '').toUpperCase());
    const visibleDocs = docs.filter(doc =>
        (statusFilter === 'ALL' || (doc.status || 'ACTIVE') === statusFilter)
        && (!searchTerm || doc.title.toLowerCase().includes(searchTerm.toLowerCase()))
        && (selectedCategory === 'TODOS' || doc.category === selectedCategory));

    const handleOpenDocument = async (doc: db.DocRecord) => {
        if (openingDocId === doc.id) return;
        try {
            setOpeningDocId(doc.id);
            setViewer({ document: doc, access: await db.requestDocumentAccess(doc.id) });
        } catch (err: any) { showToast(db.getMutationErrorMessage(err)); }
        finally { setOpeningDocId(null); }
    };

    const [reportConfig, setReportConfig] = useState<any>({
        period: '2026',
        instance: '',
        titular: user?.name || '',
        content: '',
        agreements: [],
        month: new Date().getMonth() + 1
    });

    const [duplicityAlerts, setDuplicityAlerts] = useState<string[]>([]);


    const DOC_TYPES = ['ACTA', 'MINUTA', 'REGLAMENTO', 'CONVENIO', 'INFORME', 'PRESENTACIÃƒâ€œN', 'OTRO'];

    const NATIVE_FORMATS = [
        { id: 'minuta', name: 'Minuta de Reunion', icon: <FileText size={20} /> },
        { id: 'mensual', name: 'Informe Mensual', icon: <Calendar size={20} /> },
        { id: 'trimestral', name: 'Informe Trimestral', icon: <Layers size={20} /> },
        { id: 'semestral', name: 'Informe Semestral', icon: <Trello size={20} /> },
        { id: 'anual', name: 'Memoria Anual', icon: <Book size={20} /> },
    ];

    const loadDocs = useCallback(() => {
        setLoading(true);
        if (!user) { setLoading(false); return; }
        Promise.all([
            db.getMemory(user),
            db.getHierarchy(user)
        ]).then(([d, h]) => {
            setDocs(d || []);
            setHierarchy(h);
            setLoading(false);
        }).catch(err => {
            console.error('Error loading memory:', err);
            setDocs([]);
            setLoading(false);
        });
    }, [user]);

    useEffect(() => { loadDocs(); }, [loadDocs]);

    useEffect(() => {
        if (selectedFormat && reportConfig.instance && reportConfig.period) {
            const checkDuplicity = async () => {
                const inst = hierarchy?.commissions?.find((c: any) => c.name === reportConfig.instance);
                if (!inst) return;

                const { data } = await db.supabase.from('cpx_monthly_reports')
                    .select('id')
                    .eq('commission_id', inst.id)
                    .eq('month', MONTHS.indexOf(reportConfig.period.toUpperCase()) + 1)
                    .eq('format', selectedFormat)
                    .maybeSingle();

                if (data) {
                    setDuplicityAlerts([`Integridad: Ya existe un reporte de tipo ${selectedFormat} para ${reportConfig.instance} en ${reportConfig.period}. Guardar sobrescribirá el anterior.`]);
                } else {
                    setDuplicityAlerts([]);
                }
            }
            checkDuplicity();
        }
    }, [selectedFormat, reportConfig.instance, reportConfig.period, hierarchy]);

    const handleUpload = async () => {
        if (!newDoc.title.trim()) {
            showToast('El título es obligatorio');
            return;
        }

        try {
            setLoading(true);
            const commId = newDoc.commission_id;
            if (!commId) { showToast('No se encontro una instancia para asociar'); setLoading(false); return; }

            if ((!newDoc.url && !selectedFile) || (newDoc.url && selectedFile)) throw new Error('Selecciona exactamente una fuente: archivo o enlace externo.');
            const uploadFingerprint = JSON.stringify([commId, selectedFile?.name, selectedFile?.size, selectedFile?.lastModified]);
            let uploadId = selectedFile && uploadReservationRef.current?.fingerprint === uploadFingerprint ? uploadReservationRef.current.uploadId : undefined;
            if (selectedFile && !uploadId) {
                uploadId = await db.uploadFile(selectedFile, commId, memoryMutationOptions('upload', uploadFingerprint));
                uploadReservationRef.current = { fingerprint: uploadFingerprint, uploadId };
            }
            await db.addMemory(commId, {
                title: newDoc.title, type: newDoc.type, category: newDoc.category,
                source_type: selectedFile ? 'FILE' : 'EXTERNAL_LINK',
                ...(uploadId ? { upload_id: uploadId } : { url: newDoc.url })
            }, memoryMutationOptions('create', [newDoc, uploadId]));

            setShowUpload(false);
            setNewDoc({ title: '', type: 'ACTA', url: '', commission_id: '', category: 'DOCUMENTO' });
            setSelectedFile(null);
            uploadReservationRef.current = null;
            memoryMutationKeysRef.current.clear();
            loadDocs();
        } catch (err: any) {
            showToast(err.message || 'Error al subir documento');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGeneratedReport = async () => {
        if (!reportConfig.instance) {
            showToast('Selecciona una instancia para el reporte');
            return;
        }

        try {
            setLoading(true);
            const targetComm = [...(hierarchy?.commissions || []), ...(hierarchy?.independents || [])].find(c => c.name === reportConfig.instance);
            if (!targetComm) throw new Error("Instancia no válida");

            const title = `${NATIVE_FORMATS.find(f => f.id === selectedFormat)?.name} - ${reportConfig.period}`;

            await db.addMemory(targetComm.id, {
                title,
                type: 'INFORME',
                url: '#', // Indicamos que es un reporte interno
                content: reportConfig
            }, memoryMutationOptions('generate', [targetComm.id, title, reportConfig]));

            showToast('Reporte guardado exitosamente en el Repositorio Institucional');
            setViewMode('REPOSITORY');
            setSelectedFormat(null);
            loadDocs();
        } catch (err: any) {
            showToast(err.message || "Error al guardar reporte");
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = () => {
        // @ts-ignore
        if (typeof html2pdf === 'undefined') {
            showToast("Preparando motor de PDF. Por favor, intenta de nuevo en un segundo.");
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            document.head.appendChild(script);
            return;
        }

        const element = document.getElementById('report-document-container');
        const opt = {
            margin: 1,
            filename: `${reportConfig.instance}_${selectedFormat}_${reportConfig.period}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };
        // @ts-ignore
        html2pdf().set(opt).from(element).save();
    };


    const handleArchiveRestore = async (doc: db.DocRecord) => {
        try {
            if (doc.status === 'ARCHIVED') await db.restoreMemory(doc, memoryMutationOptions('restore', [doc.id, doc.revision]));
            else await db.archiveMemory(doc, memoryMutationOptions('archive', [doc.id, doc.revision]));
            loadDocs();
        } catch (err) { showToast(db.getMutationErrorMessage(err)); }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
            <p className="text-indigo-500 font-black uppercase tracking-widest animate-pulse">Accediendo a la Memoria Institucional...</p>
        </div>
    );

    const allInstances = hierarchy ? [...(hierarchy.commissions || []), ...(hierarchy.independents || [])] : [];

    return (
        <div className="space-y-12">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-slate-800 pb-8">
                <div>
                    <span className="text-indigo-400 text-[10px] font-black uppercase tracking-[0.4em] mb-2 block">
                        {viewMode === 'REPOSITORY' ? 'Repositorio Nacional' : 'Generador de Formatos'}
                    </span>
                    <h1 className="text-5xl font-black text-white uppercase tracking-tighter italic">
                        Memoria <span className="text-indigo-500">Institucional</span>
                    </h1>
                </div>

                <div className="flex bg-slate-900/50 p-1.5 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                    <button
                        onClick={() => { setViewMode('REPOSITORY'); setSelectedFormat(null); }}
                        className={cn("px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                            viewMode === 'REPOSITORY' ? "bg-white text-slate-950 shadow-xl" : "text-white/40 hover:text-white")}
                    >
                        Repositorio
                    </button>
                    <button
                        onClick={() => setViewMode('FORMATS')}
                        className={cn("px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                            viewMode === 'FORMATS' ? "bg-white text-slate-950 shadow-xl" : "text-white/40 hover:text-white")}
                    >
                        Formatos Nativos
                    </button>
                </div>

                {viewMode === 'REPOSITORY' && canEditMemory && (
                    <button
                        onClick={() => setShowUpload(true)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-indigo-500/20"
                    >
                        <Plus size={16} /> Subir Documento
                    </button>
                )}
            </header>

            {viewMode === 'FORMATS' && !selectedFormat && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                    {NATIVE_FORMATS.map(f => (
                        <button
                            key={f.id}
                            onClick={() => {
                                setSelectedFormat(f.id);
                                setReportConfig({ ...reportConfig, instance: hierarchy?.commissions?.[0]?.name || '' });
                            }}
                            className="bg-slate-900 border border-slate-800 p-8 rounded-[2rem] hover:bg-slate-800 transition-all group flex flex-col items-center text-center space-y-4 shadow-xl"
                        >
                            <div className="w-16 h-16 bg-indigo-500/10 text-indigo-500 rounded-3xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                                {f.icon}
                            </div>
                            <div>
                                <h3 className="text-white font-black uppercase text-sm">{f.name}</h3>
                                <p className="text-white/60 text-[10px] font-bold mt-1 tracking-widest">GENERADOR NATIVO</p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {selectedFormat && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] p-12 shadow-2xl relative">
                    <button
                        onClick={() => setSelectedFormat(null)}
                        className="absolute top-8 right-8 text-white/60 hover:text-slate-900 no-print"
                    >
                        <X size={24} />
                    </button>

                    {duplicityAlerts.length > 0 && (
                        <div className="mb-6 bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-center gap-4 text-amber-800 text-[10px] font-black uppercase tracking-widest no-print">
                            <AlertTriangle size={18} className="text-amber-500 shrink-0" />
                            <span>{duplicityAlerts[0]}</span>
                        </div>
                    )}

                    <div className="border-b-[4px] border-indigo-600 pb-12 mb-12">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-indigo-600 font-black text-sm uppercase tracking-widest mb-2">Documento Oficial</p>
                                <div className="flex items-center gap-4 no-print mb-4">
                                    <select
                                        className="bg-slate-100 border-none text-3xl font-black text-slate-900 uppercase tracking-tighter outline-none cursor-pointer rounded-xl px-2"
                                        value={selectedFormat || ''}
                                        onChange={(e) => {
                                            const newFmt = e.target.value;
                                            setSelectedFormat(newFmt);
                                            // Sincronizar contenido si cambia el formato para evitar perdida accidental o duplicidad
                                            setReportConfig(prev => ({ ...prev, content: `Borrador de ${NATIVE_FORMATS.find(f => f.id === newFmt)?.name} - ${prev.period}` }));
                                        }}
                                    >
                                        {NATIVE_FORMATS.map(f => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter print:block hidden">
                                    {NATIVE_FORMATS.find(f => f.id === selectedFormat)?.name}
                                </h2>

                            </div>

                            <div className="text-right">
                                <div className="h-14 ml-auto mb-4 flex items-center justify-end">
                                    <img
                                        src="/assets/logo-coparmex.jpg"
                                        className="h-full object-contain"
                                        alt="COPARMEX"
                                        title="Sello de Integridad Institucional"
                                        onError={(e) => {
                                            // Fallback if image fails
                                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x8??text=COPARMEX';
                                        }}
                                    />
                                </div>
                                <p className="text-slate-950 text-[10px] font-black uppercase tracking-widest">Confidencial Ã¢â‚¬Â¢ Protocolo de Integridad 2026</p>
                            </div>


                        </div>

                        <div className="grid grid-cols-3 gap-8 mt-12 bg-slate-50 p-8 rounded-3xl border border-slate-100">
                            <div>
                                <label className="text-[9px] font-black text-white/60 uppercase tracking-widest block mb-1">Periodo / Fecha</label>
                                <select
                                    className="w-full bg-transparent text-slate-900 font-bold text-sm focus:outline-none focus:border-b border-indigo-200 uppercase"
                                    value={reportConfig.period}
                                    onChange={e => setReportConfig({ ...reportConfig, period: e.target.value })}
                                >
                                    {MONTHS.map(m => <option key={m} value={m}>{m} 2026</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[9px] font-black text-white/60 uppercase tracking-widest block mb-1">Instancia</label>
                                <select
                                    className="w-full bg-transparent text-slate-900 font-bold text-sm focus:outline-none focus:border-b border-indigo-200 uppercase"
                                    value={reportConfig.instance}
                                    onChange={e => setReportConfig({ ...reportConfig, instance: e.target.value })}
                                >
                                    {hierarchy?.commissions?.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[9px] font-black text-white/60 uppercase tracking-widest block mb-1">Titular Responsable</label>
                                <input
                                    className="w-full bg-transparent text-slate-900 font-bold text-sm focus:outline-none focus:border-b border-indigo-200 uppercase"
                                    value={reportConfig.titular}
                                    onChange={e => setReportConfig({ ...reportConfig, titular: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-12" id="report-document-container">
                        <section>
                            <h4 className="text-slate-900 font-black text-sm uppercase tracking-widest mb-6 border-l-4 border-indigo-500 pl-4">Contenido del Informe / Minuta</h4>
                            <textarea
                                className="w-full min-h-[300px] bg-slate-50 border border-slate-200 rounded-3xl p-8 text-slate-800 text-sm focus:outline-none focus:ring-2 ring-indigo-500/10 leading-relaxed font-medium no-print"
                                placeholder="Escribe aquí el cuerpo del documento..."
                                value={reportConfig.content}
                                onChange={e => setReportConfig({ ...reportConfig, content: e.target.value })}
                            />
                            <div className="hidden print:block text-slate-800 text-sm whitespace-pre-wrap leading-relaxed">
                                {reportConfig.content}
                            </div>
                        </section>

                        <section>
                            <h4 className="text-slate-900 font-black text-sm uppercase tracking-widest mb-6 border-l-4 border-indigo-500 pl-4">Acuerdos y Compromisos</h4>
                            <div className="rounded-3xl border border-slate-200 overflow-hidden">
                                <GenericTableEditor
                                    isAdmin={true}
                                    columns={[
                                        { key: 'acuerdo', label: 'Acuerdo / Compromiso' },
                                        { key: 'responsable', label: 'Responsable' },
                                        { key: 'fecha', label: 'Fecha Límite' }
                                    ]}
                                    data={reportConfig.agreements || []}
                                    onChange={val => setReportConfig({ ...reportConfig, agreements: val })}
                                />
                            </div>
                        </section>

                        <div className="flex justify-between items-center mt-12 no-print border-t border-slate-100 pt-8">
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest">
                                <ShieldCheck size={16} /> Verificado por Auditoría de Integridad
                            </div>
                            <div className="flex gap-4">
                                <button
                                    disabled={!canEditMemory} onClick={handleSaveGeneratedReport}
                                    className="px-8 py-3 rounded-2xl bg-emerald-600 text-white font-black text-[10px] uppercase tracking-widest shadow-xl shadow-emerald-500/20 hover:bg-emerald-500 transition-all hover:-translate-y-1"
                                >
                                    Guardar en Repositorio
                                </button>
                                <button
                                    onClick={handleExportPDF}
                                    className="px-8 py-3 rounded-2xl bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest shadow-xl shadow-slate-500/20 hover:bg-slate-800 transition-all hover:-translate-y-1"
                                >
                                    Exportar PDF Maestro
                                </button>
                            </div>
                        </div>

                    </div>

                </motion.div>
            )}

            {viewMode === 'REPOSITORY' && !selectedFormat && (
                <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8">
                    <div className="flex flex-wrap gap-3 mb-6 text-white">
                        <input aria-label="Buscar documentos" placeholder="Buscar documentos..." className="bg-slate-800 rounded-xl p-3" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        <select aria-label="Estado documental" className="bg-slate-800 rounded-xl p-3" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="ACTIVE">Activos</option><option value="ARCHIVED">Archivados</option><option value="ALL">Todos</option></select>
                        <select aria-label="Categoría documental" className="bg-slate-800 rounded-xl p-3" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}><option value="TODOS">Todas las categorías</option>{Array.from(new Set(docs.map(d=>d.category || 'DOCUMENTO'))).map(c=><option key={c}>{c}</option>)}</select>
                        <span>{visibleDocs.length} de {docs.length} documentos</span>
                    </div>
                    {visibleDocs.length === 0 ? (
                        <div className="text-center py-20">
                            <FolderOpen size={48} className="mx-auto text-slate-800 mb-6" />
                            <p className="text-white font-black uppercase text-xs tracking-[0.2em]">Memoria Vacía Ã¢â‚¬Â¢ Esperando Documentación</p>
                            <button disabled={!canEditMemory} onClick={() => setShowUpload(true)} className="mt-8 bg-indigo-600 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-500/20">
                                Iniciar Registro Institucional
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {visibleDocs.map((doc) => (
                                <div key={doc.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-950/50 p-6 rounded-3xl border border-slate-800/50 hover:bg-slate-800/20 transition-all gap-4 group">
                                    <div className="flex items-center gap-5">
                                        <div className="w-12 h-12 bg-slate-800 rounded-2xl flex shrink-0 items-center justify-center text-white/60">
                                            <BookOpen size={24} />
                                        </div>
                                        <div>
                                            <h4 className="text-white text-base font-black uppercase tracking-tight leading-none mb-1">{doc.title}</h4>
                                            <span className="text-white/50 text-[10px] font-bold uppercase tracking-widest">{doc.commission_name || 'INSTANCIA NACIONAL'} Ã¢â‚¬Â¢ {new Date(doc.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                                        <span className="bg-indigo-500/10 text-indigo-400 text-[10px] uppercase font-black px-4 py-2 rounded-xl border border-indigo-500/20">{doc.type} · {doc.category} · {doc.status}</span>
                                        <button
                                            onClick={() => handleOpenDocument(doc)}
                                            disabled={openingDocId === doc.id}
                                            title={openingDocId === doc.id ? 'Abriendo...' : 'Abrir documento'}
                                            className="text-white/70 hover:text-white bg-slate-800 hover:bg-slate-700 aspect-square w-10 h-10 flex items-center justify-center rounded-xl transition-all disabled:opacity-50 disabled:cursor-wait"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                        {canEditMemory && doc.status === 'ACTIVE' && <button onClick={() => setEditingDoc(doc)} className="text-white p-2 rounded-xl bg-slate-800">Editar</button>}
                                        {canAdminMemory && <><button onClick={() => handleArchiveRestore(doc)} className="text-white p-2 rounded-xl bg-slate-800">{doc.status === 'ARCHIVED' ? 'Restaurar' : 'Archivar'}</button><button onClick={() => setAclDoc(doc)} className="text-white p-2 rounded-xl bg-slate-800">Acceso</button></>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {editingDoc && <DocumentMetadataForm document={editingDoc} onClose={() => setEditingDoc(null)} onSave={async fields => { await db.updateMemory(editingDoc.id, fields, editingDoc.revision!, memoryMutationOptions('edit', [editingDoc.id, editingDoc.revision, fields])); loadDocs(); }} />}
            {aclDoc && <DocumentAclForm document={aclDoc} onClose={() => setAclDoc(null)} onSaved={loadDocs} />}
            {viewer && <DocumentViewer document={viewer.document} access={viewer.access} onClose={() => setViewer(null)} />}
            {showUpload && canEditMemory && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm">
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-[2.5rem] p-10 shadow-2xl relative">
                        <button onClick={() => setShowUpload(false)} className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={24} /></button>
                        <h2 className="text-2xl font-black text-white uppercase mb-8">Subir Documento</h2>
                        <div className="space-y-6">
                            <label className="block text-white text-xs">Categoría<select className="block w-full bg-slate-800 rounded-xl p-3" value={newDoc.category} onChange={e=>setNewDoc({...newDoc,category:e.target.value})}><option>DOCUMENTO</option><option>MODELO_GOBERNANZA</option></select></label>
                            <div>
                                <label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Título</label>
                                <input className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-indigo-500/50" placeholder="Ej. Acta de Asamblea..." value={newDoc.title} onChange={e => setNewDoc({ ...newDoc, title: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Tipo</label>
                                    <select className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-indigo-500/50" value={newDoc.type} onChange={e => setNewDoc({ ...newDoc, type: e.target.value })}>
                                        {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Fuente</label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setSelectedFile(null)}
                                            className={cn("flex-1 p-3 rounded-xl border text-[10px] font-black uppercase transition-all", !selectedFile ? "bg-indigo-600 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-white/40")}
                                        > URL </button>
                                        <label className={cn("flex-1 p-3 rounded-xl border text-[10px] font-black uppercase transition-all cursor-pointer flex items-center justify-center", selectedFile ? "bg-indigo-600 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-white/40")}>
                                            <input type="file" className="hidden" onChange={e => { setSelectedFile(e.target.files[0] || null); if (e.target.files[0]) setNewDoc({ ...newDoc, url: '' }) }} />
                                            PC
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {!selectedFile ? (
                                <div>
                                    <label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">URL (Google Drive / Dropbox)</label>
                                    <input className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-indigo-500/50" placeholder="https://..." value={newDoc.url} onChange={e => setNewDoc({ ...newDoc, url: e.target.value })} />
                                </div>
                            ) : (
                                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-4 flex items-center gap-4">
                                    <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center text-white"><File size={20} /></div>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-white text-xs font-bold truncate">{selectedFile.name}</p>
                                        <p className="text-white/40 text-[9px] font-medium">LISTO PARA SUBIR</p>
                                    </div>
                                    <button onClick={() => setSelectedFile(null)} className="text-white/40 hover:text-white"><X size={16} /></button>
                                </div>
                            )}

                            <div>
                                <label className="text-[10px] font-black uppercase text-white/70 tracking-widest mb-1 block">Instancia</label>
                                <select className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-indigo-500/50" value={newDoc.commission_id} onChange={e => setNewDoc({ ...newDoc, commission_id: e.target.value })}>
                                    <option value="">Seleccionar instancia...</option>
                                    {allInstances.map((inst: any) => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-4 mt-8">
                            <button onClick={() => setShowUpload(false)} className="flex-1 bg-slate-800 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all">Cancelar</button>
                            <button onClick={handleUpload} className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-500/20">Subir Documento</button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

const AIDeskModule = ({ user }: { user: any }) => {
    return (
        <div className="h-[calc(100vh-180px)] min-h-[500px] w-full max-w-7xl mx-auto rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-800 bg-[#030712] mb-12">
            <AIDeskCoparmex user={user} />
        </div>
    );
};

// --- Modulo de Reportes Mensuales ---
const MonthlyReportModule = ({ user, selectedMonth, setSelectedMonth }: { user: any, selectedMonth: string, setSelectedMonth: (m: string) => void }) => {
    const settings = useContext(SettingsContext);
    const { isVP, isND, isPresident, isSA, isInstance, isGlobal } = useRole(user);
    const [hierarchy, setHierarchy] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    // El estado de mes se recibe ahora como prop para sincronizacion global
    const [selectedFormat, setSelectedFormat] = useState('Mensual');
    const [selectedInstance, setSelectedInstance] = useState<any>(null);
    const [indicadores, setindicadores] = useState<any[]>([]);
    const [reportLoading, setReportLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [observations, setObservations] = useState("");
    const [execSummary, setExecSummary] = useState("");
    const [reportData, setReportData] = useState<any>(null);
    const [extraTables, setExtraTables] = useState<any[]>([]); // Dynamic rows
    const [extraColumns, setExtraColumns] = useState<any[]>([
        { key: 'col1', label: 'Descripcion' },
        { key: 'col2', label: 'Dato / Valor' },
        { key: 'col3', label: 'Estado / Avance' }
    ]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterVP, setFilterVP] = useState<string>("TODAS");
    const [filterType, setFilterType] = useState<string>("TODAS");

    const vps = hierarchy?.vicepresidencies || [];
    const types = [COMISION_TYPE, "COMITE", "CONSEJERIA"];
    const allInstances = hierarchy ? [...(hierarchy.vicepresidencies || []), ...(hierarchy.commissions || []), ...(hierarchy.independents || [])] : [];

    const handleAIAnalysis = async () => {
        if (!indicadores || indicadores.length === 0) return;
        try {
            const compactData = indicadores
                .map((i: any) => {
                    const curr = i.history?.find((h: any) => h.period === selectedMonth) || { meta: 0, real: 0 };
                    const monthIdx = MONTHS.indexOf(selectedMonth);
                    const metrics = getIndicatorMetrics(i, getElapsedCompletedMonths(monthIdx), false);
                    const canonicalReal = metrics.real;
                    const canonicalMeta = metrics.meta;
                    const canonicalPct = metrics.pct;
                    return `- ${i.nombre}: Mes: ${curr.real}/${curr.meta} | Acumulado Al Año: ${canonicalReal}/${canonicalMeta} (${canonicalPct}%)`;
                })
                .join('\n');

            const reportPrompt = `Eres el Asistente de Inteligencia Articulada de COPARMEX. Analiza los datos de la instancia "${selectedInstance?.name || 'Instancia Nacional'}" para el mes "${selectedMonth}".

REGLAS:
- Responde siempre en español.
- Solo analiza datos compartidos.
- Formato obligatorio:

RESUMEN EJECUTIVO: [2-3 líneas resumiendo el avance]
LOGROS DESTACADOS: [indicadores con buen cumplimiento]
ÁREAS DE RIESGO: [indicadores críticos]
ACCIONES PRIORITARIAS: [2-3 recomendaciones]
Datos de cumplimiento (Actual/Meta):
${compactData}`;

            const text = await askAI(reportPrompt, "");
            setObservations(text);
            setExecSummary(text.slice(0, 500) + (text.length > 500 ? "..." : ""));
        } catch (err: any) {
            console.error('[AI_REPORT_ERROR]', err);
            const isQuota = err.messag?.includes('429');
            showToast(isQuota ? "Cuota de IA excedida (429). Google ha limitado las consultas gratuitas por ahora. Intenta de nuevo en un minuto." : "Error al generar análisis: " + err.message);
        } finally {
            setReportLoading(false);
        }
    };

    useEffect(() => {
        db.getHierarchy(user).then(h => {
            setHierarchy(h);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [user]);

    const viewReport = async (instance: any, monthSelect: string, formatSelect: string) => {
        try {
            setReportLoading(true);
            setSelectedInstance(instance);
            const targetMonth = monthSelect || selectedMonth;
            const targetFormat = formatSelect || selectedFormat;
            const data = await db.getIndicators(instance.id);
            setindicadores(data || []);

            // Fetch or Initialize Report Record
            const { data: report, error } = await db.supabase.from('cpx_monthly_reports')
                .select('*')
                .eq('commission_id', instance.id)
                .eq('month', monthToNum(targetMonth))
                .eq('year', 2026)
                .eq('format', targetFormat)
                .maybeSingle();

            if (report) {
                setReportData(report);
                setObservations(report.observations || "Sin observaciones generadas.");
                setExecSummary(report.summary || "Consolidado de cumplimiento basado en indicadores clave.");
                if (report.extra_tables) setExtraTables(report.extra_tables);
                if (report.extra_columns) setExtraColumns(report.extra_columns);
            } else {
                setReportData({
                    commission_id: instance.id,
                    month: monthToNum(targetMonth),
                    year: 2026,
                    format: targetFormat,
                    status: 'BORRADOR'
                });
                setObservations("Pendiente de análisis con IA.");
                setExecSummary("Consolidado estratégico pendiente.");
                setExtraTables([]);
                setExtraColumns([
                    { key: 'col1', label: 'Descripcion' },
                    { key: 'col2', label: 'Dato / Valor' },
                    { key: 'col3', label: 'Estado / Avance' }
                ]);
            }


        } catch (err) {
            console.error("Error loading report data", err);
        } finally {
            setReportLoading(false);
        }
    };


    const handleSaveReport = async (updates: any) => {
        try {
            const payload = {
                ...reportData,
                ...updates,
                commission_id: selectedInstance.id,
                month: monthToNum(selectedMonth),
                year: 2026,
                format: selectedFormat,
                updated_at: new Date().toISOString()
            };

            const response = await db.saveTrackingReportScoped(selectedInstance.id, payload);
            setReportData(response.report);
            showToast('Cambios guardados');
        } catch (err: any) {
            showToast('Error al guardar reporte: ' + err.message);
        }
    };

    const handleExportPDF = () => {
        // @ts-ignore
        if (typeof html2pdf === 'undefined') {
            showToast("Preparando motor de PDF. Por favor, intenta de nuevo en un segundo.");
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            document.head.appendChild(script);
            return;
        }

        const element = document.getElementById('report-monthly-container');
        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `Informe_${String(selectedInstance.name || 'Instancia').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')}_${selectedMonth}_2026.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };
        // @ts-ignore
        html2pdf().set(opt).from(element).save();
    };

    if (loading) return <div className="p-20 text-center text-emerald-500 font-black animate-pulse">PREPARANDO MOTOR DE REPORTES...</div>;


    if (selectedInstance) {
        const currentMonth = getCurrentPeriod('Mensual');
        return (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <button onClick={() => setSelectedInstance(null)} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors font-black text-[10px] uppercase tracking-widest">
                    <ChevronRight size={16} className="rotate-180" /> Volver al listado
                </button>

                {reportLoading ? (
                    <div className="py-20 text-center text-emerald-500 font-black animate-pulse uppercase tracking-widest">Analizando indicadores Estratégicos...</div>
                ) : (
                    <>
                        <div id="report-monthly-container" className="bg-white p-8 md:p-12 rounded-[3rem] text-slate-900 shadow-2xl overflow-hidden relative">
                            <div className="flex justify-between items-center mb-10 no-print">
                                <div className="flex flex-col">
                                    <h1 className="text-4xl font-black uppercase text-slate-900 tracking-tighter leading-none mb-1">Informe Estratégico Mensual</h1>
                                    <div className="flex items-center gap-4">
                                        <h2 className="text-xl font-bold text-indigo-700 uppercase">{selectedInstance?.name}</h2>
                                        <div className="h-6 w-px bg-slate-200"></div>
                                        <select
                                            className="bg-indigo-50 text-indigo-700 font-black text-sm uppercase p-1 rounded-lg outline-none cursor-pointer border-none shadow-sm"
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                        >
                                            {MONTHS.map(m => <option key={m} value={m}>{m} 2026</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6 no-print">
                                    <div className="text-right">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Estado del Reporte</div>
                                        <div className={cn(
                                            "text-xs font-black uppercase tracking-widest",
                                            reportData?.status === 'APROBADO' ? "text-emerald-500" :
                                                reportData?.status === 'VALIDADO' ? "text-indigo-500" : "text-amber-500"
                                        )}>
                                            {reportData?.status || 'BORRADOR'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute top-0 right-0 p-8 opacity-10">
                                <img
                                    src="/assets/logo-coparmex.jpg"
                                    className="w-48 h-auto object-contain"
                                    alt=""
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                />
                            </div>
                            <div className="flex gap-3">
                                {/* VALIDAR - Titular */}
                                {!reportData?.validated_at && (user?.role === 'COMMISSION_PRESIDENT' || user?.role === 'SUPER_ADMIN') && (
                                    <button
                                        onClick={() => handleSaveReport({
                                            validated_by: user.name,
                                            validated_at: new Date().toISOString(),
                                            status: 'VALIDADO'
                                        })}
                                        className="bg-indigo-600 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                                    >
                                        <UserCheck size={14} /> VALIDAR INFORME
                                    </button>
                                )}
                                {/* APROBAR - VP */}
                                {reportData?.validated_at && !reportData?.approved_at && (user?.role === 'VICE_PRESIDENT' || user?.role === 'SUPER_ADMIN') && (
                                    <button
                                        onClick={() => handleSaveReport({
                                            approved_by: user.name,
                                            approved_at: new Date().toISOString(),
                                            status: 'APROBADO'
                                        })}
                                        className="bg-emerald-600 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 shadow-lg shadow-emerald-500/20 flex items-center gap-2"
                                    >
                                        <ShieldCheck size={14} /> APROBAR INFORME
                                    </button>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleSaveReport({
                                        observations,
                                        summary: execSummary,
                                        extra_tables: extraTables,
                                        extra_columns: extraColumns
                                    })}

                                    className="px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500 hover:text-white"
                                >
                                    Guardar Cambios
                                </button>

                                <button
                                    onClick={() => setIsEditing(!isEditing)}
                                    className={cn(
                                        "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                        isEditing ? "bg-emerald-600 text-white" : "bg-slate-100 text-white/70 hover:bg-slate-200"
                                    )}
                                >
                                    {isEditing ? 'Finalizar Edicion' : 'Editar Reporte'}
                                </button>
                            </div>

                            <div className="flex gap-1 p-1 bg-slate-100/50 rounded-xl mb-6 w-fit no-print">
                                {['Mensual', 'Minuta', 'Trimestral'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => {
                                            setSelectedFormat(f);
                                            viewReport(selectedInstance, selectedMonth, f);
                                        }}
                                        className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${selectedFormat === f
                                            ? 'bg-white text-emerald-600 shadow-md'
                                            : 'text-slate-400 hover:text-slate-600'
                                            }`}
                                    >
                                        {f === 'Mensual' ? 'Informe Mensual' : f === 'Minuta' ? 'Minuta de Sesión' : 'Cierre Trimestral'}
                                    </button>
                                ))}
                            </div>

                            <div className="border-b-[6px] border-emerald-600 pb-8 mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6 relative z-10">
                                <div>
                                    <span className="bg-emerald-600 text-white text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-[0.2em] mb-4 inline-block">Documento Oficial de Inteligencia</span>
                                    <h1 className="text-4xl md:text-5xl font-black uppercase text-slate-900 tracking-tighter leading-none mb-3 italic">
                                        {selectedFormat === 'Minuta' ? 'Minuta de' : selectedFormat === 'Trimestral' ? 'Informe' : 'Informe'}<br />
                                        {selectedFormat === 'Minuta' ? 'Sesión' : selectedFormat === 'Trimestral' ? 'Trimestral' : 'Estratégico'}
                                    </h1>
                                    <h2 className="text-2xl font-black text-emerald-700 uppercase">{selectedInstance.name}</h2>
                                </div>
                                <div className="text-right flex flex-col items-end">
                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 no-print">Cambiar Periodo Reportado</div>
                                    <select
                                        className="bg-slate-100 border-none text-2xl font-black text-emerald-600 uppercase italic leading-none focus:ring-0 cursor-pointer no-print rounded-lg px-2"
                                        value={selectedMonth}
                                        onChange={(e) => {
                                            setSelectedMonth(e.target.value);
                                            viewReport(selectedInstance, e.target.value, selectedFormat || '');
                                        }}
                                    >
                                        {MONTHS_FULL.map((m, idx) => (
                                            <option key={m} value={MONTHS[idx]}>{m} 2026</option>
                                        ))}
                                    </select>
                                    <div className="hidden print:block text-4xl font-black text-emerald-600 uppercase italic leading-none">
                                        {selectedMonth} 2026
                                    </div>

                                    <div className="mt-6 flex flex-col items-end">
                                        <p className="text-sm font-black text-slate-800 uppercase tracking-tight">{selectedInstance.president_name || 'Titular no asignado'}</p>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Titular de la Instancia</p>
                                    </div>
                                </div>

                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 relative z-10">
                                <div className="lg:col-span-2 space-y-10">
                                    <section>
                                        <h3 className="text-sm font-black uppercase text-slate-900 flex items-center gap-3 mb-6">
                                            <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                                                <Target size={16} />
                                            </span>
                                            Desempeño de indicadores (KPIs)
                                        </h3>
                                        {indicadores.length === 0 ? (
                                            <div className="p-8 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-center">
                                                <p className="text-white/60 text-[10px] font-black uppercase tracking-widest">Sin indicadores configurados para este periodo</p>
                                            </div>
                                        ) : (
                                            <div className="overflow-hidden bg-slate-50 rounded-[2rem] border border-slate-100">
                                                <table className="w-full text-left border-collapse">
                                                    <thead>
                                                        <tr className="bg-slate-900 text-white text-[9px] uppercase font-black tracking-widest">
                                                            <th className="p-6">Metrica</th>
                                                            <th className="p-6 text-right">Meta</th>
                                                            <th className="p-6 text-right">Real</th>
                                                            <th className="p-6 text-right">Desempeño</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {indicadores.map((ind) => {
                                                            const currObj = ind.history?.find((h: any) => h.period === selectedMonth) || { meta: ind.metaAnual / 12 || 0, real: 0 };

                                                            // Cálculo acumulado (Al Año)
                                                            const monthIdx = MONTHS.indexOf(selectedMonth);
                                                            const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(monthIdx), false);
                                                            const canonicalReal = metrics.real;
                                                            const canonicalMeta = metrics.meta;
                                                            let periodPct = metrics.pct;

                                                            return (
                                                                <tr key={ind.id} className="border-b border-slate-200/50 hover:bg-emerald-50/50 transition-colors">
                                                                    <td className="p-6 break-words min-w-[200px]">
                                                                        <p className="font-black text-slate-800 text-sm uppercase leading-tight whitespace-normal">{ind.nombre}</p>
                                                                        <p className="text-[9px] font-bold text-white/60 uppercase tracking-widest mt-1">{ind.unit || ind.unidad}</p>
                                                                    </td>

                                                                    <td className="p-6 text-right font-black text-slate-700">
                                                                        <div className="text-[10px] text-slate-400 mb-1">MES</div>
                                                                        {currObj.real} / {currObj.meta}
                                                                    </td>
                                                                    <td className="p-6 text-right font-black text-slate-900">
                                                                        <div className="text-[10px] text-slate-600 mb-1 font-black uppercase">Acumulado (Año)</div>
                                                                        {canonicalReal} / {canonicalMeta}
                                                                    </td>
                                                                    <td className="p-6 text-right">
                                                                        <div className="flex flex-col items-end gap-1">
                                                                            <span className={cn(
                                                                                "px-3 py-1 font-black rounded-lg text-[10px] tracking-widest uppercase",
                                                                                periodPct >= (settings?.threshold_green || 90) ? "bg-emerald-100 text-emerald-700" :
                                                                                    periodPct >= (settings?.threshold_amber || 75) ? "bg-amber-100 text-amber-700" :
                                                                                        "bg-red-100 text-red-700"
                                                                            )}>
                                                                                {periodPct}%
                                                                            </span>
                                                                            <div className="w-16 h-1 bg-slate-200 rounded-full overflow-hidden">
                                                                                <div className={cn("h-full", periodPct >= (settings?.threshold_green || 90) ? "bg-emerald-500" : periodPct >= (settings?.threshold_amber || 75) ? "bg-amber-500" : "bg-red-500")} style={{ width: `${Math.min(periodPct, 100)}%` }}></div>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </section>

                                    <section>

                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-sm font-black uppercase text-slate-900 flex items-center gap-3">
                                                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                                    <PlusSquare size={16} />
                                                </span>
                                                Datos Adicionales y Seguimiento (CRUD)
                                            </h3>
                                        </div>
                                        <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 overflow-hidden">
                                            <GenericTableEditor
                                                isAdmin={user?.role === 'SUPER_ADMIN'}
                                                columns={extraColumns}
                                                onColumnsChange={setExtraColumns}
                                                data={extraTables || []}
                                                onChange={setExtraTables}
                                            />

                                            <p className="text-[9px] text-white/40 font-bold uppercase mt-4 text-center tracking-[0.2em]">
                                                * Administrador puede agregar/quitar filas y columnas dinámicamente
                                            </p>
                                        </div>
                                    </section>


                                    <section>
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-sm font-black uppercase text-slate-900 flex items-center gap-3">
                                                <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                                                    <Zap size={16} />
                                                </span>
                                                {selectedFormat === 'Minuta' ? 'Acuerdos y Conclusiones' : 'Análisis Cualitativo y Observaciones'}
                                            </h3>
                                        </div>
                                        <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
                                            {isEditing ? (
                                                <textarea
                                                    className="w-full bg-white border border-slate-200 p-4 rounded-xl text-slate-600 text-sm leading-relaxed outline-none focus:border-emerald-500"
                                                    rows={4}
                                                    value={observations}
                                                    onChange={e => setObservations(e.target.value)}
                                                />
                                            ) : (
                                                <p className="text-slate-600 text-sm leading-relaxed italic whitespace-pre-wrap">
                                                    "{observations}"
                                                </p>
                                            )}
                                        </div>
                                    </section>
                                </div>

                                <div className="space-y-8">
                                    <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl">
                                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400 mb-6">Resumen Ejecutivo</h4>
                                        {isEditing ? (
                                            <textarea
                                                className="w-full bg-slate-800 border border-slate-700 p-4 rounded-xl text-slate-300 text-[10px] font-bold uppercase tracking-widest outline-none focus:border-emerald-500 mb-6"
                                                rows={3}
                                                value={execSummary}
                                                onChange={e => setExecSummary(e.target.value)}
                                            />
                                        ) : (
                                            <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-6 leading-relaxed">
                                                {execSummary}
                                            </p>
                                        )}
                                        <div className="space-y-6">
                                            <div className="flex flex-col">
                                                <span className="text-3xl font-black italic">{indicadores.length}</span>
                                                <span className="text-[9px] font-black uppercase text-white/70 tracking-widest mt-1">indicadores Activos</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-3xl font-black italic">
                                                    {Math.round(indicadores.reduce((acc, ind) => {
                                                        const monthIdx = MONTHS.indexOf(selectedMonth);
                                                        const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(monthIdx), false);
                                                        return acc + Math.min(100, metrics.pct);
                                                    }, 0) / (indicadores.length || 1))}%
                                                </span>
                                                <span className="text-[9px] font-black uppercase text-white/70 tracking-widest mt-1">Cumplimiento Global</span>
                                            </div>

                                        </div>
                                    </div>

                                    <div className="border-2 border-dashed border-slate-200 p-8 rounded-[2.5rem]">
                                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Firmas de Validación</h4>
                                        <div className="space-y-8 pt-4">
                                            <div className="border-b border-slate-200 pb-4 text-center">
                                                {reportData?.validated_at ? (
                                                    <div className="animate-in fade-in zoom-in duration-700">
                                                        <p className="text-emerald-600 font-['Dancing_Script'] text-xl mb-1">{reportData.validated_by}</p>
                                                        <p className="text-[7px] text-slate-400 font-mono uppercase">ID: {reportData.id?.slice(0, 8)} | TS: {new Date(reportData.validated_at).toLocaleString()}</p>
                                                        <p className="text-[8px] font-black uppercase text-emerald-600 mt-2 flex items-center justify-center gap-1">
                                                            <CheckCircle2 size={10} /> VALIDACIÃƒâ€œN ELECTRÃƒâ€œNICA
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="h-16 flex items-center justify-center">
                                                        <p className="text-[8px] font-black uppercase text-slate-400 italic">Pendiente de Validación</p>
                                                    </div>
                                                )}
                                                <p className="text-[9px] font-black uppercase text-slate-900 mt-2 border-t border-slate-100 pt-2">Presidente de la Instancia Nacional</p>
                                            </div>

                                            <div className="border-b border-slate-200 pb-4 text-center">
                                                {reportData?.approved_at ? (
                                                    <div className="animate-in fade-in zoom-in duration-700">
                                                        <p className="text-blue-600 font-['Dancing_Script'] text-xl mb-1">{reportData.approved_by}</p>
                                                        <p className="text-[7px] text-slate-400 font-mono uppercase">AUTH_VP: {reportData.approved_by?.slice(0, 3).toUpperCase()} | TS: {new Date(reportData.approved_at).toLocaleString()}</p>
                                                        <p className="text-[8px] font-black uppercase text-blue-600 mt-2 flex items-center justify-center gap-1">
                                                            <ShieldCheck size={10} /> APROBACIÃƒâ€œN VICEPRESIDENCIA
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="h-16 flex items-center justify-center border-t border-slate-100 pt-10">
                                                        <p className="text-[8px] font-black uppercase text-slate-400 italic">Pendiente de Aprobación VP</p>
                                                    </div>
                                                )}
                                                <p className="text-[9px] font-black uppercase text-slate-900 mt-2 font-black">Vicepresidente Nacional CPX</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button onClick={handleExportPDF} className="w-full mt-8 bg-emerald-600 text-white py-6 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-3 no-print">
                            <Download size={20} /> Exportar Reporte Maestro (PDF)
                        </button>
                    </>
                )}
            </div>
        );
    }


    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div>
                    <span className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.3em] mb-2 block font-['Outfit']">Extracción de Inteligencia y Resultados</span>
                    <h1 className="text-5xl font-black text-white uppercase tracking-tighter italic">Informes <span className="text-emerald-500 italic">Mensuales</span></h1>
                </div>

                <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto no-print">
                    <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-2">Mes del Reporte</span>
                        <select
                            value={selectedMonth}
                            onChange={e => setSelectedMonth(e.target.value)}
                            className="bg-slate-900 border border-slate-800 rounded-2xl py-4 px-6 text-white text-xs font-black uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all cursor-pointer shadow-lg"
                        >
                            {MONTHS.map(m => <option key={m} value={m}>{m} 2026</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-2">Vicepresidencia</span>
                        <select
                            value={filterVP}
                            onChange={e => setFilterVP(e.target.value)}
                            className="bg-slate-900 border border-slate-800 rounded-2xl py-4 px-6 text-white text-xs font-black uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all cursor-pointer shadow-lg"
                        >
                            <option value="TODAS">Todas</option>
                            {vps.map((vp: any) => <option key={vp.id} value={vp.id}>{vp.name}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-2">Tipo</span>
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-slate-900 border border-slate-800 rounded-2xl py-4 px-6 text-white text-xs font-black uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all cursor-pointer shadow-lg"
                        >
                            <option value="TODAS">Todos</option>
                            {types.map((t: string) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="relative w-full md:w-80 self-end">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="BUSCAR INSTANCIA..."
                            className="w-full bg-slate-900 border border-slate-800 rounded-2xl py-4 pl-12 pr-6 text-white text-xs font-black uppercase tracking-widest focus:outline-none focus:border-emerald-500/50 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </header>

            {user?.role === 'SUPER_ADMIN' && (
                <section className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-emerald-500/10 transition-all"></div>
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6 relative z-10">
                        <div>
                            <h2 className="text-xl font-black text-white uppercase tracking-tight">Reporte Maestro de Gobernanza 2026</h2>
                            <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1 leading-relaxed max-w-lg">Consolidado estratégico de todas las vicepresidencias e instancias nacionales en un solo documento ejecutivo.</p>
                        </div>
                        <button className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-2xl shadow-emerald-500/20 flex items-center gap-3 active:scale-95 shrink-0">
                            <Download size={20} /> Descargar Master PDF
                        </button>
                    </div>
                </section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allInstances
                    .filter((inst: any) => {
                        if (!inst || !inst.name) return false;
                        if (filterVP !== "TODAS" && inst.parent_id !== filterVP && inst.id !== filterVP) return false;
                        if (filterType !== "TODAS" && inst.type !== filterType && inst.parent_id) return false;
                        return inst.name.toLowerCase().includes(searchTerm.toLowerCase());
                    })
                    .map(inst => (
                        <div key={inst.id} className="bg-slate-900 border border-slate-800 p-10 rounded-[2.5rem] hover:border-emerald-500/40 transition-all group relative overflow-hidden flex flex-col justify-between h-[320px]">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/0 rounded-full -mr-20 -mt-20 blur-3xl group-hover:bg-emerald-500/5 transition-all"></div>
                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-10">
                                    <div className="w-16 h-16 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-center text-white/70 group-hover:text-emerald-500 group-hover:border-emerald-500/30 transition-all shadow-xl">
                                        <FileText size={32} strokeWidth={1.5} />
                                    </div>
                                    <span className="bg-slate-950 text-slate-600 text-[9px] font-black px-4 py-2 rounded-xl border border-slate-800 uppercase tracking-widest group-hover:border-emerald-500/20 transition-all italic">{inst.type}</span>
                                </div>
                                <h3 className="text-white font-black uppercase text-lg leading-tight group-hover:text-emerald-400 transition-colors mb-2">{inst.name}</h3>
                                <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.2em]">{inst.president_name || 'SIN TITULAR ASIGNADO'}</p>
                            </div>

                            <button
                                onClick={() => viewReport(inst, selectedMonth, selectedFormat)}
                                className="relative z-10 w-full bg-slate-800 hover:bg-emerald-600 text-white py-5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 active:scale-95 shadow-lg group-hover:shadow-emerald-500/10"
                            >
                                <Search size={18} /> Ver Detalle de Informe
                            </button>
                        </div>
                    ))}
            </div>
        </div>
    );
};

// --- Indicator Sync Module ---

const IndicatorSyncModule = ({ user }: any) => {
    const [showSourceDropdown, setShowSourceDropdown] = useState(false);
    const [sourceSearchText, setSourceSearchText] = useState('');
    const [sourceTypeFilter, setSourceTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');
    const [showLocalPayloadPreview, setShowLocalPayloadPreview] = useState(false);
    const [indicators, setIndicators] = useState<any[]>([]);
    const [targetInstances, setTargetInstances] = useState<any[]>([]);
    const [selectedIndicator, setSelectedIndicator] = useState<any>(null);
    const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [syncMode, setSyncMode] = useState<db.IndicatorSyncSafeMode>('CATALOG_AND_ORDER_KEEP_DESTINATION_DATA');
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');
    const [showRemotePreviewModal, setShowRemotePreviewModal] = useState(false);
    const [remotePreviewLoading, setRemotePreviewLoading] = useState(false);
    const [remotePreviewResults, setRemotePreviewResults] = useState<Record<string, {
        loading?: boolean;
        status: 'VALIDATED' | 'BLOCKED_SAFE' | 'FAILED' | 'PLAN_NOT_FOUND' | 'PLAN_AMBIGUOUS' | 'APPLIED';
        httpStatus?: number | null;
        requestId?: string;
        summary?: string;
        error?: string;
    }>>({});
    const canSeeTechnicalVersion = ['SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL', 'ADMIN'].includes(String(user?.role || '').toUpperCase());

    const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(null);
    const [executionLocked, setExecutionLocked] = useState(false);

    const generateFingerprint = () => {
        if (!selectedIndicator || selectedTargets.length === 0) return null;
        return JSON.stringify({
            source_plan_id: selectedIndicator.id,
            target_plan_ids: [...selectedTargets].sort(),
            year: 2026,
            mode: 'SOLO_ITEMS_ESTRUCTURA',
            preserve_target_data: true,
            replace_existing: false,
            copy_goals: false,
            copy_progress: false,
            copy_evidence: false
        });
    };

    useEffect(() => {
        if (previewFingerprint && generateFingerprint() !== previewFingerprint) {
            setPreviewFingerprint(null);
            setRemotePreviewResults({});
        }
    }, [selectedIndicator, selectedTargets, syncMode]);

    const refresh = async () => {
        setLoading(true);
        try {
            const [h, ind] = await Promise.all([
                db.getHierarchy(user),
                db.getAllCommissions()
            ]);
            setTargetInstances([...(h.commissions || []), ...(h.independents || [])]);
            setIndicators(ind);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    // Enforcer de seguridad para normalizar syncMode a la modalidad simplificada autorizada
    useEffect(() => {
        if (syncMode !== 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA') {
            console.log("[SECURITY_UX_GUARD] Normalizing syncMode to CATALOG_AND_ORDER_KEEP_DESTINATION_DATA");
            setSyncMode('CATALOG_AND_ORDER_KEEP_DESTINATION_DATA');
        }
    }, [syncMode]);

    const handleRemoteValidation = async () => {
        if (!selectedIndicator || selectedTargets.length === 0) return;

        let token: string;
        try {
            token = db.getGovSecureTokenOrThrow();
        } catch (e: any) {
            showToast(e.message || "Error de sesión", "error");
            return;
        }

        setRemotePreviewLoading(true);
        setShowRemotePreviewModal(true);

        const results: Record<string, any> = {};
        for (const targetId of selectedTargets) {
            results[targetId] = { loading: true };
        }
        setRemotePreviewResults({ ...results });

        const activeYear = 2026; // Ciclo de planificación activo inequívoco en la base de código actual

        try {
            // Resolver planes de forma puntual
            const planResolutions = await db.resolvePointWorkPlans(
                [selectedIndicator.id, ...selectedTargets],
                activeYear
            );

            const sourceResolution = planResolutions[selectedIndicator.id];

            for (const targetId of selectedTargets) {
                const target = targetInstances.find(t => t.id === targetId);
                if (!target) {
                    results[targetId] = {
                        status: 'FAILED',
                        error: "Instancia destino no encontrada en catálogo local."
                    };
                    setRemotePreviewResults({ ...results });
                    continue;
                }

                // Validar plan de origen
                if (!sourceResolution || sourceResolution.error_code === 'PLAN_NOT_FOUND') {
                    results[targetId] = {
                        status: 'PLAN_NOT_FOUND',
                        error: "La instancia origen no tiene un plan de trabajo activo para el año 2026."
                    };
                    setRemotePreviewResults({ ...results });
                    continue;
                }

                if (sourceResolution.error_code === 'PLAN_AMBIGUOUS') {
                    results[targetId] = {
                        status: 'PLAN_AMBIGUOUS',
                        error: `La instancia origen tiene múltiples planes activos en 2026: ${sourceResolution.candidates?.map(c => `${c.id} (${c.status})`).join(', ') || ''}`
                    };
                    setRemotePreviewResults({ ...results });
                    continue;
                }

                // Validar plan de destino
                const destResolution = planResolutions[targetId];
                if (!destResolution || destResolution.error_code === 'PLAN_NOT_FOUND') {
                    results[targetId] = {
                        status: 'PLAN_NOT_FOUND',
                        error: "La instancia destino no tiene un plan de trabajo activo para el año 2026."
                    };
                    setRemotePreviewResults({ ...results });
                    continue;
                }

                if (destResolution.error_code === 'PLAN_AMBIGUOUS') {
                    results[targetId] = {
                        status: 'PLAN_AMBIGUOUS',
                        error: `La instancia destino tiene múltiples planes activos en 2026: ${destResolution.candidates?.map(c => `${c.id} (${c.status})`).join(', ') || ''}`
                    };
                    setRemotePreviewResults({ ...results });
                    continue;
                }

                const sourcePlanId = sourceResolution.plan_id!;
                const targetPlanId = destResolution.plan_id!;

                try {
                    const payload = {
                        source_plan_id: sourcePlanId,
                        target_plan_id: targetPlanId,
                        mode: "SOLO_ITEMS_ESTRUCTURA",
                        preserve_target_data: true,
                        replace_existing: false,
                        copy_goals: false,
                        copy_progress: false,
                        copy_evidence: false,
                        execute_real: false,
                        phase: "2E_ITEMS_STRUCTURE_ONLY"
                    };

                    const data = await invokeStructureSyncEdge(token, payload);

                    if (typeof data !== 'object' || data === null) {
                        throw new Error("Respuesta técnica no válida: Se recibió HTML/Texto en lugar de JSON.");
                    }

                    if (data.blocked) {
                        results[targetId] = {
                            status: 'BLOCKED_SAFE',
                            httpStatus: 200,
                            requestId: data.requestId || data.cfRay || data.request_id || "No disponible",
                            summary: "Bloqueo preventivo de simulación. Ningún cambio aplicado."
                        };
                    } else {
                        const summaryLines: string[] = [];
                        if (data.inserted_items !== undefined) summaryLines.push(`A insertar: ${data.inserted_items}`);
                        if (data.updated_items !== undefined) summaryLines.push(`A actualizar: ${data.updated_items}`);
                        if (data.deleted_items !== undefined) summaryLines.push(`A eliminar: ${data.deleted_items}`);

                        results[targetId] = {
                            status: 'VALIDATED',
                            httpStatus: 200,
                            requestId: data.requestId || data.cfRay || data.request_id || "No disponible",
                            summary: summaryLines.join(' | ') || "Simulación remota completada con éxito."
                        };
                    }
                } catch (err: any) {
                    console.error(`[REMOTE_PREVIEW_ERROR] Target ${targetId}:`, err);
                    const summary = getStructureSyncErrorSummary(err);
                    results[targetId] = {
                        status: 'FAILED',
                        httpStatus: summary.httpStatus || null,
                        requestId: summary.requestId || "No disponible",
                        error: err.message || "Error al invocar la Edge Function remota."
                    };
                }
                setRemotePreviewResults({ ...results });
            }
        } catch (globalErr: any) {
            console.error("[REMOTE_PREVIEW_GLOBAL_ERROR]:", globalErr);
            showToast(`Fallo en la previsualización: ${globalErr.message}`, "error");
        } finally {
            setRemotePreviewLoading(false);
            setPreviewFingerprint(generateFingerprint());
        }
    };
    const handleExecuteSelection = async () => {
        if (!selectedIndicator || selectedTargets.length === 0) return;
        if (user?.role !== 'SUPER_ADMIN') {
            alert('Solo SUPER_ADMIN puede ejecutar la sincronizacion real.');
            return;
        }
        if (executionLocked) {
            alert('Hay una ejecucion en curso, por favor espere.');
            return;
        }
        if (previewFingerprint === null || previewFingerprint !== generateFingerprint()) {
            alert('La seleccion ha cambiado despues de la ultima validacion. Por favor, vuelva a Validar Remotamente.');
            return;
        }

        const confirmText = prompt(`EJECUCION REAL (NO SIMULADA)\n\nOrigen: ${selectedIndicator.name}\nDestinos: ${selectedTargets.length}\nModalidad: SOLO_ITEMS_ESTRUCTURA (Todas las secciones)\nMetas, progreso y evidencias SERAN PROTEGIDOS.\n\nEscriba "EJECUTAR SINCRONIZACION CONTROLADA" para confirmar:`);
        if (confirmText !== "EJECUTAR SINCRONIZACION CONTROLADA") {
            alert("Operacion cancelada.");
            return;
        }

        let token: string;
        try {
            token = db.getGovSecureTokenOrThrow();
        } catch (e: any) {
            showToast(e.message || "Error de sesion", "error");
            return;
        }

        setRemotePreviewLoading(true);
        setExecutionLocked(true);
        const results = { ...remotePreviewResults };
        const activeYear = 2026;

        try {
            const planResolutions = await db.resolvePointWorkPlans(
                [selectedIndicator.id, ...selectedTargets],
                activeYear
            );
            const sourceResolution = planResolutions[selectedIndicator.id];

            for (const targetId of selectedTargets) {
                const destResolution = planResolutions[targetId];
                if (!sourceResolution?.plan_id || !destResolution?.plan_id) continue;

                try {
                    const payload = {
                        source_plan_id: sourceResolution.plan_id,
                        target_plan_id: destResolution.plan_id,
                        mode: "SOLO_ITEMS_ESTRUCTURA",
                        preserve_target_data: true,
                        replace_existing: false,
                        copy_goals: false,
                        copy_progress: false,
                        copy_evidence: false,
                        execute_real: true,
                        phase: "2E_ITEMS_STRUCTURE_ONLY"
                    };

                    const data = await invokeStructureSyncEdge(token, payload);

                    if (typeof data !== 'object' || data === null) throw new Error("Respuesta tecnica no valida.");

                    if (data.blocked) {
                        results[targetId] = {
                            status: 'BLOCKED_SAFE',
                            httpStatus: 200,
                            requestId: data.requestId || "No disponible",
                            summary: "Bloqueo preventivo. No se escribieron datos."
                        };
                    } else {
                        const summaryLines: string[] = [];
                        if (data.inserted_items !== undefined) summaryLines.push(`Insertados: ${data.inserted_items}`);
                        if (data.updated_items !== undefined) summaryLines.push(`Actualizados: ${data.updated_items}`);
                        if (data.deleted_items !== undefined) summaryLines.push(`Eliminados: ${data.deleted_items}`);
                        results[targetId] = {
                            status: 'APPLIED',
                            httpStatus: 200,
                            requestId: data.requestId || "No disponible",
                            summary: summaryLines.join(' | ') || "Ejecucion completada con exito."
                        };
                    }
                } catch (err: any) {
                    console.error(`[EXECUTE_ERROR] Target ${targetId}:`, err);
                    const summary = getStructureSyncErrorSummary(err);
                    results[targetId] = {
                        status: 'FAILED',
                        httpStatus: summary.httpStatus || null,
                        requestId: summary.requestId || "No disponible",
                        error: err.message || "Error al invocar la Edge Function."
                    };
                }
                setRemotePreviewResults({ ...results });
            }
        } catch (globalErr: any) {
            console.error("[EXECUTE_GLOBAL_ERROR]:", globalErr);
            showToast(`Fallo en la ejecucion: ${globalErr.message}`, "error");
        } finally {
            setRemotePreviewLoading(false);
            setExecutionLocked(false);
            showToast("Ejecucion procesada para todos los destinos seleccionados.", "success");
        }
    };

    const handleSync = async () => {
        if (!selectedIndicator || selectedTargets.length === 0) return;

        // Enforcer absoluto de modalidad de sincronización
        const enforcedSyncMode: db.IndicatorSyncSafeMode = 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA';

        const modeLabelMap: Record<string, string> = {
            CATALOG_ONLY_KEEP_DESTINATION_DATA: 'Sincronizar indicadores sin tocar datos (Modo 1)',
            CATALOG_AND_ORDER_KEEP_DESTINATION_DATA: 'Sincronizar indicadores y orden conservando datos (Modo 2)'
        };

        const confirmMsgMap: Record<string, string> = {
            CATALOG_ONLY_KEEP_DESTINATION_DATA: 'Sincronizarará indicadores conservando orden, metas, avances, notas y checklist del destino.',
            CATALOG_AND_ORDER_KEEP_DESTINATION_DATA: 'Sincronizarará indicadores y orden conservando metas, avances, notas y checklist del destino.'
        };

        const confirmSync = confirm(`¿Desea ejecutar "${modeLabelMap[enforcedSyncMode]}" desde "${selectedIndicator.name}" hacia las ${selectedTargets.length} instancias seleccionadas?\n\n${confirmMsgMap[enforcedSyncMode]}`);
        if (!confirmSync) return;

        setSyncing(true);
        try {
            await db.syncIndicatorsSafe(selectedIndicator.id, selectedTargets, enforcedSyncMode);
            showToast("Sincronización estratégica multinivel completada con éxito.");
            setSyncing(false);
            setSelectedTargets([]);
            setSelectedIndicator(null);
        } catch (e: any) {
            console.error("[SYNC_ERROR]", e);
            showToast(`Error en el motor de sincronización: ${e.message || 'Error desconocido'}`);
            setSyncing(false);
        }
    };

    const toggleGroup = (type: string) => {
        const ids = targetInstances
            .filter(i => normalize(i.type) === normalize(type))
            .map(i => i.id);

        if (ids.every(id => selectedTargets.includes(id))) {
            setSelectedTargets(selectedTargets.filter(id => !ids.includes(id)));
        } else {
            setSelectedTargets([...new Set([...selectedTargets, ...ids])]);
        }
    };

    const filteredTargets = targetInstances
        .filter(i => {
            const matchesSearch = (i.name || '').toLowerCase().includes(searchText.toLowerCase());
            const matchesType = typeFilter === 'TODOS' || normalize(i.type) === normalize(typeFilter);
            return matchesSearch && matchesType;
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));

    if (loading) return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-12">
            <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mb-6"></div>
            <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em] animate-pulse">Iniciando motor de sincronización iapriori...</p>
        </div>
    );

    return (
        <div className="flex flex-col overflow-y-auto bg-slate-950/80 h-full p-4 space-y-4 w-full max-w-full font-['Outfit']">
            {/* Premium Header Compacto (Max 88px) */}
            <header className="px-5 py-2 border-b border-slate-900 bg-slate-900/60 flex items-center justify-between gap-4 shrink-0 max-h-[88px] rounded-2xl w-full max-w-full">
                <div className="flex items-center gap-3">
                    <div className="bg-emerald-500/20 p-1.5 rounded-lg shrink-0">
                        <Zap size={14} className="text-emerald-400" fill="currentColor" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-xs font-black text-white uppercase tracking-tight leading-none">
                            Sincronización multinivel
                            <span className="text-emerald-500 px-1.5 py-0.5 bg-emerald-500/10 rounded ml-2 text-[7px] tracking-widest leading-none inline-block">v18.27.48-VP-DYNAMIC-WEIGHTS-AUTH-SESSION-FIX</span>
                        </h3>
                        <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Destinos seleccionados: {selectedTargets.length}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded text-[7px] font-black uppercase tracking-widest border border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]">Sandbox seguro</span>
                </div>
            </header>

            {/* STEPPER INFORMATIVO (Max 40px) */}
            <div className="flex items-center justify-between bg-slate-900/40 border border-slate-900 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-500 max-h-[40px] shrink-0 w-full max-w-full">
                <div className="flex items-center gap-1.5">
                    <span className={cn("px-1.5 py-0.5 rounded", selectedIndicator ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400")}>1. Origen</span>
                    <span className="text-slate-700">→</span>
                    <span className={cn("px-1.5 py-0.5 rounded", selectedTargets.length > 0 ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400")}>2. Destinos</span>
                    <span className="text-slate-700">→</span>
                    <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">3. Modalidad</span>
                    <span className="text-slate-700">→</span>
                    <span className="px-1.5 py-0.5 bg-slate-800 text-slate-400 rounded">4. Revisión</span>
                </div>
                <span className="text-[7.5px] text-slate-600 font-bold">Guía Visual</span>
            </div>

            {/* SELECTOR DE ORIGEN HORIZONTAL (Max 72px cerrado) */}
            <div className="bg-slate-900/20 border border-slate-900 p-3 rounded-2xl relative flex flex-col gap-2 shrink-0 w-full max-w-full">
                <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">1. Instancia Origen</span>
                    {selectedIndicator && (
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedIndicator(null);
                                setSelectedTargets([]);
                            }}
                            className="text-[8px] font-black uppercase text-red-400 hover:text-red-300 transition-colors border border-red-500/20 px-2 py-1 rounded-lg bg-red-500/5"
                        >
                            Cambiar origen
                        </button>
                    )}
                </div>

                {!selectedIndicator ? (
                    <div className="relative w-full">
                        <div className="flex gap-2">
                            <div className="relative flex-1 group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-emerald-500 transition-colors" size={12} />
                                <input
                                    type="text"
                                    placeholder="Buscar y seleccionar instancia origen..."
                                    value={sourceSearchText}
                                    onChange={(e) => {
                                        setSourceSearchText(e.target.value);
                                        setShowSourceDropdown(true);
                                    }}
                                    onFocus={() => setShowSourceDropdown(true)}
                                    className="w-full bg-slate-900/50 border border-slate-800 rounded-lg pl-9 pr-3 py-1.5 text-[10px] font-bold text-white placeholder:text-slate-700 focus:outline-none focus:border-emerald-500/50 transition-all uppercase tracking-widest"
                                />
                            </div>
                            <div className="flex bg-slate-950/50 rounded-lg border border-slate-800 p-0.5">
                                {(['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'] as const).map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setSourceTypeFilter(t)}
                                        className={cn(
                                            "px-2 py-1 rounded text-[7px] font-black uppercase tracking-tighter transition-all",
                                            sourceTypeFilter === t ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-400"
                                        )}
                                    >
                                        {t === 'TODOS' ? 'TODAS' : t.slice(0, 3)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {showSourceDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1.5 bg-slate-950 border border-slate-800 rounded-xl shadow-2xl z-50 max-h-[200px] overflow-y-auto divide-y divide-slate-900">
                                {indicators
                                    .filter((c: any) => {
                                        const matchesSearch = (c.name || '').toLowerCase().includes(sourceSearchText.toLowerCase());
                                        const matchesType = sourceTypeFilter === 'TODOS' || normalize(c.type) === normalize(sourceTypeFilter);
                                        return matchesSearch && matchesType;
                                    })
                                    .map((c: any) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedIndicator(c);
                                                setSelectedTargets(prev => prev.filter(id => id !== c.id));
                                                setShowSourceDropdown(false);
                                            }}
                                            className="w-full text-left px-4 py-2.5 hover:bg-slate-900 flex items-center justify-between text-[9px] font-black text-slate-300 hover:text-white uppercase transition-colors"
                                        >
                                            <span className="truncate pr-4">{c.name}</span>
                                            <span className="text-[6.5px] px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-500 shrink-0">
                                                {c.type || 'N/A'}
                                            </span>
                                        </button>
                                    ))
                                }
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/20 p-2 rounded-xl text-[9px] font-black text-emerald-400 uppercase tracking-wider h-[40px]">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[7px] px-1.5 py-0.5 bg-emerald-500/15 border border-emerald-500/20 rounded font-black text-emerald-400 shrink-0">
                                {selectedIndicator.type || 'N/A'}
                            </span>
                            <span className="truncate font-bold text-white">{selectedIndicator.name}</span>
                        </div>
                        <span className="text-[7.5px] text-slate-500 font-bold shrink-0">
                            Plan base listo
                        </span>
                    </div>
                )}
            </div>

            {/* PANEL DE DESTINOS (Ancho 100%) */}
            <div className="bg-slate-900/10 border border-slate-900 p-3.5 rounded-2xl space-y-3 flex-1 flex flex-col min-h-0 overflow-hidden w-full max-w-full">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 shrink-0">
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">2. Instancias Destino</label>
                        <p className="text-[7px] text-slate-500 uppercase font-black">Selecciona las instancias que recibirán la sincronización</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[7.5px] px-2 py-1 bg-slate-900 border border-slate-800 text-slate-400 rounded font-black uppercase tracking-widest">
                            Seleccionados: {selectedTargets.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                const visibleIds = targetInstances
                                    .filter(c => c.id !== selectedIndicator?.id && (typeFilter === 'TODOS' || normalize(c.type) === normalize(typeFilter)) && (c.name || '').toLowerCase().includes(searchText.toLowerCase()))
                                    .map(c => c.id);
                                setSelectedTargets(prev => Array.from(new Set([...prev, ...visibleIds])));
                            }}
                            className="px-2.5 py-1.5 rounded-lg border border-slate-800 text-[8px] font-black uppercase text-slate-400 hover:text-white transition-colors"
                        >
                            Seleccionar visibles
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedTargets([])}
                            className="px-2.5 py-1.5 rounded-lg border border-slate-800 text-[8px] font-black uppercase text-slate-400 hover:text-red-400 transition-colors"
                        >
                            Limpiar
                        </button>
                    </div>
                </div>

                {/* Toolbar de búsqueda y filtros */}
                <div className="flex flex-col sm:flex-row gap-2.5 shrink-0">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-emerald-500 transition-colors" size={12} />
                        <input
                            type="text"
                            placeholder="Buscar destinos..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-800 rounded-lg pl-9 pr-3 py-1.5 text-[9px] font-bold text-white placeholder:text-slate-700 focus:outline-none focus:border-emerald-500/50 transition-all uppercase tracking-widest"
                        />
                    </div>
                    <div className="flex bg-slate-950/50 rounded-lg border border-slate-800 p-0.5">
                        {(['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'] as const).map(t => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTypeFilter(t)}
                                className={cn(
                                    "px-2.5 py-1 rounded text-[7px] font-black uppercase tracking-tighter transition-all",
                                    typeFilter === t ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-400"
                                )}
                            >
                                {t === 'TODOS' ? 'TODAS' : t === 'COMISION' ? 'COMISIONES' : t === 'COMITE' ? 'COMITÉS' : 'CONSEJERÍAS'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid Responsivo de Destinos */}
                <div className="flex-1 overflow-y-auto pr-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                        {filteredTargets
                            .filter(c => c.id !== selectedIndicator?.id)
                            .map((c: any) => {
                                const isChecked = selectedTargets.includes(c.id);
                                return (
                                    <label
                                        key={c.id}
                                        className={cn(
                                            "flex items-center gap-3 p-2.5 rounded-xl border transition-all cursor-pointer select-none min-h-[52px]",
                                            isChecked
                                                ? "bg-emerald-500/10 border-emerald-500/40 hover:bg-emerald-500/15"
                                                : "bg-slate-950/40 border-slate-900 hover:border-slate-800 hover:bg-slate-900/30"
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                if (isChecked) {
                                                    setSelectedTargets(prev => prev.filter(id => id !== c.id));
                                                } else {
                                                    setSelectedTargets(prev => [...prev, c.id]);
                                                }
                                            }}
                                            className="rounded border-slate-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-950 bg-slate-900 shrink-0 w-3.5 h-3.5"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[10px] font-bold text-white uppercase truncate tracking-tight leading-snug">
                                                {c.name}
                                            </p>
                                            <span className="text-[6.5px] px-1.5 py-0.2 bg-slate-900 border border-slate-800 rounded font-black text-slate-500 inline-block mt-0.5">
                                                {c.type || 'N/A'}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })
                        }
                    </div>
                </div>
            </div>

            {/* SELECCIÓN DE MODALIDAD SEGMENTADA (Max 88px) */}
            <div className="bg-slate-900/10 border border-slate-900 p-3 rounded-2xl space-y-2.5 shrink-0 w-full max-w-full">
                <div className="flex items-center justify-between">
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">3. Modalidad de Réplica</label>
                        <p className="text-[7px] text-slate-500 uppercase font-black">Configura la profundidad y alcance de la sincronización de metas</p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                    <button
                        type="button"
                        onClick={() => setSyncMode('CATALOG_AND_ORDER_KEEP_DESTINATION_DATA')}
                        className={cn(
                            "flex-1 py-2 rounded-xl text-[8.5px] font-black uppercase tracking-wider transition-all border",
                            syncMode === 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA'
                                ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                                : "bg-slate-950/40 border-slate-855 text-slate-400 hover:text-white"
                        )}
                    >
                        Solo estructura / formato
                    </button>
                </div>

                {/* Más opciones colapsado */}
                <details className="border border-slate-855 rounded-xl overflow-hidden bg-slate-950/30 p-2 group shrink-0">
                    <summary className="cursor-pointer list-none flex items-center justify-between text-slate-500 hover:text-slate-400 transition-colors">
                        <span className="text-[7.5px] font-black uppercase tracking-widest">Otras opciones / Avanzado</span>
                        <span className="text-[6px] px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded font-black text-slate-500 shrink-0 group-open:hidden">Mostrar</span>
                        <span className="text-[6px] px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded font-black text-slate-500 shrink-0 hidden group-open:inline">Ocultar</span>
                    </summary>
                    <div className="mt-2 text-[7px] text-slate-600 font-bold uppercase space-y-1">
                        <p>Opciones adicionales de repliegue de datos bloqueadas por seguridad.</p>
                    </div>
                </details>
            </div>

            {/* RESUMEN DE OPERACIÓN DE MÁXIMO 64px */}
            <div className="bg-slate-900/5 border border-slate-900 p-2.5 rounded-xl flex items-center justify-between gap-3 text-[8px] font-black uppercase tracking-widest text-slate-500 max-h-[64px] shrink-0 w-full max-w-full">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
                    <span>Origen: <span className="text-white font-bold">{selectedIndicator?.name || 'N/A'}</span></span>
                    <span className="text-slate-700">·</span>
                    <span>Destinos: <span className="text-emerald-400 font-bold">{selectedTargets.length}</span></span>
                    <span className="text-slate-700">·</span>
                    <span>Modalidad: <span className="text-cyan-400 font-bold">{syncMode === 'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS' ? 'Estructura + Metas' : 'Solo Estructura / Formato'}</span></span>
                    <span className="text-slate-700">·</span>
                    <span>Secciones: <span className="text-white font-bold">Preservadas</span></span>
                    <span className="text-slate-700">·</span>
                    <span>Datos Protegidos: <span className="text-emerald-400 font-bold">SÍ</span></span>
                </div>
            </div>

            {/* STICKY FOOTER (Max 72px) */}
            <div className="sticky bottom-0 z-10 bg-slate-900 border border-slate-900 rounded-2xl p-3 flex items-center justify-between gap-4 shadow-[0_-8px_25px_rgba(0,0,0,0.4)] shrink-0 w-full max-w-full max-h-[72px]">
                <div className="flex items-center gap-2 text-red-500 font-black uppercase text-[7.5px] tracking-wider min-w-0">
                    <ShieldAlert size={14} className="text-red-500 shrink-0" />
                    <span className="truncate">EJECUCIÓN REAL BLOQUEADA · BLOCK_SYNC = true</span>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedTargets([]);
                            setSelectedIndicator(null);
                        }}
                        className="bg-slate-850 hover:bg-slate-800 text-slate-400 px-3.5 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all border border-slate-800"
                    >
                        Limpiar
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowLocalPayloadPreview(true)}
                        disabled={!selectedIndicator || selectedTargets.length === 0}
                        className={cn(
                            "px-3.5 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all flex items-center gap-1 border",
                            selectedIndicator && selectedTargets.length > 0
                                ? "bg-indigo-600/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-600 hover:text-white"
                                : "bg-slate-900 border-slate-800 text-slate-500 cursor-not-allowed"
                        )}
                    >
                        <Eye size={12} />
                        VER PAYLOAD
                    </button>
                    <button
                        type="button"
                        onClick={handleRemoteValidation}
                        disabled={!selectedIndicator || selectedTargets.length === 0 || remotePreviewLoading}
                        className={cn(
                            "px-3.5 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all flex items-center gap-1 border",
                            selectedIndicator && selectedTargets.length > 0 && !remotePreviewLoading
                                ? "bg-emerald-600/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-600 hover:text-white"
                                : "bg-slate-900 border-slate-800 text-slate-500 cursor-not-allowed"
                        )}
                    >
                        {remotePreviewLoading ? (
                            <div className="w-3 h-3 border border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                        ) : (
                            <Zap size={12} />
                        )}
                        VALIDAR REMOTAMENTE
                    </button>
                    {(() => {
                        const isGuardValid = user?.role === 'SUPER_ADMIN'
                            && selectedIndicator
                            && selectedTargets.length > 0
                            && !selectedTargets.includes(selectedIndicator.id)
                            && new Set(selectedTargets).size === selectedTargets.length
                            && previewFingerprint !== null
                            && previewFingerprint === generateFingerprint()
                            && selectedTargets.every(t => remotePreviewResults[t]?.status === 'BLOCKED_SAFE' || remotePreviewResults[t]?.status === 'VALIDATED')
                            && !executionLocked
                            && !remotePreviewLoading;

                        return (
                            <button
                                type="button"
                                disabled={!isGuardValid}
                                onClick={handleExecuteSelection}
                                className={cn("px-3.5 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest flex items-center gap-1 transition-all", isGuardValid ? "bg-red-600 border border-red-500 text-white shadow-[0_0_15px_rgba(220,38,38,0.5)] cursor-pointer hover:bg-red-500" : "bg-red-900/30 border border-red-900/50 text-red-500/50 cursor-not-allowed")}
                            >
                                <ShieldAlert size={12} />
                                Ejecutar Selección
                            </button>
                        );
                    })()}
                </div>
            </div>

            {/* MODAL DE PREVISUALIZACION LOCAL DEL PAYLOAD */}
            {showLocalPayloadPreview && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[250] flex items-center justify-center p-4">
                    <div className="bg-slate-950 border border-slate-800 w-full max-w-2xl rounded-3xl flex flex-col overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center bg-indigo-950/30 border-b border-indigo-500/20 p-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <Eye size={20} className="text-indigo-400" />
                                <div>
                                    <h4 className="text-indigo-300 text-xs font-black uppercase tracking-widest leading-none">VER PAYLOAD LOCAL</h4>
                                    <p className="text-[7px] text-red-400 font-bold uppercase tracking-widest mt-1">PREVISUALIZACIÓN LOCAL — NO EJECUTADA</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowLocalPayloadPreview(false)}
                                className="text-slate-500 hover:text-white p-2"
                                aria-label="Cerrar previsualización"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-4">
                            <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-normal">
                                A continuación se muestra la estructura del payload local que se enviaría para la sincronización. Ninguna petición ha sido realizada al servidor y execute_real está configurado estrictamente como false.
                            </p>
                            <div className="bg-black/50 border border-slate-900 rounded-2xl p-4 font-mono text-[9px] text-slate-300 overflow-x-auto">
                                <pre className="whitespace-pre-wrap select-all">
{JSON.stringify({
    origin_id: selectedIndicator?.id,
    origin_name: selectedIndicator?.name,
    origin_type: selectedIndicator?.type,
    destinations: targetInstances.filter(t => selectedTargets.includes(t.id)).map((d: any) => ({
        id: d.id,
        name: d.name,
        type: d.type
    })),
    mode: syncMode,
    backend_mode: "SOLO_ITEMS_ESTRUCTURA",
    preserve_target_data: true,
    replace_existing: false,
    copy_goals: false,
    copy_progress: false,
    copy_evidence: false,
    execute_real: false,
    execution_status: "NO EJECUTADO"
}, null, 2)}
                                </pre>
                            </div>
                        </div>

                        <div className="bg-slate-900/50 border-t border-slate-900 p-4 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowLocalPayloadPreview(false)}
                                className="bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-600 hover:text-white px-5 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE PREVISUALIZACIÓN TRANSACCIONAL REMOTA */}
            {showRemotePreviewModal && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[250] flex items-center justify-center p-4">
                    <div className="bg-slate-950 border border-slate-800 w-full max-w-3xl rounded-3xl flex flex-col overflow-hidden shadow-2xl max-h-[85vh]">
                        <div className="flex justify-between items-center bg-emerald-950/30 border-b border-emerald-500/20 p-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <Zap size={20} className="text-emerald-400" fill="currentColor" />
                                <div>
                                    <h4 className="text-emerald-300 text-xs font-black uppercase tracking-widest leading-none">PREVISUALIZACIÓN TRANSACCIONAL REMOTA</h4>
                                    <p className="text-[7px] text-emerald-400 font-bold uppercase tracking-widest mt-1">SIMULACIÓN PRE-TRANSACCIONAL CON EXECUTE_REAL = FALSE</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (!remotePreviewLoading) setShowRemotePreviewModal(false);
                                }}
                                disabled={remotePreviewLoading}
                                className={cn("text-slate-500 hover:text-white p-2", remotePreviewLoading && "cursor-not-allowed opacity-50")}
                                aria-label="Cerrar validación"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-4 flex-1">
                            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-3 text-[8.5px] text-emerald-400 font-bold uppercase tracking-wide space-y-1">
                                <p className="flex items-center gap-2">
                                    <ShieldAlert size={12} />
                                    <span>CONFIRMACIÓN DE SEGURIDAD: EXECUTE_REAL = FALSE forzado.</span>
                                </p>
                                <p className="text-[7.5px] text-slate-400 font-medium normal-case">
                                    Esta operación simula la sincronización en el servidor y ejecuta las consultas en una transacción rollback. No se persiste ningún dato en la base de datos de los destinos.
                                </p>
                            </div>

                            <div className="space-y-2.5">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Resultados de Simulación por Destino:</p>
                                <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                                    {selectedTargets.map(targetId => {
                                        const target = targetInstances.find(t => t.id === targetId);
                                        const result = remotePreviewResults[targetId];

                                        if (!target) return null;

                                        return (
                                            <div key={targetId} className="bg-slate-900/40 border border-slate-900 rounded-xl p-3 flex flex-col gap-2">
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] font-bold text-white uppercase truncate">{target.name}</p>
                                                        <span className="text-[6.5px] px-1.5 py-0.2 bg-slate-950 border border-slate-800 rounded font-black text-slate-500 inline-block mt-0.5">{target.type || 'N/A'}</span>
                                                    </div>
                                                    <div className="shrink-0 flex items-center gap-2">
                                                        {result?.loading ? (
                                                            <span className="text-[8px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-black uppercase tracking-wider animate-pulse flex items-center gap-1">
                                                                <div className="w-1.5 h-1.5 border border-slate-500 border-t-transparent rounded-full animate-spin"></div>
                                                                Simulando...
                                                            </span>
                                                        ) : result?.status === 'VALIDATED' ? (
                                                            <span className="text-[8px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-black uppercase tracking-wider flex items-center gap-1">
                                                                ✓ VALIDATED
                                                            </span>
                                                        ) : result?.status === 'BLOCKED_SAFE' ? (
                                                            <span className="text-[8px] bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded font-black uppercase tracking-wider flex items-center gap-1">
                                                                ⚠ BLOCKED_SAFE
                                                            </span>
                                                        ) : result?.status === 'PLAN_NOT_FOUND' ? (
                                                            <span className="text-[8px] bg-amber-500/10 border border-amber-500/20 text-amber-400 px-2 py-0.5 rounded font-black uppercase tracking-wider flex items-center gap-1">
                                                                ✗ PLAN_NOT_FOUND
                                                            </span>
                                                        ) : result?.status === 'PLAN_AMBIGUOUS' ? (
                                                            <span className="text-[8px] bg-orange-500/10 border border-orange-500/20 text-orange-400 px-2 py-0.5 rounded font-black uppercase tracking-wider flex items-center gap-1">
                                                                ✗ PLAN_AMBIGUOUS
                                                            </span>
                                                        ) : (
                                                            <span className="text-[8px] bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-0.5 rounded font-black uppercase tracking-wider flex items-center gap-1">
                                                                ✗ FAILED
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {!result?.loading && (
                                                    <div className="grid grid-cols-2 gap-2 border-t border-slate-900 pt-2 font-mono text-[7.5px] text-slate-400">
                                                        <div>HTTP STATUS: <span className={result?.status === 'VALIDATED' || result?.status === 'BLOCKED_SAFE' ? "text-emerald-400" : "text-red-400"}>{result?.httpStatus !== undefined && result?.httpStatus !== null ? result.httpStatus : 'No disponible'}</span></div>
                                                        <div>REQUEST ID: <span className="text-slate-300 truncate block">{result?.requestId || 'No disponible'}</span></div>
                                                        <div className="col-span-2 mt-1 border-t border-slate-950 pt-1">
                                                            <p className="text-[7px] text-slate-500 font-bold uppercase">Resumen / Detalle:</p>
                                                            <p className="text-[8px] text-slate-300 font-sans mt-0.5 normal-case">
                                                                {result?.status === 'VALIDATED' || result?.status === 'BLOCKED_SAFE' ? result.summary : result?.error || 'Error desconocido'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-900/50 border-t border-slate-900 p-4 flex justify-between items-center shrink-0">
                            <span className="text-[7px] text-slate-500 font-bold uppercase">
                                {remotePreviewLoading ? "Validación remota en curso..." : "Validación remota terminada"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowRemotePreviewModal(false)}
                                disabled={remotePreviewLoading}
                                className={cn(
                                    "bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-600 hover:text-white px-5 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                    remotePreviewLoading && "cursor-not-allowed opacity-50 bg-slate-900 border-slate-800 text-slate-500"
                                )}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


const SidebarItem = ({ icon: Icon, label, path, active, isCollapsed }: any) => {
    const [searchParams] = useSearchParams();
    const vpId = searchParams.get('vpId');
    const fullPath = vpId ? `${path}${path.includes('?') ? '&' : '?'}vpId=${vpId}` : path;

    return (
        <Link to={fullPath} title={isCollapsed ? label : undefined} className={cn(
            "flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all group relative overflow-hidden mx-3",
            active ? "bg-emerald-500 shadow-lg shadow-emerald-500/20" : "hover:bg-slate-900/50",
            isCollapsed && "justify-center px-0 mx-2"
        )}>
            <Icon size={20} className={cn("transition-colors shrink-0", active ? "text-white" : "text-white/70 group-hover:text-slate-300")} strokeWidth={active ? 2.5 : 2} />
            {!isCollapsed && <span className={cn("text-sm font-bold transition-colors whitespace-nowrap", active ? "text-white" : "text-white/60 group-hover:text-slate-200")}>{label}</span>}
        </Link>
    );
};

const AdminShield = ({ user, children, requireRecoveryManage = false }: { user: any, children: React.ReactNode, requireRecoveryManage?: boolean }) => {
    // RBAC HOTFIX v18.18.2 Ã¢â‚¬â€ Solo SUPER_ADMIN, ADMINISTRADOR y DIRECTOR_NACIONAL acceden a rutas admin.
    // VICE_PRESIDENT removido: tenía acceso a /admin-backups permitiendo restoreCommit() global.
    const isAuthorized = requireRecoveryManage
        ? hasCapability(user?.role, 'recovery.manage')
        : user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR' || user?.role === 'DIRECTOR_NACIONAL';
    if (!isAuthorized) return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-12 text-center space-y-6">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 border border-red-500/20">
                <Shield size={40} />
            </div>
            <div className="space-y-2">
                <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Acceso Denegado</h2>
                <p className="text-white/70 text-xs font-bold uppercase tracking-widest max-w-xs">
                    {requireRecoveryManage
                        ? 'SOLO SUPER_ADMIN PUEDE OPERAR EL RECOVERY CENTER.'
                        : 'SOLO EL ADMINISTRADOR NACIONAL TIENE PERMISO PARA ACCEDER A ESTE MODULO ESTRATEGICO.'}
                </p>
            </div>
        </div>
    );
    return <>{children}</>;
};

const Layout = ({ children, user, onLogout, globalMonth, setGlobalMonth }: any) => {
    const { isVP, isND, isPresident, isSA, isInstance, isGlobal } = useRole(user);
    const { pathname } = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isDesktopMenuCollapsed, setIsDesktopMenuCollapsed] = useState(false);
    const canSeeTechnicalVersion = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR' || user?.role === 'DIRECTOR_NACIONAL' || user?.role === 'ADMIN';

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    const menu = [
        // Dashboard y Perfiles (Talento)
        { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
        { icon: Users, label: 'Talento', path: '/talent' },

        // Otros Modulos
        { icon: Building2, label: 'Estructura', path: '/hierarchy' },
        { icon: Target, label: 'Planeación', path: '/planning', module: 'Planeación' },
        { icon: BarChart3, label: 'Seguimiento', path: '/tracking', module: 'Seguimiento' },
        { icon: Activity, label: 'Control Operativo', path: '/operational-control', module: 'Control Operativo' },
        { icon: FileText, label: 'Reportes', path: '/reports', module: 'Reportes' },
        { icon: BookOpen, label: 'Memoria', path: '/memory', module: 'Memoria' },

        // Administración
        { icon: RefreshCw, label: 'Sincronización', path: '/sync', adminOnly: true },
        { icon: Users, label: 'Usuarios', path: '/admin-users', adminOnly: true },
        { icon: Settings, label: 'Configuración', path: '/admin-settings', adminOnly: true },
        { icon: Shield, label: 'Backups', path: '/admin-backups', adminOnly: true, recoveryOnly: true },
        { icon: PieChartIcon, label: 'OrgStructure', path: '/orgstructure' },
        { icon: Zap, label: 'Escritorio IA', path: '/ai-desk' },
    ].filter(item => {
        const itemModule = item.module || item.label;
        if ((item as any).recoveryOnly) {
            return hasCapability(user?.role, 'recovery.manage');
        }
        if (item.adminOnly) {
            if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR' || user?.role === 'DIRECTOR_NACIONAL') return true;
            return false;
        }
        const normalizedItemModule = normalize(itemModule);
        if (normalizedItemModule === normalize('Planeacion') || normalizedItemModule === normalize('Seguimiento')) {
            return canViewAuthorizedModule(user, itemModule);
        }
        return canAccessModule(user, itemModule);
    });

    const isRealAdmin = ['SUPER_ADMIN', 'ADMINISTRADOR', 'ADMIN'].includes(String(user?.role || '').toUpperCase());

    return (
        <div className="flex h-screen bg-black font-['Outfit'] overflow-hidden selection:bg-emerald-500/30">

            {/* Mobile Toggle */}
            <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="fixed bottom-6 right-6 z-[100] w-14 h-14 bg-emerald-600 rounded-full flex items-center justify-center text-white md:hidden shadow-2xl shadow-emerald-500/30 border border-emerald-500/20"
            >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <aside className={cn(
                "fixed inset-y-0 left-0 shrink-0 bg-slate-950 border-r border-slate-900 flex flex-col pt-6 z-[60] transition-all duration-300 ease-in-out md:relative md:translate-x-0 overflow-hidden",
                isMobileMenuOpen ? "translate-x-0 w-72 shadow-[0_0_50px_rgba(16,185,129,0.1)]" : "-translate-x-full",
                isDesktopMenuCollapsed ? "md:w-20" : "md:w-72"
            )}>
                <button
                    onClick={() => setIsDesktopMenuCollapsed(!isDesktopMenuCollapsed)}
                    className="hidden md:flex absolute top-6 -right-3 bg-slate-800 text-white rounded-full p-1.5 shadow-lg border border-slate-700 z-[70] hover:bg-emerald-500 transition-colors"
                >
                    {isDesktopMenuCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
                <div className="flex flex-col gap-3 mb-4 px-4 shrink-0 mt-2">
                    <div className="flex flex-col gap-3">
                        <div className={cn("flex flex-col gap-3", isDesktopMenuCollapsed ? "items-center" : "items-start")}>
                            <div className="shrink-0 flex items-center justify-center transition-all hover:scale-105">
                                <img
                                    src="/assets/logo-coparmex.jpg"
                                    alt="COPARMEX 2026 - Gobernanza Institucional"
                                    title={isRealAdmin && !isDesktopMenuCollapsed ? `COPARMEX 2026 - Gobernanza Institucional ${getShortVersion(APP_VERSION)}` : "COPARMEX 2026 - Gobernanza Institucional"}
                                    className={cn("object-contain rounded-lg drop-shadow-md bg-white p-1", isDesktopMenuCollapsed ? "w-10 h-10" : "h-10 w-auto")}
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/logo-coparmex.png"; }}
                                />
                            </div>
                            {!isDesktopMenuCollapsed && (
                                <div className="flex flex-col shrink-0 pr-2" style={{ contain: 'layout' }}>
                                    <h1 className="text-white text-[11px] font-black uppercase tracking-widest leading-none whitespace-nowrap">
                                        COPARMEX 2026
                                    </h1>
                                    <p className="text-emerald-400 text-[8px] font-bold uppercase tracking-wider mt-1 whitespace-nowrap flex items-center gap-1">
                                        <span>GOBERNANZA INSTITUCIONAL</span>
                                        {isRealAdmin && getShortVersion(APP_VERSION) ? (
                                            <>
                                                <span className="opacity-60 text-emerald-500">·</span>
                                                <span className="font-extrabold text-emerald-300">{getShortVersion(APP_VERSION)}</span>
                                            </>
                                        ) : null}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <nav className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden scrollbar-none pr-2">
                    {menu.map(item => (
                        <SidebarItem key={item.path} {...item} active={pathname === item.path} isCollapsed={isDesktopMenuCollapsed} />
                    ))}
                </nav>

                <div className={cn("p-4 border-t border-slate-900 space-y-4 flex flex-col items-center shrink-0", isDesktopMenuCollapsed ? "px-2" : "")}>
                    <div className="flex items-center gap-3 p-2 bg-slate-900/30 rounded-2xl group transition-all border border-slate-900 relative w-full shrink-0">
                        <div className="w-11 h-11 shrink-0 bg-slate-800 rounded-2xl flex items-center justify-center font-black text-emerald-500 shadow-xl text-sm border border-slate-800">
                            {user?.name?.[0]}
                        </div>
                        {!isDesktopMenuCollapsed && (
                            <div className="overflow-hidden flex-1">
                                <p className="text-xs font-black text-white item-name font-black tracking-tight">{user?.name}</p>
                                <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mt-0.5 item-name font-black tracking-tight">{user?.role}</p>
                            </div>
                        )}
                    </div>

                    {!isDesktopMenuCollapsed && (
                        <div className="w-full px-1 mb-2">
                            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 space-y-1">
                                <label className="text-[7px] font-black text-emerald-500 uppercase tracking-widest block">Periodo Global</label>
                                <select
                                    value={globalMonth}
                                    onChange={(e) => setGlobalMonth(e.target.value)}
                                    className="w-full bg-slate-950 border-none text-[10px] font-black text-white uppercase tracking-widest focus:ring-0 cursor-pointer p-0"
                                >
                                    {MONTHS.map(m => <option key={m} value={m}>{m} 2026</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onLogout}
                        className="w-full flex items-center justify-center gap-2 bg-red-500/5 hover:bg-red-500/10 text-red-500 border border-red-500/10 p-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
                    >
                        <LogOut size={14} /> {!isDesktopMenuCollapsed && 'CERRAR SESIÓN'}
                    </button>

                    <div className={cn("flex flex-col items-center justify-center pt-3 pb-1 border-t border-slate-900/80 w-full transition-all shrink-0", isDesktopMenuCollapsed ? "mt-1 px-1" : "mt-2 px-2")}>
                        {!isDesktopMenuCollapsed && (
                            <span className="text-[8px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-1.5 text-center leading-none">
                                Desarrollado por
                            </span>
                        )}
                        <div className={cn("flex items-center justify-center transition-all duration-200", isDesktopMenuCollapsed ? "w-8 h-8 opacity-75 hover:opacity-100" : "h-11 w-auto max-w-[180px] opacity-95 hover:opacity-100 hover:scale-105")}>
                            <img
                                src="/assets/logo-iapriori.png"
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/logo-iapriori.jpg"; }}
                                className="h-full w-auto object-contain mix-blend-screen drop-shadow-[0_0_8px_rgba(56,189,248,0.25)]"
                                alt="Desarrollado por IAPRIORI"
                                title="Desarrollado por IAPRIORI"
                                aria-label="Desarrollado por IAPRIORI"
                            />
                        </div>
                    </div>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto bg-black p-4 md:p-6 pb-24 md:pb-12 scrollbar-none scroll-smooth min-w-0">
                <div className={cn("mx-auto transition-all duration-300", pathname === '/planning' ? "w-full px-4" : "max-w-7xl")}>
                    {children}

                    {/* El Explorador de VP e Instancias ahora solo es accesible vía el modulo de Estructura (/hierarchy) o Planeacion para evitar ruido visual */}

                    <footer className="mt-20 pt-8 border-t border-slate-900 flex justify-between items-center text-[8px] font-black text-slate-700 uppercase tracking-[0.4em]">
                        <div className="flex items-center gap-4">
                            <div className="flex flex-col gap-1">
                                <span>GOBERNANZA INSTITUCIONAL · DESARROLLADO POR IAPRIORI © 2026</span>
                                <span className="text-slate-800">SISTEMA DE GOBERNANZA, SEGUIMIENTO Y CONTROL ESTRATÉGICO</span>
                            </div>
                        </div>
                        <span className="flex items-center gap-4 bg-slate-900/80 px-6 py-3 rounded-2xl border border-slate-800 shadow-xl">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.6)]"></span>
                            {canSeeTechnicalVersion && (
                                <span className="text-[10px] font-black text-emerald-500 tracking-widest">{APP_VERSION}</span>
                            )}
                        </span>
                    </footer>
                </div>
            </main>
        </div>
    );
};

const SignupPage = ({ onSignup, onBack }: { onSignup: (user: any) => void; onBack: () => void }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const { user } = await db.signUp(email, password, name);
            if (user) {
                onSignup(user);
            }
        } catch (err: any) {
            setError(err.message || 'Error al registrarse');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex items-center justify-center p-6 relative overflow-hidden font-['Outfit']">
            <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-600/10 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[120px] rounded-full"></div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-slate-900/50 backdrop-blur-2xl border border-slate-800 rounded-[3rem] p-12 shadow-2xl relative z-10"
            >
                <header className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-white mb-2 italic">Registro <span className="text-emerald-500">Free Engine</span></h2>
                    <p className="text-slate-400 text-xs">Acceso Ilimitado a la Gobernanza 4.0</p>
                </header>

                <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-white/60 tracking-widest ml-4">Nombre Completo</label>
                        <input
                            type="text"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            placeholder="Juan Perez"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-white/60 tracking-widest ml-4">Correo Institucional</label>
                        <input
                            type="email"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            placeholder="usuario@coparmex.org.mx"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-white/60 tracking-widest ml-4">Contraseña</label>
                        <input
                            type="password"
                            required
                            minLength={6}
                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            placeholder="••••••••"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-500 text-[10px] font-black uppercase">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-widest py-4 rounded-2xl shadow-xl shadow-emerald-900/20 transition-all disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="animate-spin mx-auto" size={20} /> : "Crear Cuenta Gratuita"}
                    </button>
                </form>

                <button onClick={onBack} className="w-full mt-6 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors">
                    ¿Ya tienes cuenta? Iniciar Sesión
                </button>
            </motion.div>
        </div>
    );
};

const LoginPage = ({ onLogin }: { onLogin: (user: any) => void }) => {
    const [showSignup, setShowSignup] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const { user: authUser, authFailed } = await db.signIn(email, password);
            const session = await db.getCurrentSession();
            

            if (session.user) {
                if (authFailed) {
                    alert("Acceso general activo. Para borrar, cierre sesión y vuelva a entrar. Si el problema continúa, la sesión segura no se pudo generar.");
                    localStorage.setItem('firebaseReady', 'false');
                } else {
                    localStorage.setItem('firebaseReady', 'true');
                }
                onLogin(session.user);
            } else {

                setError('Acceso denegado. Verifique su perfil.');
            }
        } catch (err: any) {
            setError(err.message || 'Error al iniciar sesion');
        } finally {
            setLoading(false);
        }
    };

    if (showSignup) return <SignupPage onSignup={onLogin} onBack={() => setShowSignup(false)} />;

    return (
        <div className="min-h-screen bg-black flex items-center justify-center p-6 relative overflow-hidden font-['Outfit']">
            <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-600/10 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[120px] rounded-full"></div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md bg-slate-900/50 backdrop-blur-2xl border border-slate-800 rounded-[3rem] p-12 shadow-2xl relative z-10"
            >
                <header className="text-center mb-10">
                    <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center shadow-2xl shadow-emerald-500/10 mx-auto mb-8 overflow-hidden border border-slate-800 p-2">
                        <img src="/assets/logo-coparmex.jpg" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/logo-coparmex.png"; }} alt="COPARMEX" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="text-3xl font-bold text-white tracking-tight italic">Gobernanza<br /><span className="text-emerald-500 italic">Institucional</span></h1>
                </header>

                <form onSubmit={handleSignIn} className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-white/60 tracking-widest ml-4">Correo</label>
                        <input
                            type="email"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="usuario@coparmex.org.mx"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-white/60 tracking-widest ml-4">Contraseña</label>
                        <input
                            type="password"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="••••••••"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-500 text-[10px] font-black uppercase">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-widest py-5 rounded-2xl shadow-xl shadow-emerald-900/20 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                    >
                        {loading ? <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div> : <><Zap size={18} fill="currentColor" /> Acceso Estratégico</>}
                    </button>
                </form>



                <footer className="mt-8 text-center flex flex-col items-center gap-4">
                    <div className="bg-slate-950 px-5 py-3 rounded-2xl border border-slate-800 shadow-xl shadow-black/50 flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                            <img src="/assets/logo-iapriori.png" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/logo-iapriori.jpg"; }} className="w-full h-full object-contain mix-blend-screen" alt="IAPRIORI" />
                        </div>
                        <div className="flex flex-col text-left">
                            <span className="text-[9px] font-black text-white/70 uppercase tracking-widest leading-none">Desarrollado por</span>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-emerald-500 font-black uppercase tracking-[0.2em] text-xs">IAPRIORI © 2026</span>
                            </div>
                        </div>
                    </div>
                </footer>
            </motion.div>
        </div>
    );
};

const App_CSS = `
  @media print {
    .no-print, .print\\:hidden, aside, button { display: none !important; }
    body, html { background: white !important; color: black !important; height: auto !important; overflow: visible !important; }
    #root, .h-screen, .overflow-hidden, .overflow-y-auto { height: auto !important; overflow: visible !important; display: block !important; }
    main { padding: 0 !important; margin: 0 !important; width: 100% !important; overflow: visible !important; display: block !important; }
    .bg-black, .bg-slate-900, .bg-slate-950, .bg-slate-800 { background: white !important; border-color: #ddd !important; }
    .text-white, .text-slate-300, .text-slate-400, .text-slate-500, .text-slate-600 { color: black !important; }
    .border-slate-800, .border-slate-900 { border-color: #eee !important; }
    .shadow-2xl, .shadow-xl { box-shadow: none !important; }
    .rounded-\\[3rem\\], .rounded-\\[2\\.5rem\\] { border-radius: 0 !important; }
    .fixed { position: static !important; }
    .animate-in, .transition-all { animation: none !important; transition: none !important; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    h1, h2, h3 { page-break-after: avoid; }
  }

  /* UX Enhancements */
  :root {
    --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
  }
  .pb-safe { padding-bottom: calc(16px + var(--safe-area-inset-bottom)); }
  .touch-target { min-height: 44px; min-width: 44px; }
`;

/** Componente para redireccionar al usuario según sus permisos de entrada */
const LandingRedirect = ({ user }: { user: any }) => {
    const modules = getUserModules(user).map(m => normalize(m));
    const canSeeDashboard = modules.includes(normalize('Dashboard'));
    const canSeeTracking = modules.includes(normalize('Seguimiento'));

    if (canSeeDashboard) return <Dashboard user={user} />;
    if (canSeeTracking) return <Navigate to="/tracking" replace />;

    // Mapeo de rutas para otros módulos
    const modulePaths: Record<string, string> = {
        [normalize('Dashboard')]: '/',
        [normalize('Seguimiento')]: '/tracking',
        [normalize('Planeacion')]: '/planning',
        [normalize('Talento')]: '/talent',
        [normalize('Reportes')]: '/reports',
        [normalize('Memoria')]: '/memory',
        [normalize('Estructura')]: '/hierarchy',
        [normalize('OrgStructure')]: '/orgstructure',
        [normalize('Escritorio IA')]: '/ai-desk'
    };

    const firstAvailable = modules[0];
    const targetPath = modulePaths[firstAvailable] || '/';

    return <Navigate to={targetPath} replace />;
};

const Safe404Fallback = () => {
    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-12 text-center space-y-6">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 border border-red-500/20 shadow-lg shadow-red-500/5 backdrop-blur-sm animate-pulse">
                <ShieldX size={40} />
            </div>
            <div className="space-y-2 max-w-xs">
                <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Módulo No Encontrado</h2>
                <p className="text-white/70 text-xs font-bold uppercase tracking-widest leading-relaxed">
                    La página, módulo o ruta que intentas solicitar no existe o no se encuentra disponible.
                </p>
                <Link
                    to="/tracking"
                    className="inline-flex items-center gap-2 mt-6 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[10px] uppercase tracking-widest py-3 px-6 rounded-xl transition-all shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/20"
                >
                    <ArrowLeft size={12} />
                    Volver al Seguimiento
                </Link>
            </div>
        </div>
    );
};

const ModuleGuard = ({ user, module, children }: { user: any, module: string, children: React.ReactNode }) => {
    const normalizedModule = normalize(module);
    const hasAccess = normalizedModule === normalize('Planeacion') || normalizedModule === normalize('Seguimiento')
        ? canViewAuthorizedModule(user, module)
        : canAccessModule(user, module);

    if (!hasAccess) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-12 text-center space-y-6">
                <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 border border-amber-500/20">
                    <ShieldAlert size={40} />
                </div>
                <div className="space-y-2">
                    <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Módulo Restringido</h2>
                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest max-w-xs">Tu perfil actual no tiene permisos para acceder al módulo de {module}.</p>
                    <Link to="/tracking" className="inline-block mt-4 text-emerald-500 font-black text-[10px] uppercase tracking-widest border-b border-emerald-500/30 pb-1">Volver al Seguimiento</Link>
                </div>
            </div>
        );
    }
    return <>{children}</>;
};

export default function App() {
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [globalMonth, setGlobalMonth] = useState(getCurrentPeriod('Mensual'));
    const [searchParams, setSearchParams] = useSearchParams();
    const urlInstId = searchParams.get('instId');
    const urlVpId = searchParams.get('vpId');
    const [selectedInstancia, setSelectedInstancia] = useState<string | null>(() => urlInstId);
    const [vpFilterId, setVpFilterId] = useState<string | null>(() => urlVpId);
    const skipStateToUrlSyncRef = useRef(false);
    const [hierarchy, setHierarchy] = useState<any>(null);
    const [toasts, setToasts] = useState<any[]>([]);

    useEffect(() => {
        globalShowToast = (message: string, type: any = 'success') => {
            const id = Date.now();
            setToasts(prev => [...prev, { id, message, type }]);
        };
    }, []);

    const [systemSettings, setSystemSettings] = useState<SystemSettings>({ ...DEFAULT_SETTINGS });

    // Puente de Sincronización v18.9.88: URL -> Global State
    useEffect(() => {
        const urlVp = searchParams.get('vpId');
        const urlInst = searchParams.get('instId');
        let hydratedFromUrl = false;
        if (urlVp && urlVp !== vpFilterId) {
            hydratedFromUrl = true;
            setVpFilterId(urlVp);
        }
        if (urlInst && urlInst !== selectedInstancia) {
            hydratedFromUrl = true;
            setSelectedInstancia(urlInst);
        }
        if (hydratedFromUrl) {
            skipStateToUrlSyncRef.current = true;
        }
    }, [searchParams]);

    // Sincronizar selectedInstancia y vpFilterId del estado local a la URL
    useEffect(() => {
        if (skipStateToUrlSyncRef.current) {
            skipStateToUrlSyncRef.current = false;
            return;
        }
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (selectedInstancia) {
            if (newParams.get('instId') !== selectedInstancia) {
                newParams.set('instId', selectedInstancia);
                changed = true;
            }
        } else {
            if (newParams.has('instId')) {
                newParams.delete('instId');
                changed = true;
            }
        }

        if (vpFilterId) {
            if (newParams.get('vpId') !== vpFilterId) {
                newParams.set('vpId', vpFilterId);
                changed = true;
            }
        } else {
            if (newParams.has('vpId')) {
                newParams.delete('vpId');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [selectedInstancia, vpFilterId, searchParams, setSearchParams]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = App_CSS;
        document.head.appendChild(style);
    }, []);

    const init = async () => {
        try {
            const { user } = await db.getCurrentSession();
            if (user) {
                setCurrentUser(user);
                if ((user.commission_id || user.instance_id) && !selectedInstancia) {
                    setSelectedInstancia(user.commission_id || user.instance_id);
                }
                if (user.role === 'SUPER_ADMIN') {
                    const list = await db.getUsers();
                    setUsers(list);
                }
                try {
                    const settings = await db.getSystemSettings();
                    if (settings) setSystemSettings(settings);
                    const h = await db.getHierarchy(user);
                    setHierarchy(h);
                } catch (e) {

                }
            }
        } catch (err) {
            console.error("Initialization failed", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    const handleLogout = async () => {
        await db.signOut();
        setCurrentUser(null);
    };

    if (loading) return (
        <div className="h-screen bg-black flex flex-col items-center justify-center space-y-6">
            <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
            <div className="text-emerald-500 font-black animate-pulse uppercase tracking-[0.5em] text-xs">Gobernanza Institucional...</div>
        </div>
    );

    if (!currentUser) {
        return <LoginPage onLogin={(user) => setCurrentUser(user)} />;
    }

    return (
        <SettingsContext.Provider value={systemSettings}>
            <Layout user={currentUser} users={users} onLogout={handleLogout} globalMonth={globalMonth} setGlobalMonth={setGlobalMonth}>
                <Routes>
                    <Route path="/" element={<LandingRedirect user={currentUser} />} />
                    <Route path="/hierarchy" element={<ModuleGuard user={currentUser} module="Estructura"><HierarchyModule user={currentUser} /></ModuleGuard>} />
                    <Route path="/planning" element={<ModuleGuard user={currentUser} module="Planeación"><PlanningModule user={currentUser} selectedInstancia={urlInstId ?? selectedInstancia} setSelectedInstancia={setSelectedInstancia} vpFilterId={urlVpId ?? vpFilterId} setVpFilterId={setVpFilterId} /></ModuleGuard>} />
                    <Route path="/tracking" element={<ModuleGuard user={currentUser} module="Seguimiento"><TrackingModule
                        user={currentUser}
                        selectedMonth={globalMonth}
                        setSelectedMonth={setGlobalMonth}
                        selectedInstancia={urlInstId ?? selectedInstancia}
                        setSelectedInstancia={setSelectedInstancia}
                        selectedVPId={urlVpId ?? vpFilterId}
                        setSelectedVPId={setVpFilterId}
                    /></ModuleGuard>} />
                    <Route path="/reports" element={<ModuleGuard user={currentUser} module="Reportes"><MonthlyReportModule user={currentUser} selectedMonth={globalMonth} setSelectedMonth={setGlobalMonth} /></ModuleGuard>} />
                    <Route path="/operational-control" element={<ModuleGuard user={currentUser} module="Control Operativo"><GovernanceOperationalControlCenter user={currentUser} selectedMonth={globalMonth} /></ModuleGuard>} />
                    <Route path="/talent" element={<ModuleGuard user={currentUser} module="Talento"><TalentModule user={currentUser} /></ModuleGuard>} />
                    <Route path="/memory" element={<ModuleGuard user={currentUser} module="Memoria"><MemoryModule user={currentUser} /></ModuleGuard>} />
                    <Route path="/ai-desk" element={<ModuleGuard user={currentUser} module="Escritorio IA"><AIDeskModule user={currentUser} /></ModuleGuard>} />
                    <Route path="/orgstructure" element={<ModuleGuard user={currentUser} module="OrgStructure"><OrgStructureModule user={currentUser} /></ModuleGuard>} />
                    <Route path="/sync" element={<AdminShield user={currentUser}><IndicatorSyncModule user={currentUser} /></AdminShield>} />
                    <Route path="/admin-users" element={<AdminShield user={currentUser}>
                        {import.meta.env.DEV && import.meta.env.VITE_ENABLE_RBAC_MOCK === 'true' ? (
                            <UsersRbacMockPanel />
                        ) : (
                            <UserManagementModule user={currentUser} />
                        )}
                    </AdminShield>} />
                    <Route path="/admin-settings" element={<AdminShield user={currentUser}><AdminSettingsModule user={currentUser} /></AdminShield>} />
                    <Route path="/admin-backups" element={<AdminShield user={currentUser} requireRecoveryManage><BackupRecoveryCenter user={currentUser} /></AdminShield>} />

                    {/* QW3: Catch-all Fallback seguro para evitar pantallas blancas */}
                    <Route path="*" element={<Safe404Fallback />} />
                </Routes>
                <AnimatePresence>
                    {toasts.map(t => (
                        <Toast
                            key={t.id}
                            message={t.message}
                            type={t.type}
                            onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                        />
                    ))}
                </AnimatePresence>
            </Layout>
        </SettingsContext.Provider>
    );
}

const Toast = ({ message, type, onClose }: { message: string, type: string, onClose: () => void }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 5000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const icons = {
        success: <CheckCircle2 className="text-emerald-400" size={20} />,
        error: <AlertCircle className="text-red-400" size={20} />,
        warning: <AlertTriangle className="text-amber-400" size={20} />,
        info: <Info className="text-blue-400" size={20} />
    };

    const colors = {
        success: "border-emerald-500/30 bg-emerald-500/10",
        error: "border-red-500/30 bg-red-500/10",
        warning: "border-amber-500/30 bg-amber-500/10",
        info: "border-blue-500/30 bg-blue-500/10"
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
            className={cn(
                "fixed bottom-8 right-8 z-[200] flex items-center gap-4 px-6 py-4 rounded-2xl border backdrop-blur-xl shadow-2xl min-w-[320px] max-w-md",
                colors[type as keyof typeof colors]
            )}
        >
            <div className="shrink-0">{icons[type as keyof typeof icons]}</div>
            <div className="flex-1">
                <p className="text-[11px] font-black text-white uppercase tracking-wider leading-tight">{message}</p>
            </div>
            <button onClick={onClose} className="text-white/20 hover:text-white transition-colors">
                <X size={16} />
            </button>
        </motion.div>
    );
}
